import {t, useLanguage, interfaceLocale} from './i18n';
import {regionMoney,assertSameCurrency} from './utils/businessRegionContext';
import {localInstant,businessZone,scheduleFields,wallDate,wallToInstant} from './utils/businessTime';
import {CustomerSessionPrograms} from './components/SessionPrograms';
import LoyaltySection from "./sections/LoyaltySection";
import MembershipSection from "./sections/MembershipSection";
import { isLoopPlatformOwner, getLoopEffectiveAuthIdentity } from "./utils/platformOwner";
import ScheduleExceptionDialog from "./adminCalendar/components/ScheduleExceptionDialog";
import { isApprovalFirstEvent } from "./utils/eventBookingFlow";
import { approveEventBooking } from "./utils/approveEventBooking";
import {withScheduleExceptionApproval, SCHEDULE_KEYS} from "./adminCalendar/scheduleExceptions";
import {useEditorNavigation} from "./hooks/useEditorNavigation";
import "./BusinessEditorPolish.css";

// LOOP BOOKING IOS WORKER CREATE STABILITY V17 — native worker create uses HTTP fallback and skips client CRM upsert.
// LOOP AGENDA IOS SERVER-SCOPED BOOKINGS RENDER V16J — operational worker render accepts backend-scoped worker booking events.
// LOOP AGENDA IOS BACKEND IDENTITY V16G — backend resolves email-mode iOS worker to real employee resource.
// LOOP AGENDA IOS WORKER IDENTITY V16F — iOS email-mode hydrates worker employeeId/profile before final paint.
// LOOP AGENDA IOS LOADER FALLBACK V16D — robust iOS debug panel + anti-infinite-loader safe paint.
// LOOP AGENDA IOS EMAIL SAFE PAINT V16E — iOS email-mode worker agenda paints after bookings fallback succeeds; no rich-profile deadlock.
// LOOP AGENDA CORRECT FIRST PAINT V16C — worker grid waits for rich profile signal before first visible agenda.
// LOOP AGENDA HYDRATION STABILITY V16B — instant worker grid keeps rich profile/photo/availability through final hydration.
// LOOP AGENDA INSTANT PAINT V16 — worker grid paints immediately with cached/route resource; data hydrates in layers.
// LOOP PENDING PANEL WORKER PARITY V15 — workers use the same PendingsPanel UI, scoped to their bookings/notification.
// LOOP WORKER FIRST PAINT LOCK V10B — never show generic worker agenda before final profile.
// LOOP WORKER CREATE + AGENDA SMOOTH V9B/V10 — close instantly, optimistic worker booking, stable worker first paint.
// LOOP WORKER CREATE SMOOTH V9 — close modal, optimistic event, and refresh after worker create.
// LOOP WORKER BOOKING CUSTOMER IDENTITY V8 — debug customer identity in worker-create payload.
// LOOP WORKER WEEK AVAILABILITY STRIPES V98 — Semana trabajador paints unavailable hours using same availability contract as Día.
// LOOP WORKER FINANCE VISUAL V97 — premium responsive Mis cobros layout for worker web/mobile.
// LOOP WORKER FINANCE PARITY V96 — Mis cobros uses the same revenue-booking status contract as BusinessAnalytics.
// LOOP WORKER FINANCE DATE NAV V95 — worker/quick finance can move day/week/month anchor.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — stable worker profile from employee doc/mirror; no fallback race.
// LOOP AGENDA MADRE WORKER HYDRATION RACE V85 — preserve real worker route/access profile when employees collection is denied or late.
// LOOP AGENDA MADRE WORKER RESOURCE PARITY V84 — worker uses same real employee resource/settings/stripes contract across web/tablet/native.
 // LOOP AGENDA MADRE WORKER SETTINGS/AVAILABILITY V83 — business visual settings always win; worker weekly availability wins over business weekly hours; global business blocks win over all.
 // LOOP AGENDA MADRE WORKER AVAILABILITY/PENDINGS V81 — business slot contract, worker/business availability intersection, employeeId-only pendings panel.
// LOOP AGENDA MADRE WORKER AVAILABILITY/PENDINGS HOTFIX V82 — define minutesToHHMM used by availability intersections.
// LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80 — strict worker panel permissions, employeeId-only pending badges, and route/logo context parity.
// LOOP AGENDA MADRE CLEANUP D7 V78 — remove obsolete responsive/native fallback CSS after V77 validation.
// LOOP AGENDA MADRE CLEANUP D6 V77 — compact duplicate event-card/toolbar CSS after D5 validation.
// LOOP AGENDA MADRE CLEANUP D5 V76 — remove obsolete V27/V28 native toolbar hardlocks after V75 validation.
// src/AdminBookingCalendar.jsx
// LOOP AGENDA MADRE CLEANUP D3 V74 — remove unused FullCalendar-era callbacks/debug-only mobile flags after agenda madre validation.
// LOOP AGENDA MADRE CLEANUP D2 V73 — remove dead legacy day/mobile/smart-create surfaces after agenda madre validation.
// LOOP AGENDA MADRE CLEANUP D1 V72 — remove dead native-list and legacy day fallback routes after agenda madre validation.
// LOOP AGENDA V40 — worker loader unblock + minWidth TDZ final fix.
// LOOP D2.9.4E.4 — clientes tab opens CRM directly instead of Projects/Portfolio wrapper
// LOOP WORKER PERMISSIONS SAVE HOTFIX — employee permission writes use native-safe Firestore paths.
// LOOP PHASE 2.9 — Inventory Automation Bridge in reservation lifecycle
// LOOP AGENDA — MOBILE ONE ROW HEADER HARD CLEANUP
// LOOP AGENDA — BULLETPROOF SINGLE SURFACE RENDER FIX
// LOOP AGENDA — WORKER DATE PARSE FIX HOTFIX
// LOOP TOP ACTIONS DATE STATUS HOTFIX
// LOOP DIRECT EDIT ON EVENT CLICK HOTFIX
// IMPORTANT: save as UTF-8 without BOM.
// LOOP WORKER OWN-ONLY V5 — workers never see company calendars.
// LOOP AGENDA — RESPONSIVE HEADER PANEL HOTFIX
// LOOP AGENDA WEEK UNIFIED HOTFIX
// LOOP AGENDA VIEW CARDS UNIFIED HOTFIX
// LOOP AGENDA — RESPONSIVE EVENT CARD DENSITY HOTFIX
// LOOP AGENDA — BUBBLE HEIGHT FIX HOTFIX
// LOOP BOOKING — EFFICIENCY SCROLL RETAIL HOTFIX
// LOOP PHASE 3C.6 — Caja shortcut opens unified Finanzas report.
// LOOP PHASE 4J — Web mobile compact toolbar actions stabilized: phone icon-only, tablet view switch visible.
// LOOP PHASE 4F — Web mobile header nudge.
// LOOP PHASE 4C — Mobile header overlay measurement + no toolbar under app header.
// LOOP PHASE 4B — Mobile header gap + worker agenda loading failsafe.
// LOOP PHASE 4A — Unified mobile agenda surface + compact mobile header audit.
// LOOP MOBILE UNIFIED V42 — web/mobile/app day surface parity: no empty overlay in worker day view.
// LOOP AGENDA BUSINESS NAME GUARD V66 — worker/company sidebars never show Firebase IDs while names hydrate.
// LOOP AGENDA MADRE PHASE A V68 — Day view always uses the mother ResourceDayGrid for owner/worker/no-worker.
// LOOP AGENDA MADRE PHASE B V69 — Week view uses the same mother agenda shell/resources for owner/worker/native.
// LOOP AGENDA MADRE PHASE B V70 — remove redundant week identity strip so Semana starts directly under toolbar.
// LOOP AGENDA MADRE PHASE C V71 — Month view joins the same mother agenda data/resource contract.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { readCalendarSection, calendarSectionSearch, permittedCalendarSection } from "./utils/calendarSectionRoute";

import { runTransaction, collection, doc, updateDoc, setDoc, serverTimestamp, addDoc, deleteDoc, Timestamp, getDoc, getDocs, query, where, writeBatch } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, auth } from "./firebase";
import { findOrCreateCustomerForBusiness } from "./utils/customerCrm";
import fsClient from "./data/firestoreClient";
import { isIosCapacitor, httpRequestJson } from "./utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "./utils/manualAuth";
import { loopBusinessNow, loopBusinessUpdateDoc } from "./utils/businessNativeSafeWrites";
import PendingsPanel from "./PendingsPanel";
import WorkerPendings from "./WorkerPendings";

import BusinessMobileNavigation from "./adminCalendar/components/BusinessMobileNavigation";
import "./adminCalendar/MobileWorkspace.css";
import MiniMonth from "./components/MiniMonth";
import BookingCreateModal from "./components/BookingCreateModal";
import { isSoloBookingBusiness } from "./utils/soloBookingBusiness";
import { calendarEmployeeId, projectSoloCalendarEvent } from "./utils/soloCalendarEvent";
import AgendaMonthView from './adminCalendar/components/AgendaMonthView';
import AgendaRangePanel from './adminCalendar/components/AgendaRangePanel';
import {weekRangeLabel,groupDenseAgendaEvents} from './utils/agendaRangeUi';
import EventViewModal from "./components/EventViewModal";
import BookingPaymentReviewPanel from "./components/BookingPaymentReviewPanel";
import BusinessAnalytics from "./components/BusinessAnalytics";
import CustomersPage from "./pages/CustomersPage";
import ServicesSection from "./sections/ServicesSection";
import WorkersSection from "./sections/WorkersSection";
import {manageWorkerAccess} from './services/workerAccess';

import "./AdminBookingCalendar.css";
import "./AgendaPolish.css";

import planLoopEarlyAccessCard from "./assets/plan-cards/plan-loop-early-access.png";
import planLoopPremiumCard from "./assets/plan-cards/plan-loop-premium.png";
import planLoopBlackCard from "./assets/plan-cards/plan-loop-black.png";
import planLoopBasicoCard from "./assets/plan-cards/plan-loop-basico.png";

import { startOfDay, startOfWeek, bookingCalendarDate as bookingScheduledAtToDate, formatSelectedDateHuman } from "./utils/dateUtils";

import { useAdminCalendarData } from "./adminCalendar/useAdminCalendarData";
import {
  applyBusinessSettingsToPermissions,
  getRolePermissionPreset,
  normalizeLoopBusinessSettings,
  normalizeWorkerPermissions,
} from "./adminCalendar/workerPermissions";
import { computeClickedDateFromEvent, snapDownToMinutes } from "./adminCalendar/timeClickUtils";
import {
  bookingLifecycleFromRecord,
  buildBookingLifecyclePatch,
  normalizeBookingStatus as normalizeCanonicalBookingStatus,
  normalizePaymentStatus as normalizeCanonicalPaymentStatus,
  shouldAutoDeductInventoryForBooking,
  hasInventoryDeducted,
} from "./utils/bookingLifecycle";


import {
  handleEventDropFactory,
  handleEventResizeFactory,
} from "./adminCalendar/calendarMutations";
import {
  buildBookingStatusActionPatch,
  buildBookingPaymentActionPatch,
} from "./adminCalendar/bookingActions";
import BookingEventCard from "./adminCalendar/components/BookingEventCard";
import AgendaDayList from "./adminCalendar/components/AgendaDayList";
import { positiveDimension, agendaWorkerColumnWidth, resolveAgendaPermission, toggleAgendaWorker, validAgendaRange, authorizedAgendaWorkers } from "./adminCalendar/agendaExperience";
import "./adminCalendar/AgendaExperience.css";
import "./adminCalendar/AgendaRangeViews.css";
import CalendarToolbar from "./adminCalendar/components/CalendarToolbar";

// ✅ Native UI pieces live under adminCalendar/nativeUi/
import useNativeInsets from "./adminCalendar/nativeUi/useNativeInsets";
import useAdminCalendarViewConfig from "./adminCalendar/nativeUi/useAdminCalendarViewConfig";


function getBookingRawFromCalendarEvent(eventLike) {
  const extended = eventLike?.extendedProps || {};
  return extended.raw || extended.booking || extended || {};
}

function normalizeBookingStatusLabel(statusValue) {
  const s = String(statusValue || "").trim().toLowerCase();
  if (["confirmed", "confirmada", "approved", "aprobada", "accepted"].includes(s)) {
    return { label: "Confirmada", tone: "confirmed" };
  }
  if (["pending", "pendiente", "to_confirm", "por_confirmar"].includes(s)) {
    return { label: "Pendiente", tone: "pending" };
  }
  if (["cancelled", "canceled", "cancelada"].includes(s)) {
    return { label: "Cancelada", tone: "cancelled" };
  }
  if (["rejected", "declined", "rechazada"].includes(s)) {
    return { label: "Rechazada", tone: "cancelled" };
  }
  return { label: s ? s.charAt(0).toUpperCase() + s.slice(1) : "Reserva", tone: "neutral" };
}

function getBookingClientName(raw, fallback = "Cliente") {
  return raw?.customerName || raw?.clientName || raw?.name || raw?.customer?.name || fallback;
}

function getBookingServiceName(raw, fallback = "Reserva") {
  return raw?.serviceName || raw?.service || raw?.packageName || raw?.serviceTitle || fallback;
}

function getServiceToneIndex(value) {
  const text = String(value || "Reserva").toLowerCase();
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 997;
  }
  return hash % 6;
}

function getBookingWorkerName(raw) {
  return String(
    raw?.employeeName ||
    raw?.workerName ||
    raw?.assignedEmployeeName ||
    raw?.resourceName ||
    raw?.employee?.name ||
    raw?.worker?.name ||
    ""
  ).trim();
}


function getBookingPriceLabel(raw) {
  const value = raw?.totalPrice ?? raw?.total ?? raw?.price ?? raw?.servicePrice ?? null;
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

function getBookingDurationMinutesForCard(raw, start, end) {
  const explicit = Number(raw?.duration ?? raw?.durationMins ?? raw?.minutes ?? raw?.visitMinutes ?? 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  if (start && end) {
    const diff = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000);
    if (Number.isFinite(diff) && diff > 0) return diff;
  }
  return null;
}

function getBookingToneClass(raw, serviceName, colorMode = "service", statusInfo = null) {
  const safeMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
  if (safeMode === "status") {
    const tone = statusInfo?.tone || normalizeBookingStatusLabel(raw?.status).tone || "neutral";
    return `status-tone-${tone}`;
  }
  if (safeMode === "worker") {
    const worker = getBookingWorkerName(raw) || raw?.employeeId || raw?.workerId || "Sin trabajador";
    return `worker-tone-${getServiceToneIndex(worker)}`;
  }
  return `service-tone-${getServiceToneIndex(serviceName || getBookingServiceName(raw))}`;
}

function getBookingAccent(raw, serviceName, colorMode = "service", statusInfo = null) {
  const safeMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
  const servicePalette = ["#0f766e", "#7c3aed", "#2563eb", "#db2777", "#ea580c", "#0891b2"];
  const workerPalette = ["#047857", "#6d28d9", "#1d4ed8", "#be185d", "#c2410c", "#0e7490"];
  const statusPalette = {
    confirmed: "#0f766e",
    pending: "#d97706",
    cancelled: "#dc2626",
    neutral: "#64748b",
  };
  if (safeMode === "status") return statusPalette[statusInfo?.tone || "neutral"] || statusPalette.neutral;
  if (safeMode === "worker") return workerPalette[getServiceToneIndex(getBookingWorkerName(raw) || raw?.employeeId || raw?.workerId || "worker")];
  return raw?.accent || raw?.color || servicePalette[getServiceToneIndex(serviceName || getBookingServiceName(raw))];
}

// LOOP AGENDA MADRE CLEANUP D3 V74
// Removed unused ProCalendarEventCard. Custom agenda renderers use BookingEventCard / inline Week-Month cards directly.
function LoopAdminPanelIcon({ name }) {
  const common = {
    width: 17,
    height: 17,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.85,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (name === "agenda") {
    return (
      <svg {...common}>
        <path d="M7 3v4" />
        <path d="M17 3v4" />
        <path d="M4.5 8.5h15" />
        <rect x="4" y="5" width="16" height="15.5" rx="3" />
        <path d="M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01" />
      </svg>
    );
  }

  if (name === "reservas") {
    return (
      <svg {...common}>
        <path d="M7 3v4" />
        <path d="M17 3v4" />
        <rect x="4" y="5" width="16" height="15.5" rx="3" />
        <path d="M4.5 9h15" />
        <path d="M8 13h8" />
        <path d="M8 16h5" />
      </svg>
    );
  }

  if (name === "clientes") {
    return (
      <svg {...common}>
        <path d="M16 19c0-2.2-1.8-4-4-4H8c-2.2 0-4 1.8-4 4" />
        <circle cx="10" cy="8" r="3.2" />
        <path d="M18 11.5a2.7 2.7 0 1 0-1.1-5.2" />
        <path d="M20.5 19c0-1.7-1.1-3.1-2.7-3.6" />
      </svg>
    );
  }

  if (name === "servicios") {
    return (
      <svg {...common}>
        <path d="M5 21V7.5A2.5 2.5 0 0 1 7.5 5H16a3 3 0 0 1 3 3v13" />
        <path d="M3.5 21h17" />
        <path d="M8 9h2.5M8 12.5h2.5M8 16h2.5" />
        <path d="M14.5 21v-5.5h2.3V21" />
      </svg>
    );
  }

  if (name === "equipo") {
    return (
      <svg {...common}>
        <circle cx="8" cy="8.2" r="3" />
        <circle cx="16.5" cy="9.2" r="2.4" />
        <path d="M3.5 19c.5-2.8 2.4-4.4 4.5-4.4s4 1.6 4.5 4.4" />
        <path d="M13.8 18.6c.5-2 1.8-3.2 3.5-3.2 1.5 0 2.8 1 3.2 2.9" />
      </svg>
    );
  }

  if (name === "fidelidad") {
    return (
      <svg {...common}>
        <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" />
      </svg>
    );
  }

  if (name === "reportes") {
    return (
      <svg {...common}>
        <path d="M4 19.5h16" />
        <path d="M7 16v-5" />
        <path d="M12 16V6.5" />
        <path d="M17 16v-8" />
        <path d="M5 5.5h14" />
      </svg>
    );
  }

  if (name === "finanzas") {
    return (
      <svg {...common}>
        <path d="M4 19.5h16" />
        <path d="M7 14.5c1.4 1.2 3.7 1.2 5.1.2 1.1-.8 1-2.3-.2-3-1-.6-2.8-.7-3.8-1.2-1.2-.6-1.3-2.1-.2-2.9 1.3-.9 3.5-.8 4.8.2" />
        <path d="M9.8 5.3v12.4" />
        <path d="M15.5 9.5h3.2" />
        <path d="M15.5 13h3.2" />
      </svg>
    );
  }

  if (name === "resumen") {
    return (
      <svg {...common}>
        <rect x="4" y="4" width="6.5" height="6.5" rx="1.5" />
        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" />
        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" />
        <path d="M14 17h6" />
        <path d="M17 14v6" />
      </svg>
    );
  }

  if (name === "inventario") {
    return (
      <svg {...common}>
        <path d="M4.5 8.5 12 4l7.5 4.5L12 13 4.5 8.5Z" />
        <path d="M4.5 8.5V16L12 20l7.5-4V8.5" />
        <path d="M12 13v7" />
      </svg>
    );
  }

  if (name === "membresias") {
    return (
      <svg {...common}>
        <rect x="3.5" y="5" width="17" height="14" rx="3" />
        <path d="M7 9.5h6" />
        <path d="M7 13h4" />
        <circle cx="16.5" cy="13.2" r="1.8" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2.1M12 19.1v2.1M4.2 4.2l1.5 1.5M18.3 18.3l1.5 1.5M2.8 12h2.1M19.1 12h2.1M4.2 19.8l1.5-1.5M18.3 5.7l1.5-1.5" />
    </svg>
  );
}

function AgendaLinkedSectionHeader({ title, subtitle = "", onBack, onSave = null, saveLabel = "Guardar cambios", saving = false }) {
  return (
    <div className="loop-admin-linked-header" role="region" aria-label={`Navegación de ${title || "sección"}`}>

      <div className="loop-admin-linked-title">
        <span>{subtitle || t("Panel del negocio")}</span>
        <strong>{title}</strong>
      </div>
      {onSave ? (
        <button type="button" className="loop-admin-linked-save" onClick={onSave} disabled={saving}>
          {saving ? t("Guardando...") : saveLabel}
        </button>
      ) : null}
    </div>
  );
}

function AgendaWorkerSetupLock({ businessName = "tu negocio", onGoWorkers, onGoServices }) {
  const safeBusinessName = String(businessName || "tu negocio").trim() || "tu negocio";
  return (
    <section className="loop-agenda-worker-setup-lock" role="region" aria-label={t("Configura tu primer trabajador")}>
      <div className="loop-agenda-worker-setup-card">
        <div className="loop-agenda-worker-setup-icon" aria-hidden="true">
          <LoopAdminPanelIcon name="equipo" />
        </div>
        <div className="loop-agenda-worker-setup-copy">
          <span>{t("Agenda bloqueada")}</span>
          <h2>{t("Configura tu primer trabajador para usar la agenda")}</h2>
          <p>
            {safeBusinessName} necesita al menos una persona o agenda de equipo para saber quién atiende cada reserva.
            Esto evita reservas sin asignar, horarios raros y datos incompletos.
          </p>
        </div>
        <div className="loop-agenda-worker-setup-actions">
          <button type="button" className="primary" onClick={onGoWorkers}>{t("Ir a Equipo")}</button>
          <button type="button" className="secondary" onClick={onGoServices}>{t("Revisar servicios")}</button>
        </div>
        <div className="loop-agenda-worker-setup-steps" aria-label={t("Pasos recomendados")}>
          <div>
            <strong>1</strong>
            <span>{t("Crea un trabajador con tu nombre o el de la persona que atiende.")}</span>
          </div>
          <div>
            <strong>2</strong>
            <span>{t("Asigna servicios y disponibilidad.")}</span>
          </div>
          <div>
            <strong>3</strong>
            <span>{t("Vuelve a Agenda para crear reservas normalmente.")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}



// LOOP AGENDA PLAN CARD V2 — harmonized with BillingSection plan names and assets.
const LOOP_AGENDA_PLAN_CARD_META = {
  early_access: {
    image: planLoopEarlyAccessCard,
    label: "Plan Loop Early Access",
    tone: "early-access",
  },
  basico: {
    image: planLoopBasicoCard,
    label: "Plan Loop Básico",
    tone: "basico",
  },
  premium: {
    image: planLoopPremiumCard,
    label: "Plan Loop Premium",
    tone: "premium",
  },
  black: {
    image: planLoopBlackCard,
    label: "Plan Loop Black",
    tone: "black",
  },
};

function normalizeAgendaLoopPlanKey(value) {
  const raw = String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

  if (!raw) return "";
  if (raw.includes("early") || raw.includes("access") || raw.includes("trial") || raw.includes("launch") || raw.includes("beta") || raw === "free") return "early_access";
  if (raw.includes("black") || raw.includes("elite") || raw.includes("enterprise") || raw.includes("vip")) return "black";
  if (raw.includes("premium")) return "premium";
  if (raw.includes("basic") || raw.includes("basico") || raw.includes("pro") || raw.includes("starter")) return "basico";
  return "";
}

function getAgendaLoopPlanCard(businessDoc = {}) {
  const candidates = [
    businessDoc?.billing?.plan,
    businessDoc?.billing?.planKey,
    businessDoc?.billing?.tier,
    businessDoc?.billing?.status,
    businessDoc?.billingPlan,
    businessDoc?.billingPlanKey,
    businessDoc?.loopPlan,
    businessDoc?.plan,
    businessDoc?.planName,
    businessDoc?.subscriptionPlan,
    businessDoc?.subscriptionTier,
    businessDoc?.tier,
    businessDoc?.businessOsPlan,
    businessDoc?.businessOSPlan,
    businessDoc?.planTier,
    businessDoc?.subscription?.plan,
    businessDoc?.subscription?.tier,
    businessDoc?.businessOs?.plan,
    businessDoc?.businessOS?.plan,
  ];

  for (const candidate of candidates) {
    const key = normalizeAgendaLoopPlanKey(candidate);
    if (key && LOOP_AGENDA_PLAN_CARD_META[key]) return LOOP_AGENDA_PLAN_CARD_META[key];
  }

  if (businessDoc?.billing?.earlyAccess === true || businessDoc?.earlyAccess === true) {
    return LOOP_AGENDA_PLAN_CARD_META.early_access;
  }

  return LOOP_AGENDA_PLAN_CARD_META.early_access;
}


function adminManualDateInputValue(raw) {
  const d = bookingScheduledAtToDate(raw) || raw?.start || raw?.date || raw?.scheduledAt || new Date();
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function adminManualTimeInputValue(raw) {
  const d = bookingScheduledAtToDate(raw) || raw?.start || raw?.date || raw?.scheduledAt || new Date();
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "09:00";
  return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
}

function adminManualBuildDate(dateValue, timeValue) {
  const date = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const time = String(timeValue || "09:00").match(/^(\d{1,2}):(\d{2})$/);
  const base = date ? new Date(Number(date[1]), Number(date[2]) - 1, Number(date[3])) : new Date();
  base.setHours(time ? Number(time[1]) : 9, time ? Number(time[2]) : 0, 0, 0);
  return base;
}

function adminManualBookingDuration(raw) {
  const value = Number(raw?.durationMinutes ?? raw?.durationMins ?? raw?.duration ?? raw?.minutes ?? raw?.visitMinutes ?? 60);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 60;
}

function adminManualPriceValue(raw) {
  const value = raw?.totalPrice ?? raw?.total ?? raw?.price ?? raw?.servicePrice ?? "";
  if (value === null || value === undefined) return "";
  return String(value);
}

function adminManualBookingToDraft(raw = {}) {
  return {
    customerName: raw.customerName || raw.clientName || raw.name || raw.customer?.name || "",
    customerPhone: raw.customerPhone || raw.clientPhone || raw.phone || raw.customer?.phone || raw.customerSnapshot?.phone || "",
    customerEmail: raw.customerEmail || raw.clientEmail || raw.email || raw.customer?.email || raw.customerSnapshot?.email || "",
    serviceId: raw.serviceId || raw.service || "",
    employeeId: raw.employeeId || raw.workerId || raw.resourceId || raw.assignedEmployeeId || "",
    date: adminManualDateInputValue(raw),
    startTime: adminManualTimeInputValue(raw),
    duration: String(adminManualBookingDuration(raw)),
    totalPrice: adminManualPriceValue(raw),
    status: raw.status || raw.confirmationStatus || "confirmed",
    notes: raw.notes || raw.internalNotes || raw.adminNotes || raw.comment || "",
  };
}

function adminManualStatusLabel(value) {
  return adminStatusFlowMeta(value).label || "Reserva";
}


function adminSmartDateInputValue(date) {
  const d = date ? new Date(date) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function adminSmartTimeInputValue(date) {
  const d = date ? new Date(date) : new Date();
  if (Number.isNaN(d.getTime())) return "09:00";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function adminSmartBuildDate(dateValue, timeValue) {
  const date = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const time = String(timeValue || "09:00").match(/^(\d{1,2}):(\d{2})$/);
  const base = date ? new Date(Number(date[1]), Number(date[2]) - 1, Number(date[3])) : new Date();
  base.setHours(time ? Number(time[1]) : 9, time ? Number(time[2]) : 0, 0, 0);
  return base;
}

function adminSmartServiceDuration(service, fallback = 60) {
  const value = Number(
    service?.durationMinutes ??
    service?.durationMins ??
    service?.duration ??
    service?.minutes ??
    service?.defaultDuration ??
    fallback
  );
  return Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;
}

function adminSmartServicePrice(service) {
  const value = service?.price ?? service?.basePrice ?? service?.totalPrice ?? service?.amount ?? "";
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)) : String(value);
}

function adminSmartServiceLabel(service) {
  return service?.name || service?.title || service?.label || service?.serviceName || "Servicio";
}

function adminSmartWorkerLabel(worker) {
  return worker?.name || worker?.displayName || worker?.employeeName || worker?.email || "Trabajador";
}

function adminSmartCustomerLabel(customer) {
  return customer?.name || customer?.customerName || customer?.clientName || "Cliente";
}

function adminSmartUniqueCustomersFromBookings(bookings = []) {
  const map = new Map();
  (Array.isArray(bookings) ? bookings : []).forEach((b) => {
    const raw = b?.extendedProps?.raw || b?.raw || b || {};
    const name = raw.customerName || raw.clientName || raw.name || raw.customer?.name || raw.customerSnapshot?.name || "";
    const phone = raw.customerPhone || raw.clientPhone || raw.phone || raw.customer?.phone || raw.customerSnapshot?.phone || "";
    const email = raw.customerEmail || raw.clientEmail || raw.email || raw.customer?.email || raw.customerSnapshot?.email || "";
    if (!name && !phone && !email) return;
    const key = String(raw.customerId || raw.clientId || email || phone || name).toLowerCase();
    if (!map.has(key)) {
      map.set(key, { id: raw.customerId || raw.clientId || null, name, phone, email });
    }
  });
  return Array.from(map.values()).slice(0, 12);
}



const ADMIN_BOOKING_STATUS_FLOW = [
  { value: "pending", label: "Pendiente", tone: "pending", helper: "Esperando confirmación" },
  { value: "confirmed", label: "Confirmada", tone: "confirmed", helper: "Lista en agenda" },
  { value: "in_progress", label: "En progreso", tone: "progress", helper: "Atendiéndose ahora" },
  { value: "completed", label: "Completada", tone: "completed", helper: "Servicio finalizado" },
  { value: "no_show", label: "No asistió", tone: "warning", helper: "Cliente no llegó" },
  { value: "cancelled", label: "Cancelada", tone: "cancelled", helper: "Reserva cancelada" },
  { value: "deleted", label: "Eliminada", tone: "cancelled", helper: "Oculta de agenda" },
  { value: "rejected", label: "Rechazada", tone: "cancelled", helper: "Solicitud rechazada" },
];

function adminStatusFlowNormalize(value) {
  const s = String(value || "").trim().toLowerCase();
  if (["approved", "accepted", "confirmada", "confirmed"].includes(s)) return "confirmed";
  if (["pendiente", "pending", "to_confirm", "por_confirmar"].includes(s)) return "pending";
  if (["started", "in-progress", "in_progress", "en_progreso"].includes(s)) return "in_progress";
  if (["done", "complete", "completed", "completada"].includes(s)) return "completed";
  if (["no-show", "noshow", "no_show", "no asistió", "no_asistio"].includes(s)) return "no_show";
  if (["deleted", "eliminada", "removed", "removed_from_calendar"].includes(s)) return "deleted";
  if (["cancelled", "canceled", "cancelada"].includes(s)) return "cancelled";
  if (["rejected", "declined", "rechazada"].includes(s)) return "rejected";
  return s || "pending";
}

function adminStatusFlowMeta(value) {
  const normalized = adminStatusFlowNormalize(value);
  return ADMIN_BOOKING_STATUS_FLOW.find((item) => item.value === normalized) || ADMIN_BOOKING_STATUS_FLOW[0];
}

function adminStatusFlowTimestampField(value) {
  const status = adminStatusFlowNormalize(value);
  if (status === "confirmed") return "confirmedAt";
  if (status === "in_progress") return "startedAt";
  if (status === "completed") return "completedAt";
  if (status === "no_show") return "noShowAt";
  if (status === "cancelled") return "cancelledAt";
  if (status === "deleted") return "deletedAt";
  if (status === "rejected") return "rejectedAt";
  return "statusChangedAt";
}

function adminStatusFlowCurrent(raw = {}) {
  return adminStatusFlowNormalize(raw?.status || raw?.confirmationStatus || raw?.state || "pending");
}

function adminBookingIdCandidates(raw = {}) {
  return [raw?.id, raw?.bookingId, raw?.docId, raw?._id]
    .map((x) => String(x || "").trim())
    .filter(Boolean);
}

function adminBookingIsHiddenFromAgenda(raw = {}) {
  if (!raw) return false;
  if (raw.calendarHidden === true || raw.hiddenFromCalendar === true || raw.removedFromAgenda === true) return true;
  if (raw.deletedAt || raw.removedFromAgendaAt) return true;
  const status = adminStatusFlowNormalize(raw.status || raw.confirmationStatus || raw.state || raw.bookingStatus || "");
  return status === "deleted" || status === "removed";
}

function adminBookingMatchesLocalHidden(raw = {}, hiddenIds = new Set()) {
  if (!hiddenIds || !hiddenIds.size) return false;
  return adminBookingIdCandidates(raw).some((id) => hiddenIds.has(id));
}

function adminCalendarEventIsHiddenFromAgenda(eventLike, hiddenIds = new Set()) {
  const kind = eventLike?.extendedProps?.kind || eventLike?.kind || "";
  if (kind === "outside-hours") return false;
  const raw = getBookingRawFromCalendarEvent(eventLike);
  return adminBookingIsHiddenFromAgenda(raw) || adminBookingMatchesLocalHidden(raw, hiddenIds);
}


function adminInventoryNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function adminInventoryUsageRows(value) {
  return (Array.isArray(value) ? value : [])
    .map((row, idx) => {
      const itemId = String(row?.itemId || row?.inventoryItemId || row?.id || "").trim();
      const quantityUsed = adminInventoryNumber(row?.quantityUsed ?? row?.quantity ?? row?.qty, 0);
      if (!itemId || quantityUsed <= 0) return null;
      return { id: row?.id || `usage-${itemId}-${idx}`, itemId, quantityUsed };
    })
    .filter(Boolean);
}

function adminInventoryBookingServiceId(raw = {}) {
  return String(raw?.serviceId || raw?.serviceID || raw?.serviceKey || raw?.serviceRef || raw?.service || "").trim();
}


async function adminListInventoryItemsForBusiness({ dbRef, businessId }) {
  if (!businessId) return [];
  if (isIosCapacitor()) {
    const rows = await fsClient.listCollectionByPath(`businesses/${businessId}/inventoryItems`, {}, { db: dbRef });
    return (Array.isArray(rows) ? rows : []).map((item) => ({ id: item.id || item.itemId, ...(item || {}) }));
  }
  const snap = await getDocs(collection(dbRef, `businesses/${businessId}/inventoryItems`));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
}

async function adminUpdateInventoryItemSafe({ dbRef, businessId, itemId, payload }) {
  if (!businessId || !itemId) return;
  const path = `businesses/${businessId}/inventoryItems/${itemId}`;
  if (isIosCapacitor()) return fsClient.updateDocByPath(path, payload, { db: dbRef });
  return updateDoc(doc(dbRef, path), payload);
}

async function adminAddInventoryMovementSafe({ dbRef, businessId, payload }) {
  if (!businessId || !payload) return;
  const path = `businesses/${businessId}/inventoryMovements`;
  if (isIosCapacitor()) return fsClient.addDocToCollectionPath(path, payload, { db: dbRef });
  return addDoc(collection(dbRef, path), payload);
}

function adminInventoryServiceHasUsage(service = {}) {
  return adminInventoryUsageRows(service?.inventoryUsage || service?.inventoryConsumption || service?.materialsUsage || service?.materials || []).length > 0;
}

async function adminBuildInventoryConsumptionSnapshot({ dbRef, businessId, booking, services = [] }) {
  const serviceId = adminInventoryBookingServiceId(booking);
  const service = (Array.isArray(services) ? services : []).find((svc) => String(svc?.id) === String(serviceId));
  const usageRows = adminInventoryUsageRows(service?.inventoryUsage || service?.inventoryConsumption || service?.materialsUsage || service?.materials || []);
  if (!businessId || !serviceId || !usageRows.length) {
    return { rows: [], totalCost: 0, serviceId, serviceName: booking?.serviceName || service?.name || "Servicio" };
  }

  const inventoryRows = await adminListInventoryItemsForBusiness({ dbRef, businessId });
  const itemMap = new Map(inventoryRows.map((item) => [String(item.id), item]));

  const rows = usageRows.map((row) => {
    const item = itemMap.get(String(row.itemId)) || {};
    const unitCost = adminInventoryNumber(item.unitCost ?? item.cost, 0);
    const quantityUsed = adminInventoryNumber(row.quantityUsed, 0);
    return {
      itemId: row.itemId,
      itemName: item.name || item.title || row.itemId,
      unit: item.unit || "un.",
      quantityUsed,
      unitCost,
      totalCost: quantityUsed * unitCost,
      stockBefore: adminInventoryNumber(item.currentStock, 0),
      stockAfter: adminInventoryNumber(item.currentStock, 0) - quantityUsed,
      insufficientStock: adminInventoryNumber(item.currentStock, 0) < quantityUsed,
    };
  });

  return {
    rows,
    totalCost: rows.reduce((sum, row) => sum + adminInventoryNumber(row.totalCost, 0), 0),
    serviceId,
    serviceName: booking?.serviceName || service?.name || service?.title || "Servicio",
  };
}

async function adminApplyInventoryConsumption({ dbRef, businessId, booking, services = [], actor = {}, loopSettings = {} }) {
  if (!businessId || !booking?.id) return null;
  if (!adminShouldAutoDeductInventory(booking, loopSettings)) return null;
  if (hasInventoryDeducted(booking) || booking.inventoryDeductedAt || booking.inventoryDeductionStatus === "deducted") {
    return {
      inventoryDeductionStatus: "deducted",
      inventoryAutoDeductStatus: "already_deducted",
      inventoryAutoDeductCheckedAt: serverTimestamp(),
    };
  }

  const snapshot = await adminBuildInventoryConsumptionSnapshot({ dbRef, businessId, booking, services });
  if (!snapshot.rows.length) {
    await adminWriteAuditLog({
      dbRef,
      businessId,
      eventType: "inventory_deduction_skipped",
      booking,
      actor,
      details: {
        reason: "service_has_no_materials",
        trigger: adminInventoryDeductTrigger(loopSettings),
        source: "service_consumption",
        serviceId: snapshot.serviceId || null,
        serviceName: snapshot.serviceName || null,
      },
    });
    return {
      inventoryDeductionStatus: "not_required",
      inventoryAutoDeductStatus: "not_required",
      inventoryAutoDeductCheckedAt: serverTimestamp(),
      inventoryDeductTrigger: adminInventoryDeductTrigger(loopSettings),
      inventoryCostAmount: 0,
      materialCostAmount: 0,
      inventoryConsumed: [],
    };
  }

  const allowNegativeStock = loopSettings?.inventorySettings?.allowNegativeStock === true;
  const insufficientRows = snapshot.rows.filter((row) => row.insufficientStock === true);
  if (insufficientRows.length && !allowNegativeStock) {
    const names = insufficientRows.map((row) => `${row.itemName || row.itemId} (${row.stockBefore} disponible, ${row.quantityUsed} requerido)`).join(", ");
    await adminWriteAuditLog({
      dbRef,
      businessId,
      eventType: "inventory_deduction_failed",
      booking,
      actor,
      severity: "warning",
      details: {
        reason: "stock_insufficient",
        trigger: adminInventoryDeductTrigger(loopSettings),
        source: "service_consumption",
        rows: insufficientRows.map((row) => ({
          itemId: row.itemId,
          itemName: row.itemName,
          stockBefore: row.stockBefore,
          quantityRequired: row.quantityUsed,
        })),
      },
    });
    const err = new Error(`stock_insufficient:${names}`);
    err.code = "stock_insufficient";
    err.rows = insufficientRows;
    throw err;
  }

  if (isIosCapacitor()) {
    for (const row of snapshot.rows) {
      await adminUpdateInventoryItemSafe({
        dbRef,
        businessId,
        itemId: row.itemId,
        payload: {
          currentStock: row.stockAfter,
          updatedAt: serverTimestamp(),
          lastConsumedAt: serverTimestamp(),
          lastConsumedByBookingId: booking.id,
        },
      });
      await adminAddInventoryMovementSafe({
        dbRef,
        businessId,
        payload: {
          type: "booking_consumption",
          bookingId: booking.id,
          serviceId: snapshot.serviceId || null,
          serviceName: snapshot.serviceName || null,
          itemId: row.itemId,
          itemName: row.itemName,
          unit: row.unit,
          quantity: -Math.abs(row.quantityUsed),
          unitCost: row.unitCost,
          totalCost: row.totalCost,
          stockBefore: row.stockBefore,
          stockAfter: row.stockAfter,
          insufficientStock: row.insufficientStock === true,
          createdAt: serverTimestamp(),
          createdByUid: actor.uid || null,
          createdByEmail: actor.email || null,
        },
      });
    }
    await adminWriteAuditLog({
      dbRef,
      businessId,
      eventType: "inventory_deducted",
      booking,
      actor,
      details: {
        source: "service_consumption",
        trigger: adminInventoryDeductTrigger(loopSettings),
        rowCount: snapshot.rows.length,
        totalCost: snapshot.totalCost,
        negativeStockWarning: snapshot.rows.some((row) => row.insufficientStock === true),
        rows: snapshot.rows.slice(0, 20).map((row) => ({
          itemId: row.itemId,
          itemName: row.itemName,
          quantity: row.quantityUsed,
          stockBefore: row.stockBefore,
          stockAfter: row.stockAfter,
        })),
      },
    });
  } else {
    const batch = writeBatch(dbRef);
    snapshot.rows.forEach((row) => {
      const itemRef = doc(dbRef, `businesses/${businessId}/inventoryItems/${row.itemId}`);
      batch.update(itemRef, {
        currentStock: row.stockAfter,
        updatedAt: serverTimestamp(),
        lastConsumedAt: serverTimestamp(),
        lastConsumedByBookingId: booking.id,
      });
      const movementRef = doc(collection(dbRef, `businesses/${businessId}/inventoryMovements`));
      batch.set(movementRef, {
        type: "booking_consumption",
        bookingId: booking.id,
        serviceId: snapshot.serviceId || null,
        serviceName: snapshot.serviceName || null,
        itemId: row.itemId,
        itemName: row.itemName,
        unit: row.unit,
        quantity: -Math.abs(row.quantityUsed),
        unitCost: row.unitCost,
        totalCost: row.totalCost,
        stockBefore: row.stockBefore,
        stockAfter: row.stockAfter,
        insufficientStock: row.insufficientStock === true,
        createdAt: serverTimestamp(),
        createdByUid: actor.uid || null,
        createdByEmail: actor.email || null,
      });
    });
    adminQueueAuditLog(batch, dbRef, businessId, adminBuildAuditLogPayload({
      eventType: "inventory_deducted",
      booking,
      actor,
      details: {
        source: "service_consumption",
        trigger: adminInventoryDeductTrigger(loopSettings),
        rowCount: snapshot.rows.length,
        totalCost: snapshot.totalCost,
        negativeStockWarning: snapshot.rows.some((row) => row.insufficientStock === true),
        rows: snapshot.rows.slice(0, 20).map((row) => ({
          itemId: row.itemId,
          itemName: row.itemName,
          quantity: row.quantityUsed,
          stockBefore: row.stockBefore,
          stockAfter: row.stockAfter,
        })),
      },
    }));
    await batch.commit();
  }

  return {
    inventoryDeductionStatus: "deducted",
    inventoryDeductedAt: serverTimestamp(),
    inventoryDeductTrigger: adminInventoryDeductTrigger(loopSettings),
    inventoryDeductedByUid: actor.uid || null,
    inventoryDeductedByEmail: actor.email || null,
    inventoryCostAmount: snapshot.totalCost,
    inventoryConsumed: snapshot.rows,
    inventoryHasNegativeStockWarning: snapshot.rows.some((row) => row.insufficientStock === true),
    materialCostAmount: snapshot.totalCost,
  };
}


function adminRetailSaleNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function adminNormalizeRetailSaleRows(rows = [], itemMap = new Map()) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, idx) => {
      const itemId = String(row?.itemId || row?.inventoryItemId || "").trim();
      if (!itemId) return null;
      const item = itemMap.get(itemId) || {};
      const quantity = adminRetailSaleNumber(row?.quantity ?? row?.qty, 0);
      if (quantity <= 0) return null;
      const unitPrice = adminRetailSaleNumber(row?.unitPrice ?? row?.price ?? item.salePrice ?? item.price, 0);
      const unitCost = adminRetailSaleNumber(row?.unitCost ?? row?.cost ?? item.unitCost ?? item.cost, 0);
      const commissionTypeRaw = String(row?.workerCommissionType || row?.commissionType || "none").trim().toLowerCase();
      const workerCommissionType = ["percentage", "fixed"].includes(commissionTypeRaw) ? commissionTypeRaw : "none";
      const workerCommissionValue = adminRetailSaleNumber(row?.workerCommissionValue ?? row?.commissionValue, 0);
      const lineTotal = Math.max(0, quantity * unitPrice);
      const costTotal = Math.max(0, quantity * unitCost);
      const workerPayoutAmount = workerCommissionType === "percentage"
        ? Math.max(0, lineTotal * (workerCommissionValue / 100))
        : workerCommissionType === "fixed"
          ? Math.max(0, workerCommissionValue)
          : 0;
      return {
        id: row?.id || `retail-${itemId}-${idx}`,
        itemId,
        itemName: row?.itemName || item.name || item.title || itemId,
        unit: row?.unit || item.unit || "unidad",
        quantity,
        unitPrice,
        unitCost,
        lineTotal,
        costTotal,
        workerCommissionType,
        workerCommissionValue,
        workerPayoutAmount,
      };
    })
    .filter(Boolean);
}

function adminRetailQuantityMap(rows = []) {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const itemId = String(row?.itemId || row?.inventoryItemId || "").trim();
    if (!itemId) return;
    map.set(itemId, (map.get(itemId) || 0) + adminRetailSaleNumber(row?.quantity ?? row?.qty, 0));
  });
  return map;
}

function adminInventorySettingsAreEnabled(loopSettings = {}) {
  const inventorySettings = loopSettings?.inventorySettings || {};
  const moduleEnabled = loopSettings?.enabledModules?.inventory !== false;
  const legacyAutoDeduct = loopSettings?.inventoryAutoDeduct;
  return moduleEnabled && !(inventorySettings.enabled === false || inventorySettings.autoDeduct === false || legacyAutoDeduct === false || inventorySettings.plan === "disabled");
}

function adminInventoryDeductTrigger(loopSettings = {}) {
  const trigger = String(loopSettings?.inventorySettings?.deductTrigger || loopSettings?.inventoryDeductTrigger || "completed").trim().toLowerCase();
  if (["paid", "payment", "cobrado"].includes(trigger)) return "paid";
  if (["completed_paid", "completed_and_paid"].includes(trigger)) return "completed_paid";
  return "completed";
}

function adminStatusIsCompleted(value) {
  return ["completed", "complete", "done", "completada", "finalizada", "finished"].includes(String(value || "").trim().toLowerCase());
}

function adminPaymentStatusIsPaid(value) {
  return ["paid", "pagado", "cobrado", "completed", "complete"].includes(String(value || "").trim().toLowerCase());
}

function adminAuditEventLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  const labels = {
    booking_status_changed: "Estado de reserva actualizado",
    payment_status_changed: "Pago actualizado",
    inventory_deducted: "Inventario descontado",
    inventory_deduction_failed: "Error descontando inventario",
    inventory_deduction_skipped: "Inventario no requerido",
    product_sales_updated: "Productos vendidos actualizados",
    product_sales_deducted: "Productos vendidos descontados",
    inventory_adjusted: "Inventario ajustado",
  };
  return labels[raw] || "Evento de auditoría";
}

function adminBuildAuditLogPayload({ eventType, booking = {}, actor = {}, details = {}, severity = "info" } = {}) {
  const bookingId = booking?.id || booking?.bookingId || null;
  return {
    type: eventType || "audit_event",
    eventType: eventType || "audit_event",
    label: adminAuditEventLabel(eventType),
    severity,
    bookingId,
    bookingClientName: getBookingClientName(booking, ""),
    bookingServiceName: getBookingServiceName(booking, ""),
    workerId: booking?.employeeId || booking?.workerId || booking?.assignedEmployeeId || booking?.resourceId || null,
    workerName: getBookingWorkerName(booking) || null,
    details: details && typeof details === "object" ? details : {},
    createdAt: serverTimestamp(),
    createdAtClient: new Date().toISOString(),
    createdByUid: actor?.uid || null,
    createdByEmail: actor?.email || null,
  };
}

function adminQueueAuditLog(batch, dbRef, businessId, payload) {
  try {
    if (!batch || !dbRef || !businessId || !payload) return;
    const logRef = doc(collection(dbRef, `businesses/${businessId}/auditLogs`));
    batch.set(logRef, { businessId, ...payload });
  } catch (err) {
    try { console.warn("[AdminCalendar] audit log skipped", err); } catch (_) {}
  }
}

async function adminWriteAuditLog({ dbRef, businessId, eventType, booking = {}, actor = {}, details = {}, severity = "info" } = {}) {
  try {
    if (!dbRef || !businessId || !eventType) return;
    const payload = {
      businessId,
      ...adminBuildAuditLogPayload({ eventType, booking, actor, details, severity }),
    };
    if (isIosCapacitor()) return fsClient.addDocToCollectionPath(`businesses/${businessId}/auditLogs`, payload, { db: dbRef });
    await addDoc(collection(dbRef, `businesses/${businessId}/auditLogs`), payload);
  } catch (err) {
    try { console.warn("[AdminCalendar] audit log write skipped", err); } catch (_) {}
  }
}

function adminShouldAutoDeductInventory(booking = {}, loopSettings = {}) {
  if (!adminInventorySettingsAreEnabled(loopSettings)) return false;
  return shouldAutoDeductInventoryForBooking(booking, {
    ...(loopSettings?.inventorySettings || {}),
    inventoryAutoDeduct: loopSettings?.inventoryAutoDeduct,
    inventoryDeductTrigger: adminInventoryDeductTrigger(loopSettings),
  });
}

function adminShouldDeductProductSales(booking = {}, loopSettings = {}) {
  return adminShouldAutoDeductInventory(booking, loopSettings);
}

function adminBookingHasProductSales(booking = {}) {
  const rows = Array.isArray(booking.productSales) ? booking.productSales : Array.isArray(booking.retailSales) ? booking.retailSales : [];
  return rows.some((row) => String(row?.itemId || row?.inventoryItemId || "").trim() && adminRetailSaleNumber(row?.quantity ?? row?.qty, 0) > 0);
}

function adminProductSalesAlreadyDeducted(booking = {}) {
  return String(booking.productSalesDeductionStatus || "").trim().toLowerCase() === "deducted" || Boolean(booking.productSalesDeductedAt);
}

async function adminApplyProductSales({ dbRef, businessId, booking, productSales = [], actor = {}, loopSettings = {} }) {
  if (!businessId || !booking?.id) return null;
  const inventoryRows = await adminListInventoryItemsForBusiness({ dbRef, businessId });
  const itemMap = new Map(inventoryRows.map((item) => [String(item.id), item]));

  const previousRows = adminNormalizeRetailSaleRows(
    Array.isArray(booking.productSales) ? booking.productSales : Array.isArray(booking.retailSales) ? booking.retailSales : [],
    itemMap
  );
  const nextRows = adminNormalizeRetailSaleRows(productSales, itemMap);
  const shouldDeductNow = adminShouldDeductProductSales(booking, loopSettings);
  const previousWasDeducted = adminProductSalesAlreadyDeducted(booking);
  const previousQty = previousWasDeducted ? adminRetailQuantityMap(previousRows) : new Map();
  const nextQty = shouldDeductNow ? adminRetailQuantityMap(nextRows) : new Map();
  const itemIds = new Set([...previousQty.keys(), ...nextQty.keys()]);
  const allowNegativeStock = loopSettings?.inventorySettings?.allowNegativeStock === true;

  if (shouldDeductNow && !allowNegativeStock) {
    const insufficientRows = [];
    itemIds.forEach((itemId) => {
      const prev = previousQty.get(itemId) || 0;
      const next = nextQty.get(itemId) || 0;
      const delta = next - prev;
      if (delta <= 0) return;
      const item = itemMap.get(itemId) || {};
      const stockBefore = adminRetailSaleNumber(item.currentStock ?? item.stock ?? item.quantity, 0);
      if (stockBefore - delta < 0) {
        insufficientRows.push({ itemId, itemName: item.name || item.title || itemId, stockBefore, quantityRequired: delta });
      }
    });
    if (insufficientRows.length) {
      const names = insufficientRows.map((row) => `${row.itemName} (${row.stockBefore} disponible, ${row.quantityRequired} requerido)`).join(", ");
      await adminWriteAuditLog({
        dbRef,
        businessId,
        eventType: "inventory_deduction_failed",
        booking,
        actor,
        severity: "warning",
        details: {
          reason: "stock_insufficient",
          trigger: adminInventoryDeductTrigger(loopSettings),
          source: "product_sales",
          rows: insufficientRows.map((row) => ({
            itemId: row.itemId,
            itemName: row.itemName,
            stockBefore: row.stockBefore,
            quantityRequired: row.quantityRequired,
          })),
        },
      });
      const err = new Error(`stock_insufficient:${names}`);
      err.code = "stock_insufficient";
      err.rows = insufficientRows;
      throw err;
    }
  }

  const stockAdjustmentRows = [];
  itemIds.forEach((itemId) => {
    const prev = previousQty.get(itemId) || 0;
    const next = nextQty.get(itemId) || 0;
    const delta = next - prev;
    if (!delta) return;
    const item = itemMap.get(itemId) || {};
    const stockBefore = adminRetailSaleNumber(item.currentStock ?? item.stock ?? item.quantity, 0);
    const stockAfter = stockBefore - delta;
    stockAdjustmentRows.push({ itemId, item, delta, stockBefore, stockAfter });
  });

  const productSalesTotalAmount = nextRows.reduce((sum, row) => sum + adminRetailSaleNumber(row.lineTotal, 0), 0);
  const productSalesCostAmount = nextRows.reduce((sum, row) => sum + adminRetailSaleNumber(row.costTotal, 0), 0);
  const productSalesPayoutAmount = nextRows.reduce((sum, row) => sum + adminRetailSaleNumber(row.workerPayoutAmount, 0), 0);
  const productSalesNetAmount = Math.max(0, productSalesTotalAmount - productSalesCostAmount - productSalesPayoutAmount);
  const hasNegativeStockWarning = Array.from(itemIds).some((itemId) => {
    const item = itemMap.get(itemId) || {};
    const stockBefore = adminRetailSaleNumber(item.currentStock ?? item.stock ?? item.quantity, 0);
    const delta = (nextQty.get(itemId) || 0) - (previousQty.get(itemId) || 0);
    return stockBefore - delta < 0;
  });

  const payload = {
    productSales: nextRows,
    retailSales: nextRows,
    productSalesTotalAmount,
    retailSalesTotalAmount: productSalesTotalAmount,
    productSalesCostAmount,
    productSalesPayoutAmount,
    productSalesNetAmount,
    productSalesHasNegativeStockWarning: hasNegativeStockWarning,
    productSalesUpdatedAt: serverTimestamp(),
    productSalesUpdatedByUid: actor.uid || null,
    productSalesUpdatedByEmail: actor.email || null,
    ...(shouldDeductNow ? {
      productSalesDeductionStatus: "deducted",
      productSalesDeductedAt: serverTimestamp(),
      productSalesDeductTrigger: adminInventoryDeductTrigger(loopSettings),
      productSalesDeductedByUid: actor.uid || null,
      productSalesDeductedByEmail: actor.email || null,
    } : {
      productSalesDeductionStatus: nextRows.length ? "pending" : null,
      productSalesDeductedAt: null,
      productSalesDeductTrigger: null,
      productSalesDeductedByUid: null,
      productSalesDeductedByEmail: null,
    }),
  };

  if (isIosCapacitor()) {
    for (const row of stockAdjustmentRows) {
      await adminUpdateInventoryItemSafe({
        dbRef,
        businessId,
        itemId: row.itemId,
        payload: {
          currentStock: row.stockAfter,
          updatedAt: serverTimestamp(),
          lastRetailSaleAt: serverTimestamp(),
          lastRetailSaleBookingId: booking.id,
        },
      });
      await adminAddInventoryMovementSafe({
        dbRef,
        businessId,
        payload: {
          type: row.delta > 0 ? "product_sale" : "product_sale_adjustment",
          bookingId: booking.id,
          itemId: row.itemId,
          itemName: row.item.name || row.item.title || row.itemId,
          unit: row.item.unit || "unidad",
          quantity: -row.delta,
          stockBefore: row.stockBefore,
          stockAfter: row.stockAfter,
          insufficientStock: row.stockAfter < 0,
          createdAt: serverTimestamp(),
          createdByUid: actor.uid || null,
          createdByEmail: actor.email || null,
        },
      });
    }
    await updateBookingDocSafe(businessId, booking.id, payload);
    await adminWriteAuditLog({
      dbRef,
      businessId,
      eventType: shouldDeductNow ? "product_sales_deducted" : "product_sales_updated",
      booking,
      actor,
      details: {
        source: "product_sales_editor",
        trigger: adminInventoryDeductTrigger(loopSettings),
        deductedNow: shouldDeductNow,
        productCount: nextRows.length,
        productSalesTotalAmount,
        productSalesCostAmount,
        productSalesPayoutAmount,
        productSalesNetAmount,
        negativeStockWarning: hasNegativeStockWarning,
        rows: nextRows.slice(0, 20).map((row) => ({
          itemId: row.itemId,
          itemName: row.itemName,
          quantity: row.quantity,
          lineTotal: row.lineTotal,
          costTotal: row.costTotal,
          workerPayoutAmount: row.workerPayoutAmount,
        })),
      },
    });
  } else {
    const batch = writeBatch(dbRef);
    stockAdjustmentRows.forEach((row) => {
      const itemRef = doc(dbRef, `businesses/${businessId}/inventoryItems/${row.itemId}`);
      batch.update(itemRef, {
        currentStock: row.stockAfter,
        updatedAt: serverTimestamp(),
        lastRetailSaleAt: serverTimestamp(),
        lastRetailSaleBookingId: booking.id,
      });
      const movementRef = doc(collection(dbRef, `businesses/${businessId}/inventoryMovements`));
      batch.set(movementRef, {
        type: row.delta > 0 ? "product_sale" : "product_sale_adjustment",
        bookingId: booking.id,
        itemId: row.itemId,
        itemName: row.item.name || row.item.title || row.itemId,
        unit: row.item.unit || "unidad",
        quantity: -row.delta,
        stockBefore: row.stockBefore,
        stockAfter: row.stockAfter,
        insufficientStock: row.stockAfter < 0,
        createdAt: serverTimestamp(),
        createdByUid: actor.uid || null,
        createdByEmail: actor.email || null,
      });
    });
    const bookingRef = doc(dbRef, `businesses/${businessId}/bookings/${booking.id}`);
    batch.update(bookingRef, payload);
    adminQueueAuditLog(batch, dbRef, businessId, adminBuildAuditLogPayload({
      eventType: shouldDeductNow ? "product_sales_deducted" : "product_sales_updated",
      booking,
      actor,
      details: {
        source: "product_sales_editor",
        trigger: adminInventoryDeductTrigger(loopSettings),
        deductedNow: shouldDeductNow,
        productCount: nextRows.length,
        productSalesTotalAmount,
        productSalesCostAmount,
        productSalesPayoutAmount,
        productSalesNetAmount,
        negativeStockWarning: hasNegativeStockWarning,
        rows: nextRows.slice(0, 20).map((row) => ({
          itemId: row.itemId,
          itemName: row.itemName,
          quantity: row.quantity,
          lineTotal: row.lineTotal,
          costTotal: row.costTotal,
          workerPayoutAmount: row.workerPayoutAmount,
        })),
      },
    }));
    await batch.commit();
  }
  return payload;
}



/* Phase 32N — Client history helpers */
function adminClientHistoryRaw(item) {
  return item?.extendedProps?.raw || item?.raw || item || {};
}

function adminClientHistoryId(raw = {}) {
  return String(raw?.id || raw?.bookingId || raw?.bookingRef || raw?.uid || "").trim();
}

function adminClientHistoryKey(raw = {}) {
  const r = adminClientHistoryRaw(raw);
  const customerId = String(r?.customerId || r?.clientId || r?.customer?.id || r?.customerSnapshot?.id || "").trim().toLowerCase();
  const email = String(r?.customerEmail || r?.clientEmail || r?.email || r?.customer?.email || r?.customerSnapshot?.email || "").trim().toLowerCase();
  const phone = String(r?.customerPhone || r?.clientPhone || r?.phone || r?.customer?.phone || r?.customerSnapshot?.phone || "").replace(/[^0-9+]/g, "").trim().toLowerCase();
  const name = String(r?.customerName || r?.clientName || r?.name || r?.customer?.name || r?.customerSnapshot?.name || "").trim().toLowerCase();
  return customerId || email || phone || name;
}

function adminClientHistoryDate(raw = {}) {
  const r = adminClientHistoryRaw(raw);
  const direct = r?.scheduledAt || r?.start || r?.date || r?.createdAt || r?.requestedAt || null;
  if (!direct) return null;
  if (typeof direct?.toDate === "function") return direct.toDate();
  if (direct?.seconds) return new Date(direct.seconds * 1000);
  const d = new Date(direct);
  return Number.isNaN(d.getTime()) ? null : d;
}

function adminClientHistoryDateLabel(date, locale = interfaceLocale()) {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }).replace(".", "");
  } catch (_) {
    return "—";
  }
}

function adminClientHistoryServiceLabel(raw = {}) {
  const r = adminClientHistoryRaw(raw);
  return r?.serviceName || r?.serviceTitle || r?.packageName || r?.service || "Servicio";
}

function adminClientHistoryPrice(raw = {}) {
  const r = adminClientHistoryRaw(raw);
  const n = Number(r?.totalPrice ?? r?.total ?? r?.price ?? r?.servicePrice ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function adminClientHistoryStatus(raw = {}) {
  const r = adminClientHistoryRaw(raw);
  const s = String(r?.status || r?.confirmationStatus || r?.state || "").toLowerCase();
  if (["completed", "complete", "done", "completada"].includes(s)) return "completed";
  if (["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada"].includes(s)) return "cancelled";
  if (["no_show", "no-show", "noshow", "no asistió", "no_asistio"].includes(s)) return "no_show";
  if (["confirmed", "confirmada", "approved", "accepted"].includes(s)) return "confirmed";
  if (["pending", "pendiente", "to_confirm"].includes(s)) return "pending";
  return s || "booking";
}

function adminClientHistoryBuild(targetBooking, bookings = [], locale = interfaceLocale()) {
  const targetRaw = adminClientHistoryRaw(targetBooking || {});
  const targetKey = adminClientHistoryKey(targetRaw);
  if (!targetRaw || !targetKey) {
    return {
      hasCustomer: false,
      customerName: "Cliente",
      totalVisits: 0,
      completed: 0,
      cancelled: 0,
      noShow: 0,
      upcoming: 0,
      totalSpent: 0,
      totalSpentLabel: "$0",
      favoriteService: "—",
      lastVisitLabel: "Sin historial",
      lastVisitSub: "Esta es la primera reserva visible en Loop.",
      recent: [],
      notes: [],
    };
  }

  const targetId = adminClientHistoryId(targetRaw);
  const now = new Date();
  const matches = (Array.isArray(bookings) ? bookings : [])
    .map(adminClientHistoryRaw)
    .filter((raw) => raw && adminClientHistoryKey(raw) === targetKey)
    .map((raw) => ({ raw, date: adminClientHistoryDate(raw), id: adminClientHistoryId(raw) }))
    .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));

  const serviceCounts = new Map();
  let totalSpent = 0;
  let completed = 0;
  let cancelled = 0;
  let noShow = 0;
  let upcoming = 0;
  const notes = [];

  for (const item of matches) {
    const service = adminClientHistoryServiceLabel(item.raw);
    if (service) serviceCounts.set(service, (serviceCounts.get(service) || 0) + 1);
    totalSpent += adminClientHistoryPrice(item.raw);
    const status = adminClientHistoryStatus(item.raw);
    if (status === "completed" || status === "confirmed") completed += 1;
    if (status === "cancelled") cancelled += 1;
    if (status === "no_show") noShow += 1;
    if (item.date && item.date >= now && item.id !== targetId) upcoming += 1;
    const note = String(item.raw?.internalNotes || item.raw?.notes || item.raw?.adminNotes || "").trim();
    if (note && !notes.includes(note)) notes.push(note);
  }

  const recent = matches
    .filter((item) => !targetId || item.id !== targetId)
    .slice(0, 4)
    .map((item) => ({
      id: item.id || `${item.date?.getTime?.() || "booking"}-${adminClientHistoryServiceLabel(item.raw)}`,
      dateLabel: adminClientHistoryDateLabel(item.date, locale),
      service: adminClientHistoryServiceLabel(item.raw),
      status: adminClientHistoryStatus(item.raw),
      price: adminClientHistoryPrice(item.raw),
    }));

  const favoriteService = Array.from(serviceCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  const previousVisit = recent[0];
  const currency = totalSpent % 1 === 0 ? totalSpent.toFixed(0) : totalSpent.toFixed(2);
  const totalVisits = matches.length;
  const averageTicket = totalVisits > 0 ? totalSpent / totalVisits : 0;

  let segment = "Nuevo";
  let segmentTone = "new";
  if (totalVisits >= 8 || totalSpent >= 500) {
    segment = "VIP";
    segmentTone = "vip";
  } else if (totalVisits >= 3) {
    segment = "Recurrente";
    segmentTone = "loyal";
  }

  let risk = "Sin historial";
  let riskTone = "neutral";
  const lastDate = previousVisit ? matches.find((item) => item.id === previousVisit.id)?.date : null;
  const daysSinceLastVisit = lastDate ? Math.max(0, Math.floor((new Date().getTime() - lastDate.getTime()) / 86400000)) : null;
  if (totalVisits > 0) {
    if (noShow >= 2 || cancelled >= 3) {
      risk = "Riesgo operativo";
      riskTone = "warning";
    } else if (daysSinceLastVisit != null && daysSinceLastVisit >= 90) {
      risk = "En riesgo";
      riskTone = "risk";
    } else if (daysSinceLastVisit != null && daysSinceLastVisit <= 30) {
      risk = "Activo";
      riskTone = "active";
    } else {
      risk = "Seguimiento";
      riskTone = "neutral";
    }
  }

  const nextAction =
    totalVisits <= 1
      ? "Primera/segunda visita: confirma preferencias y datos de contacto."
      : riskTone === "risk"
        ? "Cliente sin visita reciente: considera mensaje de reactivación."
        : riskTone === "warning"
          ? "Revisar cancelaciones/no-shows antes de confirmar."
          : segmentTone === "vip"
            ? "Cliente VIP: cuidar experiencia y preferencias."
            : "Cliente recurrente: revisar servicio favorito y notas.";

  return {
    hasCustomer: true,
    customerName: targetRaw.customerName || targetRaw.clientName || targetRaw.name || targetRaw.customer?.name || targetRaw.customerSnapshot?.name || "Cliente",
    totalVisits,
    completed,
    cancelled,
    noShow,
    upcoming,
    totalSpent,
    totalSpentLabel: `$${currency}`,
    averageTicket,
    averageTicketLabel: averageTicket > 0 ? `$${averageTicket % 1 === 0 ? averageTicket.toFixed(0) : averageTicket.toFixed(2)}` : "$0",
    segment,
    segmentTone,
    risk,
    riskTone,
    daysSinceLastVisit,
    nextAction,
    favoriteService,
    lastVisitLabel: previousVisit ? previousVisit.dateLabel : "Sin visita anterior",
    lastVisitSub: previousVisit ? `${previousVisit.service} · ${previousVisit.status}` : "Esta es la primera reserva visible en Loop.",
    recent,
    notes: notes.slice(0, 3),
  };
}


/* Phase 32O — Communication helpers (WhatsApp intentionally on standby) */
function adminCommunicationRaw(raw = {}) {
  return raw?.extendedProps?.raw || raw?.raw || raw || {};
}

function adminCommunicationClientName(raw = {}) {
  const r = adminCommunicationRaw(raw);
  return String(r?.customerName || r?.clientName || r?.name || r?.customer?.name || r?.customerSnapshot?.name || "Cliente").trim() || "Cliente";
}

function adminCommunicationEmail(raw = {}) {
  const r = adminCommunicationRaw(raw);
  return String(r?.customerEmail || r?.clientEmail || r?.email || r?.customer?.email || r?.customerSnapshot?.email || "").trim();
}

function adminCommunicationPhone(raw = {}) {
  const r = adminCommunicationRaw(raw);
  return String(r?.customerPhone || r?.clientPhone || r?.phone || r?.customer?.phone || r?.customerSnapshot?.phone || "").trim();
}

function adminCommunicationService(raw = {}) {
  const r = adminCommunicationRaw(raw);
  return String(r?.serviceName || r?.serviceTitle || r?.packageName || r?.service || "tu servicio").trim() || "tu servicio";
}

function adminCommunicationDate(raw = {}) {
  const r = adminCommunicationRaw(raw);
  const direct = bookingScheduledAtToDate(r) || r?.start || r?.scheduledAt || r?.date || null;
  if (!direct) return null;
  const d = new Date(direct);
  return Number.isNaN(d.getTime()) ? null : d;
}

function adminCommunicationDateLabel(raw = {}, locale = interfaceLocale()) {
  const d = adminCommunicationDate(raw);
  if (!d) return "la fecha acordada";
  try {
    return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" }).replace(/\./g, "");
  } catch (_) {
    return "la fecha acordada";
  }
}

function adminCommunicationTimeLabel(raw = {}, locale = interfaceLocale()) {
  const d = adminCommunicationDate(raw);
  if (!d) return "la hora acordada";
  try {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "la hora acordada";
  }
}

// LOOP AGENDA BUSINESS NAME GUARD V66
// Never show a Firebase businessId as the visible business name. Worker routes
// can mount before the business document is hydrated, so we use route state/cache
// while resolving the real business name in the background.
function adminLooksLikeFirebaseBusinessId(value = "") {
  const text = String(value || "").trim();
  return /^[A-Za-z0-9_-]{16,}$/.test(text) && !/[\sñÑáéíóúÁÉÍÓÚ#&.]/.test(text);
}

function adminCleanBusinessDisplayName(value = "", businessId = "") {
  const text = String(value || "").trim();
  const id = String(businessId || "").trim();
  if (!text) return "";
  if (id && text.toLowerCase() === id.toLowerCase()) return "";
  if (adminLooksLikeFirebaseBusinessId(text)) return "";
  return text;
}

function adminBusinessNameFromDoc(businessDoc = {}, businessId = "") {
  const candidates = [
    businessDoc?.name,
    businessDoc?.businessName,
    businessDoc?.displayName,
    businessDoc?.title,
    businessDoc?.commercialName,
    businessDoc?.legalName,
    businessDoc?.publicName,
    businessDoc?.brand?.name,
    businessDoc?.publicProfile?.name,
  ];
  for (const value of candidates) {
    const clean = adminCleanBusinessDisplayName(value, businessId);
    if (clean) return clean;
  }
  return "";
}

function adminBusinessNameFromRouteState(routeLocation = null, businessId = "") {
  const state = routeLocation && typeof routeLocation.state === "object" ? routeLocation.state : null;
  if (!state) return "";
  const candidates = [
    state.businessName,
    state.name,
    state.title,
    state.business?.name,
    state.business?.businessName,
    state.item?.businessName,
    state.item?.name,
    state.raw?.businessName,
    state.raw?.name,
  ];
  for (const value of candidates) {
    const clean = adminCleanBusinessDisplayName(value, businessId);
    if (clean) return clean;
  }
  return "";
}

function adminBusinessNameFromLocalCache(businessId = "") {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    const id = String(businessId || "").trim();
    const activeId = String(window.localStorage.getItem("loopActiveBusinessId") || window.localStorage.getItem("loopAgendaActiveBusinessId") || "").trim();
    if (id && activeId && activeId !== id) return "";
    const candidates = [
      window.localStorage.getItem("loopActiveBusinessName"),
      window.localStorage.getItem("loopAgendaActiveBusinessName"),
    ];
    for (const value of candidates) {
      const clean = adminCleanBusinessDisplayName(value, id);
      if (clean) return clean;
    }
  } catch (_) {}
  return "";
}

function adminSidebarBusinessName(businessDoc = {}, fallback = "", options = {}) {
  const businessId = String(options?.businessId || fallback || businessDoc?.id || businessDoc?.businessId || "").trim();
  const clean =
    adminBusinessNameFromDoc(businessDoc, businessId) ||
    adminCleanBusinessDisplayName(options?.routeName, businessId) ||
    adminCleanBusinessDisplayName(options?.cachedName, businessId) ||
    adminCleanBusinessDisplayName(options?.hydratedName, businessId) ||
    adminCleanBusinessDisplayName(fallback, businessId);

  return clean || (businessId ? "Cargando negocio..." : "Mi negocio");
}

function adminCommunicationBusinessName(businessDoc = {}) {
  return adminBusinessNameFromDoc(businessDoc) || "el negocio";
}

function adminSidebarAddressText(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value !== "object") return "";
  const direct = String(
    value.formatted ||
    value.formattedAddress ||
    value.fullAddress ||
    value.address ||
    value.label ||
    value.name ||
    ""
  ).trim();
  if (direct) return direct;
  return [
    value.line1,
    value.street,
    value.avenue,
    value.building,
    value.area || value.neighborhood || value.zone || value.district,
    value.city,
    value.province || value.state,
  ].filter(Boolean).map((part) => String(part).trim()).filter(Boolean).join(", ");
}

function adminSidebarBusinessAddress(businessDoc = {}) {
  const direct = adminSidebarAddressText(
    businessDoc?.displayAddress ||
    businessDoc?.formattedAddress ||
    businessDoc?.fullAddress ||
    businessDoc?.businessAddress ||
    businessDoc?.addressLine ||
    businessDoc?.address ||
    businessDoc?.location
  );
  if (direct) return direct;
  return [
    businessDoc?.street,
    businessDoc?.avenue,
    businessDoc?.building,
    businessDoc?.area || businessDoc?.neighborhood || businessDoc?.zone || businessDoc?.district,
    businessDoc?.city,
    businessDoc?.province,
  ].filter(Boolean).map((part) => String(part).trim()).filter(Boolean).join(", ");
}

function adminExtractImageUrl(value) {
  if (!value) return "";
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value && typeof value === "object") {
    const nested = value.url || value.downloadURL || value.src || value.href || value.path || value.fullPath || "";
    if (typeof nested === "string" && nested.trim()) return nested.trim();
  }
  return "";
}

function adminSidebarBusinessLogo(businessDoc = {}) {
  const candidates = [
    businessDoc?.logoUrl,
    businessDoc?.logoURL,
    businessDoc?.logo_url,
    businessDoc?.logo,
    businessDoc?.logoImage,
    businessDoc?.logoImg,
    businessDoc?.logo_img,
    businessDoc?.logoPath,
    businessDoc?.logoDownloadURL,
    businessDoc?.businessLogo,
    businessDoc?.businessLogoUrl,
    businessDoc?.profileImage,
    businessDoc?.profileImageUrl,
    businessDoc?.profilePhoto,
    businessDoc?.profilePhotoUrl,
    businessDoc?.avatar,
    businessDoc?.avatarUrl,
    businessDoc?.image,
    businessDoc?.imageUrl,
    businessDoc?.photo,
    businessDoc?.photoUrl,
    businessDoc?.photoURL,
    businessDoc?.brand?.logoUrl,
    businessDoc?.brand?.logoURL,
    businessDoc?.brand?.logo,
    businessDoc?.brand?.imageUrl,
    businessDoc?.publicProfile?.logoUrl,
    businessDoc?.publicProfile?.logoURL,
    businessDoc?.publicProfile?.logo,
    businessDoc?.publicProfile?.imageUrl,
  ];

  for (const candidate of candidates) {
    const clean = adminExtractImageUrl(candidate);
    if (clean) return clean;
  }

  return "";
}

function adminBusinessLogoFromRouteState(routeLocation = null) {
  const state = routeLocation && typeof routeLocation.state === "object" ? routeLocation.state : null;
  if (!state) return "";
  const candidates = [
    state.businessLogo,
    state.businessLogoUrl,
    state.logoUrl,
    state.logo,
    state.imageUrl,
    state.photoUrl,
    state.business?.logoUrl,
    state.business?.logo,
    state.business?.imageUrl,
    state.item?.logoUrl,
    state.item?.logo,
    state.item?.imageUrl,
    state.raw?.logoUrl,
    state.raw?.logo,
    state.raw?.imageUrl,
  ];
  for (const candidate of candidates) {
    const clean = adminExtractImageUrl(candidate);
    if (clean) return clean;
  }
  return "";
}

function adminBusinessLogoFromLocalCache(businessId = "") {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    const id = String(businessId || "").trim();
    const activeId = String(window.localStorage.getItem("loopActiveBusinessId") || window.localStorage.getItem("loopAgendaActiveBusinessId") || "").trim();
    if (id && activeId && activeId !== id) return "";
    const candidates = [
      window.localStorage.getItem("loopActiveBusinessLogo"),
      window.localStorage.getItem("loopAgendaActiveBusinessLogo"),
      window.localStorage.getItem("loopActiveBusinessLogoUrl"),
      window.localStorage.getItem("loopAgendaActiveBusinessLogoUrl"),
    ];
    for (const candidate of candidates) {
      const clean = adminExtractImageUrl(candidate);
      if (clean) return clean;
    }
  } catch (_) {}
  return "";
}

function adminCalendarSettingsCacheKey(businessId = "") {
  const id = String(businessId || "").trim();
  return id ? `loopAgendaCalendarSettings:${id}` : "";
}

function adminCalendarSettingsFromRouteState(routeLocation = null) {
  const state = routeLocation && typeof routeLocation.state === "object" ? routeLocation.state : null;
  if (!state) return null;
  const candidates = [
    state.calendarSettings,
    state.businessCalendarSettings,
    state.agendaSettings,
    state.business?.calendarSettings,
    state.item?.calendarSettings,
    state.raw?.calendarSettings,
    state.raw?.business?.calendarSettings,
  ];
  for (const value of candidates) {
    if (value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0) return value;
  }
  return null;
}

function adminReadCalendarSettingsFromLocalCache(businessId = "") {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const key = adminCalendarSettingsCacheKey(businessId);
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const settings = parsed && typeof parsed.settings === "object" ? parsed.settings : parsed;
    if (settings && typeof settings === "object" && Object.keys(settings).length > 0) return settings;
  } catch (_) {}
  return null;
}

function adminWriteCalendarSettingsToLocalCache(businessId = "", settings = null) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const key = adminCalendarSettingsCacheKey(businessId);
    if (!key || !settings || typeof settings !== "object" || !Object.keys(settings).length) return;
    window.localStorage.setItem(key, JSON.stringify({ settings, savedAt: Date.now() }));
  } catch (_) {}
}

function adminWorkerProfileFromRouteState(routeLocation = null) {
  const state = routeLocation && typeof routeLocation.state === "object" ? routeLocation.state : null;
  if (!state) return null;
  const raw = state.raw && typeof state.raw === "object" ? state.raw : {};
  const item = state.item && typeof state.item === "object" ? state.item : {};
  const worker = state.worker && typeof state.worker === "object" ? state.worker : {};
  const employee = state.employee && typeof state.employee === "object" ? state.employee : {};
  const source = { ...raw, ...item, ...worker, ...employee, ...state };
  const id = String(
    source.employeeId ||
    source.workerId ||
    source.resourceId ||
    source.id ||
    source.employee?.id ||
    source.worker?.id ||
    ""
  ).trim();
  const email = String(
    source.employeeEmail ||
    source.workerEmail ||
    source.email ||
    source.employee?.email ||
    source.worker?.email ||
    ""
  ).trim().toLowerCase();
  const name = String(
    source.workerName ||
    source.employeeName ||
    source.workerDisplayName ||
    source.employeeDisplayName ||
    source.displayName ||
    source.name ||
    source.fullName ||
    source.employee?.name ||
    source.worker?.name ||
    ""
  ).trim();
  const photoUrl = adminWorkerPhotoUrl(source) || adminWorkerPhotoUrl(source.employee || {}) || adminWorkerPhotoUrl(source.worker || "") || "";
  const availability = source.availability || source.employeeAvailability || source.workerAvailability || source.schedule || source.workingHours || source.hours || source.employee?.availability || source.worker?.availability || null;
  if (!id && !email && !name && !photoUrl && !availability) return null;
  return {
    ...source,
    id: id || (email ? `email:${email}` : ""),
    employeeId: id || (email ? `email:${email}` : ""),
    workerId: id || "",
    email,
    employeeEmail: source.employeeEmail || email || "",
    workerEmail: source.workerEmail || email || "",
    name,
    displayName: name,
    photoUrl,
    photo: photoUrl,
    image: photoUrl,
    avatarUrl: photoUrl,
    availability,
    schedule: source.schedule || null,
    workingHours: source.workingHours || null,
    hours: source.hours || null,
  };
}

function adminBusinessLogoFromWorker(worker = {}) {
  const candidates = [
    worker?.businessLogo,
    worker?.businessLogoUrl,
    worker?.businessLogoURL,
    worker?.businessImage,
    worker?.businessImageUrl,
    worker?.businessPhotoUrl,
    worker?.business?.logoUrl,
    worker?.business?.logo,
    worker?.business?.imageUrl,
    worker?.businessSnapshot?.logoUrl,
    worker?.businessSnapshot?.logo,
    worker?.businessInfo?.logoUrl,
    worker?.businessInfo?.logo,
    worker?.company?.logoUrl,
    worker?.company?.logo,
  ];
  for (const candidate of candidates) {
    const clean = adminExtractImageUrl(candidate);
    if (clean) return clean;
  }
  return "";
}

function adminWorkerPhotoUrl(worker = {}) {
  const candidates = [
    worker?.photoUrl,
    worker?.photoURL,
    worker?.photo_url,
    worker?.photo,
    worker?.image,
    worker?.imageUrl,
    worker?.imageURL,
    worker?.avatar,
    worker?.avatarUrl,
    worker?.avatarURL,
    worker?.profileImage,
    worker?.profileImageUrl,
    worker?.profilePhoto,
    worker?.profilePhotoUrl,
    worker?.picture,
    worker?.pictureUrl,
    worker?.pictureURL,
    worker?.workerPhoto,
    worker?.workerPhotoUrl,
    worker?.employeePhoto,
    worker?.employeePhotoUrl,
    worker?.publicPhotoUrl,
    worker?.userPhotoURL,
    worker?.userPhotoUrl,
  ];
  for (const candidate of candidates) {
    const clean = adminExtractImageUrl(candidate);
    if (clean) return clean;
  }
  return "";
}


function adminWorkerResourceHydrationScore(resource = {}) {
  if (!resource || typeof resource !== "object") return 0;
  const email = String(resource.email || resource.employeeEmail || resource.workerEmail || "").trim().toLowerCase();
  const name = String(resource.name || resource.displayName || resource.title || resource.employeeName || resource.workerName || "").trim();
  const genericName = !name || name === "Sin nombre" || name === "Mi agenda" || (email && name.toLowerCase() === email.split("@")[0]);
  const hasAvailability = Boolean(resource.availability || resource.employeeAvailability || resource.workerAvailability || resource.schedule || resource.workingHours || resource.hours);
  return (
    (resource.id || resource.employeeId || resource.workerId || resource.resourceId ? 20 : 0) +
    (!genericName ? 35 : 0) +
    (adminWorkerPhotoUrl(resource) ? 25 : 0) +
    (hasAvailability ? 30 : 0) +
    (resource.permissions || resource.workerPermissions ? 10 : 0) +
    (resource.__personalWorkerAgenda ? 5 : 0)
  );
}

function adminMergeWorkerResourceRich(base = {}, override = {}, forcedId = "") {
  const left = base && typeof base === "object" ? base : {};
  const right = override && typeof override === "object" ? override : {};
  const leftScore = adminWorkerResourceHydrationScore(left);
  const rightScore = adminWorkerResourceHydrationScore(right);
  const rich = rightScore >= leftScore ? right : left;
  const fallback = rightScore >= leftScore ? left : right;
  const id = String(forcedId || rich.id || rich.employeeId || rich.workerId || rich.resourceId || fallback.id || fallback.employeeId || fallback.workerId || fallback.resourceId || "").trim();
  const email = String(rich.email || rich.employeeEmail || rich.workerEmail || fallback.email || fallback.employeeEmail || fallback.workerEmail || "").trim().toLowerCase();
  const name = String(
    rich.name ||
    rich.displayName ||
    rich.title ||
    rich.employeeName ||
    rich.workerName ||
    fallback.name ||
    fallback.displayName ||
    fallback.title ||
    fallback.employeeName ||
    fallback.workerName ||
    (email ? email.split("@")[0] : "Trabajador")
  ).trim() || "Trabajador";
  const photo = adminWorkerPhotoUrl(rich) || adminWorkerPhotoUrl(fallback) || "";
  const availability =
    rich.availability || rich.employeeAvailability || rich.workerAvailability || rich.schedule || rich.workingHours || rich.hours ||
    fallback.availability || fallback.employeeAvailability || fallback.workerAvailability || fallback.schedule || fallback.workingHours || fallback.hours || null;

  return {
    ...fallback,
    ...rich,
    id,
    resourceId: id,
    employeeId: id,
    workerId: rich.workerId || fallback.workerId || id,
    email,
    employeeEmail: rich.employeeEmail || fallback.employeeEmail || email || "",
    workerEmail: rich.workerEmail || fallback.workerEmail || email || "",
    title: name,
    name,
    displayName: name,
    employeeName: rich.employeeName || fallback.employeeName || name,
    workerName: rich.workerName || fallback.workerName || name,
    photoUrl: photo || null,
    photo: photo || null,
    image: photo || null,
    avatarUrl: photo || null,
    availability,
    employeeAvailability: rich.employeeAvailability || fallback.employeeAvailability || availability,
    workerAvailability: rich.workerAvailability || fallback.workerAvailability || availability,
    schedule: rich.schedule || fallback.schedule || null,
    workingHours: rich.workingHours || fallback.workingHours || null,
    hours: rich.hours || fallback.hours || null,
    blockedDates: rich.blockedDates || fallback.blockedDates || [],
    blockedRanges: rich.blockedRanges || fallback.blockedRanges || [],
  };
}

function adminSidebarCleanLocationPart(value = "") {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ")
    .trim();
}

function adminSidebarIsGenericLocationPart(value = "") {
  const s = adminSidebarCleanLocationPart(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return [
    "panama",
    "panama panama",
    "ciudad de panama",
    "provincia de panama",
    "republica de panama",
  ].includes(s);
}

function adminSidebarIsStreetLikeLocationPart(value = "") {
  const s = adminSidebarCleanLocationPart(value).toLowerCase();
  return /\b(calle|avenida|av\.|ave\.|vía|via|edificio|local|plaza|torre|piso|apto|apartamento|ph\b|casa|carretera|centro comercial)\b/i.test(s);
}


function adminSidebarKnownZoneFromText(value = "") {
  const raw = adminSidebarCleanLocationPart(value);
  if (!raw) return "";

  const normalized = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const knownZones = [
    ["costa del este", "Costa del Este"],
    ["santa maria", "Santa María"],
    ["punta pacifica", "Punta Pacífica"],
    ["san francisco", "San Francisco"],
    ["coco del mar", "Coco del Mar"],
    ["obarrio", "Obarrio"],
    ["marbella", "Marbella"],
    ["bella vista", "Bella Vista"],
    ["el cangrejo", "El Cangrejo"],
    ["via argentina", "Vía Argentina"],
    ["avenida balboa", "Avenida Balboa"],
    ["calidonia", "Calidonia"],
    ["el dorado", "El Dorado"],
    ["condado del rey", "Condado del Rey"],
    ["altos de panama", "Altos de Panamá"],
    ["tumba muerto", "Tumba Muerto"],
    ["bethania", "Bethania"],
    ["hato pintado", "Hato Pintado"],
    ["dos mares", "Dos Mares"],
    ["el carmen", "El Carmen"],
    ["parque lefevre", "Parque Lefevre"],
    ["rio abajo", "Río Abajo"],
    ["juan diaz", "Juan Díaz"],
    ["brisas del golf", "Brisas del Golf"],
    ["san miguelito", "San Miguelito"],
    ["versalles", "Versalles"],
    ["don bosco", "Don Bosco"],
    ["pedregal", "Pedregal"],
    ["tocumen", "Tocumen"],
    ["clayton", "Clayton"],
    ["albrook", "Albrook"],
    ["diablo", "Diablo"],
    ["ancon", "Ancón"],
    ["balboa", "Balboa"],
    ["panama pacifico", "Panamá Pacífico"],
    ["costa verde", "Costa Verde"],
    ["arraijan", "Arraiján"],
    ["chorrera", "La Chorrera"],
    ["boquete", "Boquete"],
    ["david", "David"],
    ["santiago", "Santiago"],
    ["coronado", "Coronado"],
  ];

  for (const [key, label] of knownZones) {
    if (normalized.includes(key)) return label;
  }

  return "";
}

function adminSidebarZoneCandidate(value = "") {
  const clean = adminSidebarCleanLocationPart(value);
  if (!clean) return "";

  const known = adminSidebarKnownZoneFromText(clean);
  if (known) return known;

  if (adminSidebarIsGenericLocationPart(clean)) return "";
  if (adminSidebarIsStreetLikeLocationPart(clean)) return "";

  return clean;
}

function adminSidebarBusinessZone(businessDoc = {}) {
  const directCandidates = [
    businessDoc?.businessZone,
    businessDoc?.primaryZone,
    businessDoc?.zone,
    businessDoc?.zona,
    businessDoc?.area,
    businessDoc?.primaryArea,
    businessDoc?.neighborhood,
    businessDoc?.barrio,
    businessDoc?.sector,
    businessDoc?.corregimiento,
    businessDoc?.districtArea,
    businessDoc?.location?.zone,
    businessDoc?.location?.zona,
    businessDoc?.location?.area,
    businessDoc?.location?.neighborhood,
    businessDoc?.location?.barrio,
    businessDoc?.location?.sector,
    businessDoc?.location?.corregimiento,
    businessDoc?.location?.label,
    businessDoc?.location?.name,
    businessDoc?.location?.formattedAddress,
    businessDoc?.location?.fullAddress,
    businessDoc?.address?.zone,
    businessDoc?.address?.zona,
    businessDoc?.address?.area,
    businessDoc?.address?.neighborhood,
    businessDoc?.address?.barrio,
    businessDoc?.address?.sector,
    businessDoc?.address?.corregimiento,
    businessDoc?.address?.label,
    businessDoc?.address?.name,
    Array.isArray(businessDoc?.serviceAreas) ? businessDoc.serviceAreas[0] : null,
    Array.isArray(businessDoc?.coverageAreas) ? businessDoc.coverageAreas[0] : null,
  ];

  for (const candidate of directCandidates) {
    if (!candidate) continue;

    if (typeof candidate === "string") {
      const zone = adminSidebarZoneCandidate(candidate);
      if (zone) return zone;
      continue;
    }

    if (candidate && typeof candidate === "object") {
      const nestedCandidates = [
        candidate.zone,
        candidate.zona,
        candidate.area,
        candidate.neighborhood,
        candidate.barrio,
        candidate.sector,
        candidate.corregimiento,
        candidate.label,
        candidate.name,
        candidate.formattedAddress,
        candidate.fullAddress,
        candidate.address,
      ];

      for (const nested of nestedCandidates) {
        const zone = adminSidebarZoneCandidate(nested);
        if (zone) return zone;
      }
    }
  }

  const addressText = adminSidebarBusinessAddress(businessDoc);
  if (!addressText) return "";

  const knownFromAddress = adminSidebarKnownZoneFromText(addressText);
  if (knownFromAddress) return knownFromAddress;

  const parts = String(addressText)
    .split(",")
    .map(adminSidebarCleanLocationPart)
    .filter(Boolean)
    .filter((part, idx, arr) => {
      const normalized = part.toLowerCase();
      return arr.findIndex((p) => p.toLowerCase() === normalized) === idx;
    });

  const zoneParts = parts
    .map(adminSidebarZoneCandidate)
    .filter(Boolean)
    .filter((part, idx, arr) => {
      const normalized = part.toLowerCase();
      return arr.findIndex((p) => p.toLowerCase() === normalized) === idx;
    });

  if (zoneParts.length) return zoneParts[zoneParts.length - 1];

  return "";
}

function adminSidebarBusinessInitials(name = "") {
  const clean = String(name || "Mi negocio").trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (!words.length) return "MN";
  const first = words[0]?.[0] || "M";
  const second = words.length > 1 ? words[1]?.[0] : words[0]?.[1];
  return `${first || "M"}${second || "N"}`.toUpperCase();
}

function adminCommunicationTemplateVars(booking = {}, businessDoc = {}, locale = interfaceLocale()) {
  return {
    client: adminCommunicationClientName(booking),
    service: adminCommunicationService(booking),
    date: adminCommunicationDateLabel(booking, locale),
    time: adminCommunicationTimeLabel(booking, locale),
    business: adminCommunicationBusinessName(businessDoc),
  };
}

function adminCommunicationTemplates(booking = {}, businessDoc = {}, locale = interfaceLocale()) {
  const v = adminCommunicationTemplateVars(booking, businessDoc, locale);
  return [
    {
      id: "confirmation",
      label: "Confirmación",
      helper: "Confirma la cita con fecha, hora y servicio.",
      subject: `Confirmación de reserva - ${v.business}`,
      body: `Hola ${v.client}, tu reserva para ${v.service} quedó confirmada para ${v.date} a las ${v.time}.\n\nGracias,\n${v.business}`,
    },
    {
      id: "reminder",
      label: "Recordatorio",
      helper: "Mensaje listo para recordar la cita.",
      subject: `Recordatorio de reserva - ${v.business}`,
      body: `Hola ${v.client}, te recordamos tu cita para ${v.service} el ${v.date} a las ${v.time}.\n\nNos vemos pronto,\n${v.business}`,
    },
    {
      id: "details",
      label: "Reenviar detalles",
      helper: "Reenvía los detalles principales de la reserva.",
      subject: `Detalles de tu reserva - ${v.business}`,
      body: `Hola ${v.client}, estos son los detalles de tu reserva:\n\nServicio: ${v.service}\nFecha: ${v.date}\nHora: ${v.time}\nNegocio: ${v.business}`,
    },
    {
      id: "cancellation",
      label: "Cancelación",
      helper: "Aviso base cuando una reserva se cancela.",
      subject: `Reserva cancelada - ${v.business}`,
      body: `Hola ${v.client}, te avisamos que tu reserva para ${v.service} del ${v.date} a las ${v.time} fue cancelada. Puedes contactarnos para reagendar.\n\n${v.business}`,
    },
    {
      id: "review_request",
      label: "Pedir valoración",
      helper: "Solicita feedback después de completar el servicio.",
      subject: `¿Cómo fue tu experiencia? - ${v.business}`,
      body: `Hola ${v.client}, gracias por visitarnos en ${v.business}. Nos ayudaría mucho conocer tu valoración sobre ${v.service}.`,
    },
  ];
}

function adminCommunicationMailto(email, template) {
  if (!email || !template) return "";
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(template.subject || "")}&body=${encodeURIComponent(template.body || "")}`;
}

function textBlack() {
  return "#0f172a";
}

/* ---------------------------
   Native date helpers (input type="date")
--------------------------- */
function toInputDateValue(date) {
  if (!date) return "";
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDateValue(value) {
  // value: YYYY-MM-DD
  if (!value || typeof value !== "string") return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const yy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (!yy || !mm || !dd) return null;
  return new Date(yy, mm - 1, dd);
}

/* ---------------------------
   Time helpers (15 min)
--------------------------- */
function roundToMinutes(dt, minutes) {
  if (!dt) return null;
  const d = new Date(dt);
  const ms = minutes * 60 * 1000;
  const rounded = Math.round(d.getTime() / ms) * ms;
  return new Date(rounded);
}

function getAgendaWeekStart(baseDate, weekStartsOn = "monday") {
  const d = startOfDay(baseDate || new Date());
  const targetDow = weekStartsOn === "sunday" ? 0 : 1;
  const currentDow = d.getDay();
  const diff = (currentDow - targetDow + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function weekDaysFromSelectedDate(baseDate, weekStartsOn = "monday", showWeekends = true) {
  const start = getAgendaWeekStart(baseDate || new Date(), weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  }).filter((d) => showWeekends !== false || ![0, 6].includes(d.getDay()));
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getBestDayWhenLeavingRangeView(baseDate) { return startOfDay(baseDate || new Date()); }

function mergeCalendarEventsUnique(...groups) {
  const seen = new Set();
  const out = [];
  groups.flat().forEach((ev) => {
    if (!ev) return;
    const raw = getBookingRawFromCalendarEvent(ev);
    const key = String(
      ev.id ||
      ev._def?.publicId ||
      raw.id ||
      raw.bookingId ||
      `${ev.start || raw.start || raw.date || ""}-${ev.end || raw.end || raw.time || ""}-${ev.title || raw.serviceName || raw.customerName || ""}`
    );
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(ev);
  });
  return out;
}

function workerInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "W";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function shortWorkerName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "Trabajador";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1]}`;
}

/* ---------------------------
   Availability helpers
--------------------------- */
const DOW_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function parseHHMMToMinutes(hhmm) {
  const m = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23) return null;
  if (mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

function minutesToHHMM(totalMinutes) {
  const safe = Math.max(0, Math.min(24 * 60, Math.round(Number(totalMinutes || 0))));
  const hh = Math.floor(safe / 60);
  const mm = safe % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function isAvailabilityEmpty(avail) {
  if (!avail || typeof avail !== "object") return true;
  for (const k of DOW_KEYS) {
    const ranges = Array.isArray(avail[k]) ? avail[k] : [];
    if (ranges.length > 0) return false;
  }
  return true;
}

/**
 * Default schedule (preliminar):
 * Monday–Saturday 08:00–17:00, Sunday off
 */
function defaultAvailabilityMonSat8to17() {
  return {
    sunday: [],
    monday: [{ from: "08:00", to: "17:00" }],
    tuesday: [{ from: "08:00", to: "17:00" }],
    wednesday: [{ from: "08:00", to: "17:00" }],
    thursday: [{ from: "08:00", to: "17:00" }],
    friday: [{ from: "08:00", to: "17:00" }],
    saturday: [{ from: "08:00", to: "17:00" }],
  };
}

function normalizeAvailabilitySourceForCalendar(source) {
  if (!source || typeof source !== "object") return null;

  const out = {};
  let hasAnyDayKey = false;

  for (const key of DOW_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      // If this availability source has other explicit day keys, a missing day means closed
      // for that source. This is what lets a worker's own schedule override company hours.
      out[key] = [];
      continue;
    }

    hasAnyDayKey = true;
    const rawDay = source[key];

    // Explicit closed states.
    if (
      rawDay === false ||
      rawDay == null ||
      (Array.isArray(rawDay) && rawDay.length === 0) ||
      (rawDay && typeof rawDay === "object" && (rawDay.closed === true || rawDay.enabled === false || rawDay.isOpen === false))
    ) {
      out[key] = [];
      continue;
    }

    if (rawDay === true) {
      // Legacy "open" boolean without hours is not precise enough to shade.
      // Keep this neutral instead of inventing hours.
      out[key] = [];
      continue;
    }

    const rawRanges = Array.isArray(rawDay)
      ? rawDay
      : Array.isArray(rawDay?.ranges)
        ? rawDay.ranges
        : Array.isArray(rawDay?.blocks)
          ? rawDay.blocks
          : Array.isArray(rawDay?.periods)
            ? rawDay.periods
            : (rawDay.from || rawDay.to || rawDay.start || rawDay.end || rawDay.open || rawDay.close || rawDay.startTime || rawDay.endTime)
              ? [rawDay]
              : [];

    const ranges = rawRanges
      .map((r) => ({
        from: r?.from || r?.start || r?.open || r?.startTime || r?.fromTime || r?.[0],
        to: r?.to || r?.end || r?.close || r?.endTime || r?.toTime || r?.[1],
      }))
      .filter((r) => parseHHMMToMinutes(r.from) != null && parseHHMMToMinutes(r.to) != null && parseHHMMToMinutes(r.to) > parseHHMMToMinutes(r.from));

    out[key] = ranges;
  }

  return hasAnyDayKey ? out : null;
}

function getBusinessAvailabilityForCalendar(businessDoc) {
  if (!businessDoc || typeof businessDoc !== "object") return null;
  return normalizeAvailabilitySourceForCalendar(
    businessDoc.availability ||
    businessDoc.openingHours ||
    businessDoc.hours ||
    businessDoc.schedule ||
    businessDoc.businessAvailability ||
    businessDoc.workingHours ||
    businessDoc.weeklyHours ||
    businessDoc.weeklySchedule ||
    businessDoc.availabilityByDay
  );
}

function getEmployeeAvailabilityForCalendar(employee) {
  return normalizeAvailabilitySourceForCalendar(
    employee?.availability ||
    employee?.employeeAvailability ||
    employee?.workerAvailability ||
    employee?.schedule ||
    employee?.workingHours ||
    employee?.hours ||
    employee?.weeklyHours ||
    employee?.weeklySchedule ||
    employee?.availabilityByDay
  );
}

function businessBlockedRangesForCalendar(businessDoc = {}) {
  const rawRanges = [
    ...(Array.isArray(businessDoc?.blockedRanges) ? businessDoc.blockedRanges : []),
    ...(Array.isArray(businessDoc?.closedRanges) ? businessDoc.closedRanges : []),
    ...(Array.isArray(businessDoc?.unavailableRanges) ? businessDoc.unavailableRanges : []),
    ...(Array.isArray(businessDoc?.specialClosedRanges) ? businessDoc.specialClosedRanges : []),
    ...(Array.isArray(businessDoc?.holidayRanges) ? businessDoc.holidayRanges : []),
  ];

  const rawDates = [
    ...(Array.isArray(businessDoc?.blockedDates) ? businessDoc.blockedDates : []),
    ...(Array.isArray(businessDoc?.closedDates) ? businessDoc.closedDates : []),
    ...(Array.isArray(businessDoc?.unavailableDates) ? businessDoc.unavailableDates : []),
    ...(Array.isArray(businessDoc?.specialClosedDates) ? businessDoc.specialClosedDates : []),
    ...(Array.isArray(businessDoc?.holidayDates) ? businessDoc.holidayDates : []),
  ];

  const out = [];

  rawRanges.forEach((item, idx) => {
    if (!item) return;
    const from = String(item.from || item.start || item.date || "").slice(0, 10);
    const to = String(item.to || item.end || item.from || item.start || item.date || "").slice(0, 10);
    if (!from || !to) return;
    out.push({ id: item.id || `business-range-${idx}-${from}`, from, to, reason: item.reason || item.title || "Cerrado" });
  });

  rawDates.forEach((item, idx) => {
    const date = String(typeof item === "object" ? (item.date || item.from || item.start || "") : item || "").slice(0, 10);
    if (!date) return;
    out.push({ id: `business-date-${idx}-${date}`, from: date, to: date, reason: "Cerrado" });
  });

  return out;
}

function isBusinessGloballyBlockedOnCalendarDate(businessDoc, date) {
  if (!businessDoc || !date) return false;
  const dateKey = formatDateYmdAdmin(date);
  return businessBlockedRangesForCalendar(businessDoc).some((item) => {
    const from = String(item.from || "").slice(0, 10);
    const to = String(item.to || item.from || "").slice(0, 10);
    if (!from || !to) return false;
    return dateKey >= from && dateKey <= to;
  });
}

/**
 * Calendar availability resolution:
 * 1) weekly worker availability wins when it exists
 * 2) if the worker has no weekly availability, fall back to business weekly availability
 * 3) global business blocks are applied separately and always win for everyone
 * 4) null = no schedule configured anywhere, never silently use 08:00-17:00
 */
function getAvailabilityWithFallback(employee, businessDoc = null) {
  const employeeAvail = getEmployeeAvailabilityForCalendar(employee);
  const businessAvail = getBusinessAvailabilityForCalendar(businessDoc);
  const hasEmployeeSchedule = Boolean(employeeAvail);
  const hasBusinessSchedule = Boolean(businessAvail);

  if (hasEmployeeSchedule) return employeeAvail;
  if (hasBusinessSchedule) return businessAvail;

  return null;
}

function isEmployeeBlockedOnCalendarDate(employee, date) {
  if (!employee || !date) return false;
  const dateKey = formatDateYmdAdmin(date);

  if (Array.isArray(employee.blockedDates) && employee.blockedDates.some((d) => String(d || "").slice(0, 10) === dateKey)) {
    return true;
  }

  if (Array.isArray(employee.blockedRanges)) {
    for (const item of employee.blockedRanges) {
      if (!item) continue;
      const from = String(item.from || item.date || "").slice(0, 10);
      const to = String(item.to || item.from || item.date || "").slice(0, 10);
      if (!from || !to) continue;
      if (dateKey >= from && dateKey <= to) return true;
    }
  }

  return false;
}

function rangesForEmployeeOnDateWithFallback(employee, date, businessDoc = null) {
  // V83 contract:
  // - Business weekly hours do NOT cap a worker with their own schedule.
  // - Global business closed/blocked days DO block every worker.
  // - Worker-specific blocks also block that worker.
  if (isBusinessGloballyBlockedOnCalendarDate(businessDoc, date)) return [];
  if (isEmployeeBlockedOnCalendarDate(employee, date)) return [];

  const avail = getAvailabilityWithFallback(employee, businessDoc);

  // Important distinction:
  // - null means no worker/business schedule is configured anywhere. Do not paint the whole day as unavailable.
  // - [] means a schedule exists, but this specific day is closed. That should paint the full day.
  if (!avail || !date) return null;

  const dowKey = DOW_KEYS[new Date(date).getDay()];
  const ranges = Array.isArray(avail[dowKey]) ? avail[dowKey] : [];

  const out = [];
  for (const r of ranges) {
    const a = parseHHMMToMinutes(r?.from);
    const b = parseHHMMToMinutes(r?.to);
    if (a == null || b == null) continue;
    if (b <= a) continue;
    out.push({ fromMin: a, toMin: b });
  }
  out.sort((x, y) => x.fromMin - y.fromMin);
  return out;
}

function dateAtMinutes(baseDate, mins) {
  const d = new Date(baseDate);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(mins, 0, 0);
  return d;
}

function buildPanamaBookingDateTimeParts(baseDate, hhmm, durationMinutes = 60, business = {}) {
  const date = formatDateYmdAdmin(baseDate || new Date());
  const match = String(hhmm || "10:00").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) throw new Error("Hora inválida");
  const hh = Math.max(0, Math.min(23, Number(match[1])));
  const mm = Math.max(0, Math.min(59, Number(match[2])));
  const time = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  const start = localInstant(date,time,businessZone(business));
  if (Number.isNaN(start.getTime())) throw new Error("Fecha/hora inválida");
  const safeDuration = Math.max(1, Number(durationMinutes || 60));
  const end = new Date(start.getTime() + safeDuration * 60000);
  return {
    date,
    time,
    start,
    end,
    ...scheduleFields(start,business),
  };
}

/* ---------------------------
   iOS-safe auth / mutation helpers
--------------------------- */
function getEffectiveActor() {
  const identity = getLoopEffectiveAuthIdentity(auth);
  return { uid: identity?.uid || null, email: identity?.email || "" };
}


function normalizeEmailForWorkerVisibility(value) {
  return String(value || "").trim().toLowerCase();
}

function getRawBookingFromAdminEvent(ev) {
  const extended = ev?.extendedProps || {};
  return extended.raw || ev?.raw || ev?.booking || extended.booking || ev || {};
}

function normalizeWorkerMatchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function bookingMatchesOperationalWorker(bookingLike, actor, detectedEmployeeDocId, workerProfile = null, workerEmailMode = "", selectedEmployeeIds = []) {
  const booking = getRawBookingFromAdminEvent(bookingLike);
  // LOOP AGENDA IOS SERVER-SCOPED BOOKINGS RENDER V16J:
  // Cloud Function worker bookings are already server-filtered after auth. Avoid a second
  // stale selectedSet/email-mode filter from removing valid bubbles during iOS hydration.
  if (booking && booking.__workerCalendarFunctionScoped === true) return true;
  const actorEmail = normalizeEmailForWorkerVisibility(actor?.email);
  const actorUid = actor?.uid ? String(actor.uid) : "";
  const detectedId = detectedEmployeeDocId ? String(detectedEmployeeDocId) : "";
  const selectedIds = Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds.filter(Boolean).map(String) : [];
  const forcedWorkerEmail = normalizeEmailForWorkerVisibility(workerEmailMode);

  if (!booking) return false;

  const directIds = [
    booking.employeeId,
    booking.resourceId,
    booking.workerId,
    booking.assignedEmployeeId,
    booking.employeeUserUid,
    booking.employeeUserId,
    booking.assignedEmployeeUserUid,
    booking.assignedEmployeeUserId,
    booking.employeeUid,
    booking.workerUid,
  ].filter(Boolean).map(String);

  const workerIds = [
    detectedId,
    actorUid,
    workerProfile?.id,
    workerProfile?.uid,
    workerProfile?.employeeId,
    workerProfile?.userId,
    workerProfile?.authUid,
    workerProfile?.userUid,
    workerProfile?.employeeUserUid,
    ...selectedIds,
  ].filter(Boolean).map(String);

  if (workerIds.length && workerIds.some((id) => directIds.includes(id))) return true;

  const directEmails = [
    booking.employeeEmail,
    booking.workerEmail,
    booking.assignedEmployeeEmail,
    booking.employeeAuthEmail,
    booking.authEmail,
    booking.email,
    booking.employeeEmailLower,
    booking.workerEmailLower,
    booking.assignedEmployeeEmailLower,
  ].filter(Boolean).map(normalizeEmailForWorkerVisibility);

  const workerEmails = [
    forcedWorkerEmail,
    actorEmail,
    workerProfile?.email,
    workerProfile?.emailLower,
    workerProfile?.employeeEmail,
    workerProfile?.employeeEmailLower,
    workerProfile?.workerEmail,
    workerProfile?.workerEmailLower,
    workerProfile?.authEmail,
    workerProfile?.userEmail,
    workerProfile?.inviteEmail,
  ].filter(Boolean).map(normalizeEmailForWorkerVisibility);

  if (workerEmails.length && workerEmails.some((email) => directEmails.includes(email))) return true;

  const assignedIds = Array.isArray(booking.assignedEmployeeIds) ? booking.assignedEmployeeIds.map(String) : [];
  if (workerIds.length && workerIds.some((id) => assignedIds.includes(id))) return true;

  const workerNames = [
    workerProfile?.name,
    workerProfile?.displayName,
    workerProfile?.employeeName,
    workerProfile?.fullName,
  ].filter(Boolean).map(normalizeWorkerMatchText);

  const directNames = [
    booking.employeeName,
    booking.workerName,
    booking.assignedEmployeeName,
    booking.resourceName,
    booking.employee?.name,
    booking.worker?.name,
  ].filter(Boolean).map(normalizeWorkerMatchText);

  if (workerNames.length && directNames.some((name) => workerNames.includes(name))) return true;

  const assignedEmployees = Array.isArray(booking.assignedEmployees) ? booking.assignedEmployees : [];
  return assignedEmployees.some((emp) => {
    const id = String(emp?.id || emp?.employeeId || emp?.workerId || emp?.uid || emp?.userId || emp?.userUid || emp?.employeeUserUid || "");
    const email = normalizeEmailForWorkerVisibility(emp?.email || emp?.emailLower || emp?.employeeEmail || emp?.employeeEmailLower || emp?.workerEmail || emp?.workerEmailLower || emp?.authEmail || emp?.inviteEmail);
    const name = normalizeWorkerMatchText(emp?.name || emp?.displayName || emp?.employeeName);
    return Boolean(
      (id && workerIds.includes(id)) ||
      (email && workerEmails.includes(email)) ||
      (name && workerNames.includes(name))
    );
  });
}

function getRouteWorkerIdentityHardLock() {
  try {
    if (typeof window === "undefined") return { workerId: "", workerEmail: "" };
    const params = new URLSearchParams(window.location.search || "");
    return {
      workerId: String(params.get("worker") || params.get("workerId") || params.get("employeeId") || "").trim(),
      workerEmail: normalizeEmailForWorkerVisibility(params.get("workerEmail") || params.get("employeeEmail") || ""),
    };
  } catch (_) {
    return { workerId: "", workerEmail: "" };
  }
}

function filterEventsForOperationalWorker(events, isWorkerView, detectedEmployeeDocId, workerProfile = null, workerEmailMode = "", selectedEmployeeIds = []) {
  if (!isWorkerView) return Array.isArray(events) ? events : [];
  const actor = getEffectiveActor();
  return (Array.isArray(events) ? events : []).filter((ev) =>
    bookingMatchesOperationalWorker(ev, actor, detectedEmployeeDocId, workerProfile, workerEmailMode, selectedEmployeeIds)
  );
}


async function saveStaffScheduleDocSafe(activeBusinessId, bookingId, payload, options = {}) {
  return withScheduleExceptionApproval(async approval => {
    const {_scheduleExpected,...details} = payload;
    const args = {businessId:activeBusinessId,bookingId,payload:loopAdminWorkerBookingCallablePayload(details),...(_scheduleExpected ? {expected:_scheduleExpected} : {}),...options,...(approval?{approval}:{})};
    for (const key of Object.keys(args)) if (args[key] === undefined) delete args[key];
    if (isIosCapacitor()) {
      const idToken = await loopAdminGetWorkerCreateIdToken();
      if (!idToken) throw new Error("Inicia sesión para guardar.");
      const response = await httpRequestJson({url:loopAdminFunctionsBaseUrl()+"/saveStaffBookingSchedule",method:"POST",headers:{Authorization:"Bearer "+idToken},data:{data:args}});
      return response.result || response.data || response;
    }
    const response = await httpsCallable(getFunctions(), "saveStaffBookingSchedule")(args);
    return response.data;
  });
}
async function updateBookingDocSafe(activeBusinessId, bookingId, payload) {
  if (!activeBusinessId || !bookingId) throw new Error("booking path missing");
  if ([...SCHEDULE_KEYS,'status','confirmationStatus'].some(key => Object.hasOwn(payload,key))) return saveStaffScheduleDocSafe(activeBusinessId,bookingId,payload,{operation:"edit",saveDetails:true});
  const path = `businesses/${activeBusinessId}/bookings/${bookingId}`;
  if (isIosCapacitor()) return fsClient.updateDocByPath(path, payload, { db });
  return updateDoc(doc(db, path), payload);
}

function loopAdminWorkerBookingCallableValue(value) {
  if (value === undefined || typeof value === "function") return undefined;
  if (value === null) return null;
  if (value instanceof Date) return { __loopDateIso: value.toISOString() };
  if (value && typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d && !Number.isNaN(d.getTime()) ? { __loopDateIso: d.toISOString() } : null;
    } catch (_) {
      return null;
    }
  }
  if (Array.isArray(value)) {
    return value.map(loopAdminWorkerBookingCallableValue).filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const ctorName = String(value.constructor?.name || "");
    const methodName = String(value._methodName || value.methodName || "");
    if (ctorName.includes("FieldValue") || methodName.includes("serverTimestamp")) return undefined;
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      try {
        return JSON.parse(JSON.stringify(value));
      } catch (_) {
        return undefined;
      }
    }
    const out = {};
    Object.entries(value).forEach(([key, val]) => {
      const clean = loopAdminWorkerBookingCallableValue(val);
      if (clean !== undefined) out[key] = clean;
    });
    return out;
  }
  return value;
}

function loopAdminWorkerBookingCallablePayload(payload = {}) {
  const clean = loopAdminWorkerBookingCallableValue(payload);
  return clean && typeof clean === "object" && !Array.isArray(clean) ? clean : {};
}

function loopAdminFunctionsBaseUrl() {
  const projectId = String(db?.app?.options?.projectId || "").trim();
  return projectId ? `https://us-central1-${projectId}.cloudfunctions.net` : "";
}

async function loopAdminGetWorkerCreateIdToken() {
  try {
    const user = auth?.currentUser || null;
    if (user && typeof user.getIdToken === "function") {
      const token = await user.getIdToken(false);
      if (token) return token;
    }
  } catch (_) {}

  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && ma.idToken) return ma.idToken;
  } catch (_) {}

  return "";
}

function loopAdminPromiseWithTimeout(promise, timeoutMs = 15000, label = "operation_timeout") {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(label)), timeoutMs);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}


// LOOP CUSTOMER IDENTITY R33 P2 — all manual booking creation goes through
// one authenticated Function. Direct Firestore creates remain only for older
// installed app versions until the rollout is validated.
function loopAdminStaffRequestStableValue(value) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map(loopAdminStaffRequestStableValue);
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value.toDate === "function") {
    try {
      const date = value.toDate();
      return date && !Number.isNaN(date.getTime())
        ? date.toISOString()
        : null;
    } catch (_) {
      return null;
    }
  }
  if (typeof value === "object") {
    const constructorName = String(value.constructor?.name || "");
    const methodName = String(value._methodName || value.methodName || "");
    if (
      constructorName.includes("FieldValue") ||
      methodName.includes("serverTimestamp")
    ) {
      return undefined;
    }

    return Object.keys(value)
      .sort()
      .reduce((out, key) => {
        const next = loopAdminStaffRequestStableValue(value[key]);
        if (next !== undefined) out[key] = next;
        return out;
      }, {});
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value;
  return String(value || "").trim();
}

function loopAdminStaffBookingAttemptFingerprint({
  businessId = "",
  actorUid = "",
  payload = {},
} = {}) {
  const snapshot = payload?.customerSnapshot &&
    typeof payload.customerSnapshot === "object"
    ? payload.customerSnapshot
    : {};

  return JSON.stringify(loopAdminStaffRequestStableValue({
    sessionProgramAction: payload.sessionProgramAction || "continue",
    requestedSessionProgramId: payload.requestedSessionProgramId || null,
    sessionPreviousCompleted: Number(payload.sessionPreviousCompleted || 0),
    sessionExpectedActiveIds: payload.sessionExpectedActiveIds || [],
    businessId,
    actorUid,
    customerId:
      payload.customerId ||
      payload.clientId ||
      snapshot.customerId ||
      snapshot.id ||
      null,
    customerName:
      payload.customerName ||
      payload.clientName ||
      snapshot.name ||
      snapshot.customerName ||
      "",
    customerEmail:
      payload.customerEmail ||
      payload.customerEmailNormalized ||
      snapshot.emailNormalized ||
      snapshot.email ||
      "",
    customerPhone:
      payload.customerPhone ||
      payload.customerPhoneNormalized ||
      snapshot.phoneNormalized ||
      snapshot.phone ||
      "",
    scheduledAt: payload.scheduledAt || payload.startAt || null,
    date: payload.date || null,
    time: payload.time || null,
    duration: payload.duration || payload.durationMinutes || 0,
    serviceId: payload.serviceId || null,
    employeeId:
      payload.employeeId ||
      payload.workerId ||
      payload.resourceId ||
      null,
    requestedWorkerMode: payload.requestedWorkerMode || null,
    status: payload.status || payload.confirmationStatus || null,
    totalPrice: payload.totalPrice || 0,
    membershipId:
      payload.customerMembershipId ||
      payload.membershipId ||
      null,
    membershipPlanId:
      payload.membershipPlanId ||
      payload.planId ||
      null,
    notes: payload.notes || payload.internalNotes || "",
    visitMode: payload.visitMode || payload.visitTypeLocation || null,
    customerLocation: payload.customerLocation || null,
    customerExactAddress:
      payload.customerExactAddress ||
      payload.customerAddressDetails ||
      null,
  }));
}

function loopAdminCreateStaffRequestId({
  businessId = "",
  actorUid = "",
} = {}) {
  let randomPart = "";

  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      randomPart = crypto.randomUUID();
    }
  } catch (_) {}

  if (!randomPart) {
    randomPart = `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  const clean = (value, fallback) => {
    const normalized = String(value || fallback)
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    return normalized || fallback;
  };

  return [
    "staff",
    clean(actorUid, "actor"),
    clean(businessId, "business"),
    clean(randomPart, "request"),
  ].join("__").slice(0, 420);
}

async function createStaffBookingDocSafe(activeBusinessId, payload) {
  return withScheduleExceptionApproval(approval => createStaffBookingDocRaw(activeBusinessId, {...payload, ...(approval ? {scheduleExceptionApproval:approval} : {})}));
}
async function createStaffBookingDocRaw(activeBusinessId, payload) {
  if (!activeBusinessId) throw new Error("booking collection path missing");

  const cleanPayload = loopAdminWorkerBookingCallablePayload(payload);

  if (isIosCapacitor()) {
    const baseUrl = loopAdminFunctionsBaseUrl();
    const idToken = await loopAdminGetWorkerCreateIdToken();

    if (!baseUrl) throw new Error("functions_base_url_missing");
    if (!idToken) throw new Error("staff_create_token_missing");

    const url = `${baseUrl}/createStaffBookingHttp`;

    try {
      if (typeof window !== "undefined") {
        window.__LOOP_STAFF_CREATE_HTTP_DEBUG__ = {
          stage: "request_start_r33_p2",
          businessId: String(activeBusinessId),
          employeeId:
            cleanPayload.employeeId ||
            cleanPayload.workerId ||
            cleanPayload.resourceId ||
            null,
          customerId:
            cleanPayload.customerId ||
            cleanPayload.clientId ||
            null,
          clientRequestId:
            cleanPayload.clientRequestId ||
            cleanPayload.clientRequestKey ||
            null,
          at: new Date().toISOString(),
        };
      }
    } catch (_) {}

    const data = await loopAdminPromiseWithTimeout(
      httpRequestJson({
        url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          businessId: String(activeBusinessId),
          booking: cleanPayload,
        },
      }),
      20000,
      "staff_create_http_timeout"
    );

    if (data && data.ok === false) {
      const error = new Error(
        data.error ||
        data.code ||
        "staff_booking_create_failed"
      );
      error.code = data.code || "staff_booking_create_failed";
      error.details = data.details || null;
      throw error;
    }

    try {
      if (typeof window !== "undefined") {
        window.__LOOP_STAFF_CREATE_HTTP_DEBUG__ = {
          stage: "request_success_r33_p2",
          businessId: String(activeBusinessId),
          bookingId: data?.bookingId || data?.id || null,
          clientRequestId:
            cleanPayload.clientRequestId ||
            cleanPayload.clientRequestKey ||
            null,
          duplicate: data?.duplicate === true,
          at: new Date().toISOString(),
        };
      }
    } catch (_) {}

    return {
      id: data.bookingId || data.id || null,
      data,
    };
  }

  const callable = httpsCallable(getFunctions(), "createStaffBooking");
  const response = await loopAdminPromiseWithTimeout(
    callable({
      businessId: String(activeBusinessId),
      booking: cleanPayload,
    }),
    20000,
    "staff_create_callable_timeout"
  );
  const data = response && response.data ? response.data : {};

  if (data && data.ok === false) {
    const error = new Error(
      data.error ||
      data.code ||
      "staff_booking_create_failed"
    );
    error.code = data.code || "staff_booking_create_failed";
    error.details = data.details || null;
    throw error;
  }

  return {
    id: data.bookingId || data.id || null,
    data,
  };
}


// LOOP WORKER BOOKING STATUS ACTIONS V11
// Worker status changes use a callable because direct Firestore updateDoc is denied
// by rules even when Loop permissions show the action button.
async function updateWorkerBookingStatusDocSafe(activeBusinessId, booking, payload = {}, options = {}) {
  if (!activeBusinessId || !booking?.id) throw new Error("worker booking status path missing");
  const employeeId = String(
    options.employeeId ||
    payload.employeeId ||
    payload.workerId ||
    payload.resourceId ||
    payload.assignedEmployeeId ||
    booking.employeeId ||
    booking.workerId ||
    booking.resourceId ||
    booking.assignedEmployeeId ||
    ""
  ).trim();
  if (!employeeId) throw new Error("worker employee missing");
  const callable = httpsCallable(getFunctions(), "updateWorkerBookingStatus");
  const res = await withScheduleExceptionApproval(approval => callable({
    ...(approval ? {approval} : {}),
    businessId: String(activeBusinessId),
    employeeId,
    bookingId: String(booking.id),
    status: options.status || payload.status || payload.confirmationStatus || "pending",
    action: options.action || payload?.statusWorkflow?.source || "worker_status_action",
    payload: loopAdminWorkerBookingCallablePayload(payload),
  }));
  const data = res && res.data ? res.data : {};
  if (data && data.ok === false) throw new Error(data.error || data.code || "worker_booking_status_update_failed");
  return { id: data.bookingId || booking.id, data };
}

// LOOP WORKER CREATE SMOOTH V9
// Build a lightweight local event immediately after the worker-create callable succeeds.
// Firestore can take a moment to re-open the listener in worker mode; this keeps the UI
// feeling instant while the real booking replaces the optimistic copy.
function adminCalendarDateFromBookingLike(raw = {}) {
  const direct = bookingScheduledAtToDate(raw);
  if (direct && !Number.isNaN(direct.getTime())) return direct;

  const candidates = [raw.scheduledAtDate, raw.scheduledAt, raw.startAt, raw.start, raw.dateTime];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value.toDate === "function" ? value.toDate() : new Date(value);
      if (d && !Number.isNaN(d.getTime())) return d;
    } catch (_) {}
  }

  try {
    const dateText = String(raw.date || "").slice(0, 10);
    const timeText = String(raw.time || raw.startTime || "").slice(0, 5);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateText) && /^\d{1,2}:\d{2}$/.test(timeText)) {
      const d = new Date(`${dateText}T${timeText.padStart(5, "0")}:00-05:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}

  return null;
}

function adminCalendarSameLocalDay(a, b) {
  try {
    const left = a instanceof Date ? a : new Date(a);
    const right = b instanceof Date ? b : new Date(b);
    if (Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) return false;
    return left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate();
  } catch (_) {
    return false;
  }
}

function adminCalendarOptimisticEventFromBooking(raw = {}) {
  const startDate = adminCalendarDateFromBookingLike(raw);
  if (!startDate || Number.isNaN(startDate.getTime())) return null;
  const duration = Number(raw.duration || raw.durationMinutes || raw.durationMins || raw.minutes || 60) || 60;
  const endDate = new Date(startDate.getTime() + Math.max(15, duration) * 60000);
  const id = String(raw.id || raw.bookingId || raw.docId || raw._optimisticId || `optimistic-${Date.now()}`).trim();
  const serviceKey = String(raw.serviceId || raw.serviceName || raw.service || "default");
  const serviceName = raw.serviceName || raw.service || raw.packageName || raw.serviceTitle || raw.customerName || "Reserva";
  const resourceId = raw.employeeId || raw.resourceId || raw.workerId || raw.assignedEmployeeId || null;

  return {
    id,
    title: serviceName,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    resourceId,
    extendedProps: {
      raw: { ...raw, id, bookingId: raw.bookingId || id, _optimisticBooking: true },
      serviceKey,
      accent: raw.accent || "#0f766e",
    },
    backgroundColor: "#ffffff",
    borderColor: "rgba(2,6,23,0.10)",
    textColor: "#0f172a",
    display: "block",
  };
}

function adminLoyaltyNormalizePaymentStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["paid", "pagado", "pagada", "collected", "cobrado", "cobrada", "complete", "completed", "succeeded", "success", "captured", "approved", "aprobado", "aprobada"].includes(raw)) return "paid";
  if (["covered", "cubierto", "cubierta"].includes(raw)) return "covered";
  if (["refunded", "refund", "reembolsado", "devuelto"].includes(raw)) return "refunded";
  if (["pending", "pendiente", "unpaid", "open", "por_cobrar", "por cobrar"].includes(raw)) return "pending";
  return raw;
}

function adminLoyaltyNormalizePaymentMethod(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"].includes(raw)) return "loyalty_reward";
  if (["courtesy", "cortesia", "cortesía", "free", "gratis"].includes(raw)) return "courtesy";
  if (["membership", "membresia", "membresía"].includes(raw)) return "membership";
  return raw;
}

function adminLoyaltyPaymentPayloadShouldProcess(paymentPayload = {}, booking = {}) {
  const payload = paymentPayload || {};
  const status = adminLoyaltyNormalizePaymentStatus(
    payload.paymentStatus ||
    payload.payment_status ||
    payload.status ||
    payload.payment?.status ||
    ""
  );
  const method = adminLoyaltyNormalizePaymentMethod(
    payload.paymentMethod ||
    payload.method ||
    payload.payment?.method ||
    booking.paymentMethod ||
    booking.payment?.method ||
    ""
  );

  // Rewards/cortesías should not create another loyalty reward loop.
  if (["loyalty_reward", "courtesy"].includes(method)) return false;

  if (status === "paid") return true;
  if ((payload.paid === true || payload.isPaid === true || payload.paymentPaid === true) && status !== "refunded") return true;

  return false;
}

function adminBookingLoyaltyResultText(result = null) {
  if (!result) return "";
  if (result.ok === false) return "Fidelidad pendiente";
  const processed = Array.isArray(result.processed) ? result.processed.filter((x) => !x?.skipped) : [];
  if (processed.length) {
    const label = processed.map((x) => x.earnedLabel || (x.programType === "points" ? `${x.amount || 0} puntos` : `${x.amount || 0} sello(s)`)).filter(Boolean).join(" + ");
    return label ? `Fidelidad aplicada: ${label}` : "Fidelidad aplicada";
  }
  const skipped = Array.isArray(result.skipped) ? result.skipped : [];
  if (skipped.some((x) => x?.reason === "already_processed")) return "Fidelidad ya aplicada";
  if (result.reason === "no_active_matching_programs") return "Sin programa de fidelidad aplicable";
  if (result.reason === "missing_customer") return "Fidelidad pendiente: falta cliente";
  if (result.reason === "booking_not_paid") return "Fidelidad pendiente: pago no marcado como pagado";
  if (result.reason) return `Fidelidad: ${result.reason}`;
  return "";
}

async function adminTriggerBookingLoyaltySafe({ businessId, bookingId, actor = {}, source = "agenda_payment_update" } = {}) {
  if (!businessId || !bookingId) return null;
  try {
    const fn = httpsCallable(getFunctions(), "reprocessBookingLoyalty");
    const res = await fn({
      businessId,
      bookingId,
      source,
      callerUid: actor?.uid || null,
      callerEmail: actor?.email || null,
    });
    return res && res.data ? res.data : null;
  } catch (err) {
    console.warn("[AdminCalendar] loyalty reprocess skipped/failed", err);
    return { ok: false, error: String(err?.message || err || "loyalty_reprocess_failed").slice(0, 300) };
  }
}

async function updateBusinessCalendarSettingsSafe(activeBusinessId, settings) {
  if (!activeBusinessId) throw new Error("business id missing");
  const path = `businesses/${activeBusinessId}`;
  const payload = {
    calendarSettings: settings,
    calendarSettingsUpdatedAt: loopBusinessNow(),
    updatedAt: loopBusinessNow(),
  };
  return loopBusinessUpdateDoc(db, path, payload, {
    timeoutMs: isIosCapacitor() ? 10000 : 22000,
  });
}



function isEventBusinessCalendar(businessDoc) {
  if (!businessDoc) return false;
  // LOOP AGENDA V67: noWorkers/noWorkerMode means “team not configured yet”, not event business.
  // Event behavior must come from the actual vertical/type/category text below.
  const text = [
    businessDoc.businessMode,
    businessDoc.businessModel,
    businessDoc.businessType,
    businessDoc.type,
    businessDoc.category,
    businessDoc.vertical,
    businessDoc.eventCategory,
  ].map((x) => String(x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()).join(" ");
  return text.includes("event") || text.includes("evento") || text.includes("fotografia") || text.includes("animacion");
}


function adminBookingLocationLabel(raw) {
  const loc = raw?.customerLocation || raw?.location || null;
  const matched = raw?.serviceAreaMatched || raw?.matchedServiceArea || null;
  return String(
    loc?.label ||
    matched?.label ||
    raw?.customerLocationLabel ||
    raw?.serviceAreaLabel ||
    raw?.coverageArea ||
    ""
  ).trim();
}

function adminBookingExactAddress(raw) {
  return String(
    raw?.customerExactAddress ||
    raw?.customerAddressDetails ||
    raw?.exactAddress ||
    raw?.addressDetails ||
    raw?.deliveryAddress ||
    raw?.customerAddress ||
    ""
  ).trim();
}

function adminBookingIsDelivery(raw) {
  const mode = String(raw?.visitMode || raw?.visitTypeLocation || raw?.serviceMode || "").toLowerCase();
  return mode === "delivery" || mode === "domicilio" || Boolean(raw?.customerLocation || raw?.serviceAreaMatched || adminBookingExactAddress(raw));
}

function adminBookingLocationSummary(raw) {
  const isDelivery = adminBookingIsDelivery(raw);
  const zone = adminBookingLocationLabel(raw);
  const exact = adminBookingExactAddress(raw);
  const fee = Number(raw?.deliveryFee ?? raw?.locationFee ?? 0);
  return {
    isDelivery,
    modeLabel: isDelivery ? "A domicilio" : "En local",
    zone,
    exact,
    fee: Number.isFinite(fee) ? fee : 0,
    compact: [isDelivery ? "A domicilio" : "En local", zone].filter(Boolean).join(" · "),
  };
}

function adminBookingLocationPayload(raw = {}) {
  const summary = adminBookingLocationSummary(raw);
  return {
    visitMode: raw?.visitMode || raw?.visitTypeLocation || (summary.isDelivery ? "delivery" : "onsite"),
    visitTypeLocation: raw?.visitTypeLocation || raw?.visitMode || (summary.isDelivery ? "delivery" : "onsite"),
    customerLocation: raw?.customerLocation || null,
    serviceAreaMatched: raw?.serviceAreaMatched || raw?.matchedServiceArea || null,
    customerExactAddress: summary.exact || null,
    customerAddressDetails: summary.exact || null,
    deliveryFee: summary.fee || 0,
    locationFee: summary.fee || 0,
    deliverySummaryLabel: summary.compact || null,
  };
}

function bookingHasConcreteWorkerAssignment(raw = {}) {
  const directWorker = raw?.employeeId || raw?.resourceId || raw?.workerId || raw?.assignedEmployeeId;
  if (directWorker) return true;

  const assignedIds = Array.isArray(raw?.assignedEmployeeIds) ? raw.assignedEmployeeIds.filter(Boolean) : [];
  if (assignedIds.length > 0) return true;

  const assignedEmployees = Array.isArray(raw?.assignedEmployees) ? raw.assignedEmployees : [];
  return assignedEmployees.some((emp) => emp?.id || emp?.employeeId || emp?.uid || emp?.name || emp?.email);
}

function bookingCanBeAcceptedWithoutWorker(raw = {}, isEventBusiness = false) {
  if (isEventBusiness) return true;
  if (raw?.employeeNotRequired === true) return true;
  const assignmentStatus = String(raw?.assignmentStatus || "").trim().toLowerCase();
  if (["not_required", "not-required", "none", "no_worker", "sin_trabajador"].includes(assignmentStatus)) return true;
  return false;
}

function bookingNeedsWorkerBeforeAccept(raw = {}, isEventBusiness = false) {
  if (bookingCanBeAcceptedWithoutWorker(raw, isEventBusiness)) return false;
  return !bookingHasConcreteWorkerAssignment(raw);
}

function serviceAllowsConcurrentSameServiceAdmin(service) {
  if (!service) return false;
  return service.allowConcurrentSameService === true ||
    service.sameServiceCanOverlap === true ||
    service.repeatable === true ||
    service.concurrentSameService === true;
}
function formatDateYmdAdmin(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function adminBookingStartDateForCalendar(raw = {}) {
  try {
    if (raw?.scheduledAt && typeof raw.scheduledAt.toDate === "function") return raw.scheduledAt.toDate();
    if (raw?.scheduledAt) {
      const d = new Date(raw.scheduledAt);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const date = raw?.date ? String(raw.date) : "";
    const time = /^\d{1,2}:\d{2}$/.test(String(raw?.time || "")) ? String(raw.time) : "00:00";
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const d = new Date(`${date}T${time}:00-05:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}
  return null;
}

function buildMonthCalendarEventFromBookingDoc(docId, raw = {}) {
  if (adminBookingIsHiddenFromAgenda(raw)) return null;
  const start = adminBookingStartDateForCalendar(raw);
  if (!start) return null;

  const explicitEnd = raw?.endAt && typeof raw.endAt.toDate === "function"
    ? wallDate(raw.endAt.toDate(),raw)
    : raw?.endAt
      ? wallDate(new Date(raw.endAt),raw)
      : null;
  const duration = Number(raw?.duration ?? raw?.durationMins ?? raw?.minutes ?? raw?.visitMinutes ?? 60);
  const end = explicitEnd && !Number.isNaN(explicitEnd.getTime())
    ? explicitEnd
    : new Date(start.getTime() + (Number.isFinite(duration) && duration > 0 ? duration : 60) * 60000);

  const safeRaw = { ...raw, id: raw.id || docId, bookingId: raw.bookingId || docId };
  const serviceName = getBookingServiceName(safeRaw, raw?.title || "Reserva");
  const clientName = getBookingClientName(safeRaw, "Cliente");
  const workerId = raw?.employeeId || raw?.workerId || raw?.resourceId || raw?.assignedEmployeeId || null;

  return {
    id: String(docId),
    title: serviceName || clientName || "Reserva",
    start,
    end,
    resourceId: workerId ? String(workerId) : undefined,
    extendedProps: {
      ...safeRaw,
      raw: safeRaw,
      status: safeRaw.status || safeRaw.confirmationStatus || "confirmed",
    },
  };
}


const DEFAULT_AGENDA_SETTINGS = {
  timeFormat: "24h",
  scrollStartTime: "07:00",
  minTime: "07:00",
  maxTime: "21:00",
  slotMinutes: 30,
  rowDensity: "compact",
  defaultView: "day",
  showWeekends: true,
  weekStartsOn: "monday",
  colorMode: "service",
  approvalMode: "manual",
};

function normalizeAgendaSettings(next = {}) {
  const merged = { ...DEFAULT_AGENDA_SETTINGS, ...(next || {}) };
  const slot = Number(merged.slotMinutes);
  return {
    ...merged,
    timeFormat: merged.timeFormat === "12h" ? "12h" : "24h",
    scrollStartTime: /^\d{2}:\d{2}$/.test(String(merged.scrollStartTime || "")) ? merged.scrollStartTime : DEFAULT_AGENDA_SETTINGS.scrollStartTime,
    minTime: /^\d{2}:\d{2}$/.test(String(merged.minTime || "")) ? merged.minTime : DEFAULT_AGENDA_SETTINGS.minTime,
    maxTime: /^\d{2}:\d{2}$/.test(String(merged.maxTime || "")) ? merged.maxTime : DEFAULT_AGENDA_SETTINGS.maxTime,
    slotMinutes: [15, 30, 45, 60].includes(slot) ? slot : DEFAULT_AGENDA_SETTINGS.slotMinutes,
    rowDensity: ["compact", "normal", "comfortable"].includes(merged.rowDensity) ? merged.rowDensity : DEFAULT_AGENDA_SETTINGS.rowDensity,
    defaultView: ["day", "weekCompany", "month"].includes(merged.defaultView) ? merged.defaultView : DEFAULT_AGENDA_SETTINGS.defaultView,
    showWeekends: merged.showWeekends === false ? false : true,
    weekStartsOn: ["monday", "sunday"].includes(merged.weekStartsOn) ? merged.weekStartsOn : DEFAULT_AGENDA_SETTINGS.weekStartsOn,
    colorMode: ["service", "worker", "status"].includes(merged.colorMode) ? merged.colorMode : DEFAULT_AGENDA_SETTINGS.colorMode,
    approvalMode: ["auto", "manual"].includes(merged.approvalMode) ? merged.approvalMode : DEFAULT_AGENDA_SETTINGS.approvalMode,
  };
}

function serializeAgendaSettings(settings = {}) {
  const normalized = normalizeAgendaSettings(settings);
  return {
    timeFormat: normalized.timeFormat,
    scrollStartTime: normalized.scrollStartTime,
    minTime: normalized.minTime,
    maxTime: normalized.maxTime,
    slotMinutes: normalized.slotMinutes,
    rowDensity: normalized.rowDensity,
    defaultView: normalized.defaultView,
    showWeekends: normalized.showWeekends,
    weekStartsOn: normalized.weekStartsOn,
    colorMode: normalized.colorMode,
    approvalMode: normalized.approvalMode,
  };
}

function agendaSettingsSignature(activeBusinessId, settings) {
  return `${activeBusinessId || "no-business"}:${JSON.stringify(serializeAgendaSettings(settings))}`;
}

function formatFirestoreTimestamp(value, timeFormat = "24h") {
  try {
    const date = typeof value?.toDate === "function" ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString(interfaceLocale(), {
      hour: "2-digit",
      minute: "2-digit",
      hour12: timeFormat === "12h",
    });
  } catch (_) {
    return null;
  }
}

function timeStringToDuration(value, fallback = "08:00:00") {
  const text = String(value || "");
  if (!/^\d{2}:\d{2}$/.test(text)) return fallback;
  return `${text}:00`;
}

function minutesToDuration(value, fallback = "00:30:00") {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return fallback;
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
}

function formatCalendarTime(value, timeFormat = "24h") {
  const d = value instanceof Date ? value : new Date(value);
  if (!d || Number.isNaN(d.getTime())) return "";

  if (timeFormat === "12h") {
    let hour = d.getHours();
    const minute = String(d.getMinutes()).padStart(2, "0");
    const suffix = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute} ${suffix}`;
  }

  return d.toLocaleTimeString(interfaceLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatCalendarRange(start, end, timeFormat = "24h") {
  const a = formatCalendarTime(start, timeFormat);
  const b = end ? formatCalendarTime(end, timeFormat) : "";
  return [a, b].filter(Boolean).join(" - ");
}


function formatAdminMoveDateTime(date, timeFormat = "24h") {
  if (!date || Number.isNaN(new Date(date).getTime())) return "—";
  const d = new Date(date);
  const day = d.toLocaleDateString(interfaceLocale(), { weekday: "short", day: "numeric", month: "short" });
  return `${day} · ${formatCalendarTime(d, timeFormat)}`;
}

function formatAdminMoveRange(start, end, timeFormat = "24h") {
  if (!start) return "—";
  const d = new Date(start);
  const day = d.toLocaleDateString(interfaceLocale(), { weekday: "short", day: "numeric", month: "short" });
  return `${day} · ${formatCalendarRange(start, end, timeFormat)}`;
}

function adminBookingMoveIdFromEvent(eventLike, raw = {}) {
  const candidates = [
    raw?.id,
    raw?.bookingId,
    raw?.docId,
    raw?.bookingDocId,
    raw?.reservationId,
    raw?.uid,
    raw?._id,
    raw?.ref?.id,
    raw?.doc?.id,
    raw?.snapshot?.id,
    eventLike?.id,
    eventLike?._def?.publicId,
    eventLike?.extendedProps?.id,
    eventLike?.extendedProps?.bookingId,
    eventLike?.extendedProps?.docId,
    eventLike?.extendedProps?.raw?.id,
    eventLike?.extendedProps?.raw?.bookingId,
    eventLike?.extendedProps?.raw?.docId,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value && value !== "undefined" && value !== "null") return value;
  }

  const path = String(raw?.path || raw?.ref?.path || eventLike?.extendedProps?.path || "").trim();
  if (path) {
    const parts = path.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last) return last;
  }

  return "";
}

function adminBookingMoveResourceIdFromEvent(eventLike, raw = {}) {
  try {
    const resources = typeof eventLike?.getResources === "function" ? eventLike.getResources() : [];
    if (resources && resources[0]?.id) return String(resources[0].id);
  } catch (_) {}
  return String(
    eventLike?.resourceId ||
    eventLike?.resourceIds?.[0] ||
    eventLike?.extendedProps?.resourceId ||
    raw?.employeeId ||
    raw?.resourceId ||
    raw?.workerId ||
    raw?.assignedEmployeeId ||
    ""
  ).trim();
}

function adminBookingMoveHHMM(date) {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function adminBookingMoveStatus(raw = {}) {
  return String(raw?.status || raw?.confirmationStatus || raw?.state || "").trim().toLowerCase();
}

function adminBookingMoveIsCancelled(raw = {}) {
  return ["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "no_show", "noshow"].includes(adminBookingMoveStatus(raw));
}

function adminBookingMoveStart(rawOrEvent = {}) {
  const raw = rawOrEvent?.extendedProps?.raw || rawOrEvent?.raw || rawOrEvent?.booking || rawOrEvent || {};
  if (rawOrEvent?.start) {
    const d = new Date(rawOrEvent.start);
    if (!Number.isNaN(d.getTime())) return d;
  }
  try {
    const d = bookingScheduledAtToDate(raw);
    if (d && !Number.isNaN(d.getTime())) return d;
  } catch (_) {}
  return adminBookingStartDateForCalendar(raw);
}

function adminBookingMoveEnd(rawOrEvent = {}, start = null) {
  const raw = rawOrEvent?.extendedProps?.raw || rawOrEvent?.raw || rawOrEvent?.booking || rawOrEvent || {};
  if (rawOrEvent?.end) {
    const d = new Date(rawOrEvent.end);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const direct = raw?.endAt && typeof raw.endAt.toDate === "function" ? raw.endAt.toDate() : raw?.endAt ? new Date(raw.endAt) : null;
  if (direct && !Number.isNaN(direct.getTime())) return wallDate(direct,raw);
  const safeStart = start || adminBookingMoveStart(raw);
  if (!safeStart) return null;
  const duration = Number(raw?.durationMinutes || raw?.duration || raw?.serviceDuration || 60);
  return new Date(safeStart.getTime() + (Number.isFinite(duration) && duration > 0 ? duration : 60) * 60000);
}

function adminBookingMoveEmployeeLabel(employees = [], id = "") {
  if (!id) return "Sin trabajador";
  const emp = (Array.isArray(employees) ? employees : []).find((e) => String(e.id) === String(id));
  return emp?.name || emp?.displayName || emp?.email || String(id);
}

function adminBookingMoveHasConflict({ events = [], bookingId = "", resourceId = "", start, end }) {
  if (!resourceId || !start || !end) return false;
  const rid = String(resourceId);
  const currentId = String(bookingId || "");
  return (Array.isArray(events) ? events : []).some((ev) => {
    const raw = getBookingRawFromCalendarEvent(ev);
    if (!raw || adminBookingMoveIsCancelled(raw)) return false;
    const id = adminBookingMoveIdFromEvent(ev, raw);
    if (currentId && id && String(id) === currentId) return false;
    const otherRid = adminBookingMoveResourceIdFromEvent(ev, raw);
    if (!otherRid || String(otherRid) !== rid) return false;
    const otherStart = adminBookingMoveStart(ev);
    const otherEnd = adminBookingMoveEnd(ev, otherStart);
    if (!otherStart || !otherEnd) return false;
    return start < otherEnd && end > otherStart;
  });
}


function adminBookingMoveEmployeeById(employees = [], id = "") {
  if (!id) return null;
  return (Array.isArray(employees) ? employees : []).find((e) => String(e?.id) === String(id)) || null;
}

function adminBookingMoveMinutesOfDay(date) {
  const d = new Date(date);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

function adminBookingMoveAvailabilityWarning(employee, start, end, businessDoc = null) {
  if (!employee || !start || !end) return null;
  const ranges = rangesForEmployeeOnDateWithFallback(employee, start, businessDoc);
  if (ranges === null) {
    // No schedule configured anywhere: do not block or warn as outside hours.
    return null;
  }
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return {
      title: "Trabajador cerrado ese día",
      message: `${employee.name || employee.displayName || "Este trabajador"} no tiene horario activo para ese día.`,
    };
  }

  const startMin = adminBookingMoveMinutesOfDay(start);
  const endMin = adminBookingMoveMinutesOfDay(end);
  if (startMin == null || endMin == null) return null;
  const inside = ranges.some((r) => startMin >= r.fromMin && endMin <= r.toMin);
  if (inside) return null;

  const scheduleLabel = ranges
    .map((r) => `${String(Math.floor(r.fromMin / 60)).padStart(2, "0")}:${String(r.fromMin % 60).padStart(2, "0")}–${String(Math.floor(r.toMin / 60)).padStart(2, "0")}:${String(r.toMin % 60).padStart(2, "0")}`)
    .join(", ");

  return {
    title: "Fuera del horario del trabajador",
    message: `${employee.name || employee.displayName || "Este trabajador"} normalmente trabaja ${scheduleLabel}.`,
  };
}

function adminBookingMoveServiceIdFromBooking(raw = {}) {
  return String(raw?.serviceId || raw?.serviceID || raw?.selectedServiceId || raw?.service?.id || "").trim();
}

function adminBookingMoveServiceNameFromBooking(raw = {}, services = []) {
  const direct = getBookingServiceName(raw, "");
  if (direct) return direct;
  const serviceId = adminBookingMoveServiceIdFromBooking(raw);
  if (!serviceId) return "este servicio";
  const svc = (Array.isArray(services) ? services : []).find((s) => String(s?.id) === serviceId);
  return svc?.name || svc?.title || "este servicio";
}

function adminBookingMoveEmployeeServiceIds(employee = {}) {
  const possibleArrays = [
    employee.serviceIds,
    employee.servicesIds,
    employee.allowedServiceIds,
    employee.bookableServiceIds,
    employee.assignedServiceIds,
    employee.services,
    employee.bookableServices,
    employee.specialties,
  ];
  const ids = new Set();
  possibleArrays.forEach((value) => {
    if (!Array.isArray(value)) return;
    value.forEach((item) => {
      if (item == null) return;
      if (typeof item === "string" || typeof item === "number") ids.add(String(item));
      else if (typeof item === "object") {
        const id = item.id || item.serviceId || item.value || item.uid;
        if (id != null) ids.add(String(id));
      }
    });
  });
  return Array.from(ids);
}

function adminBookingMoveServiceWarning(employee, raw = {}, services = []) {
  if (!employee) return null;
  const serviceId = adminBookingMoveServiceIdFromBooking(raw);
  if (!serviceId) return null;
  const allowedIds = adminBookingMoveEmployeeServiceIds(employee);
  // If the worker has no service restrictions saved, do not warn.
  if (!allowedIds.length) return null;
  if (allowedIds.map(String).includes(String(serviceId))) return null;

  return {
    title: "Servicio no asignado a este trabajador",
    message: `${employee.name || employee.displayName || "Este trabajador"} no aparece como asignado para ${adminBookingMoveServiceNameFromBooking(raw, services)}.`,
  };
}

function adminBookingMoveBuildWarnings({ employees = [], services = [], events = [], bookingId = "", resourceId = "", start, end, raw = {}, businessDoc = null }) {
  const warnings = [];
  const employee = adminBookingMoveEmployeeById(employees, resourceId);

  if (adminBookingMoveHasConflict({ events, bookingId, resourceId, start, end })) {
    warnings.push({
      title: "Ese bloque ya tiene una reserva",
      message: `${adminBookingMoveEmployeeLabel(employees, resourceId)} ya tiene otra reserva en ese horario.`,
    });
  }

  const availabilityWarning = adminBookingMoveAvailabilityWarning(employee, start, end, businessDoc);
  if (availabilityWarning) warnings.push(availabilityWarning);

  const serviceWarning = adminBookingMoveServiceWarning(employee, raw, services);
  if (serviceWarning) warnings.push(serviceWarning);

  return warnings;
}

function SettingsOptionGroup({ value, options, onChange, ariaLabel }) {
  return (
    <div className="agenda-settings-segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? "is-active" : ""}
          onClick={() => onChange(option.value)}
        >
          {t(option.label)}
        </button>
      ))}
    </div>
  );
}

function AgendaSettingsPanel({
  settings,
  onChange,
  onSave,
  onReset,
  onApplyDefaultView,
  savedAt,
  saveStatus = "idle",
  saveError = "",
  isDirty = false,
}) {
  const update = (patch) => onChange(normalizeAgendaSettings({ ...settings, ...patch }));
  const timeFormat = settings.timeFormat || "24h";
  const minVisibleMinutes = parseHHMMToMinutes(settings.minTime);
  const maxVisibleMinutes = parseHHMMToMinutes(settings.maxTime);
  const scrollVisibleMinutes = parseHHMMToMinutes(settings.scrollStartTime);
  const hasInvalidVisibleRange =
    minVisibleMinutes != null && maxVisibleMinutes != null && maxVisibleMinutes <= minVisibleMinutes;
  const hasScrollOutsideRange =
    !hasInvalidVisibleRange &&
    scrollVisibleMinutes != null &&
    minVisibleMinutes != null &&
    maxVisibleMinutes != null &&
    (scrollVisibleMinutes < minVisibleMinutes || scrollVisibleMinutes >= maxVisibleMinutes);
  const settingsWarning = hasInvalidVisibleRange
    ? "El rango visible no es válido: la hora final debe ser mayor que la hora inicial."
    : hasScrollOutsideRange
      ? "La hora inicial visible está fuera del rango visible. La agenda podría abrir fuera del horario mostrado."
      : "";
  const blocksSave = hasInvalidVisibleRange;
  const saveButtonLabel = saveStatus === "saving"
    ? "Guardando…"
    : blocksSave
      ? "Corrige el rango visible"
      : isDirty
        ? "Guardar cambios pendientes"
        : "Guardar cambios";
  const statusLabel = saveError
    ? saveError
    : settingsWarning
      ? settingsWarning
      : saveStatus === "saving"
        ? "Guardando en Firestore…"
        : isDirty
          ? "Cambios aplicados temporalmente. Presiona guardar para mantenerlos."
          : savedAt
            ? `Guardado en Firestore ${savedAt}`
            : "Listo para guardar en Firestore";

  return (
    <div className="agenda-settings-canvas">
      <div className="agenda-settings-shell">
        <div className="agenda-settings-hero">
          <span className="agenda-settings-kicker">Loop Agenda</span>
          <h2>{t("Ajustes de agenda")}</h2>
          <p>
            Personaliza cómo se ve y se comporta tu calendario. Los cambios se aplican en pantalla
            mientras editas, pero solo quedan permanentes cuando presionas Guardar cambios.
          </p>
        </div>

        <div className="agenda-settings-grid">
          <section className="agenda-settings-card">
            <div className="agenda-settings-card-head">
              <span>{t("Visualización")}</span>
              <p>{t("Formato, vista inicial, rango visible y densidad del calendario.")}</p>
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Formato de hora")}</strong>
                <small>{t("Elige entre 13:00 o 1:00 PM.")}</small>
              </div>
              <SettingsOptionGroup
                value={timeFormat}
                ariaLabel="Formato de hora"
                options={[{ value: "24h", label: "24h" }, { value: "12h", label: "12h" }]}
                onChange={(value) => update({ timeFormat: value })}
              />
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Vista inicial")}</strong>
                <small>{t("La vista que quieres usar como estándar.")}</small>
              </div>
              <SettingsOptionGroup
                value={settings.defaultView}
                ariaLabel="Vista inicial"
                options={[{ value: "day", label: "Día" }, { value: "weekCompany", label: "Semana" }, { value: "month", label: "Mes" }]}
                onChange={(value) => {
                  update({ defaultView: value });
                  onApplyDefaultView(value);
                }}
              />
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Mostrar fines de semana")}</strong>
                <small>{t("Decide si sábado y domingo aparecen en la vista semanal y mensual.")}</small>
              </div>
              <SettingsOptionGroup
                value={settings.showWeekends ? "yes" : "no"}
                ariaLabel="Mostrar fines de semana"
                options={[{ value: "yes", label: "Sí" }, { value: "no", label: "No" }]}
                onChange={(value) => update({ showWeekends: value === "yes" })}
              />
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Primer día de la semana")}</strong>
                <small>{t("Define cómo arranca la semana en la agenda.")}</small>
              </div>
              <SettingsOptionGroup
                value={settings.weekStartsOn}
                ariaLabel="Primer día de la semana"
                options={[{ value: "monday", label: "Lunes" }, { value: "sunday", label: "Domingo" }]}
                onChange={(value) => update({ weekStartsOn: value })}
              />
            </div>

            <div className="agenda-setting-row two-controls">
              <div>
                <strong>{t("Hora inicial visible")}</strong>
                <small>{t("La hora a la que la agenda abre al cargar.")}</small>
              </div>
              <input
                type="time"
                value={settings.scrollStartTime}
                onChange={(e) => update({ scrollStartTime: e.target.value })}
              />
            </div>

            <div className="agenda-setting-row two-controls">
              <div>
                <strong>{t("Rango visible")}</strong>
                <small>{t("Oculta horas que no usas normalmente.")}</small>
              </div>
              <div className="agenda-settings-time-pair">
                <input type="time" value={settings.minTime} onChange={(e) => update({ minTime: e.target.value })} />
                <span>—</span>
                <input type="time" value={settings.maxTime} onChange={(e) => update({ maxTime: e.target.value })} />
              </div>
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Intervalo de agenda")}</strong>
                <small>{t("Define si los bloques se ven cada 15, 30 o 60 minutos.")}</small>
              </div>
              <SettingsOptionGroup
                value={Number(settings.slotMinutes)}
                ariaLabel="Intervalo de agenda"
                options={[{ value: 15, label: "15 min" }, { value: 30, label: "30 min" }, { value: 60, label: "60 min" }]}
                onChange={(value) => update({ slotMinutes: value })}
              />
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Tamaño de filas")}</strong>
                <small>{t("Útil cuando usas intervalos de 15 minutos para que la agenda no se vea gigante.")}</small>
              </div>
              <SettingsOptionGroup
                value={settings.rowDensity}
                ariaLabel="Tamaño de filas"
                options={[{ value: "compact", label: "Compacto" }, { value: "normal", label: "Normal" }, { value: "comfortable", label: "Amplio" }]}
                onChange={(value) => update({ rowDensity: value })}
              />
            </div>
          </section>

          <section className="agenda-settings-card">
            <div className="agenda-settings-card-head">
              <div className="agenda-settings-card-title-row">
                <span>{t("Reservas")}</span>
                <em>{t("Afecta reservas nuevas")}</em>
              </div>
              <p>{t("Reglas para confirmar reservas y leer más rápido tu día.")}</p>
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Confirmación de reservas online")}</strong>
                <small>{t("Define si las reservas entran directo o si necesitan aprobación del negocio.")}</small>
                <small className="agenda-setting-info-note">
                  {settings.approvalMode === "auto"
                    ? "Las reservas nuevas se confirmarán sin revisión manual si el horario está disponible."
                    : "Las reservas nuevas entrarán como pendientes y se gestionarán desde Reservas."}
                </small>
              </div>
              <SettingsOptionGroup
                value={settings.approvalMode}
                ariaLabel="Confirmación de reservas online"
                options={[{ value: "manual", label: "Requiere aprobación" }, { value: "auto", label: "Confirmar automático" }]}
                onChange={(value) => update({ approvalMode: value })}
              />
            </div>

            <div className="agenda-settings-helper-callout">
              <strong>{settings.approvalMode === "auto" ? "Confirmación automática activa" : "Aprobación manual activa"}</strong>
              <span>
                {settings.approvalMode === "auto"
                  ? "Solo aplica a reservas nuevas. Las reservas pendientes existentes no cambian de estado automáticamente."
                  : "Las reservas nuevas aparecerán en el panel de pendientes para que el negocio las revise."}
              </span>
            </div>

            <div className="agenda-setting-row">
              <div>
                <strong>{t("Colorear reservas por")}</strong>
                <small>{t("Primera base visual. Luego conectamos colores reales por servicio/trabajador.")}</small>
                <small className="agenda-setting-info-note muted">{t("Por ahora este ajuste es visual; no cambia reglas de reserva.")}</small>
              </div>
              <SettingsOptionGroup
                value={settings.colorMode}
                ariaLabel="Colorear reservas por"
                options={[{ value: "service", label: "Servicio" }, { value: "worker", label: "Trabajador" }, { value: "status", label: "Estado" }]}
                onChange={(value) => update({ colorMode: value })}
              />
            </div>

            <div className="agenda-settings-preview">
              <span>{t("Vista previa")}</span>
              <div className="agenda-settings-preview-card">
                <b>{formatCalendarRange(new Date(2026, 4, 29, 13, 30), new Date(2026, 4, 29, 14, 0), timeFormat)}</b>
                <strong>Eduarda</strong>
                <small>Corte de cabello</small>
              </div>
              <p className="agenda-settings-note">{t("Modo actual:")}{settings.approvalMode === "auto" ? "las reservas online se confirmarían automáticamente." : "las reservas online quedarían pendientes de aprobación."}
              </p>
            </div>
          </section>
        </div>

        <div className="agenda-settings-footer">
          <button
            type="button"
            className="agenda-settings-reset"
            onClick={onReset}
          >{t("Volver a valores recomendados")}</button>
          <div className="agenda-settings-save-area">
            <span className={saveError ? "agenda-settings-error" : settingsWarning ? "agenda-settings-warning" : isDirty ? "agenda-settings-dirty" : ""}>
              {statusLabel}
            </span>
            <button
              type="button"
              className={`agenda-settings-save ${isDirty ? "has-pending-changes" : ""}`}
              onClick={onSave}
              disabled={saveStatus === "saving" || blocksSave}
            >
              {saveButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



function AdminScheduleChangeDialog({
  change,
  saving = false,
  onCancel,
  onConfirm,
}) {
  if (!change) return null;

  const blocked = change.mode === "blocked";
  const warningConfirm = !blocked && Array.isArray(change.warnings) && change.warnings.length > 0;
  const title = blocked
    ? "No se puede hacer este cambio"
    : warningConfirm
      ? "Confirmar cambio con advertencias"
      : change.type === "resize"
        ? "Confirmar cambio de duración"
        : "Confirmar cambio de reserva";

  return (
    <div className="loop-schedule-change-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`loop-schedule-change-dialog ${blocked ? "is-blocked" : ""}`}>
        <div className="loop-schedule-change-head">
          <span className={`loop-schedule-change-icon ${blocked ? "is-blocked" : warningConfirm ? "is-warning" : ""}`}>
            {blocked ? "!" : warningConfirm ? "?" : "↔"}
          </span>
          <div>
            <p>{blocked ? "Revisión necesaria" : warningConfirm ? "El dueño puede continuar" : "Antes de guardar"}</p>
            <h3>{title}</h3>
          </div>
        </div>

        <div className="loop-schedule-change-body">
          <div className="loop-schedule-change-booking">
            <strong>{change.client || t("Cliente")}</strong>
            <span>{change.service || t("Reserva")}</span>
          </div>

          {blocked ? (
            <div className="loop-schedule-change-warning">
              <strong>{change.blockTitle || "Hay un conflicto de horario."}</strong>
              <span>{change.blockMessage || "Elige otro horario o trabajador para continuar."}</span>
            </div>
          ) : (
            <>
              <div className="loop-schedule-change-grid">
                <div>
                  <span>{t("Antes")}</span>
                  <strong>{change.oldRange || "—"}</strong>
                </div>
                <div>
                  <span>{t("Después")}</span>
                  <strong>{change.newRange || "—"}</strong>
                </div>
              </div>

              <div className="loop-schedule-change-details">
                <div>
                  <span>{t("Trabajador")}</span>
                  <strong>{change.workerChange || change.newWorker || t("Sin trabajador")}</strong>
                </div>
                <div>
                  <span>{t("Duración")}</span>
                  <strong>{change.durationLabel || "—"}</strong>
                </div>
              </div>

              {warningConfirm ? (
                <div className="loop-schedule-change-warning-list">
                  {change.warnings.map((warning, index) => (
                    <div key={`${warning.title || "warning"}-${index}`} className="loop-schedule-change-warning-item">
                      <strong>{warning.title || "Advertencia"}</strong>
                      <span>{warning.message || "Puedes continuar si deseas forzar el cambio."}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              <p className="loop-schedule-change-note">
                {warningConfirm
                  ? "Puedes guardar el cambio de todas formas. Loop lo marcará como cambio manual del dueño."
                  : "Este cambio actualizará la hora, duración y trabajador de la reserva en la agenda."}
              </p>
            </>
          )}
        </div>

        <div className="loop-schedule-change-actions">
          <button
            type="button"
            className="loop-schedule-change-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            {blocked ? "Entendido" : t("Cancelar")}
          </button>
          {!blocked ? (
            <button
              type="button"
              className="loop-schedule-change-primary"
              onClick={onConfirm}
              disabled={saving}
            >
              {saving ? t("Guardando...") : warningConfirm ? "Guardar de todas formas" : t("Confirmar cambio")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function getAgendaSlotHeight(slotMinutes, rowDensity = "compact") {
  const minutes = Number(slotMinutes);
  const density = ["compact", "normal", "comfortable"].includes(rowDensity) ? rowDensity : "compact";
  const table = {
    compact: { 15: 20, 30: 40, 60: 80 },
    normal: { 15: 24, 30: 48, 60: 96 },
    comfortable: { 15: 28, 30: 56, 60: 112 },
  };
  return table[density]?.[minutes] || table.compact[30];
}

// LOOP CALENDAR OVERLAP LAYOUT V1 — split overlapping bookings into horizontal columns.
// The rule is platform-agnostic: same worker lane + same time collision = same layout.
function layoutOverlappingAgendaEvents(items = []) {
  const sorted = [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aStart = a?.start instanceof Date ? a.start.getTime() : new Date(a?.start || 0).getTime();
    const bStart = b?.start instanceof Date ? b.start.getTime() : new Date(b?.start || 0).getTime();
    if (aStart !== bStart) return aStart - bStart;
    const aEnd = a?.end instanceof Date ? a.end.getTime() : new Date(a?.end || a?.start || 0).getTime();
    const bEnd = b?.end instanceof Date ? b.end.getTime() : new Date(b?.end || b?.start || 0).getTime();
    return aEnd - bEnd;
  });

  const laidOut = [];
  let cluster = [];
  let clusterEndMs = -Infinity;

  const flushCluster = () => {
    if (!cluster.length) return;
    let active = [];
    let maxColumns = 1;
    const placed = cluster.map((item) => {
      const startMs = item.start instanceof Date ? item.start.getTime() : new Date(item.start || 0).getTime();
      const endMs = item.end instanceof Date ? item.end.getTime() : new Date(item.end || item.start || 0).getTime();
      active = active.filter((entry) => entry.endMs > startMs);
      const used = new Set(active.map((entry) => entry.column));
      let column = 0;
      while (used.has(column)) column += 1;
      active.push({ column, endMs });
      maxColumns = Math.max(maxColumns, column + 1, active.length);
      return { ...item, overlapColumn: column };
    });
    placed.forEach((item) => laidOut.push({ ...item, overlapColumns: maxColumns }));
    cluster = [];
    clusterEndMs = -Infinity;
  };

  sorted.forEach((item) => {
    const startMs = item.start instanceof Date ? item.start.getTime() : new Date(item.start || 0).getTime();
    const endMs = item.end instanceof Date ? item.end.getTime() : new Date(item.end || item.start || 0).getTime();
    if (cluster.length && startMs >= clusterEndMs) flushCluster();
    cluster.push(item);
    clusterEndMs = Math.max(clusterEndMs, endMs);
  });
  flushCluster();
  return laidOut;
}

/* ---------------------------
   Loop custom resource day grid
   One CSS grid owns header + body alignment.
   This avoids FullCalendar header/body table drift on narrow web layouts.
--------------------------- */
function LoopResourceDayGrid({
  resources = [],
  events = [],
  selectedDate,
  height = 700,
  onSlotClick,
  onEventClick,
  onEventQuickChange,
  canEditEvents = false,
  slotMinutes = 30,
  rowDensity = "compact",
  timeFormat = "24h",
  minTime = "07:00",
  maxTime = "22:00",
  scrollStartTime = "08:00",
  colorMode = "service",
  businessDoc = null,
  axisWidthOverride = null,
  resourceWidthOverride = null,
  headerHeightOverride = null,
  roleContext = null,
}) {
  const scrollerRef = useRef(null);
  const gridRef = useRef(null);
  const suppressEventClickRef = useRef(false);
  // LOOP CALENDAR MOBILE TAP GUARD V1
  // Mobile creation uses a protected single tap: scrolling/panning never opens
  // the create flow, while mouse/keyboard clicks keep their normal behavior.
  const gridPanRef = useRef({
    active: false,
    pointerId: null,
    pointerType: "",
    startedAt: 0,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
    moved: false,
    slotIndex: null,
    resourceId: "",
    armedSlotEl: null,
    suppressClickUntil: 0,
  });
  const [nowTick, setNowTick] = useState(() => new Date());
  const [dragDraft, setDragDraft] = useState(null);
  const isMobileResourceGrid = typeof window !== "undefined" && window.innerWidth <= 760;
  const axisWidth = positiveDimension(axisWidthOverride, isMobileResourceGrid ? 46 : (timeFormat === "12h" ? 88 : 64));
  const minResourceWidth = positiveDimension(resourceWidthOverride, isMobileResourceGrid ? 156 : 180);
  const safeSlotMinutes = [15, 30, 60].includes(Number(slotMinutes)) ? Number(slotMinutes) : 30;
  const slotHeight = getAgendaSlotHeight(safeSlotMinutes, rowDensity);
  const headerHeight = positiveDimension(headerHeightOverride, isMobileResourceGrid ? 58 : 64);
  const safeResources = Array.isArray(resources) ? resources.filter((r) => r && r.id) : [];
  const safeEvents = Array.isArray(events) ? events : [];
  const resourceCount = Math.max(1, safeResources.length);
  const gridTemplateColumns = `${axisWidth}px repeat(${resourceCount}, minmax(${minResourceWidth}px, 1fr))`;
  const minWidth = axisWidth + resourceCount * minResourceWidth;

  const dayStart = startOfDay(selectedDate || new Date());
  const minMinutes = parseHHMMToMinutes(minTime) ?? 0;
  const rawMaxMinutes = parseHHMMToMinutes(maxTime) ?? (24 * 60);
  const maxMinutes = rawMaxMinutes <= minMinutes ? 24 * 60 : rawMaxMinutes;

  const slots = useMemo(() => {
    const totalMinutes = Math.max(safeSlotMinutes, maxMinutes - minMinutes);
    const slotCount = Math.ceil(totalMinutes / safeSlotMinutes);
    return Array.from({ length: slotCount }, (_, i) => {
      const minutes = minMinutes + i * safeSlotMinutes;
      const d = new Date(dayStart);
      d.setMinutes(minutes, 0, 0);
      const mm = String(d.getMinutes()).padStart(2, "0");
      return {
        index: i,
        minutes,
        label: mm === "00" ? formatCalendarTime(d, timeFormat) : "",
        date: d,
        isHour: mm === "00",
      };
    });
  }, [dayStart, minMinutes, maxMinutes, safeSlotMinutes, timeFormat]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // LOOP AGENDA — VERTICAL RESOURCE GRID START FIX V16
    // In narrow/portrait web layouts the grid must start at the worker header,
    // not auto-scroll down to scrollStartTime. The user's console test confirmed
    // scrollTop = 0 fixes the visual cut.
    const isPortraitNarrowWeb =
      typeof window !== "undefined" &&
      window.innerWidth <= 760 &&
      window.innerHeight > window.innerWidth;

    const scrollMinutes = parseHHMMToMinutes(scrollStartTime) ?? 8 * 60;
    const target = isPortraitNarrowWeb
      ? 0
      : Math.max(0, ((scrollMinutes - minMinutes) / safeSlotMinutes) * slotHeight - 12);

    requestAnimationFrame(() => {
      el.scrollTop = target;
    });
  }, [selectedDate, slotHeight, scrollStartTime, minMinutes, safeSlotMinutes]);

  const getVisualMaxScrollTop = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const grid = gridRef.current || el.querySelector?.(".loop-resource-day-grid");
    const gridHeight = grid
      ? Math.ceil(grid.getBoundingClientRect?.().height || grid.offsetHeight || grid.scrollHeight || 0)
      : 0;
    const fallbackMax = Math.max(0, (el.scrollHeight || 0) - (el.clientHeight || 0));
    if (!gridHeight) return fallbackMax;
    return Math.max(0, Math.min(fallbackMax, gridHeight - (el.clientHeight || 0)));
  }, []);

  const clampVisualScrollTop = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxTop = getVisualMaxScrollTop();
    if (el.scrollTop > maxTop + 1) el.scrollTop = maxTop;
    if (el.scrollTop < 0) el.scrollTop = 0;
  }, [getVisualMaxScrollTop]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    let rafId = 0;
    const onScroll = () => {
      const state = gridPanRef.current || {};
      if (state.active) {
        const scrollDistance =
          Math.abs((el.scrollLeft || 0) - Number(state.startLeft || 0)) +
          Math.abs((el.scrollTop || 0) - Number(state.startTop || 0));
        if (scrollDistance > 2) {
          state.moved = true;
          state.suppressClickUntil = Date.now() + 360;
          if (state.armedSlotEl?.classList) {
            state.armedSlotEl.classList.remove("is-touch-armed");
            state.armedSlotEl = null;
          }
        }
      }
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(clampVisualScrollTop);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    rafId = window.requestAnimationFrame(clampVisualScrollTop);
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [clampVisualScrollTop, slots.length, safeResources.length, selectedDate, slotHeight, headerHeight]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(new Date()), 60000);
    return () => window.clearInterval(id);
  }, []);

  function getEventStart(ev) {
    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
    return (
      ev?.start ? new Date(ev.start) :
      ev?.startStr ? new Date(ev.startStr) :
      bookingScheduledAtToDate(raw) ||
      null
    );
  }

  function getEventEnd(ev, start) {
    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
    if (ev?.end) return new Date(ev.end);
    if (ev?.endStr) return new Date(ev.endStr);
    const duration = Number(raw?.durationMinutes || raw?.duration || raw?.serviceDuration || 30);
    const d = new Date(start || new Date());
    d.setMinutes(d.getMinutes() + (Number.isFinite(duration) && duration > 0 ? duration : 30));
    return d;
  }

  function getEventResourceId(ev) {
    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
    const rid = String(
      ev?.resourceId ||
      ev?.resourceIds?.[0] ||
      ev?.extendedProps?.resourceId ||
      raw?.employeeId ||
      raw?.resourceId ||
      raw?.workerId ||
      raw?.assignedEmployeeId ||
      ""
    );

    // LOOP AGENDA MADRE PHASE A V68
    // No-worker bookings legitimately have no employee/resource. When the grid
    // is showing the virtual Agenda column, paint those bookings there without
    // saving a fake employee id.
    if (!rid && safeResources.length === 1) {
      return String(safeResources[0]?.id || "");
    }

    return rid;
  }


  function minutesFromPointer(clientY, laneEl) {
    if (!laneEl) return minMinutes;
    const rect = laneEl.getBoundingClientRect();
    const y = clientY - rect.top;
    const rawMinutes = minMinutes + (y / slotHeight) * safeSlotMinutes;
    const snapUnit = Number(safeSlotMinutes || 30) || 30;
    const snapped = Math.round(rawMinutes / snapUnit) * snapUnit;
    return Math.max(minMinutes, Math.min(maxMinutes, snapped));
  }

  function resourceIdFromPointer(clientX, fallbackId = "") {
    try {
      const el = document.elementFromPoint(clientX, Math.max(1, (scrollerRef.current?.getBoundingClientRect()?.top || 0) + 120));
      const lane = el?.closest?.(".loop-resource-day-lane[data-resource-id]");
      const id = lane?.getAttribute?.("data-resource-id");
      if (id) return id;
    } catch (_) {}
    try {
      const lanes = Array.from(scrollerRef.current?.querySelectorAll?.(".loop-resource-day-lane[data-resource-id]") || []);
      const hit = lanes.find((lane) => {
        const r = lane.getBoundingClientRect();
        return clientX >= r.left && clientX <= r.right;
      });
      const id = hit?.getAttribute?.("data-resource-id");
      if (id) return id;
    } catch (_) {}
    return fallbackId;
  }

  function dateFromMinutes(minutes) {
    const d = new Date(dayStart);
    d.setHours(0, 0, 0, 0);
    d.setMinutes(minutes, 0, 0);
    return d;
  }

  function startQuickChange(pointerEvent, item, type) {
    if (!canEditEvents || !onEventQuickChange) return;
    if (pointerEvent.button != null && pointerEvent.button !== 0) return;
    pointerEvent.preventDefault();
    pointerEvent.stopPropagation();

    const { ev, start, end, top, height } = item;
    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
    const oldResourceId = getEventResourceId(ev);
    const lane = pointerEvent.currentTarget.closest(".loop-resource-day-lane");
    const cardRect = pointerEvent.currentTarget.getBoundingClientRect();
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();
    const pointerOffsetMinutes = type === "move"
      ? ((pointerEvent.clientY - cardRect.top) / Math.max(1, height)) * Math.max(15, endMinutes - startMinutes)
      : 0;

    const draftBase = {
      id: adminBookingMoveIdFromEvent(ev, raw),
      ev,
      raw,
      type,
      oldStart: start,
      oldEnd: end,
      oldResourceId,
      resourceId: oldResourceId,
      startMinutes,
      endMinutes,
      top,
      height,
      pointerId: pointerEvent.pointerId,
      moved: false,
      startClientX: pointerEvent.clientX,
      startClientY: pointerEvent.clientY,
      pointerOffsetMinutes,
    };

    setDragDraft(draftBase);

    const onMove = (moveEvent) => {
      setDragDraft((prev) => {
        if (!prev || prev.pointerId !== pointerEvent.pointerId) return prev;
        const nextResourceId = type === "move" ? resourceIdFromPointer(moveEvent.clientX, prev.resourceId) : prev.resourceId;
        let nextStartMin = prev.startMinutes;
        let nextEndMin = prev.endMinutes;
        if (type === "move") {
          const duration = Math.max(safeSlotMinutes, prev.endMinutes - prev.startMinutes);
          const deltaX = Math.abs(moveEvent.clientX - prev.startClientX);
          const deltaY = Math.abs(moveEvent.clientY - prev.startClientY);
          const isWorkerOnlyGesture = deltaX > 10 && deltaY < 14 && String(nextResourceId || "") !== String(prev.oldResourceId || "");

          if (isWorkerOnlyGesture) {
            // Horizontal moves between worker columns should feel instant and precise:
            // keep the same hour/duration while only changing the worker.
            nextStartMin = prev.startMinutes;
            nextEndMin = prev.endMinutes;
          } else {
            const rawStart = minutesFromPointer(moveEvent.clientY, lane) - prev.pointerOffsetMinutes;
            nextStartMin = Math.round(rawStart / safeSlotMinutes) * safeSlotMinutes;
            nextStartMin = Math.max(minMinutes, Math.min(maxMinutes - duration, nextStartMin));
            nextEndMin = nextStartMin + duration;
          }
        } else {
          nextEndMin = minutesFromPointer(moveEvent.clientY, lane);
          nextEndMin = Math.max(prev.startMinutes + safeSlotMinutes, Math.min(maxMinutes, nextEndMin));
        }
        const movedDistance = Math.abs(moveEvent.clientX - prev.startClientX) + Math.abs(moveEvent.clientY - prev.startClientY);
        const nextTop = ((nextStartMin - minMinutes) / safeSlotMinutes) * slotHeight;
        const nextHeight = Math.max(30, ((nextEndMin - nextStartMin) / safeSlotMinutes) * slotHeight - 4);
        return {
          ...prev,
          resourceId: nextResourceId,
          startMinutes: nextStartMin,
          endMinutes: nextEndMin,
          top: nextTop,
          height: nextHeight,
          moved: prev.moved || movedDistance > 5,
        };
      });
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setDragDraft((prev) => {
        if (!prev || prev.pointerId !== pointerEvent.pointerId) return prev;
        const changed = prev.moved && (
          prev.startMinutes !== (prev.oldStart.getHours() * 60 + prev.oldStart.getMinutes()) ||
          prev.endMinutes !== (prev.oldEnd.getHours() * 60 + prev.oldEnd.getMinutes()) ||
          String(prev.resourceId || "") !== String(prev.oldResourceId || "")
        );
        if (changed) {
          suppressEventClickRef.current = true;
          window.setTimeout(() => { suppressEventClickRef.current = false; }, 250);
          onEventQuickChange({
            ev: prev.ev,
            raw: prev.raw,
            type: prev.type,
            oldStart: prev.oldStart,
            oldEnd: prev.oldEnd,
            newStart: dateFromMinutes(prev.startMinutes),
            newEnd: dateFromMinutes(prev.endMinutes),
            oldResourceId: prev.oldResourceId,
            newResourceId: prev.resourceId,
          });
        }
        return null;
      });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  function sameGridDay(date) {
    if (!date) return false;
    return (
      date.getFullYear() === dayStart.getFullYear() &&
      date.getMonth() === dayStart.getMonth() &&
      date.getDate() === dayStart.getDate()
    );
  }

  const currentTimeLine = useMemo(() => {
    if (!sameDay(nowTick, dayStart)) return null;
    const minutes = nowTick.getHours() * 60 + nowTick.getMinutes();
    if (minutes < minMinutes || minutes > maxMinutes) return null;
    return {
      top: ((minutes - minMinutes) / safeSlotMinutes) * slotHeight,
      label: formatCalendarTime(nowTick, timeFormat),
    };
  }, [nowTick, dayStart, minMinutes, maxMinutes, safeSlotMinutes, slotHeight, timeFormat]);

  const eventsByResource = useMemo(() => {
    const map = new Map();
    for (const r of safeResources) map.set(String(r.id), []);
    safeEvents.forEach((ev) => {
      const kind = ev?.extendedProps?.kind;
      const display = ev?.display || ev?.extendedProps?.display;
      if (kind === "outside-hours" || display === "background") return;

      const start = getEventStart(ev);
      if (!start || !sameGridDay(start)) return;

      const rid = getEventResourceId(ev);
      if (!rid || !map.has(rid)) return;

      const end = getEventEnd(ev, start);
      const startMin = Math.max(0, start.getHours() * 60 + start.getMinutes());
      const endMinRaw = end ? end.getHours() * 60 + end.getMinutes() : startMin + 30;
      const endMin = Math.min(maxMinutes, Math.max(startMin + 15, endMinRaw));
      const visibleStartMin = Math.max(minMinutes, startMin);
      if (endMin <= minMinutes || visibleStartMin >= maxMinutes) return;
      const top = ((visibleStartMin - minMinutes) / safeSlotMinutes) * slotHeight;
      const h = Math.max(30, ((endMin - visibleStartMin) / safeSlotMinutes) * slotHeight - 4);

      map.get(rid).push({ ev, start, end, top, height: h });
    });

    for (const [rid, list] of map.entries()) {
      map.set(rid, layoutOverlappingAgendaEvents(list));
    }
    return map;
  }, [safeEvents, safeResources, selectedDate, minMinutes, maxMinutes, safeSlotMinutes]);

  const unavailableBlocksByResource = useMemo(() => {
    const map = new Map();
    for (const resource of safeResources) {
      const ranges = rangesForEmployeeOnDateWithFallback(resource, dayStart, businessDoc);
      const blocks = [];

      // null means no worker/business availability is configured. Keep the grid neutral.
      // [] means availability exists, but this specific day is closed. Shade the full visible day.
      if (ranges === null) {
        map.set(String(resource.id), []);
        continue;
      }

      if (Array.isArray(ranges) && ranges.length) {
        let cursor = minMinutes;
        ranges.forEach((range) => {
          const from = Math.max(minMinutes, Math.min(maxMinutes, Number(range.fromMin)));
          const to = Math.max(minMinutes, Math.min(maxMinutes, Number(range.toMin)));
          if (from > cursor) blocks.push({ from: cursor, to: from });
          cursor = Math.max(cursor, to);
        });
        if (cursor < maxMinutes) blocks.push({ from: cursor, to: maxMinutes });
      } else {
        blocks.push({ from: minMinutes, to: maxMinutes });
      }
      map.set(String(resource.id), blocks
        .filter((b) => b.to - b.from >= safeSlotMinutes)
        .map((b) => ({
          ...b,
          top: ((b.from - minMinutes) / safeSlotMinutes) * slotHeight,
          height: Math.max(10, ((b.to - b.from) / safeSlotMinutes) * slotHeight),
        }))
      );
    }
    return map;
  }, [safeResources, dayStart, minMinutes, maxMinutes, safeSlotMinutes, slotHeight, businessDoc]);

  const conflictBlocksByResource = useMemo(() => {
    const map = new Map();
    if (!dragDraft) return map;
    for (const [rid, list] of eventsByResource.entries()) {
      map.set(rid, (Array.isArray(list) ? list : [])
        .filter((item) => String(adminBookingMoveIdFromEvent(item.ev, item.ev?.extendedProps?.raw || item.ev?.raw || {})) !== String(dragDraft.id))
        .map((item) => ({ top: item.top, height: item.height }))
      );
    }
    return map;
  }, [eventsByResource, dragDraft]);

  const handleGridPanStart = useCallback((event) => {
    const el = scrollerRef.current;
    if (!el || dragDraft) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    const target = event.target;
    const slotEl = target?.closest?.(".loop-resource-day-slot") || null;
    if (target?.closest?.(".loop-resource-day-event, .loop-cal-event-card, .loop-resource-current-time-label, .agenda-finance-floating-btn, button[aria-label^='Crear reserva']")) {
      // Slots are intentionally allowed: touch can either pan or become a clean tap-to-create.
      if (!slotEl) return;
    }

    const pointerType = String(event.pointerType || "").toLowerCase();
    const isTouchLike = pointerType === "touch" || pointerType === "pen";
    const slotIndexRaw = slotEl?.dataset?.slotIndex;
    const slotIndex = slotIndexRaw === undefined || slotIndexRaw === null || slotIndexRaw === ""
      ? null
      : Number(slotIndexRaw);

    gridPanRef.current = {
      active: true,
      pointerId: event.pointerId,
      pointerType,
      startedAt: Date.now(),
      startX: event.clientX,
      startY: event.clientY,
      startLeft: el.scrollLeft,
      startTop: el.scrollTop,
      moved: false,
      slotIndex: Number.isInteger(slotIndex) ? slotIndex : null,
      resourceId: String(slotEl?.dataset?.resourceId || ""),
      armedSlotEl: isTouchLike ? slotEl : null,
      suppressClickUntil: 0,
    };

    if (isTouchLike && slotEl?.classList) slotEl.classList.add("is-touch-armed");
    try { el.setPointerCapture?.(event.pointerId); } catch (_) {}
  }, [dragDraft]);

  const handleGridPanMove = useCallback((event) => {
    const state = gridPanRef.current || {};
    const el = scrollerRef.current;
    if (!el || !state.active || state.pointerId !== event.pointerId) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    const distance = Math.hypot(dx, dy);
    const isTouchLike = state.pointerType === "touch" || state.pointerType === "pen";
    const threshold = isTouchLike ? 9 : 5;
    const scrollDistance =
      Math.abs((el.scrollLeft || 0) - Number(state.startLeft || 0)) +
      Math.abs((el.scrollTop || 0) - Number(state.startTop || 0));
    const moved = distance > threshold || scrollDistance > 2;

    if (moved) {
      state.moved = true;
      state.suppressClickUntil = Date.now() + 360;
      if (state.armedSlotEl?.classList) {
        state.armedSlotEl.classList.remove("is-touch-armed");
        state.armedSlotEl = null;
      }
      const maxLeft = Math.max(0, (el.scrollWidth || 0) - (el.clientWidth || 0));
      const maxTop = getVisualMaxScrollTop();
      el.scrollLeft = Math.max(0, Math.min(maxLeft, state.startLeft - dx));
      el.scrollTop = Math.max(0, Math.min(maxTop, state.startTop - dy));
      event.preventDefault();
    }
  }, [getVisualMaxScrollTop]);

  const handleGridPanEnd = useCallback((event) => {
    const state = gridPanRef.current || {};
    const el = scrollerRef.current;
    if (state.pointerId !== event.pointerId) return;

    const cancelled = event.type === "pointercancel";
    const isTouchLike = state.pointerType === "touch" || state.pointerType === "pen";
    const elapsed = Math.max(0, Date.now() - Number(state.startedAt || 0));
    const scrollDistance = el
      ? Math.abs((el.scrollLeft || 0) - Number(state.startLeft || 0)) +
        Math.abs((el.scrollTop || 0) - Number(state.startTop || 0))
      : 0;
    const slotIndex = Number(state.slotIndex);
    const validSlotIndex = Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < slots.length;
    const cleanTouchTap = Boolean(
      !cancelled &&
      isTouchLike &&
      !state.moved &&
      scrollDistance <= 2 &&
      elapsed <= 450 &&
      validSlotIndex &&
      state.resourceId
    );

    const touchSlotGesture = Boolean(isTouchLike && validSlotIndex && state.resourceId);
    if (cancelled || state.moved || scrollDistance > 2) {
      state.suppressClickUntil = Date.now() + 360;
    }
    if (touchSlotGesture) {
      // Touch creation is owned by pointerup, not the browser's synthesized
      // click. Lock every touch-slot gesture so a pan/long-press cannot leak
      // into onClick and a clean tap cannot open twice.
      state.suppressClickUntil = Date.now() + 450;
    }

    const armedSlotEl = state.armedSlotEl;
    state.armedSlotEl = null;
    state.active = false;
    state.pointerId = null;
    try { el?.releasePointerCapture?.(event.pointerId); } catch (_) {}

    if (armedSlotEl?.classList) {
      if (cleanTouchTap) {
        window.setTimeout(() => armedSlotEl.classList.remove("is-touch-armed"), 90);
      } else {
        armedSlotEl.classList.remove("is-touch-armed");
      }
    }

    if (cleanTouchTap) {
      const slot = slots[slotIndex];
      if (slot?.date && onSlotClick) onSlotClick(slot.date, String(state.resourceId));
    }
  }, [onSlotClick, slots]);

  const shouldSuppressGridClick = () => Boolean((gridPanRef.current?.suppressClickUntil || 0) > Date.now());

  return (
    <div className="loop-resource-day-shell" style={{ height }}>
      <div
        ref={scrollerRef}
        className="loop-resource-day-scroll"
        onPointerDown={handleGridPanStart}
        onPointerMove={handleGridPanMove}
        onPointerUp={handleGridPanEnd}
        onPointerCancel={handleGridPanEnd}
      >
        <div
          ref={gridRef}
          className="loop-resource-day-grid"
          style={{
            gridTemplateColumns,
            // LOOP AGENDA — VERTICAL RESOURCE GRID START FIX V16
            // Without explicit rows, CSS Grid stretches the first auto row in
            // tall portrait layouts, creating a huge blank gap between workers
            // and 6:00 AM. Keep row 1 as the worker header and row 2 as body.
            gridTemplateRows: "auto max-content",
            gridAutoRows: "auto",
            alignContent: "start",
            minWidth,
            width: "max(100%, " + minWidth + "px)",
            "--loop-calendar-min-w": `${minWidth}px`,
            "--loop-resource-axis-w": `${axisWidth}px`,
          }}
        >
          <div className="loop-resource-day-axis-head" />
          {safeResources.map((resource) => {
            const name = resource.name || resource.displayName || resource.title || "Trabajador";
            const role = resource.role || resource.position || resource.specialty || "";
            const photo = adminWorkerPhotoUrl(resource);
            return (
              <div key={`head-${resource.id}`} className="loop-resource-day-worker-head">
                <div className="loop-worker-avatar">
                  {photo ? <img src={photo} alt={name} /> : <span>{workerInitials(name)}</span>}
                </div>
                <div className="loop-resource-day-worker-name">{shortWorkerName(name)}</div>
                {role ? <div className="loop-resource-day-worker-role">{role}</div> : null}
              </div>
            );
          })}

          <div className="loop-resource-day-axis-body">
            {slots.map((slot) => (
              <div
                key={`axis-${slot.index}`}
                className={`loop-resource-day-time ${slot.isHour ? "is-hour" : "is-half"}`}
                style={{ height: slotHeight }}
              >
                {slot.label}
              </div>
            ))}
            {currentTimeLine ? (
              <div
                className="loop-resource-current-time-label"
                style={{ top: currentTimeLine.top }}
              >
                {currentTimeLine.label}
              </div>
            ) : null}
          </div>

          {safeResources.map((resource) => {
            const rid = String(resource.id);
            const resourceEvents = eventsByResource.get(rid) || [];
            const unavailableBlocks = unavailableBlocksByResource.get(rid) || [];
            const conflictBlocks = conflictBlocksByResource.get(rid) || [];
            const laneServiceWarning = dragDraft ? adminBookingMoveServiceWarning(resource, dragDraft.raw || {}, []) : null;
            return (
              <div key={`lane-${rid}`} className={`loop-resource-day-lane ${dragDraft && String(dragDraft.resourceId || "") === rid ? "is-drag-target" : ""}`} data-resource-id={rid}>
                {slots.map((slot) => (
                  <button
                    key={`${rid}-${slot.index}`}
                    type="button"
                    className={`loop-resource-day-slot ${slot.isHour ? "is-hour" : "is-half"}`}
                    style={{ height: slotHeight }}
                    data-date={adminSmartDateInputValue(slot.date)}
                    data-time={adminSmartTimeInputValue(slot.date)}
                    data-slot-index={slot.index}
                    data-resource-id={rid}
                    onClick={(event) => {
                      if (shouldSuppressGridClick()) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                      }
                      onSlotClick && onSlotClick(slot.date, rid);
                    }}
                    aria-label={`Crear reserva ${slot.label || ""}`}
                  />
                ))}

                {unavailableBlocks.map((block, idx) => (
                  <div
                    key={`unavailable-${rid}-${idx}`}
                    className="loop-resource-availability-block is-unavailable"
                    style={{ top: block.top, height: block.height }}
                    aria-hidden="true"
                  />
                ))}

                {dragDraft ? conflictBlocks.map((block, idx) => (
                  <div
                    key={`conflict-${rid}-${idx}`}
                    className="loop-resource-availability-block is-conflict"
                    style={{ top: block.top, height: block.height }}
                    aria-hidden="true"
                  >
                    {block.height >= 38 ? <span>Ocupado</span> : null}
                  </div>
                )) : null}

                {dragDraft && laneServiceWarning ? (
                  <div className="loop-resource-service-warning-chip" aria-hidden="true">{t("Servicio no asignado")}</div>
                ) : null}

                {currentTimeLine ? (
                  <div
                    className="loop-resource-current-time-line"
                    style={{ top: currentTimeLine.top }}
                    aria-hidden="true"
                  />
                ) : null}

                {resourceEvents.map(({ ev, top, height, overlapColumn = 0, overlapColumns = 1 }, index) => {
                  const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
                  const start = getEventStart(ev);
                  const end = getEventEnd(ev, start);
                  const timeText = start ? formatCalendarRange(start, end, timeFormat) : "";
                  const safeOverlapColumns = Math.max(1, Number(overlapColumns || 1));
                  const safeOverlapColumn = Math.max(0, Math.min(safeOverlapColumns - 1, Number(overlapColumn || 0)));
                  const overlapSharePct = 100 / safeOverlapColumns;
                  const overlapLeftPx = 5 + (safeOverlapColumn > 0 ? 2 : 0);
                  const overlapRightPx = 5 + (safeOverlapColumn < safeOverlapColumns - 1 ? 2 : 0);
                  const overlapLeft = `calc(${(safeOverlapColumn * overlapSharePct).toFixed(6)}% + ${overlapLeftPx}px)`;
                  const overlapRight = `calc(${((safeOverlapColumns - safeOverlapColumn - 1) * overlapSharePct).toFixed(6)}% + ${overlapRightPx}px)`;
                  const pseudoArg = {
                    timeText,
                    event: {
                      ...ev,
                      start,
                      end,
                      title: ev?.title || raw?.serviceName || "Reserva",
                      extendedProps: {
                        ...(ev?.extendedProps || {}),
                        raw,
                        loopEventPixelHeight: Math.max(0, Number(height || 0)),
                        loopOverlapColumns: safeOverlapColumns,
                        loopOverlapColumn: safeOverlapColumn,
                      },
                    },
                  };
                  return (
                    <button
                      key={`${rid}-ev-${ev?.id || raw?.id || index}`}
                      type="button"
                      className={`loop-resource-day-event ${Math.max(0, Number(height || 0)) <= 30 ? "event-density-micro" : Math.max(0, Number(height || 0)) <= 42 ? "event-density-tiny" : Math.max(0, Number(height || 0)) <= 62 ? "event-density-short" : "event-density-standard"} ${safeOverlapColumns > 1 ? "has-overlap" : ""} ${canEditEvents ? "is-draggable" : ""} ${dragDraft && String(dragDraft.id) === String(adminBookingMoveIdFromEvent(ev, raw)) ? "is-dragging" : ""}`}
                      data-loop-overlap-columns={safeOverlapColumns}
                      data-loop-overlap-column={safeOverlapColumn}
                      style={(() => {
                        const isMoving = dragDraft && String(dragDraft.id) === String(adminBookingMoveIdFromEvent(ev, raw));
                        const eventTop = isMoving ? dragDraft.top : top;
                        const eventHeight = isMoving ? dragDraft.height : height;
                        return {
                          top: eventTop,
                          height: eventHeight,
                          "--loop-event-top": `${eventTop}px`,
                          "--loop-event-height": `${eventHeight}px`,
                          "--loop-event-left": overlapLeft,
                          "--loop-event-right": overlapRight,
                        };
                      })()}
                      onPointerDown={(e) => startQuickChange(e, { ev, raw, start, end, top, height }, "move")}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (suppressEventClickRef.current) return;
                        onEventClick && onEventClick(ev);
                      }}
                    >
                      <BookingEventCard arg={pseudoArg} timeFormat={timeFormat} colorMode={colorMode} roleContext={roleContext} />
                      {canEditEvents ? (
                        <span
                          className="loop-resource-day-resize-handle"
                          aria-hidden="true"
                          onPointerDown={(e) => startQuickChange(e, { ev, raw, start, end, top, height }, "resize")}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}




/* =========================================================
   LOOP AGENDA — UNIFIED WEEK VIEW HOTFIX
   Week now uses the same event card language as Day, with
   desktop grid / tablet horizontal scroll / mobile list.
   ========================================================= */
// LOOP AGENDA WORKER DATE PARSE FIX HOTFIX
// Important: never parse raw.date (YYYY-MM-DD) with new Date(raw.date) before
// bookingScheduledAtToDate. In browsers, date-only strings are parsed as UTC,
// which moves Panama/local dates to the previous day and makes worker/day look empty
// even while the side summary counts the reservation correctly.
function loopWeekEventStart(ev) {
  const raw = getBookingRawFromCalendarEvent(ev);

  const parseCandidate = (value) => {
    try {
      if (!value) return null;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      return d && !Number.isNaN(d.getTime()) ? d : null;
    } catch (_) {
      return null;
    }
  };

  // Full date/timestamp fields first. These are safe because they include time or are Firestore dates.
  const directCandidates = [ev?.start, bookingScheduledAtToDate(raw)];
  for (const value of directCandidates) {
    const d = parseCandidate(value);
    if (d) return d;
  }

  // Then use the project date utility, which knows how to combine raw.date + raw.time locally.
  try {
    const d = bookingScheduledAtToDate(raw);
    if (d && !Number.isNaN(d.getTime())) return d;
  } catch (_) {}

  // Last-resort local parsing for legacy bookings with separate date/time strings.
  try {
    const dateText = String(raw?.date || "").trim();
    const timeText = /^\d{1,2}:\d{2}$/.test(String(raw?.time || "")) ? String(raw.time) : "00:00";
    const m = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const t = timeText.match(/^(\d{1,2}):(\d{2})$/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), t ? Number(t[1]) : 0, t ? Number(t[2]) : 0, 0, 0);
      if (d && !Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}

  return null;
}

function loopWeekEventEnd(ev, start = null) {
  const raw = getBookingRawFromCalendarEvent(ev);
  const candidates = [ev?.end, raw?.end, raw?.endAt, raw?.endsAt, raw?.finishAt];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (d && !Number.isNaN(d.getTime())) return value === ev?.end ? d : wallDate(d,raw);
    } catch (_) {}
  }
  if (!start) return null;
  const duration = getBookingDurationMinutesForCard(raw, start, null) || Number(raw?.duration || raw?.durationMinutes || raw?.minutes || 60) || 60;
  return new Date(start.getTime() + Math.max(15, duration) * 60000);
}

function loopWeekSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function loopWeekFormatDayLabel(day, locale = interfaceLocale()) {
  try {
    return {
      dow: day.toLocaleDateString(locale, { weekday: "short" }).replace(".", "").slice(0, 3).toUpperCase(),
      day: String(day.getDate()),
      month: day.toLocaleDateString(locale, { month: "short" }).replace(".", ""),
    };
  } catch (_) {
    return { dow: "DÍA", day: String(day?.getDate?.() || ""), month: "" };
  }
}

function loopWeekEventSort(a, b) {
  const da = loopWeekEventStart(a);
  const db = loopWeekEventStart(b);
  return (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
}


// LOOP AGENDA MADRE CLEANUP D2 V73
// Removed legacy LoopUnifiedDayView. Day is rendered only by LoopResourceDayGrid.

function LoopUnifiedWeekView({
  events = [],
  selectedDate,
  height = 720,
  locale = interfaceLocale(),
  weekStartsOn = "monday",
  showWeekends = true,
  minTime = "07:00",
  maxTime = "22:00",
  rowDensity = "comfortable",
  timeFormat = "24h",
  colorMode = "service",
  viewportWidth = 1440,
  isWorkerView = false,
  resources = [],
  selectedResourceIds = [],
  businessDoc = null,
  slotMinutes = 15,
  onEventClick,
  onDayClick,
  onSlotClick,
  roleContext = null,
}) {
  const [fullDay,setFullDay]=useState(false);
  const [detail,setDetail]=useState(null);
  const closeDetail=useCallback(()=>setDetail(null),[]);
  useEffect(()=>setDetail(null),[selectedDate]);
  const isListMode = Number(viewportWidth || 1440) < 720;
  const isTablet = Number(viewportWidth || 1440) >= 720 && Number(viewportWidth || 1440) < 1120;
  const safeSelectedDate = selectedDate ? new Date(selectedDate) : new Date();
  const days = weekDaysFromSelectedDate(safeSelectedDate, weekStartsOn, showWeekends !== false);
  const now = new Date();
  const selectedDay = startOfDay(safeSelectedDate);
  const minMinutes = fullDay ? 0 : (parseHHMMToMinutes(minTime || "07:00") ?? 7 * 60);
  const maxMinutes = fullDay ? 1440 : (parseHHMMToMinutes(maxTime || "22:00") ?? 22 * 60);
  const safeMaxMinutes = Math.max(minMinutes + 60, maxMinutes);
  const hourHeight = rowDensity === "compact" ? 48 : rowDensity === "spacious" ? 70 : 58;
  const axisWidth = 72;
  const dayMinWidth = isTablet ? 168 : isWorkerView ? 172 : 152;
  const gridHeight = Math.max(420, ((safeMaxMinutes - minMinutes) / 60) * hourHeight);
  const safeHeightNumber = Number.isFinite(Number(height)) ? Number(height) : 720;
  const shellHeightStyle = typeof height === "string" ? height : safeHeightNumber;

  const visibleEvents = (Array.isArray(events) ? events : [])
    .filter((ev) => ev?.display !== "background" && ev?.extendedProps?.kind !== "outside-hours")
    .filter((ev) => {
      const start = loopWeekEventStart(ev);
      return Boolean(start && days.some((day) => loopWeekSameDay(start, day)));
    })
    .sort(loopWeekEventSort);

  const eventsByDay = days.map((day) => {
    const list = visibleEvents.filter((ev) => {
      const start = loopWeekEventStart(ev);
      return start && loopWeekSameDay(start, day);
    });
    return { day, events: list };
  });

  // LOOP WORKER WEEK AVAILABILITY STRIPES V98
  // Week is day-based, so only the personal worker agenda can safely show
  // unavailable hours without creating visual chaos. Owner/business week stays clean.
  const workerWeekAvailabilityResource = useMemo(() => {
    if (!isWorkerView) return null;
    const list = Array.isArray(resources) ? resources.filter(Boolean) : [];
    if (!list.length) return null;

    const ids = (Array.isArray(selectedResourceIds) ? selectedResourceIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean);

    if (ids.length) {
      const match = list.find((resource) => {
        const candidates = [
          resource?.id,
          resource?.employeeId,
          resource?.workerId,
          resource?.resourceId,
          resource?.uid,
          resource?.userUid,
        ].map((value) => String(value || "").trim()).filter(Boolean);
        return candidates.some((candidate) => ids.includes(candidate));
      });
      if (match) return match;
    }

    // In normal worker mode Semana has one personal resource. If a future worker
    // manager/team week has multiple resources, do not guess or shade all columns.
    return list.length === 1 ? list[0] : null;
  }, [isWorkerView, resources, selectedResourceIds]);

  const workerWeekUnavailableBlocksByDay = useMemo(() => {
    const map = new Map();
    if (!isWorkerView || !workerWeekAvailabilityResource) return map;

    const visibleStart = minMinutes;
    const visibleEnd = safeMaxMinutes;

    const clampBlock = (fromMin, toMin) => {
      const from = Math.max(visibleStart, Math.min(visibleEnd, Number(fromMin)));
      const to = Math.max(visibleStart, Math.min(visibleEnd, Number(toMin)));
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
      return {
        fromMin: from,
        toMin: to,
        top: ((from - visibleStart) / 60) * hourHeight,
        height: Math.max(1, ((to - from) / 60) * hourHeight),
      };
    };

    days.forEach((day) => {
      const key = formatDateYmdAdmin(day);
      const ranges = rangesForEmployeeOnDateWithFallback(workerWeekAvailabilityResource, day, businessDoc);
      if (ranges === null) {
        map.set(key, { blocks: [], label: "Horario no configurado", neutral: true });
        return;
      }

      const availableRanges = (Array.isArray(ranges) ? ranges : [])
        .map((range) => ({
          fromMin: Math.max(visibleStart, Math.min(visibleEnd, Number(range.fromMin))),
          toMin: Math.max(visibleStart, Math.min(visibleEnd, Number(range.toMin))),
        }))
        .filter((range) => Number.isFinite(range.fromMin) && Number.isFinite(range.toMin) && range.toMin > range.fromMin)
        .sort((a, b) => a.fromMin - b.fromMin);

      if (!availableRanges.length) {
        const allDay = clampBlock(visibleStart, visibleEnd);
        map.set(key, { blocks: allDay ? [allDay] : [], label: "Cerrado", closed: true });
        return;
      }

      const blocks = [];
      let cursor = visibleStart;
      availableRanges.forEach((range) => {
        const gap = clampBlock(cursor, range.fromMin);
        if (gap) blocks.push(gap);
        cursor = Math.max(cursor, range.toMin);
      });
      const post = clampBlock(cursor, visibleEnd);
      if (post) blocks.push(post);

      const label = availableRanges
        .map((range) => `${formatCalendarTime(dateAtMinutes(day, range.fromMin), timeFormat)}–${formatCalendarTime(dateAtMinutes(day, range.toMin), timeFormat)}`)
        .join(" · ");

      map.set(key, { blocks, label: label ? `Disponible ${label}` : "Disponible", closed: false });
    });

    return map;
  }, [isWorkerView, workerWeekAvailabilityResource, days, minMinutes, safeMaxMinutes, hourHeight, businessDoc, timeFormat]);

  const getWorkerWeekAvailabilityInfo = (day) => workerWeekUnavailableBlocksByDay.get(formatDateYmdAdmin(day)) || null;

  // LOOP AGENDA MADRE PHASE B V70
  // The week identity/resource strip was visually redundant because the toolbar
  // already carries the owner/worker/team context. Keep Semana inside the same
  // mother shell, but start the agenda grid immediately under the toolbar.
  const outsideEvents=visibleEvents.filter(ev=>{const start=loopWeekEventStart(ev),end=loopWeekEventEnd(ev,start);return start.getHours()*60+start.getMinutes()<minMinutes || end.getHours()*60+end.getMinutes()>safeMaxMinutes || start.getHours()*60+start.getMinutes()>=safeMaxMinutes || end.getDate()!==start.getDate();});
  const renderWeekResourceStrip = () => <div className="agenda-range-controls">
    <span>{t("Color por") + " "}{colorMode==='worker'?'profesional':colorMode==='status'?'estado':t("servicio")}</span>
    {!isListMode&&<div role="group" aria-label={t("Rango horario")}><button type="button" aria-pressed={!fullDay} onClick={()=>setFullDay(false)}>{t("Horario de agenda")}</button><button type="button" aria-pressed={fullDay} onClick={()=>setFullDay(true)}>{t("Día completo")}</button></div>}
    {!isListMode&&outsideEvents.length>0&&<button type="button" onClick={()=>setDetail({title:'Reservas fuera del rango horario',events:outsideEvents})}>{outsideEvents.length}{" " + t("fuera del rango · Ver")}</button>}
    {detail&&<AgendaRangePanel title={detail.title} items={detail.events.map(ev=>agendaRangeItem(ev,timeFormat,colorMode))} onClose={closeDetail} onEventClick={onEventClick}/>}
  </div>;

  const hours = [];
  for (let mins = Math.ceil(minMinutes / 60) * 60; mins <= safeMaxMinutes; mins += 60) {
    hours.push(mins);
  }

  // LOOP PHASE 1 HOTFIX V14 — week slot click opens create modal at exact day/time.
  // The week grid is custom, so FullCalendar's dateClick does not exist here.
  // Compute clicked minutes from the lane Y position and pass that Date to the main create modal.
  const safeSlotMinutes = Math.max(5, Number(slotMinutes || 15) || 15);
  const buildWeekSlotDateFromPointer = (day, clickEvent) => {
    const rect = clickEvent.currentTarget.getBoundingClientRect();
    const y = Math.max(0, Math.min(rect.height, Number(clickEvent.clientY || 0) - rect.top));
    const rawMinutes = minMinutes + (y / Math.max(1, rect.height)) * (safeMaxMinutes - minMinutes);
    const snappedMinutes = Math.floor(rawMinutes / safeSlotMinutes) * safeSlotMinutes;
    const clampedMinutes = Math.max(minMinutes, Math.min(safeMaxMinutes - safeSlotMinutes, snappedMinutes));
    return dateAtMinutes(day, clampedMinutes);
  };

  if (isListMode) {
    return (
      <div className="loop-unified-week-list" style={{ minHeight: Math.max(420, safeHeightNumber) }}>
        <div className="loop-week-list-head">
          <div>
            <span>{t("Semana")}</span>
            <strong>{weekRangeLabel(safeSelectedDate,weekStartsOn,showWeekends)}</strong>
          </div>
          <small>{visibleEvents.length}{" " + t("reserva")}{visibleEvents.length === 1 ? "" : "s"}</small>
        </div>
        {renderWeekResourceStrip()}
        <div className="loop-week-list-days">
          {eventsByDay.map(({ day, events: dayEvents }) => {
            const label = loopWeekFormatDayLabel(day, locale);
            const isToday = loopWeekSameDay(day, now);
            const isSelected = loopWeekSameDay(day, selectedDay);
            const availabilityInfo = getWorkerWeekAvailabilityInfo(day);
            return (
              <section key={day.toISOString()} className={`loop-week-list-day ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""}`}>
                <button type="button" className="loop-week-list-day-head" onClick={() => onDayClick && onDayClick(startOfDay(day))}>
                  <span>{label.dow}</span>
                  <strong>{label.day}</strong>{isToday&&<span className="agenda-today-label">{t("Hoy")}</span>}
                  <em>{dayEvents.length ? `${dayEvents.length} reserva${dayEvents.length === 1 ? "" : "s"}` : "Sin reservas"}</em>
                  {availabilityInfo && !availabilityInfo.neutral ? <small className={`loop-week-list-availability ${availabilityInfo.closed ? "is-closed" : ""}`}>{availabilityInfo.label}</small> : null}
                </button>
                {dayEvents.length ? (
                  <div className="loop-week-list-events">
                    {dayEvents.map((ev, index) => {
                      const raw = getBookingRawFromCalendarEvent(ev);
                      const start = loopWeekEventStart(ev);
                      const end = loopWeekEventEnd(ev, start);
                      const status = normalizeBookingStatusLabel(raw?.status || raw?.confirmationStatus || ev?.extendedProps?.status);
                      const serviceName = getBookingServiceName(raw, ev?.title || "Reserva");
                      const workerName = getBookingWorkerName(raw);
                      const displayTime = start ? formatCalendarRange(start, end, timeFormat) : "";
                      const safeColorMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
                      const accent = getBookingAccent(raw, serviceName, safeColorMode, status);
                      return (
                        <button
                          key={ev.id || `${day.toISOString()}-${index}`}
                          type="button"
                          className={`loop-week-list-event status-${status.tone}`}
                          style={{ "--event-accent": accent }}
                          onClick={(clickEvent) => { clickEvent.stopPropagation(); onEventClick && onEventClick(ev); }}
                        >
                          <span className="loop-week-list-event-time">{displayTime}</span>
                          <span className="loop-week-list-event-main">
                            <strong>{getBookingClientName(raw)}</strong>
                            <small>{serviceName}{workerName ? ` · ${workerName}` : ""}</small>
                          </span>
                          <span className="agenda-week-list-status">{status.label || t("Reserva")}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="loop-unified-week-shell" style={{ height: shellHeightStyle }}>
      {renderWeekResourceStrip()}
      <div className="loop-unified-week-scroll">
        <div
          className="loop-unified-week-grid"
          style={{
            gridTemplateColumns: `${axisWidth}px repeat(${days.length}, minmax(${dayMinWidth}px, 1fr))`,
            minWidth: axisWidth + days.length * dayMinWidth,
          }}
        >
          <div className="loop-unified-week-axis-head" />
          {days.map((day) => {
            const label = loopWeekFormatDayLabel(day, locale);
            const isToday = loopWeekSameDay(day, now);
            const isSelected = loopWeekSameDay(day, selectedDay);
            const count = visibleEvents.filter((ev) => {
              const start = loopWeekEventStart(ev);
              return start && loopWeekSameDay(start, day);
            }).length;
            return (
              <button
                key={`week-head-${day.toISOString()}`}
                type="button"
                aria-pressed={isSelected}
                aria-label={day.toLocaleDateString(locale,{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                className={`loop-unified-week-day-head ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""}`}
                onClick={() => onDayClick && onDayClick(startOfDay(day))}
              >
                <span>{label.dow}</span>
                <strong>{label.day}</strong>{isToday&&<span className="agenda-today-label">{t("Hoy")}</span>}
                <small>{count ? `${count} reserva${count === 1 ? "" : "s"}` : label.month}</small>
              </button>
            );
          })}

          <div className="loop-unified-week-axis-body" style={{ height: gridHeight }}>
            {hours.map((mins) => {
              const top = ((mins - minMinutes) / 60) * hourHeight;
              return (
                <div key={`week-axis-${mins}`} className="loop-unified-week-time" style={{ top }}>
                  {formatCalendarTime(dateAtMinutes(selectedDay, mins), timeFormat)}
                </div>
              );
            })}
          </div>

          {days.map((day) => {
            const dayEvents = groupDenseAgendaEvents(layoutOverlappingAgendaEvents(
              visibleEvents
                .filter((ev) => {
                  const start = loopWeekEventStart(ev);
                  return start && loopWeekSameDay(start, day) && start.getHours()*60+start.getMinutes()<safeMaxMinutes && (loopWeekEventEnd(ev,start).getHours()*60+loopWeekEventEnd(ev,start).getMinutes()>minMinutes || !loopWeekSameDay(loopWeekEventEnd(ev,start),start));
                })
                .map((ev) => {
                  const start = loopWeekEventStart(ev);
                  const end = loopWeekEventEnd(ev, start);
                  return { ev, start, end };
                })
            ));
            return (
              <div
                key={`week-day-${day.toISOString()}`}
                className={`loop-unified-week-day-lane ${onSlotClick ? "is-clickable" : ""}`}
                style={{ height: gridHeight }}
                data-date={adminSmartDateInputValue(day)}
                onClick={(clickEvent) => {
                  if (!onSlotClick) return;
                  const slotDate = buildWeekSlotDateFromPointer(day, clickEvent);
                  onSlotClick(slotDate);
                }}
              >
                {(getWorkerWeekAvailabilityInfo(day)?.blocks || []).map((block, blockIndex) => (
                  <div
                    key={`week-worker-unavailable-${day.toISOString()}-${blockIndex}`}
                    className="loop-week-worker-unavailable-block"
                    style={{ top: block.top, height: block.height }}
                    aria-hidden="true"
                  />
                ))}
                {hours.map((mins) => {
                  const top = ((mins - minMinutes) / 60) * hourHeight;
                  return <div key={`week-slot-${day.toISOString()}-${mins}`} className="loop-unified-week-slot" data-time={adminSmartTimeInputValue(dateAtMinutes(day, mins))} style={{ top }} />;
                })}
                {dayEvents.map(({ ev, start, end, overlapColumn = 0, overlapColumns = 1, groupEvents }, index) => {
                  const raw = getBookingRawFromCalendarEvent(ev);
                  if (!start) return null;
                  const safeOverlapColumns = Math.max(1, Number(overlapColumns || 1));
                  const safeOverlapColumn = Math.max(0, Math.min(safeOverlapColumns - 1, Number(overlapColumn || 0)));
                  const overlapSharePct = 100 / safeOverlapColumns;
                  const overlapLeftPx = 5 + (safeOverlapColumn > 0 ? 2 : 0);
                  const overlapRightPx = 5 + (safeOverlapColumn < safeOverlapColumns - 1 ? 2 : 0);
                  const overlapLeft = `calc(${(safeOverlapColumn * overlapSharePct).toFixed(6)}% + ${overlapLeftPx}px)`;
                  const overlapRight = `calc(${((safeOverlapColumns - safeOverlapColumn - 1) * overlapSharePct).toFixed(6)}% + ${overlapRightPx}px)`;
                  const startMins = start.getHours() * 60 + start.getMinutes();
                  const endMins = end ? (end.toDateString() !== start.toDateString() ? 1440 : end.getHours() * 60 + end.getMinutes()) : startMins + 60;
                  const top = Math.max(0, ((startMins - minMinutes) / 60) * hourHeight);
                  const rawHeight = Math.max(24, ((Math.min(maxMinutes, Math.max(startMins + 15, endMins)) - Math.max(startMins, minMinutes)) / 60) * hourHeight);
                  const heightPx = Math.min(Math.max(rawHeight, 24), Math.max(24, gridHeight - top - 2));
                  const density = heightPx <= 26 ? "micro" : heightPx <= 38 ? "tiny" : heightPx <= 58 ? "short" : "standard";
                  const status = normalizeBookingStatusLabel(raw?.status || raw?.confirmationStatus || ev?.extendedProps?.status);
                  const serviceName = getBookingServiceName(raw, ev?.title || "Reserva");
                  const safeColorMode = ["service", "worker", "status"].includes(colorMode) ? colorMode : "service";
                  const accent = getBookingAccent(raw, serviceName, safeColorMode, status);
                  return (
                    <button
                      key={ev.id || `${day.toISOString()}-${index}`}
                      type="button"
                      className={`loop-unified-week-event event-density-${density} ${safeOverlapColumns > 1 ? "has-overlap" : ""} status-${status.tone}`}
                      data-loop-overlap-columns={safeOverlapColumns}
                      data-loop-overlap-column={safeOverlapColumn}
                      style={{ top, height: heightPx, "--event-accent": accent, "--loop-event-left": overlapLeft, "--loop-event-right": overlapRight }}
                      title={groupEvents ? `${groupEvents.length} reservas simultáneas · ${formatCalendarRange(start, end, timeFormat)}` : [formatCalendarRange(start, end, timeFormat), getBookingClientName(raw), serviceName, getBookingWorkerName(raw), status.label].filter(Boolean).join(" · ")}
                      onClick={(clickEvent) => { clickEvent.stopPropagation(); if(groupEvents)setDetail({title:day.toLocaleDateString(locale,{weekday:"long",day:"numeric",month:"long"}),events:groupEvents});else onEventClick && onEventClick(ev); }}
                    >
                      {groupEvents ? <div className="agenda-week-card-v2"><strong>{groupEvents.length}{" " + t("reservas simultáneas")}</strong><span>{formatCalendarRange(start,end,timeFormat)}</span><small>{t("Ver reservas")}</small></div> : <div className="agenda-week-card-v2"><strong>{getBookingClientName(raw)}</strong><span>{formatCalendarRange(start,end,timeFormat)}</span>{heightPx>42&&<small>{serviceName}</small>}{heightPx>66&&<small>{status.label || raw.status || t("Reserva")}</small>}</div>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function loopMonthStartGrid(date, weekStartsOn = "monday") {
  const base = date ? new Date(date) : new Date();
  const first = new Date(base.getFullYear(), base.getMonth(), 1);
  const firstDay = first.getDay();
  const targetStart = weekStartsOn === "sunday" ? 0 : 1;
  const diff = (firstDay - targetStart + 7) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  return start;
}

function loopMonthDays(date, weekStartsOn = "monday") {
  const start = loopMonthStartGrid(date, weekStartsOn);
  return Array.from({ length: 42 }, (_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    return d;
  });
}

function agendaRangeItem(ev, timeFormat, colorMode) {
  const raw = getBookingRawFromCalendarEvent(ev);
  const start = loopWeekEventStart(ev), end = loopWeekEventEnd(ev, start);
  const status = normalizeBookingStatusLabel(raw?.status || raw?.confirmationStatus || ev?.extendedProps?.status);
  const service = getBookingServiceName(raw, ev?.title || "Reserva");
  return {id:ev.id,event:ev,start,end,client:getBookingClientName(raw),service,worker:getBookingWorkerName(raw),status:status.label || raw.status || "Reserva",time:start?formatCalendarRange(start,end,timeFormat):"",accent:getBookingAccent(raw,service,colorMode,status)};
}
function LoopUnifiedMonthView({events=[],timeFormat="24h",colorMode="service",...props}) {
 const items=events.filter(ev=>ev?.display!=="background"&&ev?.extendedProps?.kind!=="outside-hours").map(ev=>agendaRangeItem(ev,timeFormat,colorMode)).filter(item=>item.start&&!Number.isNaN(+item.start)).sort((a,b)=>a.start-b.start);
 return <AgendaMonthView {...props} colorMode={colorMode} items={items}/>;
}




/* ---------------------------
   Agenda Finance Mini — mobile/tablet financial surface
--------------------------- */


function agendaFinanceNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function agendaFinanceRawBooking(input = {}) {
  const extended = input?.extendedProps || {};
  return extended.raw || extended.booking || input?.raw || input?.booking || input || {};
}

function agendaFinanceMergedBooking(input = {}) {
  const raw = agendaFinanceRawBooking(input);
  if (!raw || raw === input) return input || {};
  return {
    ...(input || {}),
    ...(raw || {}),
    id: raw.id || raw.bookingId || raw.docId || input.id || input.bookingId || input.docId || "",
    start: raw.start || input.start || input.startStr || null,
    end: raw.end || input.end || input.endStr || null,
    title: raw.title || input.title || "",
  };
}

function agendaFinanceBookingKey(raw = {}, index = 0) {
  return String(
    raw.id ||
    raw.bookingId ||
    raw.docId ||
    raw.refId ||
    `${raw.customerId || raw.customerName || raw.clientName || raw.name || "cliente"}-${raw.serviceId || raw.serviceName || raw.service || raw.title || "servicio"}-${raw.scheduledAt?.seconds || raw.start || raw.date || raw.createdAt?.seconds || index}`
  );
}

function agendaFinanceMergeBookingSources(...sources) {
  const map = new Map();
  sources.flat().filter(Boolean).forEach((input, index) => {
    const raw = agendaFinanceMergedBooking(input);
    const key = agendaFinanceBookingKey(raw, index);
    if (!map.has(key)) map.set(key, raw);
    else {
      const prev = map.get(key) || {};
      map.set(key, { ...prev, ...raw });
    }
  });
  return Array.from(map.values());
}

function agendaFinanceDate(value) {
  try {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value.seconds) return new Date(Number(value.seconds) * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function agendaFinanceBookingDate(input = {}) {
  const raw = agendaFinanceMergedBooking(input);

  const direct =
    bookingScheduledAtToDate(raw) ||
    agendaFinanceDate(raw.scheduledAt) ||
    agendaFinanceDate(raw.scheduledAtClient) ||
    agendaFinanceDate(raw.startsAt) ||
    agendaFinanceDate(raw.startAt) ||
    agendaFinanceDate(raw.start) ||
    agendaFinanceDate(raw.date) ||
    agendaFinanceDate(raw.bookingDate) ||
    agendaFinanceDate(raw.appointmentDate) ||
    agendaFinanceDate(raw.requestedAt) ||
    agendaFinanceDate(raw.submittedAt) ||
    agendaFinanceDate(raw.createdAt);

  if (direct) return direct;

  const dateValue = String(raw.date || raw.dateKey || raw.scheduledDate || raw.bookingDay || "").trim();
  const timeValue = String(raw.startTime || raw.time || raw.hour || raw.slotTime || "00:00").trim();
  const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = timeValue.match(/^(\d{1,2}):(\d{2})/);
  if (dateMatch) {
    const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
    d.setHours(timeMatch ? Number(timeMatch[1]) : 0, timeMatch ? Number(timeMatch[2]) : 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function agendaFinanceStartOfDay(date) {
  const d = new Date(date || new Date());
  d.setHours(0, 0, 0, 0);
  return d;
}

function agendaFinancePeriodRange(anchorDate, period = "day") {
  const anchor = agendaFinanceStartOfDay(anchorDate || new Date());
  const start = new Date(anchor);
  const end = new Date(anchor);
  if (period === "week") {
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 7);
  } else if (period === "month") {
    start.setDate(1);
    end.setTime(start.getTime());
    end.setMonth(end.getMonth() + 1);
  } else {
    end.setDate(end.getDate() + 1);
  }
  return { start, end };
}

function agendaFinanceWithin(date, range) {
  if (!date || !range?.start || !range?.end) return false;
  const t = date.getTime();
  return t >= range.start.getTime() && t < range.end.getTime();
}

function agendaFinancePeriodLabel(period = "day") {
  if (period === "week") return "Semana";
  if (period === "month") return "Mes";
  return "Hoy";
}

function agendaFinanceDateInputValue(date) {
  const d = agendaFinanceStartOfDay(date || new Date());
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function agendaFinanceDateFromInput(value, fallback = new Date()) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return agendaFinanceStartOfDay(fallback || new Date());
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(d.getTime()) ? agendaFinanceStartOfDay(fallback || new Date()) : agendaFinanceStartOfDay(d);
}

function agendaFinanceMoveAnchor(anchorDate, period = "day", direction = 0) {
  const d = agendaFinanceStartOfDay(anchorDate || new Date());
  const step = Number(direction) || 0;
  if (period === "month") d.setMonth(d.getMonth() + step);
  else if (period === "week") d.setDate(d.getDate() + step * 7);
  else d.setDate(d.getDate() + step);
  return agendaFinanceStartOfDay(d);
}

function agendaFinanceFormatShortDate(date, options = {}) {
  try {
    return agendaFinanceStartOfDay(date || new Date()).toLocaleDateString(interfaceLocale(), options);
  } catch (_) {
    return "";
  }
}

function agendaFinanceAnchorLabel(period = "day", anchorDate = new Date()) {
  const range = agendaFinancePeriodRange(anchorDate, period);
  const start = range.start;
  const endInclusive = new Date(range.end);
  endInclusive.setDate(endInclusive.getDate() - 1);

  if (period === "month") {
    return agendaFinanceFormatShortDate(start, { month: "long", year: "numeric" });
  }

  if (period === "week") {
    const sameMonth = start.getMonth() === endInclusive.getMonth() && start.getFullYear() === endInclusive.getFullYear();
    const startLabel = agendaFinanceFormatShortDate(start, sameMonth ? { day: "numeric" } : { day: "numeric", month: "short" });
    const endLabel = agendaFinanceFormatShortDate(endInclusive, { day: "numeric", month: "short", year: "numeric" });
    return `${startLabel} - ${endLabel}`;
  }

  return agendaFinanceFormatShortDate(start, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function agendaFinanceStepLabel(period = "day", direction = -1) {
  const side = direction < 0 ? "anterior" : "siguiente";
  if (period === "month") return `Mes ${side}`;
  if (period === "week") return `Semana ${side}`;
  return `Día ${side}`;
}

function agendaFinanceStatus(raw = {}) {
  const s = String(raw.status || raw.confirmationStatus || raw.state || "").trim().toLowerCase();
  if (["confirmed", "confirmada", "approved", "accepted", "in_progress", "completed", "complete", "done", "completada"].includes(s)) return "confirmed";
  if (["pending", "pendiente", "to_confirm", "por_confirmar"].includes(s)) return "pending";
  if (["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada"].includes(s)) return "cancelled";
  if (["no_show", "no-show", "noshow", "no asistió", "no_asistio"].includes(s)) return "no_show";
  return s || "confirmed";
}

function agendaFinanceIsRevenueBooking(raw = {}) {
  return ["confirmed", "no_show"].includes(agendaFinanceStatus(raw));
}

function agendaFinanceNormalizeMethod(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["cash", "efectivo"].includes(raw)) return "cash";
  if (["yappy", "japi", "yappi"].includes(raw)) return "yappy";
  if (["card", "tarjeta", "credit_card", "debit_card"].includes(raw)) return "card";
  if (["transfer", "transferencia", "bank_transfer"].includes(raw)) return "transfer";
  if (["membership", "membresia", "membresía"].includes(raw)) return "membership";
  if (["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"].includes(raw)) return "loyalty_reward";
  if (["courtesy", "cortesia", "cortesía", "free", "gratis"].includes(raw)) return "courtesy";
  if (["other", "otro"].includes(raw)) return "other";
  return "unknown";
}

function agendaFinanceMethodLabel(value) {
  const method = agendaFinanceNormalizeMethod(value);
  const map = { cash: "Efectivo", yappy: "Yappy", card: "Tarjeta", transfer: "Transferencia", membership: "Membresía", loyalty_reward: "Fidelidad", courtesy: "Cortesía", other: "Otro", unknown: "Sin método" };
  return map[method] || map.unknown;
}

function agendaFinanceProductSalesTotal(raw = {}) {
  const direct = agendaFinanceNumber(raw.productSalesTotalAmount ?? raw.retailSalesTotalAmount ?? raw.payment?.productSalesTotalAmount ?? 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(raw.productSales) ? raw.productSales : Array.isArray(raw.retailSales) ? raw.retailSales : [];
  return rows.reduce((sum, row) => sum + agendaFinanceNumber(row.lineTotal ?? (agendaFinanceNumber(row.quantity ?? row.qty) * agendaFinanceNumber(row.unitPrice ?? row.price))), 0);
}

function getServiceId(raw = {}) {
  return String(
    raw.serviceId ||
    raw.serviceID ||
    raw.serviceKey ||
    raw.serviceRef ||
    raw.service?.id ||
    raw.service?.serviceId ||
    raw.packageId ||
    raw.package?.id ||
    ""
  ).trim();
}
function agendaFinanceExpectedAmount(raw = {}, serviceMap = new Map()) {
  const lifecycle = bookingLifecycleFromRecord(raw || {});
  const productSalesTotal = agendaFinanceProductSalesTotal(raw);
  if (lifecycle.expectedServiceAmount > 0 || lifecycle.serviceCollectedAmount > 0) return Math.max(lifecycle.expectedServiceAmount, lifecycle.serviceCollectedAmount) + productSalesTotal;
  const direct = agendaFinanceNumber(raw.totalDueAmount ?? raw.expectedServiceAmount ?? raw.totalPrice ?? raw.total ?? raw.price ?? raw.servicePrice ?? raw.amount ?? raw.payment?.totalDueAmount ?? raw.payment?.expectedServiceAmount ?? 0);
  if (direct > 0) return direct + productSalesTotal;
  const sid = getServiceId(raw);
  const service = sid ? serviceMap.get(sid) : null;
  return agendaFinanceNumber(service?.price ?? service?.amount ?? service?.cost ?? service?.servicePrice ?? service?.basePrice ?? 0) + productSalesTotal;
}

function agendaFinanceCollectedAmount(raw = {}, expected = 0) {
  const lifecycle = bookingLifecycleFromRecord({ ...(raw || {}), expectedServiceAmount: expected || raw.expectedServiceAmount });
  if (lifecycle.totalCollectedAmount > 0) return lifecycle.totalCollectedAmount;
  const explicit = agendaFinanceNumber(raw.serviceCollectedAmount ?? raw.payment?.serviceCollectedAmount ?? raw.amountCharged ?? raw.payment?.amountCharged ?? raw.paidAmount ?? raw.payment?.paidAmount ?? raw.amountPaid ?? raw.payment?.amountPaid ?? raw.paymentAmount ?? raw.payment?.paymentAmount ?? 0);
  if (explicit > 0) return explicit;
  const method = agendaFinanceNormalizeMethod(raw.paymentMethod || raw.payment?.method);
  if (["membership", "loyalty_reward", "courtesy"].includes(method)) return expected;
  const paymentStatus = normalizeCanonicalPaymentStatus(raw.paymentStatus || raw.payment?.status || raw.status, { collected: explicit, expected });
  if (paymentStatus === "paid" || paymentStatus === "collected") return expected;
  return 0;
}

function agendaFinanceTipAmount(raw = {}) {
  return agendaFinanceNumber(raw.workerTipAmount ?? raw.payment?.workerTipAmount ?? raw.tipAmount ?? raw.tip ?? raw.payment?.tipAmount ?? 0);
}

function agendaFinanceProductPayout(raw = {}) {
  const direct = agendaFinanceNumber(raw.productSalesPayoutAmount ?? raw.retailPayoutAmount ?? raw.payment?.productSalesPayoutAmount ?? 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(raw.productSales) ? raw.productSales : Array.isArray(raw.retailSales) ? raw.retailSales : [];
  return rows.reduce((sum, row) => sum + agendaFinanceNumber(row.workerPayoutAmount ?? row.payoutAmount), 0);
}

function agendaFinanceServiceCommission(raw = {}, expected = 0) {
  const direct = agendaFinanceNumber(raw.workerCommissionAmount ?? raw.commissionAmount ?? raw.payment?.workerCommissionAmount ?? raw.payment?.commissionAmount ?? 0);
  if (direct > 0) return direct;
  const productSalesTotal = agendaFinanceProductSalesTotal(raw);
  const serviceRevenue = Math.max(0, expected - productSalesTotal);
  const type = String(raw.workerCommissionType || raw.commissionType || raw.payment?.workerCommissionType || "none").trim().toLowerCase();
  const value = agendaFinanceNumber(raw.workerCommissionValue ?? raw.commissionValue ?? raw.payment?.workerCommissionValue ?? 0);
  if (type === "percentage" && value > 0) return Math.max(0, serviceRevenue * (value / 100));
  if (type === "fixed" && value > 0) return Math.max(0, value);
  return 0;
}

function agendaFinanceWorkerPayout(raw = {}, expected = 0) {
  const direct = agendaFinanceNumber(raw.totalWorkerPayoutAmount ?? raw.workerPayoutAmount ?? raw.workerPayoutEstimatedAmount ?? raw.workerPayout ?? raw.payment?.totalWorkerPayoutAmount ?? raw.payment?.workerPayoutAmount ?? raw.payment?.workerPayoutEstimatedAmount ?? 0);
  if (direct > 0) return direct;
  return agendaFinanceServiceCommission(raw, expected) + agendaFinanceTipAmount(raw) + agendaFinanceProductPayout(raw);
}

function agendaFinanceRowWorkerName(raw = {}, employeeMap = new Map()) {
  const direct = getBookingWorkerName(raw);
  if (direct) return direct;
  const wid = String(raw.employeeId || raw.workerId || raw.resourceId || raw.assignedEmployeeId || "").trim();
  const emp = wid ? employeeMap.get(wid) : null;
  return emp?.name || emp?.displayName || emp?.email || wid || "Sin trabajador";
}

function agendaFinanceBookingBelongsToWorker(raw = {}, context = {}) {
  if (!context?.isWorkerView) return true;
  return bookingMatchesOperationalWorker(raw, context.currentWorkerEmployee || context.resolvedWorkerEmployeeForPermissions || {}, context.detectedEmployeeDocId || context.effectiveWorkerEmployeeId || "", context.currentWorkerEmployee || context.resolvedWorkerEmployeeForPermissions || null, context.workerEmailMode || context.workerEmail || "", context.selectedEmployeeIds || []);
}

function buildAgendaFinanceSummary({ bookings = [], services = [], employees = [], selectedDate = new Date(), period = "day", isWorkerView = false, currentWorkerEmployee = null, resolvedWorkerEmployeeForPermissions = null, detectedEmployeeDocId = "", effectiveWorkerEmployeeId = "", workerEmailMode = "", selectedEmployeeIds = [] } = {}) {
  const range = agendaFinancePeriodRange(selectedDate, period);
  const serviceMap = new Map((Array.isArray(services) ? services : []).map((svc) => [String(svc.id || svc.serviceId || ""), svc]));
  const employeeMap = new Map((Array.isArray(employees) ? employees : []).map((emp) => [String(emp.id || emp.employeeId || ""), emp]));
  const context = { isWorkerView, currentWorkerEmployee, resolvedWorkerEmployeeForPermissions, detectedEmployeeDocId, effectiveWorkerEmployeeId, workerEmailMode, selectedEmployeeIds };
  const rows = (Array.isArray(bookings) ? bookings : [])
    .map((input, index) => {
      const raw = agendaFinanceMergedBooking(input);
      return { id: raw.id || raw.bookingId || raw.docId || `booking-${index}`, raw };
    })
    .filter(({ raw }) => agendaFinanceWithin(agendaFinanceBookingDate(raw), range))
    // LOOP WORKER FINANCE PARITY V96
    // BusinessAnalytics counts revenue rows only when status normalizes to confirmed/no_show.
    // Worker "Mis cobros" must use the same contract so worker totals match the business finance table.
    .filter(({ raw }) => agendaFinanceIsRevenueBooking(raw))
    .filter(({ raw }) => agendaFinanceBookingBelongsToWorker(raw, context))
    .map(({ id, raw }) => {
      const expected = agendaFinanceExpectedAmount(raw, serviceMap);
      const collected = agendaFinanceCollectedAmount(raw, expected);
      const pending = Math.max(0, expected - collected);
      const commission = agendaFinanceServiceCommission(raw, expected);
      const tips = agendaFinanceTipAmount(raw);
      const productPayout = agendaFinanceProductPayout(raw);
      const payout = agendaFinanceWorkerPayout(raw, expected);
      const serviceName = getBookingServiceName(raw, "Servicio");
      const clientName = getBookingClientName(raw, "Cliente");
      const workerName = agendaFinanceRowWorkerName(raw, employeeMap);
      const method = raw.paymentMethod || raw.payment?.method || raw.payment?.paymentMethod || "";
      const status = pending > 0 ? "pending" : collected > 0 ? "paid" : "open";
      const date = agendaFinanceBookingDate(raw);
      return { id, raw, date, clientName, serviceName, workerName, expected, collected, pending, commission, tips, productPayout, payout, method, status };
    })
    .sort((a, b) => (a.date ? a.date.getTime() : 0) - (b.date ? b.date.getTime() : 0));
  const totals = rows.reduce((acc, row) => {
    acc.expected += row.expected; acc.collected += row.collected; acc.pending += row.pending; acc.commission += row.commission; acc.tips += row.tips; acc.productPayout += row.productPayout; acc.payout += row.payout; acc.count += 1;
    if (row.status === "paid") acc.paidCount += 1;
    if (row.pending > 0) acc.pendingCount += 1;
    return acc;
  }, { expected: 0, collected: 0, pending: 0, commission: 0, tips: 0, productPayout: 0, payout: 0, count: 0, paidCount: 0, pendingCount: 0 });
  totals.net = Math.max(0, totals.collected - totals.payout);
  totals.workerTotal = totals.commission + totals.tips + totals.productPayout;
  const byMethod = new Map();
  rows.forEach((row) => { const label = agendaFinanceMethodLabel(row.method); byMethod.set(label, (byMethod.get(label) || 0) + row.collected); });
  return { rows, totals, byMethod: Array.from(byMethod.entries()).sort((a, b) => b[1] - a[1]), pendingRows: rows.filter((row) => row.pending > 0).slice(0, 8), recentRows: rows.slice(0, 12) };
}

function AgendaFinanceMini({ businessDoc = {}, businessId = "", firestoreDb = db, bookings = [], services = [], employees = [], selectedDate = new Date(), isWorkerView = false, currentWorkerEmployee = null, resolvedWorkerEmployeeForPermissions = null, detectedEmployeeDocId = "", effectiveWorkerEmployeeId = "", workerEmailMode = "", selectedEmployeeIds = [], onBackToAgenda = () => {}, onOpenReports = null }) {
  const language = useLanguage();
  const regionalMoney = regionMoney(businessDoc);
  const [period, setPeriod] = useState("day");
  const [financeAnchorDate, setFinanceAnchorDate] = useState(() => agendaFinanceStartOfDay(selectedDate || new Date()));
  const [directBookings, setDirectBookings] = useState([]);
  const [directLoadError, setDirectLoadError] = useState("");

  useEffect(() => {
    setFinanceAnchorDate(agendaFinanceStartOfDay(selectedDate || new Date()));
  }, [selectedDate]);

  const handleFinancePeriodChange = useCallback((nextPeriod) => {
    setPeriod(nextPeriod);
  }, []);

  const moveFinanceAnchor = useCallback((direction) => {
    setFinanceAnchorDate((prev) => agendaFinanceMoveAnchor(prev, period, direction));
  }, [period]);

  const resetFinanceAnchorToToday = useCallback(() => {
    setFinanceAnchorDate(agendaFinanceStartOfDay(new Date()));
  }, []);

  const financeAnchorInputValue = useMemo(() => agendaFinanceDateInputValue(financeAnchorDate), [financeAnchorDate]);
  const financeAnchorLabel = useMemo(() => agendaFinanceAnchorLabel(period, financeAnchorDate), [period, financeAnchorDate, language]);

  useEffect(() => {
    let mounted = true;

    async function loadDirectBookingsForFinance() {
      if (!businessId) {
        setDirectBookings([]);
        setDirectLoadError("");
        return;
      }

      try {
        const path = `businesses/${String(businessId)}/bookings`;
        let rows = [];
        if (isIosCapacitor()) {
          const got = await fsClient.listCollectionByPath(path, {}, { db: firestoreDb });
          rows = Array.isArray(got) ? got.map((x) => ({ id: x.id || x.bookingId, ...(x || {}) })) : [];
        } else {
          const snap = await getDocs(collection(firestoreDb, path));
          rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        }
        if (!mounted) return;
        setDirectBookings(rows);
        setDirectLoadError("");
      } catch (err) {
        console.warn("[AgendaFinanceMini] direct booking load failed", err);
        if (!mounted) return;
        setDirectBookings([]);
        setDirectLoadError(err?.message || String(err));
      }
    }

    loadDirectBookingsForFinance();
    return () => { mounted = false; };
  }, [businessId, firestoreDb]);

  const financeBookings = useMemo(
    () => agendaFinanceMergeBookingSources(bookings, directBookings),
    [bookings, directBookings]
  );

  const summary = useMemo(() => buildAgendaFinanceSummary({ bookings: financeBookings, services, employees, selectedDate: financeAnchorDate, period, isWorkerView, currentWorkerEmployee, resolvedWorkerEmployeeForPermissions, detectedEmployeeDocId, effectiveWorkerEmployeeId, workerEmailMode, selectedEmployeeIds }), [financeBookings, services, employees, financeAnchorDate, period, isWorkerView, currentWorkerEmployee, resolvedWorkerEmployeeForPermissions, detectedEmployeeDocId, effectiveWorkerEmployeeId, workerEmailMode, selectedEmployeeIds]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const range = agendaFinancePeriodRange(financeAnchorDate, period);
      const debugPayload = {
        version: "LOOP WORKER FINANCE PARITY V96",
        mode: isWorkerView ? "worker" : "owner",
        period,
        anchorDate: financeAnchorDate ? financeAnchorDate.toISOString() : null,
        rangeStart: range?.start ? range.start.toISOString() : null,
        rangeEnd: range?.end ? range.end.toISOString() : null,
        sourceBookings: Array.isArray(financeBookings) ? financeBookings.length : 0,
        renderedRows: Array.isArray(summary?.rows) ? summary.rows.length : 0,
        totals: summary?.totals || null,
        directLoadError: directLoadError || "",
        workerContext: {
          detectedEmployeeDocId,
          effectiveWorkerEmployeeId,
          workerEmailMode,
          selectedEmployeeIds,
          currentWorkerEmployeeId: currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || null,
          resolvedWorkerEmployeeId: resolvedWorkerEmployeeForPermissions?.id || resolvedWorkerEmployeeForPermissions?.employeeId || null,
        },
        rows: (Array.isArray(summary?.rows) ? summary.rows : []).slice(0, 30).map((row) => ({
          id: row.id,
          clientName: row.clientName,
          serviceName: row.serviceName,
          workerName: row.workerName,
          rawStatus: String(row.raw?.status || row.raw?.confirmationStatus || row.raw?.state || ""),
          normalizedStatus: agendaFinanceStatus(row.raw || {}),
          expected: row.expected,
          collected: row.collected,
          pending: row.pending,
          commission: row.commission,
          tips: row.tips,
          productPayout: row.productPayout,
          payout: row.payout,
          method: row.method,
        })),
      };
      window.__LOOP_WORKER_FINANCE_DEBUG__ = debugPayload;
      window.__LOOP_FINANCE_PARITY_DEBUG__ = debugPayload;
    } catch (_) {}
  }, [financeBookings, summary, financeAnchorDate, period, isWorkerView, directLoadError, detectedEmployeeDocId, effectiveWorkerEmployeeId, workerEmailMode, selectedEmployeeIds, currentWorkerEmployee, resolvedWorkerEmployeeForPermissions]);
  let currencyWarning = '';
  try { assertSameCurrency(summary.rows.map(row=>row.raw),businessDoc); }
  catch(error) { currencyWarning = error.message; }
  const AGENDA_FINANCE_MONEY = {format:value=>currencyWarning ? '—' : regionalMoney.format(value)};
  const roleLabel = isWorkerView ? "Mis cobros" : (period === "day" ? "Caja de hoy" : "Caja de agenda");
  const roleSubtitle = isWorkerView ? "Tu comisión, propinas y reservas del período seleccionado." : "Vista rápida operativa. La agenda sigue siendo la pantalla principal; Reportes queda para análisis completo.";
  const primaryRows = isWorkerView ? summary.recentRows : (summary.pendingRows.length ? summary.pendingRows : summary.recentRows.slice(0, 8));
  return (
    <section className={`agenda-finance-mini ${isWorkerView ? "is-worker-finance" : "is-owner-finance"}`} aria-label={t(roleLabel)}>
      <div className="agenda-finance-topbar">
        <button type="button" className="agenda-finance-back" onClick={onBackToAgenda}>{t("← Agenda")}</button>
        <div className="agenda-finance-periods" role="tablist" aria-label={t("Período financiero")}>
          {[["day", "Día"], ["week", "Semana"], ["month", "Mes"]].map(([key, label]) => <button key={key} type="button" className={period === key ? "is-active" : ""} onClick={() => handleFinancePeriodChange(key)}>{label}</button>)}
        </div>
        <div className="agenda-finance-date-nav" aria-label={t("Navegar período financiero")}>
          <button type="button" className="agenda-finance-date-step" onClick={() => moveFinanceAnchor(-1)} aria-label={agendaFinanceStepLabel(period, -1)}>‹</button>
          <label className="agenda-finance-date-picker" title={t("Elegir fecha del reporte")}>
            <span>{financeAnchorLabel}</span>
            <input
              type="date"
              value={financeAnchorInputValue}
              onChange={(event) => setFinanceAnchorDate(agendaFinanceDateFromInput(event.target.value, financeAnchorDate))}
              aria-label={t("Elegir fecha del reporte financiero")}
            />
          </label>
          <button type="button" className="agenda-finance-date-step" onClick={() => moveFinanceAnchor(1)} aria-label={agendaFinanceStepLabel(period, 1)}>›</button>
          <button type="button" className="agenda-finance-date-today" onClick={resetFinanceAnchorToToday}>{t("Hoy")}</button>
        </div>
        {!isWorkerView && typeof onOpenReports === "function" ? (
          <button type="button" className="agenda-finance-report-link" onClick={onOpenReports}>{t("Reportes completos")}</button>
        ) : null}
      </div>
      {currencyWarning && <p role="alert">{t(currencyWarning)}</p>}
      <div className="agenda-finance-hero"><div><span>{agendaFinancePeriodLabel(period)} · {financeAnchorLabel}</span><h2>{t(roleLabel)}</h2><p>{t(roleSubtitle)}</p></div><strong>{isWorkerView ? AGENDA_FINANCE_MONEY.format(summary.totals.workerTotal) : AGENDA_FINANCE_MONEY.format(summary.totals.collected)}</strong></div>
      <div className="agenda-finance-kpis">
        {isWorkerView ? <>
          <article><span>{t("Comisión")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.commission)}</strong><small>{t("Servicios realizados")}</small></article>
          <article><span>{t("Propinas")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.tips)}</strong><small>{t("Registradas")}</small></article>
          <article><span>{t("Total estimado")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.workerTotal)}</strong><small>{t("A recibir")}</small></article>
          <article><span>{t("Reservas")}</span><strong>{summary.totals.count}</strong><small>{summary.totals.pendingCount}{" " + t("pendientes")}</small></article>
        </> : <>
          <article><span>{t("Cobrado")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.collected)}</strong><small>{summary.totals.paidCount}{" " + t("reservas")}</small></article>
          <article><span>{t("Pendiente")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.pending)}</strong><small>{summary.totals.pendingCount}{" " + t("por cerrar")}</small></article>
          <article><span>{t("Payout equipo")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.payout)}</strong><small>{t("Comisión + propinas")}</small></article>
          <article><span>{t("Neto estimado")}</span><strong>{AGENDA_FINANCE_MONEY.format(summary.totals.net)}</strong><small>{t("Cobrado - payout")}</small></article>
        </>}
      </div>
      {!isWorkerView && summary.byMethod.length ? <div className="agenda-finance-methods" aria-label={t("Métodos de pago")}>{summary.byMethod.slice(0, 4).map(([label, value]) => <span key={label}>{label}<strong>{AGENDA_FINANCE_MONEY.format(value)}</strong></span>)}</div> : null}
      <article className="agenda-finance-list-card">
        <div className="agenda-finance-list-head"><div><span>{isWorkerView ? t("Mis reservas") : summary.pendingRows.length ? "Pagos por revisar" : "Reservas del período"}</span><h3>{isWorkerView ? "Detalle de mis cobros" : summary.pendingRows.length ? "Pendientes de cierre" : "Actividad financiera"}</h3></div><em>{primaryRows.length}</em></div>
        {primaryRows.length ? <div className="agenda-finance-list">{primaryRows.map((row) => <div className={`agenda-finance-row status-${row.status}`} key={row.id}><div><strong>{row.clientName}</strong><span>{row.serviceName} · {row.workerName}</span></div><div className="agenda-finance-row-money"><strong>{AGENDA_FINANCE_MONEY.format(isWorkerView ? row.payout : row.expected)}</strong><small>{isWorkerView ? `Com. ${AGENDA_FINANCE_MONEY.format(row.commission)} · Prop. ${AGENDA_FINANCE_MONEY.format(row.tips)}` : row.pending > 0 ? `${AGENDA_FINANCE_MONEY.format(row.pending)} pendiente` : agendaFinanceMethodLabel(row.method)}</small></div><em>{row.pending > 0 ? t("Pendiente") : t("Cerrado")}</em></div>)}</div> : <div className="agenda-finance-empty"><strong>{t("Sin movimiento financiero en este período.")}</strong><span>{t("Cuando haya reservas cobradas o pendientes, aparecerán aquí.")}</span></div>}
      </article>
    </section>
  );
}

/* ---------------------------
   Main AdminBookingCalendar component
--------------------------- */
export default function AdminBookingCalendar({
  businessId: businessIdProp,
  className = "",
  schedulerLicenseKey = null,
  locale = interfaceLocale(),
}) {
  useLanguage();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const calendarRef = useRef(null);
  const dropdownRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [currentMonthDate, setCurrentMonthDate] = useState(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
  );
  const [monthRangeEvents, setMonthRangeEvents] = useState([]);
  const [locallyHiddenBookingIds, setLocallyHiddenBookingIds] = useState(() => new Set());

  const [activeTab, setActiveTab] = useState(() => readCalendarSection(routeLocation.search).tab);


  const workerAgendaBootContextRef = useRef("");
  const [analyticsInitialView, setAnalyticsInitialView] = useState(() => readCalendarSection(routeLocation.search).report);

  // LOOP ADMIN NAVIGATION V2 A1.2 — predictable clean boot:
  // every admin load starts with Operación open and the other groups closed.
  // Group toggles remain interactive for the current session; the active group
  // still auto-opens when navigation moves into another section.
  const [adminSidebarGroupsOpen, setAdminSidebarGroupsOpen] = useState(() => ({
    operation: true,
    business: false,
    reports: false,
    settings: false,
  }));
  const [hydratedBusinessName, setHydratedBusinessName] = useState("");

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const params = new URLSearchParams(window.location.search || "");
      const panel = String(params.get("panel") || params.get("tab") || "").trim().toLowerCase();
      const source = String(params.get("source") || "").trim().toLowerCase();
      const bookingId = String(params.get("bookingId") || params.get("pendingBookingId") || "").trim();
      const shouldOpenPendings =
        panel === "pending" ||
        panel === "pendings" ||
        panel === "reservas" ||
        panel === "approvals" ||
        panel === "approval" ||
        Boolean(bookingId && source === "push" && (panel === "" || panel === "pending"));
      if (!shouldOpenPendings) return;
      setActiveTab("pendings");
      window.__LOOP_PENDING_ROUTE_BRIDGE__ = {
        panel,
        source,
        bookingId,
        openedTab: "pendings",
        time: new Date().toISOString(),
      };
    } catch (_) {}
  }, []);
  const [agendaSettings, setAgendaSettings] = useState(() => normalizeAgendaSettings());
  const [agendaSettingsSavedAt, setAgendaSettingsSavedAt] = useState(null);
  const [agendaSettingsSaveStatus, setAgendaSettingsSaveStatus] = useState("idle");
  const [agendaSettingsSaveError, setAgendaSettingsSaveError] = useState("");
  const [agendaSettingsDirty, setAgendaSettingsDirty] = useState(false);
  const agendaSettingsLoadedSignatureRef = useRef("");
  const agendaSettingsRef = useRef(normalizeAgendaSettings());
  const agendaSettingsPendingFirestoreSignatureRef = useRef("");
  // LOOP AGENDA BULLETPROOF SINGLE SURFACE RENDER FIX:
  // Keep startup view decisions one-shot per business/context. This prevents the UI from
  // painting Week, then Day, then another grid while employees/permissions are still loading.
  const agendaInitialViewAppliedRef = useRef("");
  const agendaSurfaceReadyTimerRef = useRef(null);
  const agendaSurfaceReadySignatureRef = useRef("");
  // LOOP AGENDA — FAST NAV + POPOVER LAYER FIX:
  // Once the agenda has painted for a business, day/week/month navigation should
  // not flash the full "Preparando agenda" gate again.
  const agendaSurfaceEverReadyRef = useRef(false);
  const agendaSurfaceReadyBusinessRef = useRef("");

  useEffect(() => {
    agendaSettingsRef.current = normalizeAgendaSettings(agendaSettings);
  }, [agendaSettings]);

  const [viewMode, setViewMode] = useState("day"); // 'day' | 'weekCompany' | 'month'
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [workerFilterOpen, setWorkerFilterOpen] = useState(false);
  const [workerFilterSearch, setWorkerFilterSearch] = useState("");
  const [mobileDayPresentation, setMobileDayPresentation] = useState("list");
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [rightRailCollapsed, setRightRailCollapsed] = useState(true);
  const [rightRailWidth, setRightRailWidth] = useState(() => {
    if (typeof window === "undefined") return 360;
    const saved = Number(window.localStorage.getItem("loopAgendaRightRailWidth") || 360);
    return Number.isFinite(saved) ? Math.max(300, Math.min(480, saved)) : 360;
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (typeof window === "undefined") return 188;
    const saved = Number(window.localStorage.getItem("loopAgendaSidebarWidth") || 188);
    return Number.isFinite(saved) ? Math.max(76, Math.min(280, saved)) : 188;
  });

  const [eventModalBooking, setEventModalBooking] = useState(null);
  const [scheduleChangeDialog, setScheduleChangeDialog] = useState(null);
  const [scheduleChangeSaving, setScheduleChangeSaving] = useState(false);
  const [scheduleChangeNotice, setScheduleChangeNotice] = useState(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [sessionBookingDraft,setSessionBookingDraft]=useState(null);
  useEffect(()=>{
    const customer=routeLocation.state?.prefillCustomer;
    const program=routeLocation.state?.prefillSessionProgram;
    if(!customer || !program)return;
    setSessionBookingDraft({customerId:customer.id,customerName:customer.name || customer.customerName || '',customerEmail:customer.email || '',customerPhone:customer.phone || '',customerSnapshot:customer,serviceId:program.serviceId,requestedSessionProgramId:program.id});
    setCreateModalOpen(true);
  },[routeLocation.key]);
  const [createModalDate, setCreateModalDate] = useState(null);
  const [createModalInitialTime, setCreateModalInitialTime] = useState(null);
  const [createModalResourceId, setCreateModalResourceId] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [manualEditBooking, setManualEditBooking] = useState(null);
  const [manualEditDraft, setManualEditDraft] = useState(null);
  const [manualEditSaving, setManualEditSaving] = useState(false);
  const [manualEditNotice, setManualEditNotice] = useState(null);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [rescheduleNotice, setRescheduleNotice] = useState(null);
  const [statusWorkflowSaving, setStatusWorkflowSaving] = useState(false);
  const [statusWorkflowNotice, setStatusWorkflowNotice] = useState(null);
  const [sessionReceipt,setSessionReceipt]=useState(null);
  const [sessionReceiptOpen,setSessionReceiptOpen]=useState(false);

  const [communicationSaving, setCommunicationSaving] = useState(false);
  const [communicationNotice, setCommunicationNotice] = useState(null);
  const [optimisticBookings, setOptimisticBookings] = useState([]);
  // R33 P2: retries of the exact same manual booking reuse one request id.
  // Changing customer, date, service, worker, status or commercial details
  // automatically creates a fresh request id.
  const staffBookingCreateAttemptRef = useRef(null);

  const closeCreateModalAfterSave = useCallback(() => {
    setSessionBookingDraft(null);
    setCreateModalOpen(false);
    setEditBooking(null);
    setCreateModalInitialTime(null);
    setCreateModalResourceId(null);
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        setCreateModalOpen(false);
        setEditBooking(null);
      }, 0);
    }
  }, []);

  const [smartCreateOpen, setSmartCreateOpen] = useState(false);
  const [smartCreateDraft, setSmartCreateDraft] = useState(null);
  const [smartCreateSaving, setSmartCreateSaving] = useState(false);
  const [smartCreateNotice, setSmartCreateNotice] = useState(null);

  // ✅ Native safe bottom padding (Android navbar / iOS home indicator)
  const [nativeBottomInsetPx, setNativeBottomInsetPx] = useState(0);

  const [workerColorPopup, setWorkerColorPopup] = useState({ open: false, employeeId: null, anchorRect: null });
  const [nativeFiltersOpen, setNativeFiltersOpen] = useState(false);

  const getResponsiveCalendarHeight = () => {
    if (typeof window === "undefined") return 760;
    const width = window.innerWidth || 1440;
    const height = window.innerHeight || 900;
    const isNarrowWeb = width <= 980;
    const isSmall = width <= 560;
    const offset = isSmall ? 154 : isNarrowWeb ? 168 : 158;
    return Math.max(isSmall ? 360 : 500, Math.min(1180, height - offset));
  };

  const [calendarHeight, setCalendarHeight] = useState(() => getResponsiveCalendarHeight());
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth
  );
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === "undefined") return 900;
    const vv = window.visualViewport;
    return Math.round((vv && vv.height) || window.innerHeight || 900);
  });

  useEffect(() => {
    function onResize() {
      setCalendarHeight(getResponsiveCalendarHeight());
      if (typeof window !== "undefined") {
        setViewportWidth(window.innerWidth || 1440);
        const vv = window.visualViewport;
        setViewportHeight(Math.round((vv && vv.height) || window.innerHeight || 900));
      }
    }
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    if (vv) {
      vv.addEventListener("resize", onResize);
      vv.addEventListener("scroll", onResize);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (vv) {
        vv.removeEventListener("resize", onResize);
        vv.removeEventListener("scroll", onResize);
      }
    };
  }, []);


  const isNativeRuntime = (() => {
    try {
      if (typeof window === "undefined") return false;
      if (!window.Capacitor) return false;
      const platform =
        typeof window.Capacitor.getPlatform === "function"
          ? window.Capacitor.getPlatform()
          : window.Capacitor.platform || null;
      return !!(platform && platform !== "web");
    } catch {
      return false;
    }
  })();


  // LOOP NATIVE PLATFORM FIT V31 — Android navigation buttons are an overlay.
  // Do not reserve layout height under the agenda, but keep floating actions touchable above it.
  const nativePlatform = (() => {
    try {
      if (typeof window === "undefined" || !window.Capacitor) return "web";
      const platform =
        typeof window.Capacitor.getPlatform === "function"
          ? window.Capacitor.getPlatform()
          : window.Capacitor.platform || "web";
      return String(platform || "web").toLowerCase();
    } catch {
      return "web";
    }
  })();
  const isIosNativeRuntime = isNativeRuntime && (nativePlatform === "ios" || isIosCapacitor());
  const isAndroidNativeRuntime = isNativeRuntime && nativePlatform === "android";
  const nativeNavigationGuardPx = isAndroidNativeRuntime ? 64 : 0;

  // LOOP AGENDA MADRE CLEANUP D1 V72
  // Legacy native-only agenda list removed. Day, Week and Month now render
  // through the single agenda madre router below.

  const handleRightRailResizeStart = useCallback((event) => {
    if (isNativeRuntime) return;
    const startX = event?.touches?.[0]?.clientX ?? event.clientX;
    const startWidth = rightRailWidth;
    if (!Number.isFinite(startX)) return;
    event.preventDefault?.();

    const getClientX = (ev) => ev?.touches?.[0]?.clientX ?? ev.clientX;
    const onMove = (ev) => {
      const clientX = getClientX(ev);
      if (!Number.isFinite(clientX)) return;
      const maxWidth = Math.max(320, Math.min(520, (window.innerWidth || 1440) - 760));
      const next = Math.max(300, Math.min(maxWidth, startWidth + (startX - clientX)));
      setRightRailWidth(next);
      try {
        window.localStorage.setItem("loopAgendaRightRailWidth", String(Math.round(next)));
      } catch (_) {}
    };

    const onUp = () => {
      document.body.classList.remove("loop-agenda-resizing-rail");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    document.body.classList.add("loop-agenda-resizing-rail");
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [isNativeRuntime, rightRailWidth]);

  const handleSidebarResizeStart = useCallback((event) => {
    if (isNativeRuntime) return;
    const startX = event?.touches?.[0]?.clientX ?? event.clientX;
    const startWidth = sidebarWidth;
    if (!Number.isFinite(startX)) return;
    event.preventDefault?.();

    const getClientX = (ev) => ev?.touches?.[0]?.clientX ?? ev.clientX;
    const onMove = (ev) => {
      const clientX = getClientX(ev);
      if (!Number.isFinite(clientX)) return;
      const maxWidth = Math.max(196, Math.min(280, (window.innerWidth || 1440) * 0.22));
      const next = Math.max(76, Math.min(maxWidth, startWidth + (clientX - startX)));
      setSidebarWidth(next);
      try {
        window.localStorage.setItem("loopAgendaSidebarWidth", String(Math.round(next)));
      } catch (_) {}
    };

    const onUp = () => {
      document.body.classList.remove("loop-agenda-resizing-sidebar");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    document.body.classList.add("loop-agenda-resizing-sidebar");
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [isNativeRuntime, sidebarWidth]);

  // ✅ drive inset from hook
  const nativeInsets = useNativeInsets({ isNativeRuntime });
  useEffect(() => {
    setNativeBottomInsetPx(nativeInsets?.nativeBottomInsetPx || 0);
  }, [nativeInsets?.nativeBottomInsetPx]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const body = document.body;
    const root = document.getElementById("root");
    const previousBodyBg = body?.style?.background || "";
    const previousRootBg = root?.style?.background || "";
    if (body) body.style.background = "#ffffff";
    if (root) root.style.background = "#ffffff";
    return () => {
      if (body) body.style.background = previousBodyBg;
      if (root) root.style.background = previousRootBg;
    };
  }, []);

  // ✅ Native date picker (hidden input)
  const nativeDateInputRef = useRef(null);
  const openNativeDatePicker = useCallback(() => {
    const el = nativeDateInputRef.current;
    if (!el) return;

    // iOS sometimes requires focus; Android may support showPicker()
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else {
        el.focus();
        el.click();
      }
    } catch {
      try {
        el.focus();
        el.click();
      } catch {}
    }
  }, []);

  // close toolbar popovers on outside click (web)
  useEffect(() => {
    function onDocClick(e) {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      if (e.target?.closest?.(".agenda-worker-filter-priority-overlay")) return;
      if (e.target?.closest?.(".agenda-worker-filter-popover")) return;
      if (e.target?.closest?.(".agenda-worker-filter-btn")) return;
      if (e.target?.closest?.(".agenda-filter-btn.agenda-worker-filter-btn")) return;
      if (e.target?.closest?.(".business-mobile-navigation")) return;
      if (e.target?.closest?.(".agenda-tools-floating-wrap")) return;
      if (e.target?.closest?.(".agenda-tools-floating-panel")) return;
      setViewDropdownOpen(false);
      setWorkerFilterOpen(false);
      setMobileToolsOpen(false);
    }
    if (viewDropdownOpen || workerFilterOpen || mobileToolsOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [viewDropdownOpen, workerFilterOpen, mobileToolsOpen]);


  // LOOP AGENDA — PRIORITY WORKER FILTER OVERLAY
  // Uses an owned overlay instead of depending on CalendarToolbar's clipped popover.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("loop-agenda-worker-filter-priority-open", Boolean(workerFilterOpen));
    return () => {
      document.body.classList.remove("loop-agenda-worker-filter-priority-open");
    };
  }, [workerFilterOpen]);

  useEffect(() => {
    if (!workerFilterOpen || typeof document === "undefined") return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setWorkerFilterOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [workerFilterOpen]);


  // close worker color popup on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!workerColorPopup.open) return;
      const popupEl = document.getElementById("worker-color-popup");
      if (popupEl && popupEl.contains(e.target)) return;
      setWorkerColorPopup({ open: false, employeeId: null, anchorRect: null });
    }
    if (workerColorPopup.open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [workerColorPopup.open]);

  // data hook
  const data = useAdminCalendarData({
    db,
    auth,
    businessIdProp,
    selectedDate,
    viewMode,
    routeState: routeLocation?.state || null,
  });

  const {
    activeBusinessId,
    businessDoc,
    employees,
    services,
    loading,
    workerAccessError,
    bookingsLoadError,

    selectedEmployeeIds,
    setSelectedEmployeeIds,

    isWorkerView,
    workerEmailMode,
    detectedEmployeeDocId,
    currentWorkerPermissions,
    currentWorkerEmployee,

    businessPendingCount,
    businessToConfirmCount,
    workerPendingCount,
    workerToConfirmCount,

    employeeHeaderColors,

    bookings,

    allEvents,
    eventsForDay,

    setReloadBookingsKey,
    reloadBookingsKey,

    iosAgendaDebug,
  } = data;

  const realBookingIdSet = useMemo(() => {
    const ids = new Set();
    (Array.isArray(bookings) ? bookings : []).forEach((raw) => {
      adminBookingIdCandidates(raw).forEach((id) => ids.add(id));
    });
    return ids;
  }, [bookings]);

  useEffect(() => {
    if (!optimisticBookings.length || !realBookingIdSet.size) return;
    setOptimisticBookings((prev) => {
      const next = (Array.isArray(prev) ? prev : []).filter((raw) => {
        const ids = adminBookingIdCandidates(raw);
        return !ids.length || !ids.some((id) => realBookingIdSet.has(id));
      });
      return next.length === prev.length ? prev : next;
    });
  }, [realBookingIdSet, optimisticBookings.length]);

  const optimisticBookingsForAgenda = useMemo(() => {
    return (Array.isArray(optimisticBookings) ? optimisticBookings : [])
      .filter((raw) => {
        const ids = adminBookingIdCandidates(raw);
        if (ids.length && ids.some((id) => realBookingIdSet.has(id))) return false;
        return !adminBookingIsHiddenFromAgenda(raw) && !adminBookingMatchesLocalHidden(raw, locallyHiddenBookingIds);
      });
  }, [optimisticBookings, realBookingIdSet, locallyHiddenBookingIds]);

  const optimisticCalendarEvents = useMemo(() => {
    return optimisticBookingsForAgenda
      .map((raw) => adminCalendarOptimisticEventFromBooking(raw))
      .filter(Boolean);
  }, [optimisticBookingsForAgenda]);

  const visibleBookings = useMemo(() => {
    const base = (Array.isArray(bookings) ? bookings : [])
      .filter((raw) => !adminBookingIsHiddenFromAgenda(raw) && !adminBookingMatchesLocalHidden(raw, locallyHiddenBookingIds));
    if (!optimisticBookingsForAgenda.length) return base;
    return [...base, ...optimisticBookingsForAgenda];
  }, [bookings, optimisticBookingsForAgenda, locallyHiddenBookingIds]);

  const visibleAllEvents = useMemo(() => {
    const base = (Array.isArray(allEvents) ? allEvents : [])
      .filter((ev) => !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds));
    if (!optimisticCalendarEvents.length) return base;
    const baseIds = new Set(base.map((ev) => String(ev?.id || ev?.extendedProps?.raw?.id || "").trim()).filter(Boolean));
    const optimistic = optimisticCalendarEvents.filter((ev) => {
      const id = String(ev?.id || ev?.extendedProps?.raw?.id || "").trim();
      return (!id || !baseIds.has(id)) && !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds);
    });
    return optimistic.length ? [...base, ...optimistic] : base;
  }, [allEvents, optimisticCalendarEvents, locallyHiddenBookingIds]);

  const visibleEventsForDay = useMemo(() => {
    const base = (Array.isArray(eventsForDay) ? eventsForDay : [])
      .filter((ev) => !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds));
    if (!optimisticCalendarEvents.length) return base;
    const baseIds = new Set(base.map((ev) => String(ev?.id || ev?.extendedProps?.raw?.id || "").trim()).filter(Boolean));
    const optimistic = optimisticCalendarEvents.filter((ev) => {
      const id = String(ev?.id || ev?.extendedProps?.raw?.id || "").trim();
      if (id && baseIds.has(id)) return false;
      if (selectedDate && !adminCalendarSameLocalDay(ev?.start, selectedDate)) return false;
      return !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds);
    });
    return optimistic.length ? [...base, ...optimistic] : base;
  }, [eventsForDay, optimisticCalendarEvents, locallyHiddenBookingIds, selectedDate]);

  const visibleMonthRangeEvents = useMemo(() => {
    const base = (Array.isArray(monthRangeEvents) ? monthRangeEvents : [])
      .filter((ev) => !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds));
    if (!optimisticCalendarEvents.length) return base;
    const baseIds = new Set(base.map((ev) => String(ev?.id || ev?.extendedProps?.raw?.id || "").trim()).filter(Boolean));
    const optimistic = optimisticCalendarEvents.filter((ev) => {
      const id = String(ev?.id || ev?.extendedProps?.raw?.id || "").trim();
      return (!id || !baseIds.has(id)) && !adminCalendarEventIsHiddenFromAgenda(ev, locallyHiddenBookingIds);
    });
    return optimistic.length ? [...base, ...optimistic] : base;
  }, [monthRangeEvents, optimisticCalendarEvents, locallyHiddenBookingIds]);

  const triggerBookingAgendaRefresh = useCallback((reason = "booking_refresh", meta = {}) => {
    const debug = { reason, ...meta, at: new Date().toISOString() };
    try {
      if (typeof window !== "undefined") window.__LOOP_WORKER_CREATE_REFRESH_DEBUG__ = debug;
    } catch (_) {}
    setReloadBookingsKey((k) => k + 1);
    if (typeof window !== "undefined") {
      [450, 1200, 2400].forEach((delay) => {
        window.setTimeout(() => setReloadBookingsKey((k) => k + 1), delay);
      });
    }
  }, [setReloadBookingsKey]);

  const routeBusinessName = useMemo(
    () => adminBusinessNameFromRouteState(routeLocation, activeBusinessId),
    [routeLocation, activeBusinessId]
  );

  const showSessionReceipt=useCallback(async result=>{
    try {
      const bookingId=result?.id || result?.data?.bookingId;
      if(!bookingId)return;
      const snap=await getDoc(doc(db,'businesses',activeBusinessId,'bookings',bookingId));
      const booking=snap.data();
      if(booking?.sessionProgramId)setSessionReceipt(booking);
    } catch (_) { /* The booking succeeded; the normal agenda refresh remains available. */ }
  },[activeBusinessId]);
  const cachedBusinessName = useMemo(
    () => adminBusinessNameFromLocalCache(activeBusinessId),
    [activeBusinessId, businessDoc?.name, businessDoc?.businessName, businessDoc?.title]
  );

  const routeBusinessLogo = useMemo(
    () => adminBusinessLogoFromRouteState(routeLocation),
    [routeLocation]
  );

  const cachedBusinessLogo = useMemo(
    () => adminBusinessLogoFromLocalCache(activeBusinessId),
    [activeBusinessId, businessDoc]
  );

  const workerBusinessLogo = useMemo(
    () => adminBusinessLogoFromWorker(currentWorkerEmployee),
    [currentWorkerEmployee]
  );

  const sidebarBusinessName = useMemo(
    () => adminSidebarBusinessName(businessDoc, activeBusinessId, {
      businessId: activeBusinessId,
      routeName: routeBusinessName,
      cachedName: cachedBusinessName,
      hydratedName: hydratedBusinessName,
    }),
    [businessDoc, activeBusinessId, routeBusinessName, cachedBusinessName, hydratedBusinessName]
  );
  const sidebarBusinessZone = useMemo(
    () => adminSidebarBusinessZone(businessDoc),
    [businessDoc]
  );
  const sidebarBusinessLogo = useMemo(
    () => adminSidebarBusinessLogo(businessDoc) || routeBusinessLogo || cachedBusinessLogo || workerBusinessLogo,
    [businessDoc, routeBusinessLogo, cachedBusinessLogo, workerBusinessLogo]
  );
  const sidebarBusinessInitials = useMemo(
    () => adminSidebarBusinessInitials(sidebarBusinessName),
    [sidebarBusinessName]
  );
  const agendaLoopPlanCard = getAgendaLoopPlanCard(businessDoc || {});

  useEffect(() => {
    if (!activeBusinessId) {
      setHydratedBusinessName("");
      return undefined;
    }

    let cancelled = false;
    const id = String(activeBusinessId || "").trim();
    const immediate =
      adminBusinessNameFromDoc(businessDoc, id) ||
      routeBusinessName ||
      cachedBusinessName;

    if (immediate) {
      setHydratedBusinessName((prev) => (prev === immediate ? prev : immediate));
      try {
        window.localStorage?.setItem?.("loopActiveBusinessId", id);
        window.localStorage?.setItem?.("loopAgendaActiveBusinessId", id);
        window.localStorage?.setItem?.("loopActiveBusinessName", immediate);
        window.localStorage?.setItem?.("loopAgendaActiveBusinessName", immediate);
        const logo = adminSidebarBusinessLogo(businessDoc) || routeBusinessLogo || cachedBusinessLogo || workerBusinessLogo;
        if (logo) {
          window.localStorage?.setItem?.("loopActiveBusinessLogo", logo);
          window.localStorage?.setItem?.("loopAgendaActiveBusinessLogo", logo);
        }
      } catch (_) {}
      return undefined;
    }

    setHydratedBusinessName("");

    async function resolveBusinessName() {
      try {
        let row = null;
        if (isIosCapacitor() && fsClient && typeof fsClient.getDocByPath === "function") {
          row = await fsClient.getDocByPath(`businesses/${id}`, { db });
        } else {
          const snap = await getDoc(doc(db, "businesses", id));
          row = snap && snap.exists && snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null;
        }
        const clean = adminBusinessNameFromDoc(row || {}, id);
        if (!cancelled && clean) {
          setHydratedBusinessName(clean);
          try {
            window.localStorage?.setItem?.("loopActiveBusinessId", id);
            window.localStorage?.setItem?.("loopAgendaActiveBusinessId", id);
            window.localStorage?.setItem?.("loopActiveBusinessName", clean);
            window.localStorage?.setItem?.("loopAgendaActiveBusinessName", clean);
            const logo = adminSidebarBusinessLogo(row || businessDoc) || routeBusinessLogo || cachedBusinessLogo || workerBusinessLogo;
            if (logo) {
              window.localStorage?.setItem?.("loopActiveBusinessLogo", logo);
              window.localStorage?.setItem?.("loopAgendaActiveBusinessLogo", logo);
            }
          } catch (_) {}
        }
      } catch (error) {
        try {
          window.__LOOP_AGENDA_BUSINESS_NAME_DEBUG__ = {
            stage: "business-name-hydration-failed",
            businessId: id,
            message: String(error?.message || error || ""),
            at: new Date().toISOString(),
          };
        } catch (_) {}
      }
    }

    resolveBusinessName();
    return () => {
      cancelled = true;
    };
  }, [activeBusinessId, businessDoc, routeBusinessName, cachedBusinessName, routeBusinessLogo, cachedBusinessLogo, workerBusinessLogo]);


  useEffect(() => {
    if (!activeBusinessId) return undefined;
    try {
      const cleanName = adminCleanBusinessDisplayName(sidebarBusinessName, activeBusinessId);
      const detail = {
        businessId: String(activeBusinessId || "").trim(),
        businessName: cleanName,
        businessLogo: sidebarBusinessLogo || "",
      };
      if (!detail.businessId) return undefined;
      window.__LOOP_ACTIVE_BUSINESS_ID__ = detail.businessId;
      window.__LOOP_ACTIVE_BUSINESS_NAME__ = detail.businessName;
      window.localStorage?.setItem?.("loopActiveBusinessId", detail.businessId);
      window.localStorage?.setItem?.("loopAgendaActiveBusinessId", detail.businessId);
      if (detail.businessName) {
        window.localStorage?.setItem?.("loopActiveBusinessName", detail.businessName);
        window.localStorage?.setItem?.("loopAgendaActiveBusinessName", detail.businessName);
      }
      if (detail.businessLogo) {
        window.localStorage?.setItem?.("loopActiveBusinessLogo", detail.businessLogo);
        window.localStorage?.setItem?.("loopAgendaActiveBusinessLogo", detail.businessLogo);
      }
      window.dispatchEvent(new CustomEvent("loop:active-business-context", { detail }));
    } catch (_) {}
    return undefined;
  }, [activeBusinessId, sidebarBusinessName, sidebarBusinessLogo]);

  const agendaFinanceSourceBookings = useMemo(
    () => agendaFinanceMergeBookingSources(visibleBookings, visibleAllEvents, visibleEventsForDay),
    [visibleBookings, visibleAllEvents, visibleEventsForDay]
  );

  const smartCreateRecentCustomers = useMemo(
    () => adminSmartUniqueCustomersFromBookings(visibleBookings),
    [visibleBookings]
  );

  const eventModalClientHistory = useMemo(
    () => adminClientHistoryBuild(eventModalBooking, visibleBookings, locale),
    [eventModalBooking, visibleBookings, locale]
  );

  const eventModalCommunicationTemplates = useMemo(
    () => adminCommunicationTemplates(eventModalBooking || {}, businessDoc || {}, locale),
    [eventModalBooking, businessDoc, locale]
  );

  const handleInlineUploadUnavailable = useCallback(() => {
    alert("Esta acción de carga de imagen/archivo todavía vive en editar empresa. La edición principal ya funciona dentro del panel.");
  }, []);

  const handleAdminAddService = useCallback(async (name, duration, price, extras = {}) => {
    if (!activeBusinessId) throw new Error("business id missing");
    const payload = {
      name: String(name || "").trim(),
      duration: Number(duration || 30),
      price: Number(price || 0),
      featured: false,
      disabled: false,
      active: true,
      visible: true,
      ...extras,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, "businesses", String(activeBusinessId), "services"), payload);
    setReloadBookingsKey((k) => k + 1);
    return { id: ref.id, ...payload };
  }, [activeBusinessId, setReloadBookingsKey]);

  const handleAdminDeleteService = useCallback(async (serviceId) => {
    if (!activeBusinessId || !serviceId) return;
    await deleteDoc(doc(db, "businesses", String(activeBusinessId), "services", String(serviceId)));
    setReloadBookingsKey((k) => k + 1);
  }, [activeBusinessId, setReloadBookingsKey]);

  const handleAdminSaveService = useCallback(async (serviceId, payload = {}) => {
    if (!activeBusinessId || !serviceId) throw new Error("service path missing");
    const path = `businesses/${String(activeBusinessId)}/services/${String(serviceId)}`;
    const now = isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
    const patch = { ...(payload || {}), updatedAt: now };
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      await fsClient.setDocByPath(path, patch, { db, merge: true });
    } else {
      await setDoc(doc(db, path), patch, { merge: true });
    }
    setReloadBookingsKey((k) => k + 1);
    return { id: serviceId, ...patch };
  }, [activeBusinessId, setReloadBookingsKey]);

  const handleAdminSaveEmployee = useCallback(async (payload = {}) => {
    if (!activeBusinessId || !payload.id) throw new Error("employee path missing");
    const { id, ...rest } = payload;
    const path = `businesses/${String(activeBusinessId)}/employees/${String(id)}`;
    const now = isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
    const normalizedPermissions = rest.permissions || rest.workerPermissions || null;
    const patch = {
      ...rest,
      ...(normalizedPermissions ? {
        permissions: normalizedPermissions,
        workerPermissions: normalizedPermissions,
        permissionsManualOverride: true,
        permissionsUpdatedAt: now,
      } : {}),
      updatedAt: now,
    };
    // Use merge writes instead of update-only writes. This is safer when an employee row
    // was created by an older flow or when native/iOS session hydration is stale.
    if (isIosCapacitor() && typeof fsClient.setDocByPath === "function") {
      await fsClient.setDocByPath(path, patch, { db, merge: true });
    } else {
      await setDoc(doc(db, path), patch, { merge: true });
    }
    setReloadBookingsKey((k) => k + 1);
  }, [activeBusinessId, setReloadBookingsKey]);

  const handleAdminAddEmployee = useCallback(async (payload = {}) => {
    if (!activeBusinessId) throw new Error("business id missing");
    const collectionPath = `businesses/${String(activeBusinessId)}/employees`;
    const now = isIosCapacitor() ? new Date().toISOString() : serverTimestamp();
    const nextPayload = {
      ...payload,
      createdAt: now,
      updatedAt: now,
    };
    if (isIosCapacitor() && typeof fsClient.addDocToCollectionPath === "function") {
      const created = await fsClient.addDocToCollectionPath(collectionPath, nextPayload, { db });
      const id = created?.id || created?.docId || created?.ref?.id || created?.name?.split?.("/")?.pop?.() || null;
      setReloadBookingsKey((k) => k + 1);
      return { id, ...nextPayload };
    }
    const ref = await addDoc(collection(db, collectionPath), nextPayload);
    setReloadBookingsKey((k) => k + 1);
    return { id: ref.id, ...nextPayload };
  }, [activeBusinessId, setReloadBookingsKey]);

  const handleAdminDeleteEmployee = useCallback(async (employeeId) => {
    if (!activeBusinessId || !employeeId) return;
    const ok = typeof window === "undefined" ? true : window.confirm("¿Eliminar este trabajador del equipo?");
    if (!ok) return;
    // fsClient currently has no guaranteed deleteDocByPath helper in all builds, so web delete remains the fallback.
    await manageWorkerAccess(activeBusinessId, employeeId, 'unlink');
    await deleteDoc(doc(db, "businesses", String(activeBusinessId), "employees", String(employeeId)));
    setReloadBookingsKey((k) => k + 1);
  }, [activeBusinessId, setReloadBookingsKey]);

  useEffect(() => {
    if (!activeBusinessId || !businessDoc) return;

    const firestoreSettings = normalizeAgendaSettings(businessDoc.calendarSettings || {});
    const nextSignature = agendaSettingsSignature(activeBusinessId, firestoreSettings);
    const sameBusinessAlreadyLoaded = agendaSettingsLoadedSignatureRef.current.startsWith(`${activeBusinessId}:`);
    const pendingFirestoreSignature = agendaSettingsPendingFirestoreSignatureRef.current;

    // After saving, React can briefly re-run this effect with the old businessDoc snapshot.
    // Do not let that stale snapshot overwrite the setting the user just saved.
    if (pendingFirestoreSignature) {
      if (nextSignature !== pendingFirestoreSignature && sameBusinessAlreadyLoaded) return;
      if (nextSignature === pendingFirestoreSignature) {
        agendaSettingsPendingFirestoreSignatureRef.current = "";
      }
    }

    if (nextSignature === agendaSettingsLoadedSignatureRef.current) return;
    if (agendaSettingsDirty && sameBusinessAlreadyLoaded) return;

    agendaSettingsRef.current = firestoreSettings;
    setAgendaSettings(firestoreSettings);
    // Do not force-render the calendar immediately from Firestore defaults on every snapshot.
    // The final startup view is applied once below, after business + worker data are stable.
    setAgendaSettingsDirty(false);
    setAgendaSettingsSaveStatus("idle");
    setAgendaSettingsSaveError("");
    setAgendaSettingsSavedAt(formatFirestoreTimestamp(businessDoc.calendarSettingsUpdatedAt, firestoreSettings.timeFormat));
    agendaSettingsLoadedSignatureRef.current = nextSignature;
  }, [activeBusinessId, businessDoc, agendaSettingsDirty]);


  useEffect(() => {
    let cancelled = false;

    async function loadMonthRangeBookings() {
      if (!activeBusinessId || activeTab !== "agenda" || viewMode !== "month") {
        if (!cancelled) setMonthRangeEvents([]);
        return;
      }

      const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const nextMonthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
      const monthEnd = new Date(nextMonthStart.getFullYear(), nextMonthStart.getMonth(), 0);
      const startYmd = formatDateYmdAdmin(monthStart);
      const endYmd = formatDateYmdAdmin(monthEnd);
      const bookingsRef = collection(db, "businesses", String(activeBusinessId), "bookings");
      const results = new Map();

      try {
        const reads = await Promise.allSettled([
          getDocs(query(bookingsRef, where("date", ">=", startYmd), where("date", "<=", endYmd))),
          getDocs(query(bookingsRef, where("scheduledAt", ">=", Timestamp.fromDate(monthStart)), where("scheduledAt", "<", Timestamp.fromDate(nextMonthStart)))),
        ]);

        reads.forEach((res) => {
          if (res.status !== "fulfilled") return;
          res.value.forEach((snap) => {
            if (results.has(snap.id)) return;
            const raw = snap.data() || {};
            const ev = buildMonthCalendarEventFromBookingDoc(snap.id, raw);
            if (ev) results.set(snap.id, ev);
          });
        });
      } catch (err) {
        console.warn("[AdminBookingCalendar] month range bookings load failed", err);
      }

      if (!cancelled) {
        setMonthRangeEvents(Array.from(results.values()).sort((a, b) => new Date(a.start) - new Date(b.start)));
      }
    }

    loadMonthRangeBookings();
    return () => { cancelled = true; };
  }, [activeBusinessId, activeTab, viewMode, selectedDate]);


  // PHASE AGENDA REVAMP: businesses with service workers open by worker/day.
  // LOOP AGENDA V74: normal service businesses must have at least one real,
  // bookable worker before the agenda can create reservations. Operational-only
  // staff does not count as a calendar worker.
  const serviceWorkerEmployees = useMemo(() => {
    return (employees || []).filter((emp) => {
      const rawType = String(emp?.workerType || emp?.staffType || emp?.type || "").trim().toLowerCase();
      const operationalOnly = emp?.isOperationalWorker === true || emp?.operationalOnly === true || emp?.hiddenFromBooking === true;
      if (operationalOnly) return false;
      if (["operational", "operativo", "operations", "staff", "support"].includes(rawType)) return false;
      if (emp?.disabled === true || emp?.active === false || emp?.__loopSelfFallback || emp?.__soloCompanyAgenda || emp?.__derivedFromBookingsV16F) return false;
      return true;
    });
  }, [employees]);

  const hasCalendarWorkers = useMemo(() => Array.isArray(serviceWorkerEmployees) && serviceWorkerEmployees.length > 0, [serviceWorkerEmployees]);
  const workerPermissions = useMemo(() => normalizeWorkerPermissions(currentWorkerPermissions), [currentWorkerPermissions]);
  const isPersonalWorkerAgenda = Boolean(isWorkerView && !workerPermissions.canViewAllCalendars && !workerPermissions.canViewSelectedCalendars);

  // LOOP AGENDA TDZ INIT FIX HOTFIX:
  // This must be declared before the startup-view effect below. React renders the
  // component top-to-bottom, so reading it before this declaration throws:
  // "Cannot access 'isEventBusinessAdmin' before initialization".
  const isEventBusinessAdmin = useMemo(() => isEventBusinessCalendar(businessDoc), [businessDoc]);

  // LOOP AGENDA MADRE PHASE A V68
  // No-worker businesses should still use the same mother agenda surface.
  // Instead of blocking the agenda until a worker exists, Day gets a single
  // virtual column named "Agenda" with no employeeId.
  const agendaRequiresWorkerSetup = false;

  useEffect(() => {
    // LOOP AGENDA BULLETPROOF SINGLE SURFACE RENDER FIX:
    // Apply the automatic owner/worker startup view only once per business/context and only
    // after the data hook is done loading. Previously this effect fired first with employees=[]
    // (forcing Week) and then again when employees arrived (forcing Day), which created the
    // visible "se apaga / se prende" agenda swap.
    if (!activeBusinessId || !businessDoc || loading) return;
    const contextKey = [
      String(activeBusinessId),
      isWorkerView ? "worker" : "owner",
      isEventBusinessAdmin ? "event" : "service",
      hasCalendarWorkers ? "workers" : "no-workers",
      agendaRequiresWorkerSetup ? "setup-lock" : "ready",
      "own-only",
    ].join(":");

    if (agendaInitialViewAppliedRef.current === contextKey) return;
    agendaInitialViewAppliedRef.current = contextKey;

    setViewMode((prev) => {
      if (isWorkerView) return (isNativeRuntime || viewportWidth < 768) ? "day" : (prev || "weekCompany");
      if (!isWorkerView && isEventBusinessAdmin) return "weekCompany";
      // LOOP AGENDA V67: a new/no-worker service business opens as a normal single agenda.
      if (!isWorkerView && !hasCalendarWorkers) return "day";
      if (!isWorkerView && hasCalendarWorkers && prev === "weekCompany") return "day";
      return prev || "day";
    });
  }, [activeBusinessId, businessDoc, loading, hasCalendarWorkers, isWorkerView, isEventBusinessAdmin, isNativeRuntime, viewportWidth, agendaRequiresWorkerSetup]);


  useEffect(() => {
    // LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80:
    // Do not hard-block worker access to Clientes/Servicios/Equipo here.
    // Those tabs are now controlled by the normalized permission map below.
    if (isWorkerView && activeTab === "settings") {
      setActiveTab("agenda");
    }
  }, [isWorkerView, activeTab]);

  // LOOP AGENDA WORKER STABLE FILTER FIX HOTFIX
  // Personal worker agendas must not fight the user's selected view.
  // The previous effect forced desktop workers into weekCompany and mobile workers into day,
  // which caused the agenda to flicker between views while loading. Now we only normalize
  // narrow/native screens to day; desktop/tablet keep whatever the user selected.
  useEffect(() => {
    // Worker agendas should open in Day once, not keep fighting the user's selected view.
    // This removes the old responsive loop that forced desktop workers into Week and then
    // small/native screens back into Day.
    if (!isPersonalWorkerAgenda) return;
    setViewDropdownOpen((prev) => (prev ? false : prev));
    setWorkerFilterOpen((prev) => (prev ? false : prev));
  }, [isPersonalWorkerAgenda]);

  // ✅ helpers for selection
  useEffect(() => {
    // FullCalendar is no longer the agenda engine. Do not call changeView/updateSize here;
    // the single custom router owns Day/Semana/Mes.
  }, []);

  const selectedSet = useMemo(() => new Set((selectedEmployeeIds || []).map(String)), [selectedEmployeeIds]);

  const routeWorkerIdentity = useMemo(() => getRouteWorkerIdentityHardLock(), []);
  const hardWorkerEmailMode = normalizeEmailForWorkerVisibility(workerEmailMode || routeWorkerIdentity.workerEmail || "");
  const hardDetectedEmployeeDocId = String(detectedEmployeeDocId || routeWorkerIdentity.workerId || "").trim();
  // LOOP WORKER OWN-ONLY V6: the URL param itself is enough to force worker privacy.
  // This prevents the agenda from being treated as owner/company if the hook state is late or stale.
  const hardWorkerView = Boolean(isWorkerView || hardWorkerEmailMode || hardDetectedEmployeeDocId);
  const workerPanelScope = Boolean(hardWorkerView || isWorkerView || isPersonalWorkerAgenda);
  const hardSelectedEmployeeIds = useMemo(() => {
    if (hardDetectedEmployeeDocId) return [String(hardDetectedEmployeeDocId)];
    if (Array.isArray(selectedEmployeeIds) && selectedEmployeeIds.length) return selectedEmployeeIds.map(String);
    return [];
  }, [hardDetectedEmployeeDocId, selectedEmployeeIds]);

  const routeWorkerProfile = useMemo(
    () => adminWorkerProfileFromRouteState(routeLocation),
    [routeLocation]
  );

  const routeCalendarSettings = useMemo(
    () => adminCalendarSettingsFromRouteState(routeLocation),
    [routeLocation]
  );

  const cachedCalendarSettings = useMemo(
    () => adminReadCalendarSettingsFromLocalCache(activeBusinessId),
    [activeBusinessId, businessDoc?.calendarSettings]
  );

  useEffect(() => {
    if (activeBusinessId && businessDoc?.calendarSettings && typeof businessDoc.calendarSettings === "object") {
      adminWriteCalendarSettingsToLocalCache(activeBusinessId, businessDoc.calendarSettings);
    }
  }, [activeBusinessId, businessDoc?.calendarSettings]);

  const businessAgendaSettings = useMemo(() => {
    // V84 visual settings contract:
    // slot interval, 12h/24h, visible day range and density belong to the business,
    // never to the worker. Worker routes can mount from route/cache before Firestore
    // hydration, so use this fallback order before falling back to defaults.
    const source =
      (businessDoc?.calendarSettings && typeof businessDoc.calendarSettings === "object" && Object.keys(businessDoc.calendarSettings).length > 0)
        ? businessDoc.calendarSettings
        : (routeCalendarSettings && typeof routeCalendarSettings === "object" && Object.keys(routeCalendarSettings).length > 0)
          ? routeCalendarSettings
          : (cachedCalendarSettings && typeof cachedCalendarSettings === "object" && Object.keys(cachedCalendarSettings).length > 0)
            ? cachedCalendarSettings
            : agendaSettings;
    return normalizeAgendaSettings(source || {});
  }, [businessDoc?.calendarSettings, routeCalendarSettings, cachedCalendarSettings, agendaSettings]);

  const effectiveAgendaSettings = useMemo(() => {
    // LOOP AGENDA MADRE WORKER AVAILABILITY/PENDINGS V81:
    // Workers do not have a separate agenda interval/density contract.
    // They inherit the business calendarSettings so owner + worker snap to the same grid.
    if (workerPanelScope || hardWorkerView || isWorkerView || isPersonalWorkerAgenda) {
      return businessAgendaSettings;
    }
    return normalizeAgendaSettings(agendaSettings);
  }, [agendaSettings, businessAgendaSettings, workerPanelScope, hardWorkerView, isWorkerView, isPersonalWorkerAgenda]);

  // ✅ open at 07:00
  const scrollTime = timeStringToDuration(effectiveAgendaSettings.scrollStartTime, "08:00:00");

  useEffect(() => {
    // LOOP AGENDA — WORKER BOOT TO AGENDA V38
    // When React reuses this route, activeTab can remain on "finance"/another
    // panel from a previous agenda session. Worker links must always boot into
    // the agenda once for their worker context, then allow normal tab changes.
    if (!hardWorkerView) return;

    const bootKey = [
      String(activeBusinessId || ""),
      String(hardDetectedEmployeeDocId || ""),
      String(hardWorkerEmailMode || ""),
      isNativeRuntime ? "native" : "web",
    ].join(":");

    if (!bootKey.trim() || workerAgendaBootContextRef.current === bootKey) return;
    workerAgendaBootContextRef.current = bootKey;

    setActiveTab("agenda");
    setViewMode("day");
    setViewDropdownOpen((prev) => (prev ? false : prev));
    setWorkerFilterOpen((prev) => (prev ? false : prev));

    try {
      window.__loopAgendaWorkerBootV38 = {
        bootKey,
        time: new Date().toISOString(),
      };
    } catch (_) {}
  }, [hardWorkerView, activeBusinessId, hardDetectedEmployeeDocId, hardWorkerEmailMode, isNativeRuntime]);

  useEffect(() => {
    if (!workerPanelScope) return;
    if (["workers", "analytics", "clients", "services", "settings"].includes(activeTab)) {
      setActiveTab("agenda");
    }
  }, [workerPanelScope, activeTab]);

  const nativeWeekDays = useMemo(
    () => weekDaysFromSelectedDate(selectedDate, effectiveAgendaSettings.weekStartsOn, effectiveAgendaSettings.showWeekends),
    [selectedDate, effectiveAgendaSettings.weekStartsOn, effectiveAgendaSettings.showWeekends]
  );




  const agendaPresentationActor = getEffectiveActor().uid || "";
  const agendaPresentationKey = activeBusinessId && agendaPresentationActor ? "loop:agenda:presentation:" + activeBusinessId + ":" + agendaPresentationActor : "";
  useEffect(() => {
    try { setMobileDayPresentation(agendaPresentationKey && localStorage.getItem(agendaPresentationKey) === "team" ? "team" : "list"); }
    catch { setMobileDayPresentation("list"); }
  }, [agendaPresentationKey]);
  const chooseMobileDayPresentation = value => {
    setMobileDayPresentation(value);
    try { if (agendaPresentationKey) localStorage.setItem(agendaPresentationKey, value); } catch { /* Storage can be disabled on this device. */ }
  };

  const agendaAuthorizedEmployees = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    if (!hardWorkerView && !isWorkerView) return list;
    const email = String(hardWorkerEmailMode || "").trim().toLowerCase();
    const byEmail = email ? list.find(e => [e.email, e.employeeEmail, e.workerEmail, e.inviteEmail].some(value => String(value || "").trim().toLowerCase() === email)) : null;
    const ownId = currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || hardDetectedEmployeeDocId || byEmail?.id;
    return authorizedAgendaWorkers(list, workerPermissions, ownId);
  }, [employees, hardWorkerView, isWorkerView, hardWorkerEmailMode, currentWorkerEmployee, hardDetectedEmployeeDocId, workerPermissions]);
  const nativeVisibleEmployees = useMemo(() => {
    if (!selectedEmployeeIds?.length) return agendaAuthorizedEmployees;
    return agendaAuthorizedEmployees.filter(e => selectedSet.has(String(e.id)) || selectedSet.has(String(e.employeeId || "")));
  }, [agendaAuthorizedEmployees, selectedEmployeeIds, selectedSet]);

  const isNoWorkerBusiness = useMemo(() => {
    const hasEmployees = Array.isArray(employees) && employees.length > 0;
    return Boolean(
      (!hasEmployees && (businessDoc?.noWorkers || businessDoc?.noWorkerMode)) ||
      businessDoc?.singleWorker ||
      businessDoc?.soloOwner ||
      businessDoc?.ownerOnly ||
      businessDoc?.businessModel === "solo" ||
      (!hasEmployees && businessDoc)
    );
  }, [businessDoc, employees]);

  const getCreateResourceId = useCallback(
    (resourceId = null) => {
      if (isSoloBookingBusiness(businessDoc || {}, employees)) return null;
      if (resourceId) {
        const cleanId = String(resourceId).trim();
        if (cleanId === "__loop_company_agenda__") return null;
        return cleanId;
      }

      const visible =
        Array.isArray(nativeVisibleEmployees) && nativeVisibleEmployees.length
          ? nativeVisibleEmployees
          : Array.isArray(employees)
            ? employees
            : [];

      if (visible.length === 1 && visible[0]?.id) return String(visible[0].id);
      if (Array.isArray(employees) && employees.length === 1 && employees[0]?.id) return String(employees[0].id);
      if (detectedEmployeeDocId) return String(detectedEmployeeDocId);

      if (isNoWorkerBusiness) return null;

      return null;
    },
    [nativeVisibleEmployees, employees, detectedEmployeeDocId, isNoWorkerBusiness, businessDoc]
  );

  // LOOP AGENDA MADRE CLEANUP D3 V74
  // Removed unused FullCalendar resourceLabelContent callback; worker headers are rendered by LoopResourceDayGrid.
  const { nativeShouldUseResourceColumns, calendarView, calendarResources, calendarEvents } =
    useAdminCalendarViewConfig({
      isNativeRuntime,
      viewMode,
      businessDoc,
      isWorkerView,
      employees,
      selectedEmployeeIds,
      allEvents: visibleAllEvents,
      eventsForDay: visibleEventsForDay,
      selectedSet,
    });

  // Event/no-worker businesses should use a normal weekly calendar by days.
  // If employees exist, worker/resource columns win even if legacy noWorkers is true.
  const webHasEmployees = Array.isArray(serviceWorkerEmployees) && serviceWorkerEmployees.length > 0;
  const calendarShouldUseCompanyNoWorkers = Boolean(
    isSoloBookingBusiness(businessDoc || {}, employees)
  );

  const shouldUseMonthView = viewMode === "month";

  const shouldUseWeekByDays = Boolean(
    viewMode === "weekCompany" &&
    isEventBusinessAdmin
  );

  const shouldUsePersonalWorkerWeekByDays = Boolean(
    isPersonalWorkerAgenda &&
    viewMode === "weekCompany"
  );

  // LOOP PHASE 4A — one visual agenda surface for owner + worker on mobile.
  // Owner already uses the custom resource grid. Workers now build a single safe
  // resource from their own identity so their mobile day view uses the same grid
  // instead of falling back to the older single-day layer.
  const fallbackResourceEmployees = hardWorkerView
    ? (Array.isArray(nativeVisibleEmployees) ? nativeVisibleEmployees : [])
    : (!isWorkerView && webHasEmployees ? employees : []);

  const fallbackResourcesFromEmployees = Array.isArray(fallbackResourceEmployees) && fallbackResourceEmployees.length
    ? fallbackResourceEmployees.map((e) => {
        const cleanEmail = String(e?.email || e?.employeeEmail || e?.workerEmail || hardWorkerEmailMode || workerEmailMode || "").trim().toLowerCase();
        const fallbackName = cleanEmail ? cleanEmail.split("@")[0] : "Mi agenda";
        const display = String(
          e.name ||
          e.displayName ||
          e.employeeName ||
          e.workerName ||
          e.fullName ||
          e.workerDisplayName ||
          e.employeeDisplayName ||
          fallbackName
        ).trim() || fallbackName;
        return {
          ...e,
          id: String(e.id || e.employeeId),
          employeeId: String(e.id || e.employeeId),
          title: display,
          name: display,
          displayName: display,
          role: e.role || e.position || e.specialty || e.title || "Trabajador",
          photoUrl: adminWorkerPhotoUrl(e) || null,
          photo: adminWorkerPhotoUrl(e) || null,
          image: adminWorkerPhotoUrl(e) || null,
          avatarUrl: adminWorkerPhotoUrl(e) || null,
          availability: e.availability || e.employeeAvailability || e.workerAvailability || e.schedule || e.workingHours || e.hours || null,
          schedule: e.schedule || null,
          workingHours: e.workingHours || null,
          hours: e.hours || null,
          blockedDates: e.blockedDates || [],
          blockedRanges: e.blockedRanges || [],
        };
      }).filter((r) => r.id && r.id !== "undefined")
    : [];


  // LOOP PHASE 1 HOTFIX V12 — owner/admin visibility for worker-created email-only bookings.
  // Worker fallback creates reservations with employeeEmail when the employee doc is not readable.
  // Resource calendars need a resource id to render those bookings, so create a lightweight email resource.
  const emailOnlyResourcesFromEvents = useMemo(() => {
    const map = new Map();
    (Array.isArray(visibleAllEvents) ? visibleAllEvents : []).forEach((ev) => {
      const rid = String(ev?.resourceId || "").trim();
      if (!rid.startsWith("email:")) return;
      const raw = ev?.extendedProps?.raw || ev?.raw || {};
      const email = rid.slice(6);
      const title = raw.employeeName || raw.workerName || raw.assignedEmployeeName || email || "Trabajador";
      if (!map.has(rid)) {
        map.set(rid, {
          id: rid,
          title,
          name: title,
          displayName: title,
          role: "Trabajador",
          photoUrl: null,
          photo: null,
          image: null,
          avatarUrl: null,
          __emailFallback: true,
        });
      }
    });
    return Array.from(map.values());
  }, [visibleAllEvents]);

  const fallbackResourcesWithEmail = useMemo(() => {
    const base = Array.isArray(fallbackResourcesFromEmployees) ? fallbackResourcesFromEmployees : [];
    const ids = new Set(base.map((r) => String(r.id)));
    const extras = (Array.isArray(emailOnlyResourcesFromEvents) ? emailOnlyResourcesFromEvents : [])
      .filter((r) => r?.id && !ids.has(String(r.id)));
    return base.concat(extras);
  }, [fallbackResourcesFromEmployees, emailOnlyResourcesFromEvents]);


  // LOOP AGENDA MADRE PHASE A V68
  // A business with no bookable workers still renders the same resource grid,
  // using one neutral column named "Agenda". This keeps the visual contract
  // identical for owner, worker, desktop, tablet and native.
  const canUseSoloCompanyAgenda = Boolean(
    ["day", "weekCompany", "month"].includes(String(viewMode || "day")) &&
    businessDoc &&
    !hardWorkerView &&
    !isWorkerView &&
    !webHasEmployees
  );

  const soloCompanyAgendaResource = useMemo(() => {
    if (!canUseSoloCompanyAgenda) return [];
    const businessLabel = String(
      businessDoc?.name ||
      businessDoc?.businessName ||
      businessDoc?.publicName ||
      ""
    ).trim();
    const title = "Agenda";
    return [{
      id: "__loop_company_agenda__",
      resourceId: "__loop_company_agenda__",
      employeeId: "__loop_company_agenda__",
      title,
      name: title,
      displayName: title,
      role: businessLabel || "Agenda del negocio",
      photoUrl: adminSidebarBusinessLogo(businessDoc) || null,
      photo: adminSidebarBusinessLogo(businessDoc) || null,
      image: adminSidebarBusinessLogo(businessDoc) || null,
      avatarUrl: adminSidebarBusinessLogo(businessDoc) || null,
      availability: businessDoc?.availability || businessDoc?.openingHours || businessDoc?.schedule || null,
      schedule: businessDoc?.schedule || businessDoc?.openingHours || null,
      workingHours: businessDoc?.workingHours || businessDoc?.openingHours || null,
      hours: businessDoc?.hours || businessDoc?.openingHours || null,
      __soloCompanyAgenda: true,
    }];
  }, [canUseSoloCompanyAgenda, businessDoc]);

  // LOOP AGENDA MADRE PHASE A V68
  // A worker must also have a resource column even when the employee doc is
  // still hydrating or was resolved only by email/route. Without this virtual
  // resource, desktop worker Day can fall back to the older flat day agenda.
  const personalWorkerAgendaResource = useMemo(() => {
    if (!(hardWorkerView || isWorkerView || isPersonalWorkerAgenda) || !["day", "weekCompany", "month"].includes(String(viewMode || "day"))) return [];
    const routeProfile = (routeWorkerProfile && typeof routeWorkerProfile === "object") ? routeWorkerProfile : {};
    const currentProfile = (currentWorkerEmployee && typeof currentWorkerEmployee === "object") ? currentWorkerEmployee : {};
    const source = adminMergeWorkerResourceRich(routeProfile, currentProfile, hardDetectedEmployeeDocId || detectedEmployeeDocId || "");
    const email = String(
      source.email ||
      source.workerEmail ||
      source.employeeEmail ||
      hardWorkerEmailMode ||
      workerEmailMode ||
      ""
    ).trim().toLowerCase();
    const id = String(
      source.id ||
      source.employeeId ||
      source.workerId ||
      hardDetectedEmployeeDocId ||
      detectedEmployeeDocId ||
      (email ? `email:${email}` : "__loop_worker_agenda__")
    ).trim();
    const name = String(
      source.name ||
      source.displayName ||
      source.employeeName ||
      source.workerName ||
      source.workerDisplayName ||
      source.employeeDisplayName ||
      source.fullName ||
      (email ? email.split("@")[0] : "Mi agenda")
    ).trim() || "Mi agenda";
    return [adminMergeWorkerResourceRich(
      {
        ...(source || {}),
        id,
        resourceId: id,
        employeeId: id,
        title: name,
        name,
        displayName: name,
        role: source.role || source.position || source.specialty || source.title || "Trabajador",
        __personalWorkerAgenda: true,
      },
      source || {},
      id
    )].filter((r) => r.id && r.id !== "undefined");
  }, [hardWorkerView, isWorkerView, isPersonalWorkerAgenda, viewMode, currentWorkerEmployee, routeWorkerProfile, hardWorkerEmailMode, workerEmailMode, hardDetectedEmployeeDocId, detectedEmployeeDocId]);

  // PHASE 2B — Native worker-column parity with mobile web
  // In Capacitor, worker businesses should render the same worker/resource day agenda
  // used by mobile web. This avoids the old native week/day hybrid where worker
  // headers disappeared and bookings floated without clear columns.
  const nativeHasVisibleWorkers = Boolean(
    isNativeRuntime &&
    webHasEmployees &&
    !calendarShouldUseCompanyNoWorkers &&
    !isEventBusinessAdmin &&
    !isWorkerView
  );

  const nativeWorkerResources = nativeHasVisibleWorkers
    ? (Array.isArray(calendarResources) && calendarResources.length > 0
        ? calendarResources
        : (Array.isArray(nativeVisibleEmployees) && nativeVisibleEmployees.length > 0
            ? nativeVisibleEmployees.map((e) => ({
                ...e,
                id: String(e.id || e.employeeId),
                title: e.name || e.displayName || "Sin nombre",
                name: e.name || e.displayName || "Sin nombre",
                displayName: e.displayName || e.name || "Sin nombre",
                role: e.role || e.position || e.specialty || e.title || "",
                photoUrl: adminWorkerPhotoUrl(e) || null,
                photo: adminWorkerPhotoUrl(e) || null,
                image: adminWorkerPhotoUrl(e) || null,
                avatarUrl: adminWorkerPhotoUrl(e) || null,
                availability: e.availability || e.employeeAvailability || e.workerAvailability || e.schedule || e.workingHours || e.hours || null,
                schedule: e.schedule || null,
                workingHours: e.workingHours || null,
                hours: e.hours || null,
                blockedDates: e.blockedDates || [],
                blockedRanges: e.blockedRanges || [],
              })).filter((r) => r.id && r.id !== "undefined")
            : fallbackResourcesWithEmail))
    : [];

  const canUsePersonalWorkerResourceDay = Boolean(
    (isPersonalWorkerAgenda || hardWorkerView || isWorkerView) &&
    viewMode === "day" &&
    !calendarShouldUseCompanyNoWorkers &&
    !isEventBusinessAdmin &&
    (
      (Array.isArray(fallbackResourcesWithEmail) && fallbackResourcesWithEmail.length > 0) ||
      (Array.isArray(personalWorkerAgendaResource) && personalWorkerAgendaResource.length > 0)
    )
  );

  // LOOP AGENDA MADRE PHASE A V68
  // Day view must always use the mother resource grid. For businesses without
  // workers, the resource is virtual and openCreateModalForDate strips it back
  // to null before saving.
  const canUseSoloCompanyResourceDay = Boolean(
    viewMode === "day" &&
    canUseSoloCompanyAgenda &&
    Array.isArray(soloCompanyAgendaResource) &&
    soloCompanyAgendaResource.length > 0
  );

  const shouldForceMotherDayResourceGrid = Boolean(
    viewMode === "day" &&
    !shouldUseMonthView &&
    (
      nativeHasVisibleWorkers ||
      canUsePersonalWorkerResourceDay ||
      canUseSoloCompanyResourceDay ||
      (webHasEmployees && !calendarShouldUseCompanyNoWorkers && !isEventBusinessAdmin)
    )
  );

  // LOOP AGENDA MADRE CLEANUP D3 V74
  // Week is no longer represented as FullCalendar timeGridWeek anywhere in the
  // agenda contract. It is a custom mother renderer just like Month.
  const effectiveCalendarView = shouldForceMotherDayResourceGrid
    ? "resourceTimeGridDay"
    : shouldUseMonthView
      ? "loopMonth"
      : (shouldUseWeekByDays || shouldUsePersonalWorkerWeekByDays)
        ? "loopWeek"
        : calendarView;

  const hasTeamCalendarScope = Boolean((hardWorkerView || isWorkerView) && (workerPermissions.canViewAllCalendars || workerPermissions.canViewSelectedCalendars));
  const rawEffectiveCalendarResources = hasTeamCalendarScope
    ? (employees || []).filter(employee => !selectedEmployeeIds?.length || selectedEmployeeIds.includes(employee.id)).map(employee => ({...employee, title: employee.name || 'Trabajador'}))
    : nativeHasVisibleWorkers
    ? nativeWorkerResources
    : shouldUseMonthView || shouldUseWeekByDays || shouldUsePersonalWorkerWeekByDays
      ? []
      : (canUseSoloCompanyResourceDay
          ? soloCompanyAgendaResource
          : (canUsePersonalWorkerResourceDay
              ? (() => {
                  const personal = Array.isArray(personalWorkerAgendaResource) && personalWorkerAgendaResource.length > 0 ? personalWorkerAgendaResource.slice(0, 1) : [];
                  const fallback = Array.isArray(fallbackResourcesWithEmail) && fallbackResourcesWithEmail.length > 0 ? fallbackResourcesWithEmail.slice(0, 1) : [];
                  const scoreResource = (r = {}) => {
                    const name = String(r.name || r.displayName || r.title || r.employeeName || r.workerName || "").trim();
                    const email = String(r.email || r.employeeEmail || r.workerEmail || "").trim().toLowerCase();
                    const isGeneric = !name || name === "Sin nombre" || name === "Mi agenda" || (email && name.toLowerCase() === email.split("@")[0]);
                    return (r.id && !String(r.id).startsWith("email:") ? 20 : 0) +
                      (!isGeneric ? 35 : 0) +
                      (adminWorkerPhotoUrl(r) ? 25 : 0) +
                      (r.availability || r.schedule || r.workingHours || r.hours ? 30 : 0);
                  };
                  if (personal.length && (!fallback.length || scoreResource(personal[0]) >= scoreResource(fallback[0]))) return personal;
                  return fallback.length ? fallback : personal;
                })()
              : (Array.isArray(calendarResources) && calendarResources.length > 0 ? calendarResources : fallbackResourcesWithEmail)));


  const employeeByIdForAvailability = useMemo(() => {
    const map = new Map();
    const lists = [employees, nativeVisibleEmployees, fallbackResourceEmployees, currentWorkerEmployee ? [currentWorkerEmployee] : [], routeWorkerProfile ? [routeWorkerProfile] : []];
    const addKey = (key, emp) => {
      const safe = String(key || "").trim();
      if (!safe || safe === "undefined" || safe === "null" || !emp) return;
      const current = map.get(safe);
      if (!current || adminWorkerResourceHydrationScore(emp) > adminWorkerResourceHydrationScore(current)) {
        map.set(safe, emp);
      }
    };
    lists.forEach((list) => {
      (Array.isArray(list) ? list : []).forEach((emp) => {
        if (!emp) return;
        [
          emp.id,
          emp.employeeId,
          emp.workerId,
          emp.resourceId,
          emp.uid,
          emp.userUid,
          emp.userId,
          emp.authUid,
          emp.linkedUid,
          emp.linkedUserId,
          emp.employeeUserUid,
          emp.employeeUid,
          emp.workerUid,
          emp.workerUserUid,
        ].forEach((key) => addKey(key, emp));

        [
          emp.email,
          emp.employeeEmail,
          emp.workerEmail,
          emp.inviteEmail,
          emp.contactEmail,
          emp.userEmail,
          ...(Array.isArray(emp.accessEmails) ? emp.accessEmails : []),
          ...(Array.isArray(emp.allowedEmails) ? emp.allowedEmails : []),
        ].forEach((email) => {
          const clean = String(email || "").trim().toLowerCase();
          if (clean) addKey(`email:${clean}`, emp);
        });
      });
    });
    return map;
  }, [employees, nativeVisibleEmployees, fallbackResourceEmployees, currentWorkerEmployee, routeWorkerProfile]);

  const effectiveCalendarResources = useMemo(() => {
    const out = (Array.isArray(rawEffectiveCalendarResources) ? rawEffectiveCalendarResources : []).map((resource) => {
      const rid = String(resource?.id || resource?.employeeId || resource?.resourceId || "").trim();
      const employeeSource = employeeByIdForAvailability.get(rid) || null;
      const merged = employeeSource ? adminMergeWorkerResourceRich(resource, employeeSource, rid) : adminMergeWorkerResourceRich(resource, {}, rid);
      return merged;
    });
    try {
      if (typeof window !== "undefined") {
        window.__LOOP_WORKER_RESOURCE_HYDRATION_DEBUG__ = {
          version: "LOOP AGENDA HYDRATION STABILITY V16B",
          resourceCount: out.length,
          resources: out.map((r) => ({
            id: r.id || r.employeeId || null,
            name: r.name || r.displayName || r.title || null,
            hasPhoto: Boolean(adminWorkerPhotoUrl(r)),
            hasAvailability: Boolean(r.availability || r.schedule || r.workingHours || r.hours),
            score: adminWorkerResourceHydrationScore(r),
          })),
          at: new Date().toISOString(),
        };
      }
    } catch (_) {}
    return out;
  }, [rawEffectiveCalendarResources, employeeByIdForAvailability]);

  // LOOP AGENDA MADRE PHASE B V69
  // Week uses its own resource identity strip because the week renderer is by days,
  // while Day is by resource columns. Data/perms stay shared; visual identity stays shared.
  const weekCalendarResources = useMemo(() => {
    if (viewMode !== "weekCompany") return [];
    if (canUseSoloCompanyAgenda && Array.isArray(soloCompanyAgendaResource) && soloCompanyAgendaResource.length) {
      return soloCompanyAgendaResource;
    }
    if (hardWorkerView || isWorkerView || isPersonalWorkerAgenda) {
      // LOOP WORKER WEEK AVAILABILITY STRIPES V98
      // Semana also needs the availability-rich personal profile, not the first
      // email/access fallback. Pick the same best worker resource contract used by Día.
      const personal = Array.isArray(personalWorkerAgendaResource) && personalWorkerAgendaResource.length > 0 ? personalWorkerAgendaResource.slice(0, 1) : [];
      const fallback = Array.isArray(fallbackResourcesWithEmail) && fallbackResourcesWithEmail.length > 0 ? fallbackResourcesWithEmail.slice(0, 1) : [];
      const scoreResource = (r = {}) => {
        const name = String(r.name || r.displayName || r.title || r.employeeName || r.workerName || "").trim();
        const email = String(r.email || r.employeeEmail || r.workerEmail || "").trim().toLowerCase();
        const isGeneric = !name || name === "Sin nombre" || name === "Mi agenda" || (email && name.toLowerCase() === email.split("@")[0]);
        return (r.id && !String(r.id).startsWith("email:") ? 20 : 0) +
          (!isGeneric ? 35 : 0) +
          (adminWorkerPhotoUrl(r) ? 25 : 0) +
          (r.availability || r.schedule || r.workingHours || r.hours ? 30 : 0);
      };
      if (personal.length && (!fallback.length || scoreResource(personal[0]) >= scoreResource(fallback[0]))) return personal;
      return fallback.length ? fallback : personal;
    }
    if (Array.isArray(fallbackResourcesWithEmail) && fallbackResourcesWithEmail.length) return fallbackResourcesWithEmail;
    if (Array.isArray(nativeVisibleEmployees) && nativeVisibleEmployees.length) {
      return nativeVisibleEmployees.map((e) => ({
        ...e,
        id: String(e.id || e.employeeId),
        title: e.name || e.displayName || "Sin nombre",
        name: e.name || e.displayName || "Sin nombre",
        displayName: e.displayName || e.name || "Sin nombre",
        role: e.role || e.position || e.specialty || e.title || "",
        photoUrl: adminWorkerPhotoUrl(e) || null,
        photo: adminWorkerPhotoUrl(e) || null,
        image: adminWorkerPhotoUrl(e) || null,
        avatarUrl: adminWorkerPhotoUrl(e) || null,
      })).filter((r) => r.id && r.id !== "undefined");
    }
    return [];
  }, [viewMode, canUseSoloCompanyAgenda, soloCompanyAgendaResource, hardWorkerView, isWorkerView, isPersonalWorkerAgenda, fallbackResourcesWithEmail, personalWorkerAgendaResource, nativeVisibleEmployees]);

  const effectiveCalendarResourceCount = Array.isArray(effectiveCalendarResources)
    ? effectiveCalendarResources.length
    : 0;

  // LOOP AGENDA V45 — SINGLE MOBILE AGENDA RENDERER CONTRACT.
  // One mobile/tablet/app rule now decides the day renderer. This prevents the
  // native app from using the worker/resource agenda while web mobile/tablet
  // falls back to the older unified day surface.
  //
  // Contract after Agenda Madre:
  // - Day => LoopResourceDayGrid for worker, owner/admin and no-worker companies.
  // - Semana => LoopUnifiedWeekView.
  // - Mes => LoopUnifiedMonthView.
  // Only data scope and layout density change by role/device.
  const isMobileAgendaSurface = Boolean(isNativeRuntime || viewportWidth < 981);

  // LOOP AGENDA MADRE CLEANUP D3 V74
  // Removed debug-only mobile collapse flags. Day no longer has a legacy single-day surface to choose from.

  const agendaUsesResourceDayGrid = Boolean(
    viewMode === "day" &&
    effectiveCalendarResourceCount > 0
  );
  // LOOP AGENDA MADRE CLEANUP D3 V74
  // Removed empty phantom-FullCalendar sync effect.

  // ✅ Background shading events outside working hours (resource day view only)
  const availabilityResourceEmployees = useMemo(() => {
    if (hardWorkerView || isWorkerView || isPersonalWorkerAgenda) {
      return Array.isArray(effectiveCalendarResources) && effectiveCalendarResources.length
        ? effectiveCalendarResources
        : (Array.isArray(employees) ? employees : []);
    }
    return Array.isArray(employees) ? employees : [];
  }, [hardWorkerView, isWorkerView, isPersonalWorkerAgenda, effectiveCalendarResources, employees]);

  const outsideHoursBackgroundEvents = useMemo(() => {
    const isResourceDay =
      viewMode === "day" &&
      !calendarShouldUseCompanyNoWorkers &&
      effectiveCalendarView === "resourceTimeGridDay";

    if (!isResourceDay) return [];

    const day = startOfDay(selectedDate);
    const list = [];

    for (const emp of availabilityResourceEmployees || []) {
      const rid = String(emp.id || emp.employeeId || emp.resourceId || "");
      if (!rid) continue;
      const ranges = rangesForEmployeeOnDateWithFallback(emp, day, businessDoc);

      const base = {
        resourceId: rid,
        display: "background",
        title: "",
        editable: false,
        interactive: false,
        overlap: true,
        extendedProps: { kind: "outside-hours" },
      };

      if (ranges === null) {
        // No schedule configured: keep the calendar visually neutral instead of marking everything outside hours.
        continue;
      }

      if (ranges.length === 0) {
        list.push({
          ...base,
          id: `bg_${rid}_${day.toISOString()}_allday`,
          start: dateAtMinutes(day, 0).toISOString(),
          end: dateAtMinutes(day, 24 * 60).toISOString(),
        });
        continue;
      }

      const firstStart = ranges[0].fromMin;
      if (firstStart > 0) {
        list.push({
          ...base,
          id: `bg_${rid}_${day.toISOString()}_pre`,
          start: dateAtMinutes(day, 0).toISOString(),
          end: dateAtMinutes(day, firstStart).toISOString(),
        });
      }

      for (let i = 0; i < ranges.length - 1; i++) {
        const cur = ranges[i];
        const next = ranges[i + 1];
        if (next.fromMin > cur.toMin) {
          list.push({
            ...base,
            id: `bg_${rid}_${day.toISOString()}_gap_${i}`,
            start: dateAtMinutes(day, cur.toMin).toISOString(),
            end: dateAtMinutes(day, next.fromMin).toISOString(),
          });
        }
      }

      const lastEnd = ranges[ranges.length - 1].toMin;
      if (lastEnd < 24 * 60) {
        list.push({
          ...base,
          id: `bg_${rid}_${day.toISOString()}_post`,
          start: dateAtMinutes(day, lastEnd).toISOString(),
          end: dateAtMinutes(day, 24 * 60).toISOString(),
        });
      }
    }

    return list;
  }, [availabilityResourceEmployees, selectedDate, viewMode, businessDoc, isWorkerView, effectiveCalendarView, calendarShouldUseCompanyNoWorkers]);

  const workerVisibleCalendarEvents = useMemo(() => {
    return filterEventsForOperationalWorker(
      calendarEvents || [],
      hardWorkerView,
      hardDetectedEmployeeDocId,
      currentWorkerEmployee,
      hardWorkerEmailMode,
      hardSelectedEmployeeIds
    );
  }, [calendarEvents, hardWorkerView, hardDetectedEmployeeDocId, currentWorkerEmployee, hardWorkerEmailMode, hardSelectedEmployeeIds]);

  const bookingDocCalendarEvents = useMemo(() => {
    return (Array.isArray(visibleBookings) ? visibleBookings : [])
      .map((raw, index) => buildMonthCalendarEventFromBookingDoc(raw?.id || raw?.bookingId || raw?.docId || `booking-${index}`, raw))
      .filter(Boolean);
  }, [visibleBookings]);

  // LOOP AGENDA DAY MONTH WORKER FIX HOTFIX
  // Some worker calendars receive events only through allEvents/monthRangeEvents, while
  // the old FullCalendar worker day path used calendarEvents and could render empty.
  // Build one unified event pool and then apply the worker visibility filter once.
  const workerUnifiedEventPool = useMemo(() => {
    const pool = mergeCalendarEventsUnique(
      workerVisibleCalendarEvents || [],
      visibleEventsForDay || [],
      calendarEvents || [],
      visibleAllEvents || [],
      visibleMonthRangeEvents || [],
      bookingDocCalendarEvents || []
    );
    if (!hardWorkerView) return pool;
    // LOOP WORKER OWN-ONLY V6: never fall back to the full company pool.
    return filterEventsForOperationalWorker(pool, true, hardDetectedEmployeeDocId, currentWorkerEmployee, hardWorkerEmailMode, hardSelectedEmployeeIds);
  }, [workerVisibleCalendarEvents, visibleEventsForDay, calendarEvents, visibleAllEvents, visibleMonthRangeEvents, bookingDocCalendarEvents, hardWorkerView, hardDetectedEmployeeDocId, currentWorkerEmployee, hardWorkerEmailMode, hardSelectedEmployeeIds]);

  const mergedEvents = useMemo(() => {
    const basePool = mergeCalendarEventsUnique(
      workerUnifiedEventPool || [],
      shouldUseMonthView ? (visibleMonthRangeEvents || []) : [],
      shouldUseMonthView ? (visibleAllEvents || []) : []
    );
    const baseEvents = shouldUseMonthView || hardWorkerView
      ? filterEventsForOperationalWorker(
          basePool,
          hardWorkerView,
          hardDetectedEmployeeDocId,
          currentWorkerEmployee,
          hardWorkerEmailMode,
          hardSelectedEmployeeIds
        )
      : (workerUnifiedEventPool || []);
    const normalizedBaseEvents = canUseSoloCompanyResourceDay
      ? (baseEvents || []).map(projectSoloCalendarEvent)
      : baseEvents;
    return [...outsideHoursBackgroundEvents, ...normalizedBaseEvents];
  }, [outsideHoursBackgroundEvents, workerUnifiedEventPool, shouldUseMonthView, visibleAllEvents, visibleMonthRangeEvents, hardWorkerView, hardDetectedEmployeeDocId, currentWorkerEmployee, hardWorkerEmailMode, hardSelectedEmployeeIds, canUseSoloCompanyResourceDay]);

  // LOOP AGENDA — SINGLE CUSTOM RENDER ROUTER HARD FIX
  // All agenda modes use this explicit event source.
  // Worker agendas use the worker pool; company agendas use merged events.
  const unifiedAgendaEvents = hardWorkerView ? (workerUnifiedEventPool || []) : (mergedEvents || []);

  // LOOP AGENDA MADRE PHASE C V71
  // Month uses the same event pool as Day/Week, but applies the visible worker
  // selection before rendering because the month grid has no resource columns.
  const monthAgendaEvents = useMemo(() => {
    const base = Array.isArray(unifiedAgendaEvents) ? unifiedAgendaEvents : [];
    if (viewMode !== "month") return base;
    const selectedIds = new Set((Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds : []).map(String));
    const isScopedWorker = Boolean(hardWorkerView || isWorkerView || isPersonalWorkerAgenda);
    if (!isScopedWorker && selectedIds.size === 0) return base;

    return base.filter((ev) => {
      const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || {};
      if (isScopedWorker) {
        return filterEventsForOperationalWorker(
          [ev],
          true,
          hardDetectedEmployeeDocId,
          currentWorkerEmployee,
          hardWorkerEmailMode,
          hardSelectedEmployeeIds
        ).length > 0;
      }

      const ids = [
        ev?.resourceId,
        ev?.extendedProps?.resourceId,
        raw?.employeeId,
        raw?.workerId,
        raw?.resourceId,
        raw?.assignedEmployeeId,
        ...(Array.isArray(raw?.assignedEmployeeIds) ? raw.assignedEmployeeIds : []),
      ].filter(Boolean).map(String);

      return ids.some((id) => selectedIds.has(id));
    });
  }, [unifiedAgendaEvents, viewMode, selectedEmployeeIds, hardWorkerView, isWorkerView, isPersonalWorkerAgenda, hardDetectedEmployeeDocId, currentWorkerEmployee, hardWorkerEmailMode, hardSelectedEmployeeIds]);

  const formatAgendaSideTime = useCallback((value) => {
    try {
      return formatCalendarTime(value, effectiveAgendaSettings.timeFormat);
    } catch (_) {
      return "";
    }
  }, [effectiveAgendaSettings.timeFormat]);

  // LOOP WORKER OWN-ONLY V7: the right-rail / day summary must use the same
  // filtered event source as the visible agenda. Previously this used allEvents,
  // so worker calendars rendered correctly but the summary still counted company bookings.
  const sidePanelVisibleEvents = useMemo(() => {
    return hardWorkerView ? (unifiedAgendaEvents || []) : (visibleAllEvents || []);
  }, [hardWorkerView, unifiedAgendaEvents, visibleAllEvents]);

  const webUpcomingEvents = useMemo(() => {
    const now = new Date();
    return (sidePanelVisibleEvents || [])
      .filter((ev) => {
        const d = ev?.start ? new Date(ev.start) : null;
        return d && !Number.isNaN(d.getTime()) && d >= now;
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start))
      .slice(0, 5);
  }, [sidePanelVisibleEvents]);

  const webDayEvents = useMemo(() => {
    return (sidePanelVisibleEvents || []).filter((ev) => {
      const d = ev?.start ? new Date(ev.start) : null;
      return d && !Number.isNaN(d.getTime()) && sameDay(d, selectedDate);
    });
  }, [sidePanelVisibleEvents, selectedDate]);

  useEffect(() => {
    try {
      window.__LOOP_AGENDA_WORKER_DEBUG__ = {
        version: "worker-settings-availability-v83",
        path: window.location.pathname,
        search: window.location.search,
        isWorkerView,
        hardWorkerView,
        routeWorkerIdentity,
        detectedEmployeeDocId,
        hardDetectedEmployeeDocId,
        workerEmailMode,
        hardWorkerEmailMode,
        selectedEmployeeIds,
        hardSelectedEmployeeIds,
        currentWorkerEmployeeId: currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || null,
        currentWorkerEmployeeEmail: currentWorkerEmployee?.email || currentWorkerEmployee?.employeeEmail || currentWorkerEmployee?.workerEmail || null,
        employeesCount: Array.isArray(employees) ? employees.length : 0,
        nativeVisibleEmployeesCount: Array.isArray(nativeVisibleEmployees) ? nativeVisibleEmployees.length : 0,
        allEventsCount: Array.isArray(allEvents) ? allEvents.length : 0,
        eventsForDayCount: Array.isArray(eventsForDay) ? eventsForDay.length : 0,
        calendarEventsCount: Array.isArray(calendarEvents) ? calendarEvents.length : 0,
        monthRangeEventsCount: Array.isArray(monthRangeEvents) ? monthRangeEvents.length : 0,
        bookingDocCalendarEventsCount: Array.isArray(bookingDocCalendarEvents) ? bookingDocCalendarEvents.length : 0,
        workerPoolCount: Array.isArray(workerUnifiedEventPool) ? workerUnifiedEventPool.length : 0,
        unifiedCount: Array.isArray(unifiedAgendaEvents) ? unifiedAgendaEvents.length : 0,
        monthAgendaCount: Array.isArray(monthAgendaEvents) ? monthAgendaEvents.length : 0,
        sidePanelVisibleCount: Array.isArray(sidePanelVisibleEvents) ? sidePanelVisibleEvents.length : 0,
        webDayCount: Array.isArray(webDayEvents) ? webDayEvents.length : 0,
        businessCalendarSettings: businessDoc?.calendarSettings || null,
        effectiveAgendaSettings,
        effectiveResourceCount: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.length : 0,
        effectiveResourcesAvailability: (Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources : []).slice(0, 8).map((r) => ({
          id: r?.id || r?.employeeId || null,
          name: r?.name || r?.displayName || r?.title || null,
          hasAvailability: Boolean(getEmployeeAvailabilityForCalendar(r)),
          usesBusinessFallback: !Boolean(getEmployeeAvailabilityForCalendar(r)),
          blockedDatesCount: Array.isArray(r?.blockedDates) ? r.blockedDates.length : 0,
          blockedRangesCount: Array.isArray(r?.blockedRanges) ? r.blockedRanges.length : 0,
        })),
        sampleUnified: (Array.isArray(unifiedAgendaEvents) ? unifiedAgendaEvents : []).slice(0, 8).map((ev) => {
          const raw = ev?.extendedProps?.raw || ev?.raw || ev || {};
          return {
            id: ev?.id || raw?.id || raw?.bookingId || null,
            title: ev?.title || raw?.serviceName || raw?.customerName || null,
            resourceId: ev?.resourceId || raw?.resourceId || null,
            employeeId: raw?.employeeId || null,
            workerId: raw?.workerId || null,
            employeeEmail: raw?.employeeEmail || null,
            workerEmail: raw?.workerEmail || null,
            employeeName: raw?.employeeName || raw?.workerName || null,
          };
        }),
      };
    } catch (_) {}
  }, [isWorkerView, hardWorkerView, routeWorkerIdentity, detectedEmployeeDocId, hardDetectedEmployeeDocId, workerEmailMode, hardWorkerEmailMode, selectedEmployeeIds, hardSelectedEmployeeIds, currentWorkerEmployee, employees, nativeVisibleEmployees, allEvents, eventsForDay, calendarEvents, monthRangeEvents, bookingDocCalendarEvents, workerUnifiedEventPool, unifiedAgendaEvents, monthAgendaEvents, sidePanelVisibleEvents, webDayEvents, businessDoc, effectiveAgendaSettings, effectiveCalendarResources]);

  const webMostBookedServices = useMemo(() => {
    const counts = new Map();
    webDayEvents.forEach((ev) => {
      const raw = getBookingRawFromCalendarEvent(ev);
      const label = getBookingServiceName(raw, ev?.title || "Servicio");
      if (!label) return;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [webDayEvents]);

  const webServiceLegend = useMemo(() => {
    const seen = new Set();
    const fromEvents = (allEvents || [])
      .map((ev) => {
        const raw = getBookingRawFromCalendarEvent(ev);
        return getBookingServiceName(raw, ev?.title || "Servicio");
      })
      .filter(Boolean);

    const fromServices = (services || [])
      .map((service) => service?.name || service?.title || service?.serviceName || service?.label)
      .filter(Boolean);

    return [...fromEvents, ...fromServices]
      .map((label) => String(label || "").trim())
      .filter((label) => {
        const key = label.toLowerCase();
        const compact = key.replace(/\s+/g, "");
        const isNoise =
          !key ||
          key === "servicio" ||
          key === "reserva" ||
          key === "package" ||
          compact.length < 3 ||
          /^(.)\1{2,}$/.test(compact) ||
          /^[a-z]$/.test(compact);

        if (isNoise || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 6)
      .map((label) => ({
        label,
        tone: getServiceToneIndex(label),
      }));
  }, [allEvents, services]);

  // LOOP AGENDA MADRE CLEANUP D1 V72
  // Removed legacy native list data builders. Native now uses the same
  // Day / Week / Month visual router as web and tablet.

  const eventClassNames = useCallback((arg) => {
    const kind = arg?.event?.extendedProps?.kind;
    if (kind === "outside-hours") return ["outside-hours-bg"];

    const raw = getBookingRawFromCalendarEvent(arg?.event);
    const status = normalizeBookingStatusLabel(raw?.status || raw?.confirmationStatus || arg?.event?.extendedProps?.status);
    const serviceName = getBookingServiceName(raw, arg?.event?.title || "Reserva");
    const safeColorMode = ["service", "worker", "status"].includes(effectiveAgendaSettings.colorMode) ? effectiveAgendaSettings.colorMode : "service";
    return [
      getBookingToneClass(raw, serviceName, safeColorMode, status),
      `status-${status.tone}`,
      `tone-by-${safeColorMode}`,
    ];
  }, [effectiveAgendaSettings.colorMode]);

  const renderEventContent = useCallback((arg) => {
    const kind = arg?.event?.extendedProps?.kind;
    const display = arg?.event?.display;
    if (kind === "outside-hours" || display === "background") return null;
    return <BookingEventCard arg={arg} timeFormat={effectiveAgendaSettings.timeFormat} colorMode={effectiveAgendaSettings.colorMode} roleContext={agendaRoleContext} />;
  }, [effectiveAgendaSettings.timeFormat, effectiveAgendaSettings.colorMode]);

  const calendarKey = `${selectedDate.toISOString()}-${JSON.stringify(selectedEmployeeIds)}-${String(
    workerEmailMode || ""
  )}-${reloadBookingsKey}-${String(isWorkerView)}-${String(activeBusinessId || "")}-${viewMode}-${String(
    isNativeRuntime
  )}-${String(nativeShouldUseResourceColumns)}-${Array.isArray(employees) ? employees.length : 0}-${Array.isArray(allEvents) ? allEvents.length : 0}-${Array.isArray(workerVisibleCalendarEvents) ? workerVisibleCalendarEvents.length : 0}-${Array.isArray(monthRangeEvents) ? monthRangeEvents.length : 0}-${effectiveAgendaSettings.timeFormat}-${effectiveAgendaSettings.slotMinutes}-${effectiveAgendaSettings.rowDensity}-${effectiveAgendaSettings.minTime}-${effectiveAgendaSettings.maxTime}-${String(effectiveAgendaSettings.showWeekends)}-${effectiveAgendaSettings.weekStartsOn}`;

  const isCurrentUserOwnerOrAdmin = useCallback(() => {
    const actor = getEffectiveActor();
    if (!businessDoc || (!actor.uid && !actor.email)) return false;
    if (isLoopPlatformOwner(auth)) return true;
    const uid = actor.uid ? String(actor.uid) : null;
    const email = actor.email ? String(actor.email).toLowerCase() : "";
    if (businessDoc.ownerUid && uid && String(businessDoc.ownerUid) === uid) return true;
    if (businessDoc.ownerEmail && email && String(businessDoc.ownerEmail).toLowerCase() === email) return true;
    if (businessDoc.admins && uid && uid in businessDoc.admins) return true;
    if (businessDoc.managers && uid && uid in businessDoc.managers) return true;
    return false;
  }, [businessDoc]);

  const ownerOrAdmin = isCurrentUserOwnerOrAdmin();

  // LOOP PHASE 1 HOTFIX V4 — resolve worker permissions from the employee row visible by email/id.
  // Debug showed hardWorkerView=true and hardWorkerEmailMode set, but currentWorkerEmployee=null.
  // In that case we must resolve the employee from employees[] by email/id before checking permissions.
  const resolvedWorkerEmployeeForPermissions = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    if (currentWorkerEmployee && (currentWorkerEmployee.id || currentWorkerEmployee.employeeId || currentWorkerEmployee.email || currentWorkerEmployee.workerEmail || currentWorkerEmployee.employeeEmail)) {
      return currentWorkerEmployee;
    }

    const wantedId = String(hardDetectedEmployeeDocId || detectedEmployeeDocId || "").trim();
    if (wantedId) {
      const byId = list.find((emp) => String(emp?.id || emp?.employeeId || emp?.uid || "").trim() === wantedId);
      if (byId) return byId;
    }

    const wantedEmail = normalizeEmailForWorkerVisibility(hardWorkerEmailMode || workerEmailMode || "");
    if (wantedEmail) {
      const byEmail = list.find((emp) => {
        const scalarEmails = [
          emp?.email, emp?.emailLower, emp?.emailNormalized,
          emp?.workerEmail, emp?.workerEmailLower, emp?.workerEmailNormalized,
          emp?.employeeEmail, emp?.employeeEmailLower, emp?.employeeEmailNormalized,
          emp?.inviteEmail, emp?.inviteEmailLower, emp?.inviteEmailNormalized,
          emp?.contactEmail, emp?.contactEmailLower, emp?.contactEmailNormalized,
        ]
          .map((value) => normalizeEmailForWorkerVisibility(value || ""))
          .filter(Boolean);

        const arrayEmails = []
          .concat(Array.isArray(emp?.accessEmails) ? emp.accessEmails : [])
          .concat(Array.isArray(emp?.allowedEmails) ? emp.allowedEmails : [])
          .concat(Array.isArray(emp?.emails) ? emp.emails : [])
          .map((value) => normalizeEmailForWorkerVisibility(value || ""))
          .filter(Boolean);

        return scalarEmails.includes(wantedEmail) || arrayEmails.includes(wantedEmail);
      });
      if (byEmail) return byEmail;
    }

    if (hardWorkerView && Array.isArray(nativeVisibleEmployees) && nativeVisibleEmployees.length === 1) {
      return nativeVisibleEmployees[0];
    }

    return null;
  }, [employees, currentWorkerEmployee, hardDetectedEmployeeDocId, detectedEmployeeDocId, hardWorkerEmailMode, workerEmailMode, hardWorkerView, nativeVisibleEmployees]);

  const hasWorkerPermissionContext = Boolean(
    hardWorkerView ||
    resolvedWorkerEmployeeForPermissions ||
    currentWorkerEmployee ||
    hardDetectedEmployeeDocId ||
    hardWorkerEmailMode ||
    detectedEmployeeDocId ||
    workerEmailMode
  );
  const effectiveWorkerEmployeeId = String(
    resolvedWorkerEmployeeForPermissions?.id ||
    resolvedWorkerEmployeeForPermissions?.employeeId ||
    currentWorkerEmployee?.id ||
    currentWorkerEmployee?.employeeId ||
    hardDetectedEmployeeDocId ||
    detectedEmployeeDocId ||
    ""
  ).trim();

  const resolvedWorkerPermissions = useMemo(() => {
    if (resolvedWorkerEmployeeForPermissions) return normalizeWorkerPermissions(resolvedWorkerEmployeeForPermissions);
    return workerPermissions;
  }, [resolvedWorkerEmployeeForPermissions, workerPermissions]);

  // LOOP iOS WORKER CREATE PERMISSION FIX V1
  // Android usually has auth.currentUser ready here. On iOS/Capacitor the app
  // intentionally uses the manual/native auth session while Firebase Web Auth can
  // stay empty. Use getEffectiveActor() so worker self-create fallback behaves
  // the same on iOS and Android.
  const effectiveActorForWorkerCreate = getEffectiveActor();
  const authEmailForWorkerCreate = normalizeEmailForWorkerVisibility(
    effectiveActorForWorkerCreate?.email ||
    auth?.currentUser?.email ||
    ""
  );
  const authUidForWorkerCreate = effectiveActorForWorkerCreate?.uid || auth?.currentUser?.uid || null;

  const createWorkerEmailFallback = normalizeEmailForWorkerVisibility(
    hardWorkerEmailMode ||
    workerEmailMode ||
    resolvedWorkerEmployeeForPermissions?.email ||
    resolvedWorkerEmployeeForPermissions?.workerEmail ||
    resolvedWorkerEmployeeForPermissions?.employeeEmail ||
    currentWorkerEmployee?.email ||
    currentWorkerEmployee?.workerEmail ||
    currentWorkerEmployee?.employeeEmail ||
    authEmailForWorkerCreate ||
    ""
  );
  const workerRouteEmailMatchesAuth = Boolean(createWorkerEmailFallback && authEmailForWorkerCreate && createWorkerEmailFallback === authEmailForWorkerCreate);
  const workerRouteCreateFallback = Boolean(!ownerOrAdmin && hardWorkerView && workerRouteEmailMatchesAuth);

  const workerPermissionValue = key => resolveAgendaPermission(key, [
    currentWorkerPermissions, resolvedWorkerPermissions, workerPermissions,
    resolvedWorkerEmployeeForPermissions?.permissions, resolvedWorkerEmployeeForPermissions?.workerPermissions,
    resolvedWorkerEmployeeForPermissions, resolvedWorkerEmployeeForPermissions?.rolePermissions,
    currentWorkerEmployee?.permissions, currentWorkerEmployee?.workerPermissions,
    currentWorkerEmployee, currentWorkerEmployee?.rolePermissions,
  ]);

  const canCreateBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canCreateBookings"));
  const canEditBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canEditBookings"));
  const canAcceptBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canAcceptBookings"));
  const canRejectBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canRejectBookings"));
  const canCancelBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canCancelBookings"));
  const canCompleteBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canCompleteBookings"));
  const canMoveBooking = ownerOrAdmin || (hasWorkerPermissionContext && !workerAccessError && workerPermissionValue("canMoveBookings"));

  const agendaRoleContext = {
    role: ownerOrAdmin ? "owner" : workerPanelScope ? "worker" : "staff",
    isOwnerOrAdmin: ownerOrAdmin,
    isWorkerView: Boolean(workerPanelScope && !ownerOrAdmin),
    label: isLoopPlatformOwner(auth) ? "Administrador de Loop" : ownerOrAdmin ? "Owner/Admin" : workerPanelScope ? "Trabajador" : "Equipo",
    sublabel: ownerOrAdmin ? "Vista completa del negocio" : workerPanelScope ? "Mi agenda" : "Vista operativa",
    canViewAllCalendars: ownerOrAdmin || (hasWorkerPermissionContext && workerPermissionValue("canViewAllCalendars")),
    canViewPrices: ownerOrAdmin || (hasWorkerPermissionContext && (workerPermissionValue("canViewPrices") || workerPermissionValue("canViewFinancials"))),
    canViewCustomerPhone: ownerOrAdmin || (hasWorkerPermissionContext && workerPermissionValue("canViewCustomerPhone")),
    canViewCustomerEmail: ownerOrAdmin || (hasWorkerPermissionContext && workerPermissionValue("canViewCustomerEmail")),
    canCreateBooking,
    canEditBooking,
    canAcceptBooking,
    canRejectBooking,
    canCancelBooking,
    canCompleteBooking,
    canMoveBooking,
  };

  const createPermissionDebug = {
    ownerOrAdmin,
    canCreateBooking,
    hasWorkerPermissionContext,
    effectiveWorkerEmployeeId,
    isWorkerView,
    hardWorkerView,
    detectedEmployeeDocId,
    hardDetectedEmployeeDocId,
    workerEmailMode,
    hardWorkerEmailMode,
    employeesCount: Array.isArray(employees) ? employees.length : 0,
    nativeVisibleEmployeesCount: Array.isArray(nativeVisibleEmployees) ? nativeVisibleEmployees.length : 0,
    workerPermissionsCanCreate: workerPermissions?.canCreateBookings,
    isIosCapacitorRuntime: isIosCapacitor(),
    authCurrentUserEmail: auth?.currentUser?.email || null,
    effectiveActorEmailForWorkerCreate: effectiveActorForWorkerCreate?.email || null,
    authUidForWorkerCreate,
    authEmailForWorkerCreate,
    createWorkerEmailFallback,
    workerRouteEmailMatchesAuth,
    workerRouteCreateFallback,
    resolvedWorkerPermissionsCanCreate: resolvedWorkerPermissions?.canCreateBookings,
    resolvedWorkerEmployeeId: resolvedWorkerEmployeeForPermissions?.id || resolvedWorkerEmployeeForPermissions?.employeeId || null,
    resolvedWorkerEmployeeEmail: resolvedWorkerEmployeeForPermissions?.email || resolvedWorkerEmployeeForPermissions?.workerEmail || resolvedWorkerEmployeeForPermissions?.employeeEmail || resolvedWorkerEmployeeForPermissions?.inviteEmail || null,
    resolvedWorkerEmployeePermissionsCanCreate: resolvedWorkerEmployeeForPermissions?.permissions?.canCreateBookings,
    resolvedWorkerDirectCanCreate: resolvedWorkerEmployeeForPermissions?.canCreateBookings,
    currentWorkerEmployeeId: currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || null,
    currentWorkerEmployeeEmail: currentWorkerEmployee?.email || currentWorkerEmployee?.workerEmail || currentWorkerEmployee?.employeeEmail || null,
    currentWorkerPermissionsCanCreate: currentWorkerEmployee?.permissions?.canCreateBookings,
    currentWorkerDirectCanCreate: currentWorkerEmployee?.canCreateBookings,
    canCompleteBooking,
  };
  try {
    if (typeof window !== "undefined") window.__loopCreatePermissionDebug = createPermissionDebug;
  } catch (_) {}

  // LOOP PHASE 1 HOTFIX V13 — worker manual create must be self-only.
  // A worker should never be able to assign a manual reservation to another worker.
  // Owner/admin keeps full team list; worker receives exactly one row: themselves.
  const bookingCreateEmployees = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    if (isSoloBookingBusiness(businessDoc || {}, list)) return [];
    if (ownerOrAdmin || !createWorkerEmailFallback) return list;

    const wantedEmail = normalizeEmailForWorkerVisibility(createWorkerEmailFallback);
    const existing = list.find((emp) => {
      const values = [
        emp?.email,
        emp?.emailLower,
        emp?.emailNormalized,
        emp?.employeeEmail,
        emp?.employeeEmailLower,
        emp?.employeeEmailNormalized,
        emp?.workerEmail,
        emp?.workerEmailLower,
        emp?.workerEmailNormalized,
        emp?.inviteEmail,
        emp?.inviteEmailLower,
        emp?.contactEmail,
        ...(Array.isArray(emp?.accessEmails) ? emp.accessEmails : []),
        ...(Array.isArray(emp?.allowedEmails) ? emp.allowedEmails : []),
      ].filter(Boolean).map(normalizeEmailForWorkerVisibility);
      return values.includes(wantedEmail);
    });

    if (existing) {
      return [{
        ...existing,
        id: existing.id || existing.employeeId || wantedEmail,
        employeeId: existing.employeeId || existing.id || wantedEmail,
        email: existing.email || existing.employeeEmail || existing.workerEmail || wantedEmail,
        emailLower: normalizeEmailForWorkerVisibility(existing.emailLower || existing.email || wantedEmail),
        employeeEmail: existing.employeeEmail || existing.email || existing.workerEmail || wantedEmail,
        employeeEmailLower: normalizeEmailForWorkerVisibility(existing.employeeEmailLower || existing.employeeEmail || existing.email || wantedEmail),
        workerEmail: existing.workerEmail || existing.employeeEmail || existing.email || wantedEmail,
        workerEmailLower: normalizeEmailForWorkerVisibility(existing.workerEmailLower || existing.workerEmail || existing.email || wantedEmail),
        __loopSelfOnly: true,
      }];
    }

    const fallbackName =
      resolvedWorkerEmployeeForPermissions?.name ||
      resolvedWorkerEmployeeForPermissions?.displayName ||
      currentWorkerEmployee?.name ||
      currentWorkerEmployee?.displayName ||
      (wantedEmail ? wantedEmail.split("@")[0] : "Trabajador");

    const selfEmployee = {
      id: wantedEmail,
      employeeId: wantedEmail,
      name: fallbackName || "Trabajador",
      displayName: fallbackName || "Trabajador",
      email: wantedEmail,
      emailLower: wantedEmail,
      emailNormalized: wantedEmail,
      employeeEmail: wantedEmail,
      employeeEmailLower: wantedEmail,
      employeeEmailNormalized: wantedEmail,
      workerEmail: wantedEmail,
      workerEmailLower: wantedEmail,
      workerEmailNormalized: wantedEmail,
      workerType: "service",
      type: "service",
      accessRole: "worker",
      loopRole: "worker",
      permissions: resolvedWorkerPermissions || workerPermissions || {},
      __loopSelfFallback: true,
      __loopSelfOnly: true,
    };

    return [selfEmployee];
  }, [
    employees,
    businessDoc,
    ownerOrAdmin,
    createWorkerEmailFallback,
    resolvedWorkerEmployeeForPermissions,
    currentWorkerEmployee,
    resolvedWorkerPermissions,
    workerPermissions,
  ]);

  // LOOP PHASE 2.0.4 — business settings enforce modules/visibility across Business OS.
  const loopBusinessSettings = useMemo(() => normalizeLoopBusinessSettings(businessDoc || {}), [businessDoc]);
  const actorRoleSource = useMemo(() => {
    if (ownerOrAdmin) return { accessRole: "owner" };
    return resolvedWorkerEmployeeForPermissions || currentWorkerEmployee || { accessRole: "worker" };
  }, [ownerOrAdmin, resolvedWorkerEmployeeForPermissions, currentWorkerEmployee]);
  const actorBusinessPermissions = useMemo(() => {
    const base = ownerOrAdmin ? getRolePermissionPreset("owner") : resolvedWorkerPermissions;
    return applyBusinessSettingsToPermissions(base, loopBusinessSettings, actorRoleSource);
  }, [ownerOrAdmin, resolvedWorkerPermissions, loopBusinessSettings, actorRoleSource]);
  const analyticsModuleEnabled = loopBusinessSettings.enabledModules.analytics !== false;
  const crmModuleEnabled = loopBusinessSettings.enabledModules.crm !== false;
  const whatsappManualEnabled = loopBusinessSettings.enabledModules.whatsappManual !== false;
  const canViewCustomers = crmModuleEnabled && (ownerOrAdmin || actorBusinessPermissions.canViewCustomers);
  const canSendWhatsApp = whatsappManualEnabled && (ownerOrAdmin || actorBusinessPermissions.canSendWhatsApp);
  const canViewBusinessAnalytics = analyticsModuleEnabled && (ownerOrAdmin || actorBusinessPermissions.canViewReports || actorBusinessPermissions.canViewFinancials || actorBusinessPermissions.canViewInventory);
  const canViewAgendaFinance = analyticsModuleEnabled && Boolean(activeBusinessId) && (ownerOrAdmin || actorBusinessPermissions.canViewFinancials || actorBusinessPermissions.canEditPayments || actorBusinessPermissions.canViewReports || actorBusinessPermissions.canViewCommissions || actorBusinessPermissions.canViewOwnPayout || actorBusinessPermissions.canViewOwnTips);
  const canUseUnifiedFinanceShortcut = workerPanelScope ? canViewAgendaFinance : canViewBusinessAnalytics;
  const effectivePendingBadgeCount = workerPanelScope ? (workerPendingCount || 0) : (businessPendingCount || 0);
  const effectiveToConfirmBadgeCount = workerPanelScope ? (workerToConfirmCount || 0) : (businessToConfirmCount || 0);

  // LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80
  // Worker panel baseline:
  // - no special permissions: Agenda + Reservas only
  // - finance permissions: add Finanzas
  // - future partial-admin permissions: reveal Clientes/Servicios/Equipo only when explicitly allowed.
  const canWorkerViewPanelClients = workerPanelScope && canViewCustomers;
  const canWorkerViewPanelServices = workerPanelScope && Boolean(actorBusinessPermissions.canManageBusinessSettings || actorBusinessPermissions.canManageModules);
  const canWorkerViewPanelTeam = workerPanelScope && Boolean(actorBusinessPermissions.canManageWorkers);
  const canWorkerViewPanelSettings = workerPanelScope && Boolean(actorBusinessPermissions.canManageBusinessSettings || actorBusinessPermissions.canManageModules || actorBusinessPermissions.canManageBilling);

  const agendaMobileToolsItems = useMemo(() => {
    const items = [
      { key: "agenda", tab: "agenda", label: "Agenda", icon: "agenda", badge: null },
      { key: "pendings", tab: "pendings", label: "Reservas", icon: "reservas", badge: effectivePendingBadgeCount > 0 ? effectivePendingBadgeCount : null },
    ];

    if (workerPanelScope) {
      if (canViewAgendaFinance) {
        items.push({ key: "worker-finance", tab: "finance", label: "Finanzas", icon: "finanzas", badge: null });
      }
      if (canWorkerViewPanelClients) {
        items.push({ key: "worker-clients", tab: "clients", label: "Clientes", icon: "clientes", badge: null });
      }
      if (canWorkerViewPanelServices) {
        items.push({ key: "worker-services", tab: "services", label: "Servicios", icon: "servicios", badge: null });
      }
      if (canWorkerViewPanelTeam) {
        items.push({ key: "worker-team", tab: "workers", label: "Equipo", icon: "equipo", badge: null });
      }
      if (canWorkerViewPanelSettings) {
        items.push({ key: "worker-settings", tab: "settings", label: "Ajustes", icon: "ajustes", badge: null });
      }
      return items;
    }

    if (canViewCustomers) items.push({ key: "clients", tab: "clients", label: "Clientes", icon: "clientes", badge: null });
    items.push({ key: "services", tab: "services", label: "Servicios", icon: "servicios", badge: null });
    items.push({ key: "workers", tab: "workers", label: "Equipo", icon: "equipo", badge: null });
    if (canViewBusinessAnalytics) items.push({ key: "finance", tab: "finance", label: "Finanzas", icon: "finanzas", badge: null });
    if (canViewBusinessAnalytics) items.push({ key: "analytics", tab: "analytics", label: "Reportes", icon: "reportes", badge: null });
    items.push({ key: "settings", tab: "settings", label: "Ajustes", icon: "ajustes", badge: null });
    return items;
  }, [
    effectivePendingBadgeCount,
    canViewAgendaFinance,
    canViewBusinessAnalytics,
    canViewCustomers,
    workerPanelScope,
    canWorkerViewPanelClients,
    canWorkerViewPanelServices,
    canWorkerViewPanelTeam,
    canWorkerViewPanelSettings,
  ]);

  const canViewFinanceSidebar = analyticsModuleEnabled && (
    ownerOrAdmin ||
    actorBusinessPermissions.canViewFinancials ||
    actorBusinessPermissions.canViewReports
  );
  const canViewInventorySidebar = (
    loopBusinessSettings.enabledModules.inventory !== false &&
    loopBusinessSettings.inventorySettings?.enabled !== false &&
    loopBusinessSettings.inventorySettings?.plan !== "disabled" &&
    (ownerOrAdmin || actorBusinessPermissions.canViewInventory)
  );
  // LOOP SIDEBAR LOYALTY + MEMBERSHIPS V60.2
  // Keep the membership report discoverable before the first plan is configured.
  const canViewMembershipsSidebar = canViewBusinessAnalytics;

  const adminSidebarDesktopGroups = useMemo(() => {
    if (workerPanelScope) {
      const findTab = (tab) => (agendaMobileToolsItems || []).find((item) => item.tab === tab) || null;
      return [
        { id: "operation", label: "Operación", items: [findTab("agenda"), findTab("pendings")].filter(Boolean) },
        { id: "business", label: "Negocio", items: [findTab("clients"), findTab("services"), findTab("workers")].filter(Boolean) },
        { id: "reports", label: "Gestión", items: [findTab("finance")].filter(Boolean) },
        { id: "settings", label: "Configuración", items: [findTab("settings")].filter(Boolean) },
      ].filter((group) => group.items.length > 0);
    }

    return [
      {
        id: "operation",
        label: "Operación",
        items: [
          { key: "agenda", tab: "agenda", label: "Agenda", icon: "agenda", badge: null },
          { key: "pendings", tab: "pendings", label: "Reservas", icon: "reservas", badge: effectivePendingBadgeCount > 0 ? effectivePendingBadgeCount : null },
        ],
      },
      {
        id: "business",
        label: "Negocio",
        items: [
          canViewCustomers ? { key: "clients", tab: "clients", label: "Clientes", icon: "clientes", badge: null } : null,
          { key: "services", tab: "services", label: "Servicios", icon: "servicios", badge: null },
          { key: "workers", tab: "workers", label: "Equipo", icon: "equipo", badge: null },
          { key: "loyalty-settings", tab: "loyalty", label: "Fidelidad", icon: "fidelidad", badge: null },
          { key: "memberships-settings", tab: "memberships", label: "Membresías", icon: "membresias", badge: null },
        ].filter(Boolean),
      },
      {
        id: "reports",
        label: "Reportes",
        items: [
          canViewBusinessAnalytics ? { key: "summary", tab: "analytics", analyticsView: "overview", label: "Resumen", icon: "resumen", badge: null } : null,
          canViewFinanceSidebar ? { key: "finance-report", tab: "analytics", analyticsView: "finance", label: "Finanzas", icon: "finanzas", badge: null } : null,
          canViewInventorySidebar ? { key: "inventory-report", tab: "analytics", analyticsView: "inventory", label: "Inventario", icon: "inventario", badge: null } : null,
          canViewMembershipsSidebar ? { key: "memberships-report", tab: "analytics", analyticsView: "memberships", label: "Reporte de membresías", icon: "membresias", badge: null } : null,
          canViewBusinessAnalytics && canViewCustomers ? { key: "crm-report", tab: "analytics", analyticsView: "crm", label: "Reporte de clientes", icon: "clientes" } : null,
          canViewBusinessAnalytics ? { key: "exports-report", tab: "analytics", analyticsView: "exports", label: "Exportar", icon: "resumen" } : null,
          canViewBusinessAnalytics && (ownerOrAdmin || actorBusinessPermissions.canViewReports || actorBusinessPermissions.canViewAnalytics) ? { key: "traffic-report", tab: "analytics", analyticsView: "traffic", label: "Tráfico", icon: "resumen" } : null,
          canViewBusinessAnalytics && (ownerOrAdmin || actorBusinessPermissions.canViewReports || actorBusinessPermissions.canViewAnalytics) ? { key: "insights-report", tab: "analytics", analyticsView: "insights", label: "Advisor", icon: "resumen" } : null,
        ].filter(Boolean),
      },
      {
        id: "settings",
        label: "Configuración",
        items: [
          { key: "settings", tab: "settings", label: "Ajustes", icon: "ajustes", badge: null },
        ],
      },
    ].filter((group) => group.items.length > 0);
  }, [
    workerPanelScope,
    agendaMobileToolsItems,
    canViewCustomers,
    canViewBusinessAnalytics,
    canViewFinanceSidebar,
    canViewInventorySidebar,
    canViewMembershipsSidebar,
    effectivePendingBadgeCount, ownerOrAdmin, actorBusinessPermissions.canViewReports, actorBusinessPermissions.canViewAnalytics,
  ]);

  const unifiedAgendaMenuItems = useMemo(() => workerPanelScope && workerAccessError ? [{key:"agenda",tab:"agenda",label:"Agenda",icon:"agenda"}] : adminSidebarDesktopGroups.flatMap(group => group.items), [adminSidebarDesktopGroups, workerPanelScope, workerAccessError]);

  const activeAdminSidebarGroupId = useMemo(() => {
    const group = (adminSidebarDesktopGroups || []).find((candidate) => (
      (candidate.items || []).some((item) => {
        if (item.analyticsView) {
          if (item.analyticsView === "finance" && activeTab === "finance") return true;
          return activeTab === "analytics" && analyticsInitialView === item.analyticsView;
        }
        return activeTab === item.tab;
      })
    ));
    return group?.id || "";
  }, [adminSidebarDesktopGroups, activeTab, analyticsInitialView]);

  useEffect(() => {
    if (!activeAdminSidebarGroupId) return;
    setAdminSidebarGroupsOpen((prev) => (
      prev?.[activeAdminSidebarGroupId] === false
        ? { ...prev, [activeAdminSidebarGroupId]: true }
        : prev
    ));
  }, [activeAdminSidebarGroupId]);

  const agendaAllowedPanelTabs = useMemo(
    () => Array.from(new Set(unifiedAgendaMenuItems.map(item => item.tab).filter(Boolean))),
    [unifiedAgendaMenuItems]
  );


  const canChangeStatus = canEditBooking || canAcceptBooking || canRejectBooking || canCancelBooking;

  useEffect(() => {
    // Restore the requested destination when permissions finish loading. Never
    // discard the URL just because the initial menu only contains Agenda.
    const route = permittedCalendarSection(routeLocation.search, agendaAllowedPanelTabs, workerPanelScope);
    setActiveTab(route.tab);
    setAnalyticsInitialView(route.report);
  }, [routeLocation.search, agendaAllowedPanelTabs, workerPanelScope]);

  const openCreateModalForDate = useCallback((date, resourceId = null) => {
    if (!canCreateBooking) {
      try { console.warn("[Loop] create booking permission denied", createPermissionDebug); } catch (_) {}
      alert("No tienes permiso para crear reservas desde aquí. Revisa window.__loopCreatePermissionDebug en la consola.");
      return;
    }

    if (agendaRequiresWorkerSetup) {
      try {
        if (typeof window !== "undefined") {
          window.__loopAgendaWorkerSetupLock = {
            businessId: activeBusinessId || null,
            reason: "missing_bookable_worker",
            at: new Date().toISOString(),
          };
        }
      } catch (_) {}
      alert("Primero configura al menos un trabajador en Equipo para usar la agenda.");
      return;
    }

    const sourceDate = date ? new Date(date) : new Date(selectedDate || new Date());
    const validDate = Number.isNaN(sourceDate.getTime()) ? new Date() : sourceDate;
    const rounded = snapDownToMinutes(validDate, Number(effectiveAgendaSettings.slotMinutes || 15) || 15);
    const safeRounded = rounded && !Number.isNaN(new Date(rounded).getTime()) ? new Date(rounded) : validDate;
    const rawClickedResourceId = resourceId ? String(resourceId).trim() : null;
    const clickedResourceId = rawClickedResourceId === "__loop_company_agenda__" ? null : rawClickedResourceId;
    const resolvedResourceId = getCreateResourceId(clickedResourceId) || clickedResourceId || null;
    const workerCreateResourceId = effectiveWorkerEmployeeId || resolvedResourceId || createWorkerEmailFallback || null;
    const finalResourceId = hasWorkerPermissionContext && !ownerOrAdmin ? workerCreateResourceId : resolvedResourceId;

    setCreateModalOpen(false);
    setEditBooking(null);
    setCreateModalDate(safeRounded);
    setCreateModalInitialTime(adminSmartTimeInputValue(safeRounded));
    setCreateModalResourceId(finalResourceId);

    try {
      if (typeof window !== "undefined") {
        window.__loopLastCreateModalSeed = {
          date: safeRounded.toISOString(),
          time: adminSmartTimeInputValue(safeRounded),
          resourceId: finalResourceId || null,
          rawResourceId: clickedResourceId || null,
        };
      }
    } catch (_) {}

    setTimeout(() => setCreateModalOpen(true), 0);
  }, [
    canCreateBooking,
    createPermissionDebug,
    selectedDate,
    effectiveAgendaSettings.slotMinutes,
    getCreateResourceId,
    effectiveWorkerEmployeeId,
    createWorkerEmailFallback,
    hasWorkerPermissionContext,
    ownerOrAdmin,
    agendaRequiresWorkerSetup,
    activeBusinessId,
  ]);



  const buildSmartCreateDraft = useCallback((date, resourceId = null) => {
    const d = snapDownToMinutes(date ? new Date(date) : selectedDate || new Date(), Number(effectiveAgendaSettings.slotMinutes || 15));
    const cleanDraftResourceId = String(resourceId || "").trim() === "__loop_company_agenda__" ? null : resourceId;
    const workerCreateResourceId = effectiveWorkerEmployeeId || getCreateResourceId(cleanDraftResourceId) || cleanDraftResourceId || createWorkerEmailFallback || null;
    const resolvedResourceId = hasWorkerPermissionContext && !ownerOrAdmin ? workerCreateResourceId : getCreateResourceId(cleanDraftResourceId);
    const compatibleServices = Array.isArray(services) ? services : [];
    const firstService = compatibleServices[0] || null;
    const duration = adminSmartServiceDuration(firstService, Number(effectiveAgendaSettings.slotMinutes || 60) || 60);
    return {
      customerName: "",
      customerPhone: "",
      customerEmail: "",
      serviceId: firstService?.id ? String(firstService.id) : "",
      employeeId: resolvedResourceId ? String(resolvedResourceId) : "",
      date: adminSmartDateInputValue(d),
      startTime: adminSmartTimeInputValue(d),
      duration: String(duration),
      totalPrice: adminSmartServicePrice(firstService),
      notes: "",
    };
  }, [effectiveAgendaSettings.slotMinutes, detectedEmployeeDocId, effectiveWorkerEmployeeId, getCreateResourceId, hasWorkerPermissionContext, isWorkerView, ownerOrAdmin, selectedDate, services, isNativeRuntime, viewportWidth]);

  const smartCreateSelectedService = useMemo(
    () => (Array.isArray(services) ? services : []).find((s) => String(s.id) === String(smartCreateDraft?.serviceId || "")) || null,
    [services, smartCreateDraft?.serviceId]
  );

  const smartCreateSelectedWorker = useMemo(
    () => (Array.isArray(employees) ? employees : []).find((e) => String(e.id) === String(smartCreateDraft?.employeeId || "")) || null,
    [employees, smartCreateDraft?.employeeId]
  );

  const smartCreateWarnings = useMemo(() => {
    if (!smartCreateDraft) return [];
    const start = adminSmartBuildDate(smartCreateDraft.date, smartCreateDraft.startTime);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + (Number(smartCreateDraft.duration) || 60));
    return adminBookingMoveBuildWarnings({
      employees,
      services,
      events: allEvents,
      bookingId: "",
      resourceId: smartCreateDraft.employeeId,
      start,
      end,
      raw: { serviceId: smartCreateDraft.serviceId, serviceName: smartCreateSelectedService ? adminSmartServiceLabel(smartCreateSelectedService) : "" },
      businessDoc,
    });
  }, [allEvents, employees, services, smartCreateDraft, smartCreateSelectedService, businessDoc]);

  const openSmartCreateForDate = useCallback((date, resourceId = null) => {
    // Unified booking flow: skip the old "reserva rápida" chooser and open
    // the main BookingCreateModal directly, prefilled with date/time/worker.
    openCreateModalForDate(date, resourceId);
  }, [openCreateModalForDate]);

  const updateSmartCreateDraft = useCallback((field, value) => {
    setSmartCreateDraft((prev) => {
      const next = { ...(prev || {}), [field]: value };
      if (field === "serviceId") {
        const svc = (Array.isArray(services) ? services : []).find((s) => String(s.id) === String(value));
        if (svc) {
          next.duration = String(adminSmartServiceDuration(svc, Number(next.duration || 60) || 60));
          const price = adminSmartServicePrice(svc);
          if (price) next.totalPrice = price;
        }
      }
      return next;
    });
  }, [services]);

  const applySmartCustomer = useCallback((customer) => {
    if (!customer) return;
    setSmartCreateDraft((prev) => ({
      ...(prev || {}),
      customerName: customer.name || "",
      customerPhone: customer.phone || "",
      customerEmail: customer.email || "",
    }));
  }, []);

  const openFullCreateFromSmart = useCallback(() => {
    if (!smartCreateDraft) return;
    const base = adminSmartBuildDate(smartCreateDraft.date, smartCreateDraft.startTime);
    setSmartCreateOpen(false);
    setCreateModalOpen(false);
    setCreateModalDate(base);
    setCreateModalInitialTime(adminSmartTimeInputValue(base));
    setCreateModalResourceId(smartCreateDraft.employeeId || null);
    setEditBooking(null);
    setTimeout(() => setCreateModalOpen(true), 0);
  }, [smartCreateDraft]);

  const saveSmartCreateBooking = useCallback(async () => {
    if (!smartCreateDraft) return;
    if (!String(smartCreateDraft.customerName || "").trim()) {
      alert("Agrega el nombre del cliente.");
      return;
    }
    setSmartCreateSaving(true);
    try {
      const base = adminSmartBuildDate(smartCreateDraft.date, smartCreateDraft.startTime);
      await onCreateOrUpdateBooking({
        id: null,
        customerName: smartCreateDraft.customerName,
        customerPhone: smartCreateDraft.customerPhone,
        customerEmail: smartCreateDraft.customerEmail,
        time: smartCreateDraft.startTime,
        resourceId: smartCreateDraft.employeeId || null,
        duration: Number(smartCreateDraft.duration || 60) || 60,
        serviceId: smartCreateDraft.serviceId || null,
        totalPrice: smartCreateDraft.totalPrice === "" ? null : Number(smartCreateDraft.totalPrice),
        notes: smartCreateDraft.notes,
        internalNotes: smartCreateDraft.notes,
        smartBaseDate: base,
      });
      setSmartCreateOpen(false);
      setSmartCreateDraft(null);
      setSmartCreateNotice({ title: "Reserva creada", message: `${smartCreateDraft.customerName || "Cliente"} · ${smartCreateDraft.startTime}` });
      setTimeout(() => setSmartCreateNotice(null), 2600);
    } catch (err) {
      if (err?.code === "schedule-exception-cancelled") return;
      console.error("[SmartCreate] save failed", err);
      alert("No pude crear la reserva. Revisa los datos e intenta de nuevo.");
    } finally {
      setSmartCreateSaving(false);
    }
  }, [smartCreateDraft]);

  const lastDateClickRef = useRef({ time: 0, key: "", resourceId: null });

  const buildClickedDateFromDomEvent = useCallback(
    (event) => {
      const target = event?.target instanceof Element ? event.target : null;
      const pointEl =
        typeof document !== "undefined" && event
          ? document.elementFromPoint(event.clientX, event.clientY)
          : null;

      const dateEl =
        (target && target.closest("[data-date]")) ||
        (pointEl && pointEl.closest("[data-date]"));

      const timeEl =
        (target && target.closest("[data-time]")) ||
        (pointEl && pointEl.closest("[data-time]"));

      let base = new Date(selectedDate || new Date());
      const dateText = dateEl ? dateEl.getAttribute("data-date") : "";
      const dm = String(dateText || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dm) {
        base = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]));
      }

      const timeText = timeEl ? timeEl.getAttribute("data-time") : "";
      const tm = String(timeText || "").match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (tm) {
        base.setHours(Number(tm[1]), Number(tm[2]), 0, 0);
      }

      return snapDownToMinutes(base, Number(effectiveAgendaSettings.slotMinutes || 15) || 15);
    },
    [selectedDate, effectiveAgendaSettings.slotMinutes]
  );

  const buildClickedResourceIdFromDomEvent = useCallback((event) => {
    try {
      const target = event?.target instanceof Element ? event.target : null;
      const pointEl =
        typeof document !== "undefined" && event
          ? document.elementFromPoint(event.clientX, event.clientY)
          : null;
      const resourceEl =
        (target && target.closest("[data-resource-id]")) ||
        (pointEl && pointEl.closest("[data-resource-id]"));
      const id = resourceEl?.getAttribute?.("data-resource-id");
      return id ? String(id) : null;
    } catch (_) {
      return null;
    }
  }, []);

  const handleCalendarDateClick = (arg) => {
    const now = Date.now();
    const resourceId = getCreateResourceId(arg?.resource?.id || arg?.resourceId || null);

    let clickedDate = null;
    try {
      clickedDate = computeClickedDateFromEvent(arg, 15);
    } catch {
      clickedDate = null;
    }
    if (!clickedDate) clickedDate = arg?.date ? new Date(arg.date) : new Date();

    const rounded = snapDownToMinutes(clickedDate, 15);
    const dateKey = rounded.toISOString();
    const last = lastDateClickRef.current;

    if (last.key === dateKey && last.resourceId === resourceId && now - last.time < 700) {
      openCreateModalForDate(rounded, resourceId);
      lastDateClickRef.current = { time: 0, key: "", resourceId: null };
    } else {
      lastDateClickRef.current = { time: now, key: dateKey, resourceId };
      setTimeout(() => {
        if (lastDateClickRef.current.time === now) {
          lastDateClickRef.current = { time: 0, key: "", resourceId: null };
        }
      }, 720);
    }
  };

  const handleCalendarDoubleClickCapture = useCallback(
    (event) => {
      if (activeTab !== "agenda" || agendaRequiresWorkerSetup) return;
      const target = event?.target;
      if (target && target.closest && target.closest(".fc-event")) return;
      const clickedDate = buildClickedDateFromDomEvent(event);
      const clickedResourceId = buildClickedResourceIdFromDomEvent(event) || getCreateResourceId(null);
      openCreateModalForDate(clickedDate, clickedResourceId);
    },
    [activeTab, agendaRequiresWorkerSetup, buildClickedDateFromDomEvent, buildClickedResourceIdFromDomEvent, getCreateResourceId, openCreateModalForDate]
  );


  const onCreateOrUpdateBooking = useCallback(
    async ({ requestedSessionProgramId = null, sessionProgramAction = "continue", sessionPreviousCompleted = 0, sessionExpectedActiveIds = [], collectionEventId = null, id = null, expectedSchedule = null, previousStatus = null, previousConfirmationStatus = null, status = null, confirmationStatus = null, customerId = null, customerName, customerEmail, customerPhone, customerSnapshot, time, resourceId, duration, serviceId, requestedWorkerMode, assignmentStatus, employeeNotRequired, eventOptions, eventDetails, visitType, visitTypeLabel, visitHours, selectedCharacterIds = [], selectedCharacters = [], durationPrice = null, charactersPrice = null, totalPrice = null, assignedEmployeeIds = [], assignedEmployees = [], visitMode = null, visitTypeLocation = null, customerLocation = null, serviceAreaMatched = null, customerExactAddress = null, customerAddressDetails = null, deliveryFee = 0, locationFee = 0, notes = null, internalNotes = null, smartBaseDate = null, bookingSource = null, createdFrom = null, createdByRole = null, channel = null, origin = null, manual = null, isManual = null, adminCreated = null, onlineBooking = null, isOnlineBooking = null,
      paymentStatus = null, paymentMethod = null, amountCharged = null, paidAmount = null, discountAmount = 0, tipAmount = 0, collectionApplied = false, collectionBasePaidAmount = null, priceAdjustmentDraft = null, bookingPriceOverride = null,
      workerCommissionType = "none", commissionType = null, workerCommissionValue = 0, commissionValue = null,
      commissionBaseAmount = 0, commissionIncludesTips = false, commissionOnlyCompleted = true,
      workerPayoutAmount = 0, workerPayoutStatus = "pending", workerPaymentNotes = null, businessNetAmount = null, payment = null, productSales = [], retailSales = [], productSalesTotalAmount = 0, retailSalesTotalAmount = 0,
      membershipApplied = false, membershipBenefitApplied = false, customerMembershipId = null, membershipId = null,
      membershipPlanId = null, membershipPlanName = null, membershipBenefitType = null, membershipCreditsUsed = 0,
      membershipDiscountPercent = 0, membershipDiscountAmount = 0, membershipBenefitLabel = null, deleteReservationIntent = false, cancellationSource = null, cancellationReason = null, notifyCustomerCancellation = false }) => {
      if (!activeBusinessId) throw new Error("businessId missing");
      if (!id && agendaRequiresWorkerSetup) {
        throw new Error("Antes de crear reservas, configura al menos un trabajador en Equipo.");
      }
      const baseBookingDate = smartBaseDate ? new Date(smartBaseDate) : createModalDate;
      if (!baseBookingDate) throw new Error("date missing");
      const t = (time || "10:00").trim();
      if (!/^\d{1,2}:\d{2}$/.test(t)) throw new Error("Hora inválida");

      const canonical = buildPanamaBookingDateTimeParts(baseBookingDate, t, Number(duration || 60) || 60, businessDoc || {});
      const noWorkerCompanyBooking = Boolean(calendarShouldUseCompanyNoWorkers && !isEventBusinessAdmin);
      const safeResourceIdForBooking = noWorkerCompanyBooking || String(resourceId || "").trim() === "__loop_company_agenda__"
        ? null
        : resourceId;
      const schedRounded = roundToMinutes(canonical.start, 15);
      const endRounded = roundToMinutes(canonical.end, 15);
      const canonicalDate = canonical.date;
      const canonicalTime = canonical.time;

      let resolvedCustomerId = customerId || null;
      let resolvedCustomerSnapshot = customerSnapshot || null;

      // LOOP PHASE 1 HOTFIX V10 — worker booking create must not be blocked by CRM permissions.
      // Worker accounts can create their own booking by email, but may not have permission to query/write
      // businesses/{businessId}/customers. For workers, keep a lightweight snapshot on the booking and
      // skip the CRM upsert. Owners/admins keep the full CRM behavior.
      // LOOP BOOKING IOS WORKER CREATE STABILITY V17
      // Worker routes must not run client-side CRM upsert. It can hang or be denied
      // on iOS before the worker-create Function is ever called. The worker Function
      // hydrates customer identity with Admin SDK using the id/snapshot in payload.
      // R33 P2: new bookings are canonicalized server-side for both managers
      // and workers. Client-side CRM sync remains only for booking edits.
      const shouldUpsertCustomerForBooking = Boolean(ownerOrAdmin && id);
      if (shouldUpsertCustomerForBooking) {
        try {
          const customerResult = await findOrCreateCustomerForBusiness(db, activeBusinessId, {
            name: customerName,
            customerName,
            customerEmail,
            customerPhone,
            source: id ? "manual" : "booking",
            createdByRole: ownerOrAdmin ? "owner" : "worker",
            createdByEmail: getEffectiveActor().email || null,
          }, {
            incrementBookings: id ? false : true,
            bookingDate: Timestamp.fromDate(schedRounded),
            createdByRole: ownerOrAdmin ? "owner" : "worker",
            createdByEmail: getEffectiveActor().email || null,
          });
          if (customerResult && customerResult.id) {
            resolvedCustomerId = customerResult.id;
            resolvedCustomerSnapshot = {
              name: customerResult.name || customerName || "Cliente",
              phone: customerResult.phone || customerResult.phoneNormalized || customerPhone || "",
              email: customerResult.email || customerResult.emailNormalized || customerEmail || "",
              source: customerResult.source || "booking",
            };
          }
        } catch (customerErr) {
          console.warn("[BookingCreate] customer upsert failed; booking will still be saved", customerErr);
        }
      } else if (
        customerSnapshot ||
        String(customerName || customerEmail || customerPhone || "").trim()
      ) {
        resolvedCustomerSnapshot = {
          ...(customerSnapshot && typeof customerSnapshot === "object"
            ? customerSnapshot
            : {}),
          name:
            String(
              customerSnapshot?.name ||
              customerSnapshot?.customerName ||
              customerName ||
              ""
            ).trim() || "Cliente",
          phone:
            String(
              customerSnapshot?.phone ||
              customerSnapshot?.phoneNormalized ||
              customerPhone ||
              ""
            ).trim(),
          email:
            String(
              customerSnapshot?.emailNormalized ||
              customerSnapshot?.email ||
              customerEmail ||
              ""
            ).trim(),
          source:
            customerSnapshot?.source ||
            (ownerOrAdmin
              ? "manager_booking_snapshot"
              : "worker_booking_snapshot"),
          serverIdentityDeferred: true,
        };
      }

      const payload = {
        requestedSessionProgramId, sessionProgramAction, sessionPreviousCompleted, sessionExpectedActiveIds,
        ...(expectedSchedule ? {_scheduleExpected: expectedSchedule} : {}),
        customerId: resolvedCustomerId,
        customerName: String(customerName || "").trim() || "Cliente",
        customerEmail: customerEmail && customerEmail.trim() ? String(customerEmail).trim() : null,
        customerPhone: customerPhone && customerPhone.trim() ? String(customerPhone).trim() : null,
        customerSnapshot: resolvedCustomerSnapshot || null,
        scheduledAt: Timestamp.fromDate(schedRounded),
        startAt: Timestamp.fromDate(schedRounded),
        endAt: Timestamp.fromDate(endRounded),
        date: canonicalDate,
        time: canonicalTime,
        ...scheduleFields(schedRounded,businessDoc || {}),
        duration: Number(duration || 60) || 60,
        durationMinutes: Number(duration || 60) || 60,
        status: id ? (status || previousStatus || "confirmed") : (status || "confirmed"),
        confirmationStatus: id ? (confirmationStatus || status || previousConfirmationStatus || previousStatus || "confirmed") : (confirmationStatus || status || "confirmed"),

        // Loop analytics source classification.
        bookingSource: bookingSource || "admin_manual",
        createdFrom: createdFrom || "admin_calendar",
        // LOOP PHASE 1 HOTFIX V9 — never trust modal's owner defaults for worker routes.
        // BookingCreateModal historically defaulted createdByRole/adminCreated to owner.
        // Firestore worker-create rules require createdByRole === "worker" for worker accounts.
        createdByRole: ownerOrAdmin ? (createdByRole || "owner") : "worker",
        channel: channel || "admin_calendar",
        origin: origin || "booking_create_modal",
        manual: manual ?? true,
        isManual: isManual ?? true,
        adminCreated: ownerOrAdmin ? (adminCreated ?? true) : false,
        onlineBooking: onlineBooking ?? false,
        isOnlineBooking: isOnlineBooking ?? false,

        requestedWorkerMode: requestedWorkerMode || ((isEventBusinessAdmin || noWorkerCompanyBooking) ? "none" : (safeResourceIdForBooking ? "specific" : "any")),
        assignmentStatus: assignmentStatus || ((isEventBusinessAdmin || noWorkerCompanyBooking) ? "not_required" : (safeResourceIdForBooking ? "assigned" : "unassigned")),
        employeeNotRequired: Boolean(employeeNotRequired || isEventBusinessAdmin || noWorkerCompanyBooking),
        eventOptions: eventOptions || null,
        eventDetails: eventDetails || null,
        visitType: visitType || eventDetails?.visitType || null,
        visitTypeLabel: visitTypeLabel || eventDetails?.visitTypeLabel || null,
        visitHours: visitHours || eventDetails?.visitHours || null,
        selectedCharacterIds: Array.isArray(selectedCharacterIds) ? selectedCharacterIds : [],
        selectedCharacters: Array.isArray(selectedCharacters) ? selectedCharacters : [],
        durationPrice,
        charactersPrice,
        totalPrice,

        // LOOP D2.2.2 — Preserve membership benefit metadata from BookingCreateModal.
        // Without these fields, the backend trigger cannot consume membership credits.
        membershipApplied: Boolean(membershipApplied || membershipBenefitApplied || customerMembershipId || membershipId),
        membershipBenefitApplied: Boolean(membershipApplied || membershipBenefitApplied || customerMembershipId || membershipId),
        customerMembershipId: customerMembershipId || membershipId || null,
        membershipId: membershipId || customerMembershipId || null,
        membershipPlanId: membershipPlanId || null,
        membershipPlanName: membershipPlanName || null,
        membershipBenefitType: membershipBenefitType || null,
        membershipCreditsUsed: Number(membershipCreditsUsed || 0) || 0,
        membershipDiscountPercent: Number(membershipDiscountPercent || 0) || 0,
        membershipDiscountAmount: Number(membershipDiscountAmount || 0) || 0,
        membershipBenefitLabel: membershipBenefitLabel || null,

        // Finance / commissions come from BookingCreateModal. Keep both new and legacy aliases.
        paymentStatus: paymentStatus || payment?.status || "pending",
        paymentMethod: paymentMethod || payment?.method || "cash",
        amountCharged: Number(amountCharged ?? paidAmount ?? payment?.amountCharged ?? 0) || 0,
        paidAmount: Number(paidAmount ?? amountCharged ?? payment?.amountCharged ?? 0) || 0,
        bookingPriceOverride,
        discountAmount: Number(discountAmount ?? payment?.discountAmount ?? 0) || 0,
        tipAmount: Number(tipAmount ?? payment?.tipAmount ?? 0) || 0,
        workerCommissionType: workerCommissionType || commissionType || payment?.workerCommissionType || "none",
        commissionType: workerCommissionType || commissionType || payment?.workerCommissionType || "none",
        workerCommissionValue: Number(workerCommissionValue ?? commissionValue ?? payment?.workerCommissionValue ?? 0) || 0,
        commissionValue: Number(workerCommissionValue ?? commissionValue ?? payment?.workerCommissionValue ?? 0) || 0,
        commissionBaseAmount: Number(commissionBaseAmount ?? payment?.commissionBaseAmount ?? 0) || 0,
        commissionIncludesTips: Boolean(commissionIncludesTips ?? payment?.commissionIncludesTips),
        commissionOnlyCompleted: commissionOnlyCompleted !== false,
        workerPayoutAmount: Number(workerPayoutAmount ?? payment?.workerPayoutAmount ?? 0) || 0,
        workerPayoutStatus: workerPayoutStatus || payment?.workerPayoutStatus || "pending",
        workerPaymentNotes: String(workerPaymentNotes || payment?.workerPaymentNotes || "").trim() || null,
        businessNetAmount: businessNetAmount == null ? null : Number(businessNetAmount) || 0,
        payment: payment || {
          status: paymentStatus || "pending",
          method: paymentMethod || "cash",
          amountCharged: Number(amountCharged ?? paidAmount ?? 0) || 0,
          discountAmount: Number(discountAmount || 0) || 0,
          tipAmount: Number(tipAmount || 0) || 0,
          workerCommissionType: workerCommissionType || commissionType || "none",
          commissionType: workerCommissionType || commissionType || "none",
          workerCommissionValue: Number(workerCommissionValue ?? commissionValue ?? 0) || 0,
          commissionValue: Number(workerCommissionValue ?? commissionValue ?? 0) || 0,
          commissionBaseAmount: Number(commissionBaseAmount || 0) || 0,
          commissionIncludesTips: Boolean(commissionIncludesTips),
          commissionOnlyCompleted: commissionOnlyCompleted !== false,
          workerPayoutAmount: Number(workerPayoutAmount || 0) || 0,
          workerPayoutStatus: workerPayoutStatus || "pending",
          workerPaymentNotes: String(workerPaymentNotes || "").trim() || null,
          businessNetAmount: businessNetAmount == null ? null : Number(businessNetAmount) || 0,
        },
        productSales: Array.isArray(productSales) ? productSales : Array.isArray(retailSales) ? retailSales : [],
        retailSales: Array.isArray(retailSales) && retailSales.length ? retailSales : Array.isArray(productSales) ? productSales : [],
        productSalesTotalAmount: Number(productSalesTotalAmount || retailSalesTotalAmount || 0) || 0,
        retailSalesTotalAmount: Number(retailSalesTotalAmount || productSalesTotalAmount || 0) || 0,
        visitMode: visitMode || visitTypeLocation || (customerLocation ? "delivery" : "onsite"),
        visitTypeLocation: visitTypeLocation || visitMode || (customerLocation ? "delivery" : "onsite"),
        customerLocation: customerLocation || null,
        serviceAreaMatched: serviceAreaMatched || null,
        customerExactAddress: String(customerExactAddress || customerAddressDetails || "").trim() || null,
        customerAddressDetails: String(customerAddressDetails || customerExactAddress || "").trim() || null,
        deliveryFee: Number(deliveryFee || 0),
        locationFee: Number(locationFee || deliveryFee || 0),
        deliverySummaryLabel: adminBookingLocationSummary({
          visitMode,
          visitTypeLocation,
          customerLocation,
          serviceAreaMatched,
          customerExactAddress,
          customerAddressDetails,
          deliveryFee: Number(deliveryFee || 0),
          locationFee: Number(locationFee || deliveryFee || 0),
        }).compact || null,
        assignedEmployeeIds: Array.isArray(assignedEmployeeIds) ? assignedEmployeeIds : [],
        assignedEmployees: Array.isArray(assignedEmployees) ? assignedEmployees : [],
        notes: String(notes || internalNotes || "").trim() || null,
        internalNotes: String(internalNotes || notes || "").trim() || null,
        updatedAt: serverTimestamp(),
        lastEditedAt: id ? serverTimestamp() : null,
        modifiedByUid: getEffectiveActor().uid || null,
        editedVia: id ? "adminBookingModal" : null,
      };

      const fallbackWorkerEmailForPayload = createWorkerEmailFallback || normalizeEmailForWorkerVisibility(getEffectiveActor().email || "");
      if (!noWorkerCompanyBooking && !isEventBusinessAdmin && (safeResourceIdForBooking || fallbackWorkerEmailForPayload)) {
        const cleanResourceId = safeResourceIdForBooking ? String(safeResourceIdForBooking) : "";
        const emp = cleanResourceId ? employees.find((e) => String(e.id) === cleanResourceId) : null;
        if (emp) {
          payload.employeeId = String(emp.id || cleanResourceId);
          const empUid = emp.userUid || emp.userId || emp.employeeUserUid || emp.employeeUserId || emp.employeeUid || null;
          payload.employeeEmail = emp.email || emp.employeeEmail || emp.workerEmail || emp.inviteEmail || fallbackWorkerEmailForPayload || null;
          payload.employeeUserUid = empUid || null;
          payload.employeeUserId = empUid || null;
          payload.employeeName = emp.name || emp.displayName || null;
        } else {
          payload.employeeId = cleanResourceId && !cleanResourceId.includes("@") ? cleanResourceId : null;
          payload.employeeEmail = fallbackWorkerEmailForPayload || (cleanResourceId.includes("@") ? cleanResourceId : null);
          payload.employeeEmailLower = payload.employeeEmail ? String(payload.employeeEmail).toLowerCase() : null;
          payload.employeeName = resolvedWorkerEmployeeForPermissions?.name || currentWorkerEmployee?.name || null;
        }
      } else {
        payload.employeeId = null;
        payload.employeeEmail = null;
        payload.employeeName = null;
      }

      if (serviceId) {
        payload.serviceId = serviceId;
        const svc = services.find((s) => String(s.id) === String(serviceId));
        if (svc) {
          payload.serviceName = svc.name || svc.title || svc.id;
          payload.allowConcurrentSameService = serviceAllowsConcurrentSameServiceAdmin(svc);
          payload.sameServiceCanOverlap = serviceAllowsConcurrentSameServiceAdmin(svc);
          payload.uniqueResource = !serviceAllowsConcurrentSameServiceAdmin(svc);
        }
      } else {
        payload.serviceId = null;
        payload.serviceName = null;
      }

      const normalizedNextStatusForCancellation = String(payload.status || payload.confirmationStatus || "").trim().toLowerCase();
      const isCancellationStatusForPayload = ["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "deleted", "eliminada"].includes(normalizedNextStatusForCancellation);
      if (id && (deleteReservationIntent === true || isCancellationStatusForPayload)) {
        const isDeleteIntent = deleteReservationIntent === true || ["deleted", "eliminada"].includes(normalizedNextStatusForCancellation);
        if (isDeleteIntent) {
          payload.status = "deleted";
          payload.confirmationStatus = "deleted";
          payload.calendarHidden = true;
          payload.hiddenFromCalendar = true;
          payload.removedFromAgenda = true;
          payload.deletedAt = serverTimestamp();
          payload.deletedByUid = getEffectiveActor().uid || null;
          payload.deletedByEmail = getEffectiveActor().email || null;
          payload.deletedByRole = ownerOrAdmin ? "owner" : "worker";
        }
        payload.cancellationSource = cancellationSource || (isDeleteIntent ? "admin_booking_modal_delete_state" : "admin_booking_modal_status");
        payload.cancellationReason = String(cancellationReason || (isDeleteIntent ? "Eliminada desde agenda" : "Cancelada desde agenda")).trim();
        payload.cancelledAt = payload.cancelledAt || serverTimestamp();
        payload.cancelledByUid = getEffectiveActor().uid || null;
        payload.cancelledByEmail = getEffectiveActor().email || null;
        payload.cancelledByRole = ownerOrAdmin ? "owner" : "worker";
        payload.deleteReservationIntent = Boolean(isDeleteIntent);
        payload.notifyCustomerCancellation = notifyCustomerCancellation !== false;
      }

      const normalizedNextStatusForCompletion = adminStatusFlowNormalize(payload.status || payload.confirmationStatus || "");
      if (normalizedNextStatusForCompletion === "completed") {
        payload.completedAt = payload.completedAt || serverTimestamp();
        payload.completedByUid = getEffectiveActor().uid || null;
        payload.completedByEmail = getEffectiveActor().email || null;
        payload.completedByRole = ownerOrAdmin ? "owner" : "worker";
      }

      // LOOP PHASE 2.5C — canonical lifecycle/payment fields before every booking write.
      Object.assign(payload, buildBookingLifecyclePatch({ ...(id ? { id } : {}), ...payload }, payload));

      // Delete/agenda-hide is not a normal lifecycle state. Re-assert after canonical patch
      // so helpers cannot map it back to cancelled/confirmed and make it reappear visually.
      if (id && (deleteReservationIntent === true || payload.removedFromAgenda === true || payload.status === "deleted")) {
        payload.status = "deleted";
        payload.confirmationStatus = "deleted";
        payload.calendarHidden = true;
        payload.hiddenFromCalendar = true;
        payload.removedFromAgenda = true;
        payload.deleteReservationIntent = true;
        payload.statusLabel = adminStatusFlowMeta("deleted").label;
        payload.statusTone = adminStatusFlowMeta("deleted").tone;
        setLocallyHiddenBookingIds((prev) => {
          const next = new Set(prev || []);
          next.add(String(id));
          return next;
        });
      }

      if (id) {
        if(collectionApplied || priceAdjustmentDraft) {
          if(!(ownerOrAdmin || actorBusinessPermissions.canEditPayments)) throw new Error("No tienes permiso para registrar cobros.");
          await saveStaffScheduleDocSafe(activeBusinessId,id,payload,{
            operation:"edit",saveDetails:true,
            financialDraft:{
              eventId:collectionEventId || `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              collectionApplied:Boolean(collectionApplied),
              basePaidCents:Math.round(Number(collectionBasePaidAmount)*100),
              priceAdjustment:priceAdjustmentDraft || null,
            },
          });
        } else await updateBookingDocSafe(activeBusinessId, id, payload);
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdByUid = getEffectiveActor().uid || null;
        payload.createdByEmail = getEffectiveActor().email || null;
        payload.createdByOwner = ownerOrAdmin;
        payload.createdByWorker = !ownerOrAdmin;
        payload.createdByWorkerEmail = !ownerOrAdmin ? (createWorkerEmailFallback || normalizeEmailForWorkerVisibility(getEffectiveActor().email || "") || null) : null;

        // LOOP PHASE 1 HOTFIX V9 — final backend-rule alignment for worker manual create.
        // Ensure the actual write payload matches firestore.rules canWorkerCreateBookingForOwnEmail().
        if (!ownerOrAdmin) {
          const workerEmailForWrite = normalizeEmailForWorkerVisibility(payload.createdByWorkerEmail || payload.employeeEmail || getEffectiveActor().email || "");
          payload.createdByRole = "worker";
          payload.adminCreated = false;
          payload.createdByOwner = false;
          payload.createdByWorker = true;
          payload.createdByWorkerEmail = workerEmailForWrite || null;
          payload.employeeEmail = normalizeEmailForWorkerVisibility(payload.employeeEmail || workerEmailForWrite || "") || null;
          payload.employeeEmailLower = payload.employeeEmail || null;
          if (!payload.employeeName && workerEmailForWrite) payload.employeeName = workerEmailForWrite.split("@")[0];
          if (!payload.requestedWorkerMode || payload.requestedWorkerMode === "any") payload.requestedWorkerMode = "specific";
          if (!payload.assignmentStatus || payload.assignmentStatus === "unassigned") payload.assignmentStatus = "assigned";
        }

        const actorForRequest = getEffectiveActor();
        const staffAttemptFingerprint = loopAdminStaffBookingAttemptFingerprint({
          businessId: activeBusinessId,
          actorUid: actorForRequest.uid || "",
          payload,
        });
        const previousStaffAttempt = staffBookingCreateAttemptRef.current;
        let staffClientRequestId = "";

        if (
          previousStaffAttempt &&
          previousStaffAttempt.fingerprint === staffAttemptFingerprint &&
          previousStaffAttempt.requestId
        ) {
          staffClientRequestId = previousStaffAttempt.requestId;
        } else {
          staffClientRequestId = loopAdminCreateStaffRequestId({
            businessId: activeBusinessId,
            actorUid: actorForRequest.uid || "",
          });
          staffBookingCreateAttemptRef.current = {
            fingerprint: staffAttemptFingerprint,
            requestId: staffClientRequestId,
            createdAt: Date.now(),
          };
        }

        payload.clientRequestId = staffClientRequestId;
        payload.clientRequestKey = staffClientRequestId;
        payload.bookingRequestClientVersion = "r33_p2";

        try {
          if (typeof window !== "undefined") {
            window.__loopLastBookingCreatePayloadDebug = {
              employeeId: payload.employeeId || null,
              employeeEmail: payload.employeeEmail || null,
              employeeEmailLower: payload.employeeEmailLower || null,
              createdByRole: payload.createdByRole || null,
              createdByWorker: payload.createdByWorker === true,
              createdByWorkerEmail: payload.createdByWorkerEmail || null,
              status: payload.status || null,
              confirmationStatus: payload.confirmationStatus || null,
              businessId: activeBusinessId || null,
              customerId: payload.customerId || payload.clientId || null,
              customerName: payload.customerName || payload.clientName || null,
              hasCustomerSnapshot: Boolean(payload.customerSnapshot),
              customerSnapshotId: payload.customerSnapshot?.customerId || payload.customerSnapshot?.id || null,
              linkedUserUid: payload.linkedUserUid || payload.customerUserUid || payload.customerSnapshot?.linkedUserUid || payload.customerSnapshot?.userUid || null,
              hasCustomerEmail: Boolean(payload.customerEmail || payload.customerEmailNormalized || payload.customerSnapshot?.email || payload.customerSnapshot?.emailNormalized),
              hasCustomerPhone: Boolean(payload.customerPhone || payload.customerPhoneNormalized || payload.customerSnapshot?.phone || payload.customerSnapshot?.phoneNormalized || payload.customerSnapshot?.phoneE164),
            };
          }
        } catch (_) {}

        if (!ownerOrAdmin) {
          // LOOP WORKER CREATE SMOOTH V9B
          // Do not keep the worker staring at the create modal while the callable,
          // triggers and Firestore listener finish syncing. Paint an optimistic
          // booking immediately, close the modal, then reconcile with the real id.
          const tempBookingId = `worker-optimistic-${Date.now()}`;
          const optimisticBookingBase = {
            ...payload,
            id: tempBookingId,
            bookingId: tempBookingId,
            docId: tempBookingId,
            businessId: activeBusinessId,
            _optimisticBooking: true,
            _optimisticPendingServer: true,
            optimisticCreatedAt: new Date().toISOString(),
          };
          setOptimisticBookings((prev) => {
            const rows = Array.isArray(prev) ? prev : [];
            const next = rows.filter((row) => !adminBookingIdCandidates(row).includes(tempBookingId));
            return [...next, optimisticBookingBase].slice(-10);
          });
          closeCreateModalAfterSave();
          setStatusWorkflowNotice({ title: "Guardando reserva", message: "Esperando confirmación..." });
          if (typeof window !== "undefined") {
            window.__LOOP_WORKER_CREATE_CLOSE_DEBUG__ = {
              step: "closed_before_worker_create_callable",
              bookingId: tempBookingId,
              employeeId: payload.employeeId || null,
              customerId: payload.customerId || payload.clientId || null,
              at: new Date().toISOString(),
            };
            window.setTimeout(() => setStatusWorkflowNotice(null), 2800);
          }
          triggerBookingAgendaRefresh("worker_create_started", { bookingId: tempBookingId, employeeId: payload.employeeId || null });

          let workerCreateResult = null;
          let createdBookingId = tempBookingId;
          try {
            workerCreateResult = await createStaffBookingDocSafe(activeBusinessId, payload);
            createdBookingId = String(workerCreateResult?.id || workerCreateResult?.data?.bookingId || workerCreateResult?.data?.id || tempBookingId).trim() || tempBookingId;
            if (
              staffBookingCreateAttemptRef.current &&
              staffBookingCreateAttemptRef.current.requestId === payload.clientRequestId
            ) {
              staffBookingCreateAttemptRef.current = null;
            }
          } catch (workerCreateErr) {
            setOptimisticBookings((prev) => (Array.isArray(prev) ? prev : []).filter((row) => !adminBookingIdCandidates(row).includes(tempBookingId)));
            setStatusWorkflowNotice({ title: "No se pudo crear", message: workerCreateErr?.message || "Intenta nuevamente." });
            if (typeof window !== "undefined") {
              window.__LOOP_WORKER_CREATE_CLOSE_DEBUG__ = {
                step: "worker_create_callable_failed",
                bookingId: tempBookingId,
                employeeId: payload.employeeId || null,
                customerId: payload.customerId || payload.clientId || null,
                error: workerCreateErr?.message || String(workerCreateErr || ""),
                at: new Date().toISOString(),
              };
              window.setTimeout(() => setStatusWorkflowNotice(null), 3800);
            }
            triggerBookingAgendaRefresh("worker_create_failed", { bookingId: tempBookingId, employeeId: payload.employeeId || null });
            throw workerCreateErr;
          }

          setOptimisticBookings((prev) => {
            const rows = Array.isArray(prev) ? prev : [];
            return rows.map((row) => {
              if (!adminBookingIdCandidates(row).includes(tempBookingId)) return row;
              return {
                ...row,
                id: createdBookingId,
                bookingId: createdBookingId,
                docId: createdBookingId,
                _optimisticPendingServer: false,
                optimisticResolvedAt: new Date().toISOString(),
              };
            });
          });
          if (typeof window !== "undefined") {
            window.__LOOP_WORKER_CREATE_CLOSE_DEBUG__ = {
              step: "worker_create_callable_resolved",
              bookingId: createdBookingId,
              optimisticBookingId: tempBookingId,
              employeeId: payload.employeeId || null,
              customerId: payload.customerId || payload.clientId || null,
              at: new Date().toISOString(),
            };
          }
          if (services.some(service=>String(service.id)===String(payload.serviceId) && service.sessionPackage?.enabled)) void showSessionReceipt(workerCreateResult);
          triggerBookingAgendaRefresh("worker_create_success", { bookingId: createdBookingId, optimisticBookingId: tempBookingId, employeeId: payload.employeeId || null });
        } else {
          const managerCreateResult = await createStaffBookingDocSafe(activeBusinessId, payload);
          if (
            staffBookingCreateAttemptRef.current &&
            staffBookingCreateAttemptRef.current.requestId === payload.clientRequestId
          ) {
            staffBookingCreateAttemptRef.current = null;
          }
          if (services.some(service=>String(service.id)===String(payload.serviceId) && service.sessionPackage?.enabled)) void showSessionReceipt(managerCreateResult);
          triggerBookingAgendaRefresh("owner_create_success", {
            bookingId:
              managerCreateResult?.id ||
              managerCreateResult?.data?.bookingId ||
              null,
            employeeId: payload.employeeId || null,
            duplicate:
              managerCreateResult?.data?.duplicate === true,
          });
        }
      }
      if (id) triggerBookingAgendaRefresh("booking_update_success", { bookingId: id });
    },
    [activeBusinessId, createModalDate, employees, services, triggerBookingAgendaRefresh, isEventBusinessAdmin, createWorkerEmailFallback, resolvedWorkerEmployeeForPermissions, currentWorkerEmployee, ownerOrAdmin, calendarShouldUseCompanyNoWorkers, webHasEmployees, agendaRequiresWorkerSetup, closeCreateModalAfterSave, showSessionReceipt]
  );

  const handleEventClick = (info) => {
    const raw = info?.event?.extendedProps?.raw || info?.event?.raw || info?.event?.booking || null;
    if (!raw) return;
    // LOOP DIRECT EDIT ON EVENT CLICK HOTFIX:
    // Clicking a reservation now opens the same create/edit modal directly.
    openEditForBooking(raw);
  };

  const openEditForBooking = (b) => {
    if (!b) return;
    if (!canEditBooking) { alert("No tienes permiso para editar reservas."); return; }
    setEditBooking(b);
    const d = bookingScheduledAtToDate(b) || startOfDay(new Date());
    setCreateModalDate(d);
    setCreateModalResourceId(b.employeeId || null);
    setCreateModalOpen(true);
  };



  const openManualEditForBooking = (b) => {
    if (!b) return;
    if (!canEditBooking) { alert("No tienes permiso para editar reservas."); return; }
    setManualEditBooking(b);
    setManualEditDraft(adminManualBookingToDraft(b));
    setEventModalBooking(null);
  };

  const closeManualEdit = () => {
    if (manualEditSaving) return;
    setManualEditBooking(null);
    setManualEditDraft(null);
  };

  const updateManualEditDraft = (key, value) => {
    setManualEditDraft((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const handleRescheduleBooking = async (booking, draft = {}) => {
    if (!activeBusinessId || !booking?.id) return;
    if (!canEditBooking) { alert("No tienes permiso para reprogramar reservas."); return; }
    setRescheduleSaving(true);
    try {
      const start = draft.start ? new Date(draft.start) : adminSmartBuildDate(draft.date, draft.time);
      const duration = Math.max(15, Number(draft.duration || booking.durationMinutes || booking.duration || 60) || 60);
      const end = draft.end ? new Date(draft.end) : new Date(start);
      if (!draft.end) end.setMinutes(end.getMinutes() + duration);
      const employeeId = draft.employeeId || null;
      const employee = employeeId ? employees.find((emp) => String(emp.id) === String(employeeId)) : null;
      const dateValue = adminSmartDateInputValue(start);
      const timeValue = adminSmartTimeInputValue(start);
      const payload = {
        scheduledAt: Timestamp.fromDate(start),
        startAt: Timestamp.fromDate(start),
        start: Timestamp.fromDate(start),
        endAt: Timestamp.fromDate(end),
        end: Timestamp.fromDate(end),
        date: dateValue,
        time: timeValue,
        duration,
        durationMinutes: duration,
        durationMins: duration,
        minutes: duration,
        employeeId,
        workerId: employeeId,
        resourceId: employeeId,
        employeeName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
        workerName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
        updatedAt: serverTimestamp(),
        rescheduledAt: serverTimestamp(),
        rescheduledFrom: {
          scheduledAt: booking.scheduledAt || booking.startAt || booking.start || null,
          date: booking.date || null,
          time: booking.time || null,
          employeeId: booking.employeeId || booking.workerId || booking.resourceId || null,
        },
        rescheduleSummary: {
          source: "event_view_modal",
          changedAtClient: new Date().toISOString(),
          date: dateValue,
          time: timeValue,
          duration,
          targetWorkerId: employeeId,
          targetWorkerName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
          conflictCount: Number(draft.conflictCount || 0),
        },
      };
      await updateBookingDocSafe(activeBusinessId, booking.id, payload);
      setEventModalBooking((prev) => prev && String(prev.id) === String(booking.id) ? { ...prev, ...payload, id: booking.id } : prev);
      setReloadBookingsKey((k) => k + 1);
      setRescheduleNotice({ title: "Reserva reprogramada", message: `${booking.customerName || booking.clientName || "Cliente"} · ${timeValue}` });
      window.setTimeout(() => setRescheduleNotice(null), 3200);
    } catch (err) {
      if (err?.code === "schedule-exception-cancelled") return;
      console.error("[AdminCalendar] reschedule failed", err);
      alert("No pude reprogramar la reserva. Intenta nuevamente.");
    } finally {
      setRescheduleSaving(false);
    }
  };

  const saveManualEditBooking = async () => {
    if (!activeBusinessId || !manualEditBooking?.id || !manualEditDraft) return;
    setManualEditSaving(true);
    try {
      const start = adminManualBuildDate(manualEditDraft.date, manualEditDraft.startTime);
      const duration = Math.max(15, Number(manualEditDraft.duration) || 60);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + duration);
      const employee = employees.find((e) => String(e.id) === String(manualEditDraft.employeeId));
      const service = services.find((s) => String(s.id) === String(manualEditDraft.serviceId));
      const priceNumber = Number(manualEditDraft.totalPrice);
      const hasPrice = manualEditDraft.totalPrice !== "" && Number.isFinite(priceNumber);
      const payload = {
        customerName: manualEditDraft.customerName || null,
        clientName: manualEditDraft.customerName || null,
        customerPhone: manualEditDraft.customerPhone || null,
        phone: manualEditDraft.customerPhone || null,
        customerEmail: manualEditDraft.customerEmail || null,
        email: manualEditDraft.customerEmail || null,
        serviceId: manualEditDraft.serviceId || null,
        serviceName: service ? (service.name || service.title || service.id) : manualEditBooking.serviceName || manualEditBooking.service || null,
        employeeId: manualEditDraft.employeeId || null,
        workerId: manualEditDraft.employeeId || null,
        resourceId: manualEditDraft.employeeId || null,
        employeeName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
        workerName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
        scheduledAt: Timestamp.fromDate(start),
        start: Timestamp.fromDate(start),
        end: Timestamp.fromDate(end),
        date: Timestamp.fromDate(start),
        time: manualEditDraft.startTime,
        duration,
        durationMins: duration,
        durationMinutes: duration,
        minutes: duration,
        status: adminStatusFlowNormalize(manualEditDraft.status || "confirmed"),
        confirmationStatus: adminStatusFlowNormalize(manualEditDraft.status || "confirmed"),
        statusLabel: adminStatusFlowMeta(manualEditDraft.status || "confirmed").label,
        statusTone: adminStatusFlowMeta(manualEditDraft.status || "confirmed").tone,
        statusWorkflow: {
          previousStatus: adminStatusFlowCurrent(manualEditBooking),
          currentStatus: adminStatusFlowNormalize(manualEditDraft.status || "confirmed"),
          currentLabel: adminStatusFlowMeta(manualEditDraft.status || "confirmed").label,
          source: "manual_editor",
          changedAtClient: new Date().toISOString(),
        },
        notes: manualEditDraft.notes || null,
        internalNotes: manualEditDraft.notes || null,
        updatedAt: serverTimestamp(),
        manualEditedAt: serverTimestamp(),
        manualEditSummary: {
          source: "admin_manual_editor",
          editedAtClient: new Date().toISOString(),
          targetWorkerId: manualEditDraft.employeeId || null,
          targetWorkerName: employee ? (employee.name || employee.displayName || "Trabajador") : null,
          serviceName: service ? (service.name || service.title || service.id) : null,
          date: manualEditDraft.date,
          time: manualEditDraft.startTime,
          duration,
        },
      };
      if (hasPrice) {
        payload.totalPrice = priceNumber;
        payload.total = priceNumber;
        payload.price = priceNumber;
      }
      // LOOP PHASE 2.9 — manual editor also refreshes canonical lifecycle before inventory automation.
      Object.assign(payload, buildBookingLifecyclePatch(manualEditBooking, payload));
      const actor = getEffectiveActor();
      payload.modifiedByUid = actor.uid || null;
      payload.modifiedByEmail = actor.email || null;
      const autoDeductionPayload = await applyAutoInventoryDeductionForBooking(manualEditBooking, payload, actor);
      if (autoDeductionPayload) Object.assign(payload, autoDeductionPayload);
      await updateBookingDocSafe(activeBusinessId, manualEditBooking.id, payload);
      setManualEditNotice({ title: "Reserva actualizada", message: `${manualEditDraft.customerName || "Cliente"} · ${adminManualStatusLabel(manualEditDraft.status)}` });
      setManualEditBooking(null);
      setManualEditDraft(null);
      setReloadBookingsKey((k) => k + 1);
      window.setTimeout(() => setManualEditNotice(null), 3200);
    } catch (err) {
      if (err?.code === "schedule-exception-cancelled") return;
      console.error("[AdminCalendar] manual edit failed", err);
      alert("No pude guardar los cambios de la reserva. Intenta nuevamente.");
    } finally {
      setManualEditSaving(false);
    }
  };



  const registerBookingCommunication = useCallback(async (booking, template, channel = "copy") => {
    if (!activeBusinessId || !booking?.id || !template?.id) return;
    try {
      const actor = getEffectiveActor();
      await updateBookingDocSafe(activeBusinessId, booking.id, {
        updatedAt: serverTimestamp(),
        communicationLastActionAt: serverTimestamp(),
        communicationHistoryLast: {
          type: template.id,
          label: template.label,
          channel,
          status: channel === "email" ? "prepared" : "copied",
          source: "admin_booking_detail",
          createdAtClient: new Date().toISOString(),
          createdByUid: actor.uid || null,
          createdByEmail: actor.email || null,
        },
      });
      setReloadBookingsKey((k) => k + 1);
    } catch (err) {
      console.warn("[AdminCalendar] communication log skipped", err);
    }
  }, [activeBusinessId, getEffectiveActor, setReloadBookingsKey]);

  const copyBookingCommunicationMessage = useCallback(async (booking, template) => {
    if (!template?.body) return;
    setCommunicationSaving(true);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(template.body);
      } else {
        window.prompt("Copia este mensaje", template.body);
      }
      await registerBookingCommunication(booking, template, "copy");
      setCommunicationNotice({ title: "Mensaje copiado", message: `${template.label} listo para pegar.` });
      window.setTimeout(() => setCommunicationNotice(null), 2800);
    } catch (err) {
      console.error("[AdminCalendar] copy communication failed", err);
      alert("No pude copiar el mensaje. Intenta nuevamente.");
    } finally {
      setCommunicationSaving(false);
    }
  }, [registerBookingCommunication]);

  const openBookingEmailDraft = useCallback(async (booking, template) => {
    const email = adminCommunicationEmail(booking);
    if (!email) {
      setCommunicationNotice({ title: "Falta email", message: "Agrega un email al cliente para preparar este mensaje." });
      window.setTimeout(() => setCommunicationNotice(null), 3200);
      return;
    }
    setCommunicationSaving(true);
    try {
      await registerBookingCommunication(booking, template, "email");
      const href = adminCommunicationMailto(email, template);
      if (href) window.location.href = href;
      setCommunicationNotice({ title: "Email preparado", message: `${template.label} abierto en tu app de correo.` });
      window.setTimeout(() => setCommunicationNotice(null), 3200);
    } catch (err) {
      console.error("[AdminCalendar] email communication failed", err);
      alert("No pude preparar el email. Intenta nuevamente.");
    } finally {
      setCommunicationSaving(false);
    }
  }, [registerBookingCommunication]);

  const applyAutoInventoryDeductionForBooking = useCallback(async (booking, patch = {}, actor = getEffectiveActor()) => {
    if (!activeBusinessId || !booking?.id) return null;
    const bookingForDeduction = {
      ...(booking || {}),
      ...(patch || {}),
      payment: {
        ...(booking?.payment || {}),
        ...((patch && patch.payment && typeof patch.payment === "object") ? patch.payment : {}),
        status: patch?.paymentStatus || patch?.payment?.status || booking?.payment?.status,
      },
    };

    if (!adminShouldAutoDeductInventory(bookingForDeduction, loopBusinessSettings)) {
      return {
        inventoryAutoDeductCheckedAt: serverTimestamp(),
        inventoryAutoDeductStatus: "waiting_trigger",
        inventoryAutoDeductTrigger: adminInventoryDeductTrigger(loopBusinessSettings),
      };
    }

    const deductionPayload = {
      inventoryAutoDeductCheckedAt: serverTimestamp(),
      inventoryAutoDeductTrigger: adminInventoryDeductTrigger(loopBusinessSettings),
      inventoryAutoDeductStatus: "triggered",
    };

    try {
      const servicePayload = await adminApplyInventoryConsumption({
        dbRef: db,
        businessId: activeBusinessId,
        booking: bookingForDeduction,
        services,
        actor,
        loopSettings: loopBusinessSettings,
      });
      if (servicePayload) Object.assign(deductionPayload, servicePayload);
    } catch (serviceErr) {
      console.warn("[AdminCalendar] service inventory deduction failed", serviceErr);
      deductionPayload.inventoryAutoDeductStatus = "error";
      deductionPayload.inventoryDeductionStatus = "error";
      deductionPayload.inventoryDeductionError = String(serviceErr?.message || serviceErr || "inventory_error").slice(0, 300);
      if (serviceErr?.code === "stock_insufficient") {
        deductionPayload.inventoryStockInsufficient = true;
      }
    }

    try {
      if (adminBookingHasProductSales(bookingForDeduction) && !adminProductSalesAlreadyDeducted(bookingForDeduction)) {
        const productPayload = await adminApplyProductSales({
          dbRef: db,
          businessId: activeBusinessId,
          booking: bookingForDeduction,
          productSales: Array.isArray(bookingForDeduction.productSales) ? bookingForDeduction.productSales : bookingForDeduction.retailSales || [],
          actor,
          loopSettings: loopBusinessSettings,
        });
        if (productPayload) Object.assign(deductionPayload, productPayload);
      }
    } catch (productErr) {
      console.warn("[AdminCalendar] product sales deduction failed", productErr);
      deductionPayload.inventoryAutoDeductStatus = "error";
      deductionPayload.productSalesDeductionStatus = "error";
      deductionPayload.productSalesDeductionError = String(productErr?.message || productErr || "product_sales_error").slice(0, 300);
      if (productErr?.code === "stock_insufficient") {
        deductionPayload.productSalesStockInsufficient = true;
      }
    }

    if (deductionPayload.inventoryAutoDeductStatus === "triggered") {
      deductionPayload.inventoryAutoDeductStatus = "completed";
    }
    return deductionPayload;
  }, [activeBusinessId, getEffectiveActor, loopBusinessSettings, services]);

  const updateBookingStatusWorkflow = useCallback(async (booking, nextStatus, source = "quick_status") => {
    if (!activeBusinessId || !booking?.id) return;
    if (!canEditBooking && !ownerOrAdmin) {
      alert("No tienes permiso para cambiar el estado de la reserva.");
      return;
    }
    const normalized = adminStatusFlowNormalize(nextStatus);
    if (normalized === "completed" && !canCompleteBooking) {
      alert("No tienes permiso para marcar reservas como completadas.");
      return;
    }
    const meta = adminStatusFlowMeta(normalized);
    const previousStatus = adminStatusFlowCurrent(booking);
    if (previousStatus === normalized && source !== "force") {
      setStatusWorkflowNotice({ title: "Estado sin cambios", message: `${getBookingClientName(booking)} ya está como ${meta.label}.` });
      window.setTimeout(() => setStatusWorkflowNotice(null), 2600);
      return;
    }
    setStatusWorkflowSaving(true);
    try {
      const actor = getEffectiveActor();
      const timestampField = adminStatusFlowTimestampField(normalized);
      if (isApprovalFirstEvent(booking) && normalized === "confirmed") {
        await approveEventBooking(activeBusinessId, booking.id);
        setReloadBookingsKey((k) => k + 1);
        setEventModalBooking(null);
        return;
      }
      const payload = {
        status: normalized,
        confirmationStatus: normalized,
        statusLabel: meta.label,
        statusTone: meta.tone,
        updatedAt: serverTimestamp(),
        statusChangedAt: serverTimestamp(),
        [timestampField]: serverTimestamp(),
        modifiedByUid: actor.uid || null,
        modifiedByEmail: actor.email || null,
        statusWorkflow: {
          previousStatus,
          previousLabel: adminStatusFlowMeta(previousStatus).label,
          currentStatus: normalized,
          currentLabel: meta.label,
          currentTone: meta.tone,
          source,
          changedAtClient: new Date().toISOString(),
          changedByUid: actor.uid || null,
          changedByEmail: actor.email || null,
        },
        statusHistoryLast: {
          from: previousStatus,
          to: normalized,
          label: meta.label,
          source,
          changedAtClient: new Date().toISOString(),
          changedByUid: actor.uid || null,
          changedByEmail: actor.email || null,
        },
      };
      const snapshotEmail = String(booking?.customerSnapshot?.email || booking?.customer?.email || "").trim().toLowerCase();
      if (snapshotEmail && !booking.customerEmail && !booking.userEmail && !booking.clientEmail) {
        payload.customerEmail = snapshotEmail;
      }
      const snapshotUid = String(booking?.customerSnapshot?.linkedUserUid || booking?.customerSnapshot?.userUid || booking?.customerSnapshot?.uid || booking?.customer?.userUid || "").trim();
      if (snapshotUid && !booking.customerUid && !booking.userUid && !booking.clientUid) {
        payload.customerUid = snapshotUid;
      }
      if (normalized === "confirmed") {
        payload.acceptedAt = serverTimestamp();
        payload.assignmentStatus = bookingCanBeAcceptedWithoutWorker(booking, isEventBusinessAdmin) ? "not_required" : (booking.assignmentStatus || "assigned");
      }
      if (normalized === "completed" && !booking.paymentStatus && !booking?.payment?.status) {
        payload.paymentStatus = "pending";
        payload.paymentMethod = booking.paymentMethod || "cash";
      }
      // LOOP PHASE 2.5C — status drives canonical booking + attendance lifecycle.
      Object.assign(payload, buildBookingStatusActionPatch({
        booking,
        nextStatus: normalized,
        source,
        actor,
        statusMeta: meta,
        previousStatus,
        basePayload: payload,
      }));
      if (!ownerOrAdmin && hardWorkerView) {
        await updateWorkerBookingStatusDocSafe(activeBusinessId, booking, payload, {
          status: normalized,
          action: source || "worker_status_flow",
        });
      } else {
        const autoDeductionPayload = await applyAutoInventoryDeductionForBooking(booking, payload, actor);
        if (autoDeductionPayload) Object.assign(payload, autoDeductionPayload);
        await updateBookingDocSafe(activeBusinessId, booking.id, payload);
        await adminWriteAuditLog({
          dbRef: db,
          businessId: activeBusinessId,
          eventType: "booking_status_changed",
          booking,
          actor,
          details: {
            source,
            from: previousStatus,
            to: normalized,
            label: meta.label,
            inventoryAutoDeductStatus: payload.inventoryAutoDeductStatus || null,
            inventoryDeductionStatus: payload.inventoryDeductionStatus || null,
            productSalesDeductionStatus: payload.productSalesDeductionStatus || null,
          },
        });
      }
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking(null);
      setStatusWorkflowNotice({ title: "Estado actualizado", message: `${getBookingClientName(booking)} → ${meta.label}` });
      window.setTimeout(() => setStatusWorkflowNotice(null), 3200);
    } catch (err) {
      if (err?.code === "schedule-exception-cancelled") return;
      console.error("[AdminCalendar] status workflow update failed", err);
      alert("No pude actualizar el estado. Intenta nuevamente.");
    } finally {
      setStatusWorkflowSaving(false);
    }
  }, [activeBusinessId, canEditBooking, canCompleteBooking, ownerOrAdmin, hardWorkerView, getEffectiveActor, isEventBusinessAdmin, applyAutoInventoryDeductionForBooking, setReloadBookingsKey]);


  const updateBookingPayment = useCallback(async (booking, paymentPayload) => {
    if (!activeBusinessId || !booking?.id) return;
    if (!(ownerOrAdmin || actorBusinessPermissions.canEditPayments)) {
      alert("No tienes permiso para editar pagos de la reserva.");
      return;
    }
    setStatusWorkflowSaving(true);
    try {
      const actor = getEffectiveActor();
      const payload = {
        ...(paymentPayload || {}),
        paymentUpdatedAt: serverTimestamp(),
        manualReceiptEventId: `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        updatedAt: serverTimestamp(),
        paymentUpdatedByUid: actor.uid || null,
        paymentUpdatedByEmail: actor.email || null,
      };
      if (paymentPayload?.paymentStatus === "paid") payload.paidAt = serverTimestamp();
      if (paymentPayload?.workerPayoutStatus === "paid") {
        payload.workerPaidAt = serverTimestamp();
        payload.workerPayoutPaidAt = serverTimestamp();
      }
      if (paymentPayload?.workerPayoutStatus) {
        payload.workerPayoutUpdatedAt = serverTimestamp();
      }
      // LOOP R0.2.5 — payment changes go through the shared booking action layer.
      Object.assign(payload, buildBookingPaymentActionPatch({
        booking,
        paymentPayload: payload,
        actor,
      }));
      const autoDeductionPayload = await applyAutoInventoryDeductionForBooking(booking, payload, actor);
      if (autoDeductionPayload) Object.assign(payload, autoDeductionPayload);
      await updateBookingDocSafe(activeBusinessId, booking.id, payload);

      let loyaltyResult = null;
      if (ownerOrAdmin && adminLoyaltyPaymentPayloadShouldProcess(paymentPayload, booking)) {
        loyaltyResult = await adminTriggerBookingLoyaltySafe({
          businessId: activeBusinessId,
          bookingId: booking.id,
          actor,
          source: "agenda_payment_update",
        });
      }

      await adminWriteAuditLog({
        dbRef: db,
        businessId: activeBusinessId,
        eventType: "payment_status_changed",
        booking,
        actor,
        details: {
          paymentStatus: paymentPayload?.paymentStatus || null,
          paymentMethod: paymentPayload?.paymentMethod || null,
          amountCharged: paymentPayload?.amountCharged ?? paymentPayload?.paidAmount ?? null,
          workerPayoutStatus: paymentPayload?.workerPayoutStatus || null,
          inventoryAutoDeductStatus: payload.inventoryAutoDeductStatus || null,
          inventoryDeductionStatus: payload.inventoryDeductionStatus || null,
          productSalesDeductionStatus: payload.productSalesDeductionStatus || null,
          loyaltyResult: loyaltyResult ? {
            ok: loyaltyResult.ok !== false,
            processed: Array.isArray(loyaltyResult.processed) ? loyaltyResult.processed.length : 0,
            skipped: Array.isArray(loyaltyResult.skipped) ? loyaltyResult.skipped.length : 0,
            reason: loyaltyResult.reason || loyaltyResult.error || null,
          } : null,
        },
      });
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking((prev) => prev && prev.id === booking.id ? { ...prev, ...paymentPayload, loyaltyLastResult: loyaltyResult || prev.loyaltyLastResult || null } : prev);
      const loyaltyText = adminBookingLoyaltyResultText(loyaltyResult);
      setStatusWorkflowNotice({
        title: "Pago actualizado",
        message: `${getBookingClientName(booking)} · ${paymentPayload?.paymentStatus || "pago"}${loyaltyText ? ` · ${loyaltyText}` : ""}`,
      });
      window.setTimeout(() => setStatusWorkflowNotice(null), 3600);
    } catch (err) {
      console.error("[AdminCalendar] payment update failed", err);
      alert("No pude actualizar el pago. Intenta nuevamente.");
    } finally {
      setStatusWorkflowSaving(false);
    }
  }, [activeBusinessId, canEditBooking, ownerOrAdmin, actorBusinessPermissions, getEffectiveActor, applyAutoInventoryDeductionForBooking, setReloadBookingsKey]);

  const updateBookingProductSales = useCallback(async (booking, salesPayload) => {
    if (!activeBusinessId || !booking?.id) return;
    const canEditProductSales = ownerOrAdmin || canEditBooking || actorBusinessPermissions.canEditInventory || actorBusinessPermissions.canAdjustInventory || actorBusinessPermissions.canEditPayments;
    if (!canEditProductSales) {
      alert("No tienes permiso para editar productos vendidos.");
      return;
    }
    setStatusWorkflowSaving(true);
    try {
      const actor = getEffectiveActor();
      const payload = await adminApplyProductSales({
        dbRef: db,
        businessId: activeBusinessId,
        booking,
        productSales: salesPayload?.productSales || [],
        actor,
        loopSettings: loopBusinessSettings,
      });
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking((prev) => prev && prev.id === booking.id ? { ...prev, ...(payload || {}) } : prev);
      setStatusWorkflowNotice({ title: "Productos actualizados", message: `${getBookingClientName(booking)} · ${payload?.productSales?.length || 0} producto(s)` });
      window.setTimeout(() => setStatusWorkflowNotice(null), 3200);
    } catch (err) {
      console.error("[AdminCalendar] product sales update failed", err);
      alert("No pude guardar los productos vendidos. Revisa inventario y permisos.");
    } finally {
      setStatusWorkflowSaving(false);
    }
  }, [activeBusinessId, canEditBooking, ownerOrAdmin, actorBusinessPermissions, loopBusinessSettings, getEffectiveActor, setReloadBookingsKey]);

  const handleAcceptBooking = async (b) => {
    if (!activeBusinessId || !b || !b.id) return;
    if (!canAcceptBooking) { alert("No tienes permiso para aceptar reservas."); return; }

    if (isApprovalFirstEvent(b)) {
      if (statusWorkflowSaving) return;
      setStatusWorkflowSaving(true);
      try {
        await approveEventBooking(activeBusinessId, b.id);
        setReloadBookingsKey((k) => k + 1);
        setEventModalBooking(null);
      } catch (error) {
        alert(error?.message || "No pudimos aprobar el evento. Actualiza la agenda e inténtalo de nuevo.");
      } finally { setStatusWorkflowSaving(false); }
      return;
    }

    if (bookingNeedsWorkerBeforeAccept(b, isEventBusinessAdmin)) {
      alert("Para aceptar esta reserva primero debes asignar un trabajador. Te abriré la edición de la reserva para que elijas el trabajador y guardes.");
      setEventModalBooking(null);
      openEditForBooking(b);
      return;
    }

    try {
      const actor = getEffectiveActor();
      const payload = {
        status: "confirmed",
        confirmationStatus: "confirmed",
        statusLabel: adminStatusFlowMeta("confirmed").label,
        statusTone: adminStatusFlowMeta("confirmed").tone,
        statusWorkflow: { previousStatus: adminStatusFlowCurrent(b), currentStatus: "confirmed", currentLabel: adminStatusFlowMeta("confirmed").label, source: "accept_action", changedAtClient: new Date().toISOString() },
        assignmentStatus: bookingCanBeAcceptedWithoutWorker(b, isEventBusinessAdmin) ? "not_required" : "assigned",
        ...adminBookingLocationPayload(b),
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        modifiedByUid: actor.uid || null,
        acceptedByUid: actor.uid || null,
        acceptedByEmail: actor.email || null,
      };
      if (!ownerOrAdmin && hardWorkerView) {
        await updateWorkerBookingStatusDocSafe(activeBusinessId, b, payload, { status: "confirmed", action: "accept_action" });
      } else {
        await updateBookingDocSafe(activeBusinessId, b.id, payload);
      }
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking(null);
    } catch (e) {
      console.error(e);
      if (e?.code === "schedule-exception-cancelled") return;
      alert(`Error aceptando la reserva: ${e?.message || "revisa permisos"}`);
    }
  };

  const handleRejectBooking = async (b) => {
    if (!activeBusinessId || !b || !b.id) return;
    if (!canRejectBooking) { alert("No tienes permiso para rechazar reservas."); return; }
    try {
      const actor = getEffectiveActor();
      const payload = {
        status: "rejected",
        confirmationStatus: "rejected",
        statusLabel: adminStatusFlowMeta("rejected").label,
        statusTone: adminStatusFlowMeta("rejected").tone,
        statusWorkflow: { previousStatus: adminStatusFlowCurrent(b), currentStatus: "rejected", currentLabel: adminStatusFlowMeta("rejected").label, source: "reject_action", changedAtClient: new Date().toISOString() },
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        modifiedByUid: actor.uid || null,
      };
      if (!ownerOrAdmin && hardWorkerView) {
        await updateWorkerBookingStatusDocSafe(activeBusinessId, b, payload, { status: "rejected", action: "reject_action" });
      } else {
        await updateBookingDocSafe(activeBusinessId, b.id, payload);
      }
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking(null);
    } catch (e) {
      console.error(e);
      alert(`Error rechazando la reserva: ${e?.message || "revisa permisos"}`);
    }
  };

  const handleCancelBooking = async (b) => {
    if (!activeBusinessId || !b || !b.id) return;
    if (!canCancelBooking) { alert("No tienes permiso para eliminar reservas."); return; }
    if (!window.confirm("¿Eliminar esta reserva de la agenda? Se libera el espacio visual y se conserva el historial interno.")) return;
    try {
      const actor = getEffectiveActor();
      setLocallyHiddenBookingIds((prev) => {
        const next = new Set(prev || []);
        adminBookingIdCandidates(b).forEach((id) => next.add(id));
        next.add(String(b.id));
        return next;
      });
      const payload = {
        status: "deleted",
        confirmationStatus: "deleted",
        statusLabel: adminStatusFlowMeta("deleted").label,
        statusTone: adminStatusFlowMeta("deleted").tone,
        calendarHidden: true,
        hiddenFromCalendar: true,
        removedFromAgenda: true,
        deleteReservationIntent: true,
        cancellationSource: "event_view_modal_delete",
        cancellationReason: "Eliminada desde agenda",
        statusWorkflow: { previousStatus: adminStatusFlowCurrent(b), currentStatus: "deleted", currentLabel: adminStatusFlowMeta("deleted").label, source: "delete_action", changedAtClient: new Date().toISOString() },
        cancelledAt: serverTimestamp(),
        deletedAt: serverTimestamp(),
        deletedByUid: actor.uid || null,
        deletedByEmail: actor.email || null,
        deletedByRole: ownerOrAdmin ? "owner" : "worker",
        updatedAt: serverTimestamp(),
        modifiedByUid: actor.uid || null,
      };
      if (!ownerOrAdmin && hardWorkerView) {
        await updateWorkerBookingStatusDocSafe(activeBusinessId, b, payload, { status: "deleted", action: "delete_action" });
      } else {
        await updateBookingDocSafe(activeBusinessId, b.id, payload);
      }
      setReloadBookingsKey((k) => k + 1);
      setEventModalBooking(null);
    } catch (e) {
      console.error(e);
      alert(`Error eliminando la reserva: ${e?.message || "revisa permisos"}`);
    }
  };

  const canEdit = canMoveBooking;

  const showScheduleChangeNotice = useCallback((notice = {}) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setScheduleChangeNotice({
      id,
      tone: notice.tone || "success",
      title: notice.title || "Cambio guardado",
      message: notice.message || "",
    });
    window.setTimeout(() => {
      setScheduleChangeNotice((current) => current && current.id === id ? null : current);
    }, notice.duration || 3200);
  }, []);

  const resolveBookingIdForScheduleChange = useCallback((raw = {}, eventLike = null, start = null, end = null, resourceId = "") => {
    const direct = adminBookingMoveIdFromEvent(eventLike, raw);
    if (direct) return direct;

    const targetStart = start ? new Date(start) : adminBookingMoveStart(raw);
    const targetEnd = end ? new Date(end) : adminBookingMoveEnd(raw, targetStart);
    const targetResourceId = String(resourceId || adminBookingMoveResourceIdFromEvent(eventLike, raw) || raw?.employeeId || raw?.resourceId || "").trim();
    const targetClient = String(getBookingClientName(raw, "") || "").trim().toLowerCase();
    const targetService = String(getBookingServiceName(raw, "") || "").trim().toLowerCase();

    const candidates = [
      ...(Array.isArray(visibleBookings) ? visibleBookings : []),
      ...(Array.isArray(visibleAllEvents) ? visibleAllEvents.map((ev) => getBookingRawFromCalendarEvent(ev)) : []),
      ...(Array.isArray(mergedEvents) ? mergedEvents.map((ev) => getBookingRawFromCalendarEvent(ev)) : []),
    ].filter(Boolean);

    let best = null;
    let bestScore = -1;

    for (const candidate of candidates) {
      const id = adminBookingMoveIdFromEvent(null, candidate);
      if (!id) continue;

      const candStart = adminBookingMoveStart(candidate);
      const candEnd = adminBookingMoveEnd(candidate, candStart);
      const candResourceId = String(candidate?.employeeId || candidate?.resourceId || candidate?.workerId || candidate?.assignedEmployeeId || "").trim();
      const candClient = String(getBookingClientName(candidate, "") || "").trim().toLowerCase();
      const candService = String(getBookingServiceName(candidate, "") || "").trim().toLowerCase();

      let score = 0;
      if (targetStart && candStart && Math.abs(candStart.getTime() - targetStart.getTime()) < 2 * 60000) score += 5;
      if (targetEnd && candEnd && Math.abs(candEnd.getTime() - targetEnd.getTime()) < 2 * 60000) score += 2;
      if (targetResourceId && candResourceId && String(targetResourceId) === String(candResourceId)) score += 4;
      if (targetClient && candClient && targetClient === candClient) score += 3;
      if (targetService && candService && targetService === candService) score += 2;

      if (score > bestScore) {
        bestScore = score;
        best = id;
      }
    }

    return bestScore >= 5 ? best : "";
  }, [visibleBookings, visibleAllEvents, mergedEvents]);

  const scheduleWriteInFlight = useRef(false);
  const saveAgendaSchedule = useCallback(async (bookingId, payload) => {
    if (scheduleWriteInFlight.current) throw new Error("Ya se está guardando un cambio.");
    scheduleWriteInFlight.current = true;
    try {
      const summary = payload.scheduleChangeSummary || {};
      return await saveStaffScheduleDocSafe(activeBusinessId,bookingId,payload,{operation:"move",actorEmployeeId:ownerOrAdmin?undefined:effectiveWorkerEmployeeId || currentWorkerEmployee?.id,expected:{start:summary.fromStart,end:summary.fromEnd,employeeId:summary.fromWorkerId}});
    } finally { scheduleWriteInFlight.current = false; }
  }, [ownerOrAdmin, activeBusinessId, effectiveWorkerEmployeeId, currentWorkerEmployee?.id]);

  const confirmAndApplyBookingScheduleChange = useCallback(async ({
    booking,
    eventLike = null,
    oldStart,
    oldEnd,
    newStart,
    newEnd,
    oldResourceId = "",
    newResourceId = "",
    type = "move",
    revert = null,
  }) => {
    const raw = booking || getBookingRawFromCalendarEvent(eventLike) || {};
    const client = getBookingClientName(raw, "Cliente");
    const safeNewStart = newStart ? new Date(newStart) : null;
    const safeNewEnd = newEnd ? new Date(newEnd) : null;
    const safeOldStart = oldStart ? new Date(oldStart) : adminBookingMoveStart(raw);
    const safeOldEnd = oldEnd ? new Date(oldEnd) : adminBookingMoveEnd(raw, safeOldStart);
    const finalResourceId = calendarEmployeeId(newResourceId || oldResourceId || raw.employeeId || raw.resourceId);

    const bookingId = resolveBookingIdForScheduleChange(raw, eventLike, safeOldStart, safeOldEnd, oldResourceId || finalResourceId);

    const closeWithRevert = () => {
      if (typeof revert === "function") revert();
    };

    if (!activeBusinessId || !bookingId) {
      closeWithRevert();
      showScheduleChangeNotice({
        tone: "error",
        title: "No pude guardar el cambio",
        message: "No pude identificar esta reserva. Ábrela una vez desde la agenda e intenta nuevamente.",
        duration: 5200,
      });
      return;
    }

    if (!canMoveBooking) {
      closeWithRevert();
      showScheduleChangeNotice({
        tone: "error",
        title: "Sin permiso para mover reservas",
        message: "Pide a un administrador que ajuste tus permisos de agenda.",
        duration: 5200,
      });
      return;
    }

    if (!validAgendaRange(safeNewStart, safeNewEnd)) {
      closeWithRevert();
      showScheduleChangeNotice({
        tone: "error",
        title: "Hora inválida",
        message: "La hora final debe ser posterior a la inicial. Elige otro bloque de la agenda.",
        duration: 4200,
      });
      return;
    }

    const durationMinutes = Math.max(15, Math.round((safeNewEnd.getTime() - safeNewStart.getTime()) / 60000));
    const oldWorkerIdForCompare = String(oldResourceId || raw.employeeId || raw.resourceId || "").trim();
    const sameStart = safeOldStart && Math.abs(safeOldStart.getTime() - safeNewStart.getTime()) < 60000;
    const sameEnd = safeOldEnd && Math.abs(safeOldEnd.getTime() - safeNewEnd.getTime()) < 60000;
    const workerOnlyChange = type === "move" && sameStart && sameEnd && finalResourceId && oldWorkerIdForCompare && String(finalResourceId) !== String(oldWorkerIdForCompare);
    const oldWorker = adminBookingMoveEmployeeLabel(employees, oldResourceId || raw.employeeId || raw.resourceId || "");
    const newWorker = adminBookingMoveEmployeeLabel(employees, finalResourceId);
    const actor = getEffectiveActor();

    const payload = {
      scheduledAt: Timestamp.fromDate(wallToInstant(safeNewStart,businessDoc || {})),
      startAt: Timestamp.fromDate(wallToInstant(safeNewStart,businessDoc || {})),
      endAt: Timestamp.fromDate(wallToInstant(safeNewEnd,businessDoc || {})),
      date: formatDateYmdAdmin(safeNewStart),
      time: adminBookingMoveHHMM(safeNewStart),
      ...scheduleFields(wallToInstant(safeNewStart,businessDoc || {}),businessDoc || {}),
      duration: durationMinutes,
      durationMinutes,
      updatedAt: serverTimestamp(),
      scheduleChangedAt: serverTimestamp(),
      scheduleChangeType: workerOnlyChange ? "worker" : (type === "resize" ? "resize" : "move"),
      modifiedByUid: actor.uid || null,
      modifiedByEmail: actor.email || null,
      scheduleChangeSummary: {
        fromStart: safeOldStart ? wallToInstant(safeOldStart,businessDoc || {}).toISOString() : null,
        fromEnd: safeOldEnd ? wallToInstant(safeOldEnd,businessDoc || {}).toISOString() : null,
        toStart: wallToInstant(safeNewStart,businessDoc || {}).toISOString(),
        toEnd: wallToInstant(safeNewEnd,businessDoc || {}).toISOString(),
        fromWorkerId: oldResourceId || raw.employeeId || raw.resourceId || null,
        toWorkerId: finalResourceId || null,
      },
    };

    if (finalResourceId && !(raw.employeeNotRequired === true || String(raw.assignmentStatus || "").toLowerCase() === "not_required")) {
      payload.employeeId = finalResourceId;
      payload.resourceId = finalResourceId;
      payload.assignmentStatus = "assigned";
      const emp = employees.find((e) => String(e.id) === String(finalResourceId));
      if (emp) {
        const empUid = emp.userUid || emp.userId || emp.employeeUserUid || emp.employeeUserId || emp.employeeUid || null;
        payload.employeeEmail = emp.email || emp.employeeEmail || emp.workerEmail || emp.inviteEmail || null;
        payload.employeeUserUid = empUid || null;
        payload.employeeUserId = empUid || null;
        payload.employeeName = emp.name || emp.displayName || null;
      }
    }

    const warnings = adminBookingMoveBuildWarnings({
      employees,
      services,
      events: mergedEvents,
      bookingId,
      resourceId: finalResourceId,
      start: safeNewStart,
      end: safeNewEnd,
      raw,
      businessDoc,
    });

    if (warnings.some(warning => warning.title === "Servicio no asignado a este trabajador")) {
      setScheduleChangeDialog({
        mode: "confirm",
        bookingId,
        payload,
        type,
        client,
        service: getBookingServiceName(raw, "Reserva"),
        oldRange: safeOldStart ? formatAdminMoveRange(safeOldStart, safeOldEnd, effectiveAgendaSettings.timeFormat) : "—",
        newRange: formatAdminMoveRange(safeNewStart, safeNewEnd, effectiveAgendaSettings.timeFormat),
        oldWorker,
        newWorker,
        workerChange: oldWorker && newWorker && oldWorker !== newWorker ? `${oldWorker} → ${newWorker}` : newWorker,
        durationLabel: `${durationMinutes} min`,
        warnings: warnings.filter(warning => warning.title === "Servicio no asignado a este trabajador"),
        revert,
      });
      return;
    }

    try {
      await saveAgendaSchedule(bookingId, payload);
      setReloadBookingsKey((k) => k + 1);

      const changedWorker = oldWorker && newWorker && oldWorker !== newWorker;
      const message = workerOnlyChange
        ? `${client} ahora está con ${newWorker}.`
        : type === "resize"
          ? `${client} ahora dura ${durationMinutes} min.`
          : changedWorker
            ? `${client}: ${formatAdminMoveRange(safeNewStart, safeNewEnd, effectiveAgendaSettings.timeFormat)} · ${newWorker}.`
            : `${client}: ${formatAdminMoveRange(safeNewStart, safeNewEnd, effectiveAgendaSettings.timeFormat)}.`;

      showScheduleChangeNotice({
        tone: "success",
        title: workerOnlyChange ? "Trabajador actualizado" : type === "resize" ? "Duración actualizada" : "Reserva actualizada",
        message,
      });
    } catch (err) {
      console.error("[AdminBookingCalendar] schedule change failed", err);
      closeWithRevert();
      if (err?.code === 'schedule-exception-cancelled') return;
      showScheduleChangeNotice({
        tone: "error",
        title: "No se pudo actualizar la reserva",
        message: "Revisa la conexión o los permisos e intenta nuevamente.",
        duration: 5200,
      });
    }
  }, [businessDoc, saveAgendaSchedule, activeBusinessId, canMoveBooking, employees, services, mergedEvents, effectiveAgendaSettings.timeFormat, resolveBookingIdForScheduleChange, showScheduleChangeNotice, setReloadBookingsKey]);

  const handleScheduleChangeCancel = useCallback(() => {
    setScheduleChangeDialog((current) => {
      if (current && typeof current.revert === "function") current.revert();
      return null;
    });
  }, []);

  const handleScheduleChangeConfirm = useCallback(async () => {
    const current = scheduleChangeDialog;
    if (!current || current.mode !== "confirm" || !current.bookingId || !current.payload) return;

    try {
      setScheduleChangeSaving(true);
      await saveAgendaSchedule(current.bookingId, current.payload);
      setScheduleChangeDialog(null);
      setReloadBookingsKey((k) => k + 1);
      showScheduleChangeNotice({
        tone: "success",
        title: "Cambio manual guardado",
        message: current.warnings?.length ? "La reserva fue actualizada aunque tenía advertencias." : "La reserva fue actualizada.",
      });
    } catch (err) {
      console.error("[AdminBookingCalendar] schedule change failed", err);
      if (current && typeof current.revert === "function") current.revert();
      if (err?.code === "schedule-exception-cancelled") {setScheduleChangeDialog(null);return;}
      setScheduleChangeDialog({
        mode: "blocked",
        client: current.client,
        service: current.service,
        blockTitle: "No se pudo actualizar la reserva.",
        blockMessage: "Revisa la conexión o los permisos e intenta nuevamente.",
      });
    } finally {
      setScheduleChangeSaving(false);
    }
  }, [saveAgendaSchedule, activeBusinessId, scheduleChangeDialog, setReloadBookingsKey]);

  const handleEventDrop = useCallback((info) => {
    const raw = getBookingRawFromCalendarEvent(info?.event);
    confirmAndApplyBookingScheduleChange({
      booking: raw,
      eventLike: info?.event,
      oldStart: info?.oldEvent?.start || null,
      oldEnd: info?.oldEvent?.end || null,
      newStart: info?.event?.start || null,
      newEnd: info?.event?.end || null,
      oldResourceId: info?.oldResource?.id || adminBookingMoveResourceIdFromEvent(info?.oldEvent, raw),
      newResourceId: info?.newResource?.id || adminBookingMoveResourceIdFromEvent(info?.event, raw),
      type: "move",
      revert: info?.revert ? () => info.revert() : null,
    });
  }, [confirmAndApplyBookingScheduleChange]);

  const handleEventResize = useCallback((info) => {
    const raw = getBookingRawFromCalendarEvent(info?.event);
    confirmAndApplyBookingScheduleChange({
      booking: raw,
      eventLike: info?.event,
      oldStart: info?.oldEvent?.start || null,
      oldEnd: info?.oldEvent?.end || null,
      newStart: info?.event?.start || null,
      newEnd: info?.event?.end || null,
      oldResourceId: adminBookingMoveResourceIdFromEvent(info?.oldEvent, raw),
      newResourceId: adminBookingMoveResourceIdFromEvent(info?.event, raw),
      type: "resize",
      revert: info?.revert ? () => info.revert() : null,
    });
  }, [confirmAndApplyBookingScheduleChange]);

  const licenseHideStyle = `
    .fc .fc-license-message,
    .fc-license-message,
    .fc .fc-scheduler-license-message,
    .fc .fc-license-container,
    .fc .fc-license {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      width: 0 !important;
      overflow: hidden !important;
    }
  `;

  const dynamicResourceHeaderCss = useMemo(() => {
    if (!employeeHeaderColors || Object.keys(employeeHeaderColors).length === 0) return "";
    let css = "";
    for (const [eid, color] of Object.entries(employeeHeaderColors)) {
      const safeId = String(eid).replace(/"/g, "");
      css += `
        .fc .fc-col-header-cell[data-resource-id="${safeId}"] {
          background: ${color} !important;
        }
        .fc .fc-col-header-cell[data-resource-id="${safeId}"] .fc-col-header-cell-cushion {
          color: ${textBlack()} !important;
          font-weight: 900 !important;
        }
      `;
    }
    return css;
  }, [employeeHeaderColors]);

  // ✅ Native tabs padding (safe area + Android nav)
  const nativeTabsBottomPad = isNativeRuntime ? nativeBottomInsetPx : 0;

  // ✅ Multi-select toggle (checkboxes) — used by WEB sidebar
  const toggleEmployeeId = (eid) => {
    const id = String(eid);
    setSelectedEmployeeIds(prev => toggleAgendaWorker(prev, id, agendaAuthorizedEmployees.map(e => String(e.id || e.employeeId || "")).filter(Boolean)));
  };

  const editorNavigation = useEditorNavigation();
  const handleAdminSidebarTab = tab => editorNavigation.navigate(() => handleAdminSidebarTabNow(tab));
  const handleAdminSidebarTabNow = (tab) => {
    if (!tab || !agendaAllowedPanelTabs.includes(tab)) return;
    navigate({ pathname: routeLocation.pathname, search: calendarSectionSearch(routeLocation.search, tab) }, { state: routeLocation.state });
    if (tab === "finance") {
      setAnalyticsInitialView("finance");
      setActiveTab("finance");
      return;
    }
    if (tab === "analytics") {
      setAnalyticsInitialView("overview");
    }
    setActiveTab(tab);
  };

  const handleAdminSidebarItem = (item) => {
    if (!item || !unifiedAgendaMenuItems.some(allowed => allowed.key === item.key)) return;
    if (item.settingsTab) {
      const businessRouteId = String(activeBusinessId || "").trim();
      if (!businessRouteId) return;
      navigate(`/business/${encodeURIComponent(businessRouteId)}/edit?tab=${encodeURIComponent(item.settingsTab)}`);
      return;
    }
    if (item.analyticsView) {
      navigate({ pathname: routeLocation.pathname, search: calendarSectionSearch(routeLocation.search, "analytics", item.analyticsView) }, { state: routeLocation.state });
      setAnalyticsInitialView(item.analyticsView);
      setActiveTab("analytics");
      return;
    }
    handleAdminSidebarTab(item.tab);
  };

  const adminSidebarItemIsActive = (item) => {
    if (!item) return false;
    if (item.settingsTab) return false;
    if (item.analyticsView) {
      if (item.analyticsView === "finance" && activeTab === "finance") return true;
      return activeTab === "analytics" && analyticsInitialView === item.analyticsView;
    }
    return activeTab === item.tab;
  };

  useEffect(() => {
    try {
      const key = 'loop-business-menu:' + (getEffectiveActor().uid || 'guest') + ':' + activeBusinessId;
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved && typeof saved === 'object') setAdminSidebarGroupsOpen(previous => ({...previous,...saved}));
    } catch (_) {}
  }, [activeBusinessId]);

  const toggleAdminSidebarGroup = (groupId) => {
    if (!groupId || sidebarWidth <= 148) return;
    const next = {...adminSidebarGroupsOpen,[groupId]:adminSidebarGroupsOpen?.[groupId] === false};
    setAdminSidebarGroupsOpen(next);
    try { localStorage.setItem('loop-business-menu:' + (getEffectiveActor().uid || 'guest') + ':' + activeBusinessId,JSON.stringify(next)); } catch (_) {}
  };

  // LOOP ADMIN LINKED SECTIONS V58.2 — show contextual header/save in every size for editable linked panels.
  const editableLinkedTabs = false;
  const showLinkedSectionHeader = isNativeRuntime || viewportWidth < 980 || editableLinkedTabs;
  const linkedSectionTitle = {
    pendings: "Reservas",
    clients: "Clientes",
    services: "Servicios",
    workers: "Equipo",
    loyalty: "Fidelidad",
    memberships: "Membresías",
    settings: "Ajustes",
    finance: isWorkerView ? "Mis cobros" : "Finanzas",
    analytics: { crm: "Reporte de clientes", exports: "Exportar", traffic: "Tráfico", insights: "Advisor", overview: "Resumen", finance: "Finanzas", inventory: "Inventario", memberships: "Reporte de membresías" }[analyticsInitialView] || "Reportes",
  }[activeTab] || "Panel";
  const linkedSectionSubtitle = "Espacio de trabajo del negocio";



  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const snapshot = {
      phase: "V84-worker-resource-parity",
      entrypoints: {
        web: "AdminBookingCalendarWeb re-exports AdminBookingCalendar",
        native: "AdminBookingCalendarNative re-exports AdminBookingCalendar",
      },
      activeBusinessId,
      activeTab,
      isNativeRuntime,
      isWorkerView,
      hardWorkerView,
      viewMode,
      effectiveCalendarView,
      agendaUsesResourceDayGrid,
      canUseSoloCompanyAgenda,
      canUseSoloCompanyResourceDay,
      agendaRequiresWorkerSetup,
      legacyUnifiedDayRemoved: true,
      calendarShouldUseCompanyNoWorkers,
      isEventBusinessAdmin,
      mobileAgendaContract: {
        phase: "V84-worker-resource-parity",
        isMobileAgendaSurface,
        expectedDayRenderer: viewMode === "day" ? "LoopResourceDayGrid" : null,
        expectedWeekRenderer: viewMode === "weekCompany" ? "LoopUnifiedWeekView" : null,
        expectedMonthRenderer: viewMode === "month" ? "LoopUnifiedMonthView" : null,
        legacyMobileCollapseRemoved: true,
      },
      weekResourceCount: Array.isArray(weekCalendarResources) ? weekCalendarResources.length : 0,
      weekResourceIds: Array.isArray(weekCalendarResources) ? weekCalendarResources.map((r) => String(r.id || r.resourceId || "")).filter(Boolean) : [],
      resourceCount: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.length : 0,
      resourceIds: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.map((r) => String(r.id || r.resourceId || "")).filter(Boolean) : [],
      resourceNames: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.map((r) => String(r.name || r.displayName || r.title || "")).filter(Boolean) : [],
      resourceHasPhotos: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.map((r) => Boolean(adminWorkerPhotoUrl(r))) : [],
      effectiveAgendaSettings,
      businessCalendarSettings: businessDoc?.calendarSettings || null,
      routeCalendarSettings,
      cachedCalendarSettings,
      routeWorkerProfile: routeWorkerProfile ? { id: routeWorkerProfile.id || routeWorkerProfile.employeeId || "", name: routeWorkerProfile.name || routeWorkerProfile.displayName || "", hasPhoto: Boolean(adminWorkerPhotoUrl(routeWorkerProfile)) } : null,
      visibleEmployees: Array.isArray(nativeVisibleEmployees) ? nativeVisibleEmployees.length : 0,
      totalEmployees: Array.isArray(employees) ? employees.length : 0,
      events: Array.isArray(unifiedAgendaEvents) ? unifiedAgendaEvents.length : 0,
      loading,
    };

    window.__loopAgendaSnapshot = snapshot;
    window.__LOOP_MOBILE_AGENDA_CONTRACT__ = snapshot.mobileAgendaContract;
    window.__loopAgendaAudit = () => {
      const dom = {
        resourceGrid: document.querySelectorAll(".loop-resource-day-shell").length,
        unifiedDay: document.querySelectorAll(".loop-unified-day-shell").length,
        unifiedWeek: document.querySelectorAll(".loop-unified-week-shell,.loop-unified-week-list").length,
        unifiedMonth: document.querySelectorAll(".loop-unified-month-shell").length,
        nativeOldRoot: document.querySelectorAll(".admin-native-root").length,
        fullCalendar: document.querySelectorAll(".fc").length,
        mobileToolbar: document.querySelectorAll(".agenda-web-toolbar").length,
      };
      const rectOf = (selector) => {
        const el = document.querySelector(selector);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { top: Math.round(r.top), bottom: Math.round(r.bottom), height: Math.round(r.height) };
      };
      const appHeader = rectOf("#app-main-header, .loop-ref-header, .loop-mobile-header-v2, header");
      const toolbar = rectOf(".agenda-web-toolbar");
      const resourceHead = rectOf(".loop-resource-day-worker-head, .loop-resource-day-axis-head");
      const toolbarControl = rectOf(".agenda-web-toolbar .agenda-current-date, .agenda-web-toolbar .agenda-nav-btn, .agenda-web-toolbar button");
      const root = rectOf(".admin-calendar-root.phase23-clean");
      const headerOffsetCss = (() => {
        try {
          const rootEl = document.querySelector(".admin-calendar-root.phase23-clean");
          return rootEl ? getComputedStyle(rootEl).getPropertyValue("--loop-agenda-header-offset").trim() : "";
        } catch (_) {
          return "";
        }
      })();
      const currentHeaderOffset = (() => {
        const n = Number.parseFloat(String(headerOffsetCss || "0").replace("px", ""));
        return Number.isFinite(n) ? n : 0;
      })();
      const layout = {
        appHeader,
        root,
        toolbar,
        toolbarControl,
        resourceHead,
        headerOffsetCss,
        toolbarBaseTopWithoutDynamicOffset: toolbar ? Math.round(toolbar.top - currentHeaderOffset) : null,
        gapHeaderToToolbar: appHeader && toolbar ? Math.round(toolbar.top - appHeader.bottom) : null,
        gapToolbarToGrid: toolbar && resourceHead ? Math.round(resourceHead.top - toolbar.bottom) : null,
        controlTopInsideToolbar: toolbar && toolbarControl ? Math.round(toolbarControl.top - toolbar.top) : null,
        controlBottomInsideToolbar: toolbar && toolbarControl ? Math.round(toolbar.bottom - toolbarControl.bottom) : null,
        toolbarUnderHeader: Boolean(appHeader && toolbar && toolbar.top < appHeader.bottom - 1),
        whiteBandLikely: Boolean(appHeader && toolbar && Math.round(toolbar.top - appHeader.bottom) > 8),
      };
      const result = { ...snapshot, dom, layout };
      try {
        console.groupCollapsed("[Loop Agenda Audit] unified surface");
        console.table([snapshot]);
        console.table([dom]);
        console.table([layout]);
        console.groupEnd();
      } catch (_) {}
      return result;
    };

    return undefined;
  }, [
    activeBusinessId,
    activeTab,
    isNativeRuntime,
    isWorkerView,
    hardWorkerView,
    viewMode,
    effectiveCalendarView,
    agendaUsesResourceDayGrid,
    isMobileAgendaSurface,
    effectiveCalendarResources,
    nativeVisibleEmployees,
    employees,
    unifiedAgendaEvents,
    loading,
    effectiveAgendaSettings,
    businessDoc?.calendarSettings,
    routeCalendarSettings,
    cachedCalendarSettings,
    routeWorkerProfile,
  ]);


  useEffect(() => {
    // LOOP PHASE 4M — STABLE TOOLBAR HITBOXES HARDLOCK
    // The CSS file has many historical mobile toolbar rules. In Chrome mobile
    // emulation the toolbar container was in the correct place, but the three
    // child groups rendered ~17px above it. Since the runtime cascade kept
    // winning even over the final CSS block, we apply a small inline-important
    // hardlock only for WEB mobile agenda. Native is intentionally untouched.
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (isNativeRuntime || activeTab !== "agenda") return undefined;

    let rafId = 0;
    let observer = null;

    const setImportant = (el, prop, value) => {
      if (!el || !el.style || value === undefined || value === null) return;
      try { el.style.setProperty(prop, String(value), "important"); } catch (_) {}
    };

    // Phase 4N: when Chrome DevTools crosses back from phone/tablet to desktop,
    // the previous hardlock had left inline !important styles and icon-only text
    // behind. This cleanup returns the toolbar to the normal desktop CSS contract.
    const clearImportant = (el, props = []) => {
      if (!el || !el.style) return;
      props.forEach((prop) => {
        try { el.style.removeProperty(prop); } catch (_) {}
      });
    };

    const hardlockProps = [
      "position", "display", "flex-direction", "align-items", "justify-content",
      "align-content", "place-items", "gap", "height", "min-height", "max-height",
      "width", "min-width", "max-width", "padding", "padding-top", "padding-bottom",
      "padding-left", "padding-right", "margin", "margin-top", "margin-bottom",
      "top", "left", "right", "bottom", "inset", "transform", "translate",
      "overflow", "box-sizing", "z-index", "pointer-events", "isolation", "contain",
      "flex", "flex-basis", "align-self", "grid-area", "grid-column", "grid-row",
      "border", "border-radius", "font-size", "line-height", "color", "font-weight",
      "text-indent", "white-space", "clip-path", "visibility", "opacity",
      "background", "border-color", "box-shadow"
    ];

    const clearToolbarHardlock = (rootEl) => {
      if (!rootEl) return;
      rootEl.classList.remove("loop-toolbar-inline-hardlock");
      rootEl.classList.remove("is-phone-toolbar-active", "is-tablet-toolbar-active");
      clearImportant(rootEl, [
        "margin-top", "padding-top", "top", "transform", "height", "min-height",
        "max-height", "--loop-mobile-toolbar-h", "--loop-agenda-mobile-toolbar-h",
        "--loop-agenda-header-offset"
      ]);

      const toolbarEl = rootEl.querySelector(".agenda-web-toolbar");
      const targets = [
        toolbarEl,
        ...(toolbarEl ? Array.from(toolbarEl.querySelectorAll(
          ".agenda-web-toolbar-left, .agenda-date-anchor, .agenda-web-toolbar-right, .agenda-nav-btn, .agenda-today-btn, .agenda-current-date, .agenda-view-switch, .agenda-view-switch button, .agenda-new-booking-btn, .agenda-filter-btn, .agenda-worker-filter-btn, .agenda-panel-toggle-btn"
        )) : []),
      ].filter(Boolean);

      targets.forEach((el) => {
        clearImportant(el, hardlockProps);
        if (el.dataset) {
          delete el.dataset.loopToolbarHardlock;
        }
      });


    };

    const applyToolbarHardlock = () => {
      const isMobile = window.matchMedia("(max-width: 980px)").matches;
      const isPhoneCompact = window.matchMedia("(max-width: 599px)").matches;
      const isTabletCompact = window.matchMedia("(min-width: 600px) and (max-width: 980px)").matches;
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean.is-web-revamp");
      if (!rootEl) return;

      const toolbarEl = rootEl.querySelector(".agenda-web-toolbar.is-mobile-toolbar, .agenda-web-toolbar");
      if (!toolbarEl) return;

      if (!isMobile) {
        clearToolbarHardlock(rootEl);
        return;
      }

      rootEl.classList.add("loop-toolbar-inline-hardlock");
      rootEl.classList.toggle("is-phone-toolbar-active", Boolean(isPhoneCompact));
      rootEl.classList.toggle("is-tablet-toolbar-active", Boolean(isTabletCompact));
      setImportant(rootEl, "margin-top", "0px");
      setImportant(rootEl, "padding-top", "0px");
      setImportant(rootEl, "top", "auto");
      setImportant(rootEl, "transform", "none");
      setImportant(rootEl, "--loop-mobile-toolbar-h", "50px");
      setImportant(rootEl, "--loop-agenda-mobile-toolbar-h", "50px");
      setImportant(rootEl, "--loop-agenda-header-offset", "0px");

      const toolbarStyles = {
        position: "relative",
        display: "flex",
        "flex-direction": "row",
        "align-items": "center",
        "justify-content": "space-between",
        "align-content": "center",
        "place-items": "center",
        gap: "8px",
        height: "50px",
        "min-height": "50px",
        "max-height": "50px",
        padding: "6px 8px",
        margin: "0px",
        top: "auto",
        left: "auto",
        right: "auto",
        bottom: "auto",
        inset: "auto",
        transform: "none",
        translate: "none",
        overflow: "visible",
        "box-sizing": "border-box",
        "z-index": "120",
      };
      Object.entries(toolbarStyles).forEach(([prop, value]) => setImportant(toolbarEl, prop, value));
      setImportant(toolbarEl, "pointer-events", "auto");
      toolbarEl.dataset.loopToolbarHardlock = "4M";

      const leftEl = toolbarEl.querySelector(".agenda-web-toolbar-left, .loop-calendar-toolbar-left");
      const dateEl = toolbarEl.querySelector(".agenda-date-anchor, .loop-calendar-toolbar-date");
      const rightEl = toolbarEl.querySelector(".agenda-web-toolbar-right, .loop-calendar-toolbar-right");

      const groupBase = {
        position: "static",
        display: "flex",
        "align-items": "center",
        "justify-content": "center",
        "align-self": "center",
        "grid-area": "auto",
        "grid-column": "auto",
        "grid-row": "auto",
        height: "38px",
        "min-height": "38px",
        "max-height": "38px",
        margin: "0px",
        padding: "0px",
        top: "auto",
        left: "auto",
        right: "auto",
        bottom: "auto",
        inset: "auto",
        transform: "none",
        translate: "none",
        "box-sizing": "border-box",
        "z-index": "auto",
      };

      [leftEl, dateEl, rightEl].forEach((el) => {
        if (!el) return;
        Object.entries(groupBase).forEach(([prop, value]) => setImportant(el, prop, value));
        setImportant(el, "pointer-events", "auto");
        el.dataset.loopToolbarHardlock = "4M";
      });

      if (leftEl) {
        setImportant(leftEl, "flex", "0 0 84px");
        setImportant(leftEl, "width", "84px");
        setImportant(leftEl, "min-width", "84px");
        setImportant(leftEl, "max-width", "84px");
        setImportant(leftEl, "justify-content", "flex-start");
        setImportant(leftEl, "gap", "6px");
      }
      if (dateEl) {
        setImportant(dateEl, "flex", "1 1 auto");
        setImportant(dateEl, "width", "auto");
        setImportant(dateEl, "min-width", "0px");
        setImportant(dateEl, "max-width", "none");
        const datePopoverIsOpen = toolbarEl.classList.contains("is-popover-open");
        setImportant(dateEl, "overflow", datePopoverIsOpen ? "visible" : "hidden");
      }
      if (rightEl) {
        const rightW = isPhoneCompact ? "86px" : "292px";
        setImportant(rightEl, "flex", `0 0 ${rightW}`);
        setImportant(rightEl, "width", rightW);
        setImportant(rightEl, "min-width", rightW);
        setImportant(rightEl, "max-width", rightW);
        setImportant(rightEl, "justify-content", "flex-end");
        setImportant(rightEl, "gap", isPhoneCompact ? "6px" : "8px");
        setImportant(rightEl, "overflow", "visible");
      }

      // PHASE 4J — Compact phone actions, tablet-friendly.
      // On phone, keep only arrows | date | + | menu. On tablet widths, do not hide
      // Día/Semana/Mes because there is enough horizontal room and Juan wants those visible.
      const hideInline = (el, reason = "4K-hidden-phone-secondary") => {
        if (!el) return;
        Object.entries({
          display: "none",
          visibility: "hidden",
          width: "0px",
          "min-width": "0px",
          "max-width": "0px",
          height: "0px",
          "min-height": "0px",
          "max-height": "0px",
          margin: "0px",
          padding: "0px",
          border: "0px",
          overflow: "hidden",
          transform: "none",
          translate: "none",
          "pointer-events": "none",
        }).forEach(([prop, value]) => setImportant(el, prop, value));
        el.dataset.loopToolbarHardlock = reason;
      };

      if (isPhoneCompact) {
        const secondaryMobileControls = toolbarEl.querySelectorAll(".agenda-view-switch, .agenda-view-switch button, .agenda-today-btn");
        secondaryMobileControls.forEach((el) => hideInline(el));

        // Some older toolbar versions render Día / Semana / Mes as plain buttons,
        // not inside .agenda-view-switch. Hide those by text/aria only on phone.
        toolbarEl.querySelectorAll("button").forEach((btn) => {
          const label = `${btn.getAttribute("aria-label") || ""} ${btn.textContent || ""}`.trim().toLowerCase();
          if (/^(hoy|día|dia|semana|mes)$/.test(label) || /\b(día|dia|semana|mes)\b/.test(label)) {
            if (!btn.classList.contains("agenda-current-date")) hideInline(btn, "4K-hidden-phone-view-mode");
          }
        });
      } else {
        // Tablet/small landscape: undo every phone hiding style so Día/Semana/Mes
        // is actually visible and clickable after resize. Older CSS had display:none
        // under 767px, so visibility alone was not enough.
        const viewSwitch = toolbarEl.querySelector(".agenda-view-switch");
        if (viewSwitch) {
          Object.entries({
            display: "inline-flex",
            visibility: "visible",
            opacity: "1",
            width: "auto",
            "min-width": "156px",
            "max-width": "190px",
            height: "38px",
            "min-height": "38px",
            "max-height": "38px",
            margin: "0px",
            padding: "0px",
            overflow: "hidden",
            "pointer-events": "auto",
            position: "static",
            transform: "none",
            translate: "none",
            "z-index": "2",
          }).forEach(([prop, value]) => setImportant(viewSwitch, prop, value));
          viewSwitch.dataset.loopToolbarHardlock = "4K-tablet-visible-view-switch";
        }

        toolbarEl.querySelectorAll(".agenda-view-switch button").forEach((el) => {
          Object.entries({
            display: "inline-flex",
            visibility: "visible",
            opacity: "1",
            width: "auto",
            "min-width": "48px",
            height: "36px",
            "min-height": "36px",
            "max-height": "36px",
            margin: "0px",
            padding: "0 9px",
            "pointer-events": "auto",
            position: "static",
            transform: "none",
            translate: "none",
          }).forEach(([prop, value]) => setImportant(el, prop, value));
          el.dataset.loopToolbarHardlock = "4K-tablet-visible-view-button";
        });

        const todayBtn = toolbarEl.querySelector(".agenda-today-btn");
        if (todayBtn) hideInline(todayBtn, "4K-hidden-tablet-today-to-save-space");
      }

      const controls = toolbarEl.querySelectorAll(".agenda-nav-btn, .agenda-current-date, .agenda-new-booking-btn, .agenda-filter-btn, .agenda-worker-filter-btn, .agenda-panel-toggle-btn");
      controls.forEach((el) => {
        const isDate = el.classList && el.classList.contains("agenda-current-date");
        Object.entries({
          position: "static",
          display: "inline-flex",
          "align-items": "center",
          "justify-content": "center",
          "align-self": "center",
          height: "38px",
          "min-height": "38px",
          "max-height": "38px",
          margin: "0px",
          "margin-top": "0px",
          "margin-bottom": "0px",
          "padding-top": "0px",
          "padding-bottom": "0px",
          top: "auto",
          bottom: "auto",
          inset: "auto",
          transform: "none",
          translate: "none",
          "box-sizing": "border-box",
          "pointer-events": "auto",
        }).forEach(([prop, value]) => setImportant(el, prop, value));
        if (!isDate) {
          setImportant(el, "width", "38px");
          setImportant(el, "min-width", "38px");
          setImportant(el, "max-width", "38px");
        } else {
          setImportant(el, "width", "100%");
          setImportant(el, "min-width", "0px");
          setImportant(el, "max-width", "100%");
        }
        el.dataset.loopToolbarHardlock = "4M";
      });

      // Keep the two right actions icon-only and click-safe. Older CSS can reveal
      // labels on hover or give + a wider hidden hit area. Hardlock it to 38x38.
      const addBtn = toolbarEl.querySelector(".agenda-new-booking-btn");
      if (addBtn) {
        Object.entries({
          width: "38px",
          "min-width": "38px",
          "max-width": "38px",
          height: "38px",
          "min-height": "38px",
          "max-height": "38px",
          padding: "0px",
          overflow: "hidden",
          "font-size": "23px",
          "line-height": "1",
          color: "#ffffff",
          "pointer-events": "auto",
          "z-index": "2",
          position: "relative",
          inset: "auto",
          transform: "none",
          translate: "none",
          contain: "none",
          "clip-path": "none",
        }).forEach(([prop, value]) => setImportant(addBtn, prop, value));

        // Force the visual + to be truly icon-only. Older CSS could reveal the
        // text label on hover, and the hidden label contributed to strange hitboxes.
        addBtn.setAttribute("aria-label", "Crear reserva");
        addBtn.setAttribute("title", "Crear reserva");

        // If a stale/invisible layer dispatches a click to the + from outside its
        // 38x38 visual box, cancel it before React receives the click. This keeps
        // random toolbar/date/menu taps from opening Nueva reserva.
        if (!addBtn.dataset.loopClickGuardAttached) {
          const guard = (event) => {
            const rect = addBtn.getBoundingClientRect();
            const x = event.clientX;
            const y = event.clientY;
            const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
            if (!inside) {
              event.preventDefault();
              event.stopPropagation();
              if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
              return false;
            }
            return true;
          };
          addBtn.addEventListener("click", guard, true);
          addBtn.addEventListener("pointerdown", guard, true);
          addBtn.dataset.loopClickGuardAttached = "true";
        }

        Array.from(addBtn.children || []).forEach((child) => {
          if (!child.matches("svg,i")) hideInline(child, "4L-hide-add-label");
        });
        addBtn.dataset.loopToolbarHardlock = "4M-add-native-hitbox";
      }

      const menuBtn = toolbarEl.querySelector(".agenda-panel-toggle-btn, .agenda-filter-btn.agenda-worker-filter-btn");
      if (menuBtn) {
        Object.entries({
          width: "38px",
          "min-width": "38px",
          "max-width": "38px",
          height: "38px",
          "min-height": "38px",
          "max-height": "38px",
          padding: "0px",
          overflow: "hidden",
          "font-size": "22px",
          "line-height": "1",
          "pointer-events": "auto",
          "z-index": "2",
        }).forEach(([prop, value]) => setImportant(menuBtn, prop, value));
        menuBtn.querySelectorAll(".agenda-filter-label, .agenda-panel-label, .agenda-panel-copy, span:not(.agenda-filter-icon):not(.agenda-filter-count)").forEach((child) => {
          hideInline(child, "4L-hide-menu-label");
        });
        menuBtn.dataset.loopToolbarHardlock = "4M-menu-native-hitbox";
      }

      // LOOP_DATE_POPOVER_EXTERNAL_GEOMETRY_RETIRED_V57
      // CalendarToolbar now owns date-popover geometry before paint.
      // This observer may normalize the toolbar, but must never reposition the popup.
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(applyToolbarHardlock);
    };

    window.__loopApplyToolbarHardlock = applyToolbarHardlock;
    window.__loopApplyToolbarHitboxFix = applyToolbarHardlock;
    schedule();
    const timers = [80, 220, 600, 1200, 2200].map((ms) => window.setTimeout(schedule, ms));
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    try {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [activeTab, isNativeRuntime, viewportWidth]);


  // LOOP AGENDA MADRE CLEANUP D5 V76
  // Removed obsolete V27 no-op and V28 native toolbar hardlock.
  // V29 remains the single native toolbar stabilizer until we fully move this to CSS.


  useEffect(() => {
    // LOOP PHASE 4R — NATIVE TOOLBAR FINAL STABLE V29
    // Console after V28 showed V27 was still winning:
    // toolbar display=grid height=68; left/date/right top=87 while toolbar top=94.
    // V29 disables V27 and applies one final native toolbar contract.
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!isNativeRuntime || activeTab !== "agenda") return undefined;

    let rafId = 0;
    let observer = null;
    const timers = [];

    const setImportant = (el, prop, value) => {
      if (!el || !el.style || value === undefined || value === null) return;
      try { el.style.setProperty(prop, String(value), "important"); } catch (_) {}
    };

    const applyNativeToolbarV29 = () => {
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean.is-native, .admin-calendar-root.phase23-clean");
      if (!rootEl || !rootEl.classList.contains("is-native")) return;

      // V35 owns the native viewport contract. Do not let the legacy V29
      // toolbar stabilizer reapply top:auto or shell calc(100% - 78px).
      if (rootEl.dataset.loopNativeStableViewport || /loop-native-stable-viewport-v\d+/.test(rootEl.className || "")) return;

      const toolbarEl = rootEl.querySelector(".agenda-web-toolbar");
      if (!toolbarEl) return;

      const headerVar = "var(--loop-mobile-header-total, 94px)";
      const toolbarH = "78px";
      const controlH = "38px";

      rootEl.classList.remove("loop-native-toolbar-clearance-hardlock");
      rootEl.classList.add("loop-native-toolbar-final-v29");
      rootEl.dataset.loopNativeToolbarV29 = "true";

      // V37 moved the viewport contract to the calendar route wrapper.
      // Do not mutate document.body here; otherwise MainMenu/other routes can
      // inherit overflow:hidden after leaving Agenda.

      const pageWrap = rootEl.closest(".calendar-page-wrap");
      if (pageWrap) {
        setImportant(pageWrap, "height", `calc(100dvh - ${headerVar})`);
        setImportant(pageWrap, "min-height", "0px");
        setImportant(pageWrap, "max-height", `calc(100dvh - ${headerVar})`);
        setImportant(pageWrap, "margin", "0px");
        setImportant(pageWrap, "padding", "0px");
        setImportant(pageWrap, "overflow", "hidden");
      }

      Object.entries({
        margin: "0px",
        padding: "0px",
        top: "auto",
        transform: "none",
        translate: "none",
        height: `calc(100dvh - ${headerVar})`,
        "min-height": "0px",
        "max-height": `calc(100dvh - ${headerVar})`,
        overflow: "hidden",
        "--loop-mobile-toolbar-h": toolbarH,
        "--loop-agenda-mobile-toolbar-h": toolbarH,
      }).forEach(([prop, value]) => setImportant(rootEl, prop, value));

      Object.entries({
        position: "relative",
        display: "block",
        height: toolbarH,
        "min-height": toolbarH,
        "max-height": toolbarH,
        flex: `0 0 ${toolbarH}`,
        "flex-basis": toolbarH,
        padding: "0px 8px",
        margin: "0px",
        "margin-top": "0px",
        top: "0px",
        left: "auto",
        right: "auto",
        bottom: "auto",
        inset: "auto",
        transform: "none",
        translate: "none",
        overflow: "visible",
        "box-sizing": "border-box",
        "z-index": "190",
      }).forEach(([prop, value]) => setImportant(toolbarEl, prop, value));
      toolbarEl.dataset.loopNativeToolbarClearance = "V29";

      const leftEl = toolbarEl.querySelector(".agenda-web-toolbar-left, .loop-calendar-toolbar-left");
      const dateEl = toolbarEl.querySelector(".agenda-date-anchor, .loop-calendar-toolbar-date");
      const rightEl = toolbarEl.querySelector(".agenda-web-toolbar-right, .loop-calendar-toolbar-right");

      const absBase = {
        position: "absolute",
        top: "20px",
        height: controlH,
        "min-height": controlH,
        "max-height": controlH,
        margin: "0px",
        "margin-top": "0px",
        padding: "0px",
        transform: "none",
        translate: "none",
        overflow: "visible",
        "box-sizing": "border-box",
        "z-index": "4",
        "pointer-events": "auto",
      };

      if (leftEl) {
        Object.entries(absBase).forEach(([prop, value]) => setImportant(leftEl, prop, value));
        setImportant(leftEl, "left", "8px");
        setImportant(leftEl, "right", "auto");
        setImportant(leftEl, "width", "84px");
        setImportant(leftEl, "min-width", "84px");
        setImportant(leftEl, "max-width", "84px");
        setImportant(leftEl, "display", "flex");
        setImportant(leftEl, "align-items", "center");
        setImportant(leftEl, "justify-content", "flex-start");
        setImportant(leftEl, "gap", "6px");
        leftEl.dataset.loopNativeToolbarClearance = "V29";
      }

      if (dateEl) {
        Object.entries(absBase).forEach(([prop, value]) => setImportant(dateEl, prop, value));
        setImportant(dateEl, "left", "98px");
        setImportant(dateEl, "right", "100px");
        setImportant(dateEl, "width", "auto");
        setImportant(dateEl, "min-width", "0px");
        setImportant(dateEl, "max-width", "none");
        setImportant(dateEl, "display", "flex");
        setImportant(dateEl, "align-items", "center");
        setImportant(dateEl, "justify-content", "center");
        setImportant(dateEl, "overflow", "hidden");
        dateEl.dataset.loopNativeToolbarClearance = "V29";
      }

      if (rightEl) {
        Object.entries(absBase).forEach(([prop, value]) => setImportant(rightEl, prop, value));
        setImportant(rightEl, "left", "auto");
        setImportant(rightEl, "right", "8px");
        setImportant(rightEl, "width", "84px");
        setImportant(rightEl, "min-width", "84px");
        setImportant(rightEl, "max-width", "84px");
        setImportant(rightEl, "display", "flex");
        setImportant(rightEl, "align-items", "center");
        setImportant(rightEl, "justify-content", "flex-end");
        setImportant(rightEl, "gap", "6px");
        rightEl.dataset.loopNativeToolbarClearance = "V29";
      }

      toolbarEl.querySelectorAll(".agenda-nav-btn, .agenda-current-date, .agenda-new-booking-btn, .agenda-filter-btn, .agenda-worker-filter-btn, .agenda-panel-toggle-btn").forEach((el) => {
        const isDate = el.classList && el.classList.contains("agenda-current-date");
        Object.entries({
          position: "static",
          display: "inline-flex",
          "align-items": "center",
          "justify-content": "center",
          height: controlH,
          "min-height": controlH,
          "max-height": controlH,
          margin: "0px",
          padding: "0px",
          top: "auto",
          inset: "auto",
          transform: "none",
          translate: "none",
          "box-sizing": "border-box",
          "pointer-events": "auto",
        }).forEach(([prop, value]) => setImportant(el, prop, value));

        if (isDate) {
          setImportant(el, "width", "100%");
          setImportant(el, "min-width", "0px");
          setImportant(el, "max-width", "100%");
          setImportant(el, "overflow", "hidden");
        } else {
          setImportant(el, "width", controlH);
          setImportant(el, "min-width", controlH);
          setImportant(el, "max-width", controlH);
        }

        el.dataset.loopNativeToolbarClearance = "V29";
      });

      const addButtons = Array.from(rootEl.querySelectorAll(".agenda-new-booking-btn"));
      addButtons.forEach((btn, index) => {
        if (index > 0) {
          setImportant(btn, "display", "none");
          btn.setAttribute("aria-hidden", "true");
          return;
        }

        // The plus is rendered by CalendarToolbar.
        btn.classList.add("loop-native-single-plus-v29", "loop-native-single-plus-v31", "loop-native-plus-prepaint-v31");
        btn.setAttribute("aria-label", "Crear reserva");
        btn.setAttribute("title", "Crear reserva");
        Object.entries({
          background: "var(--loop-agenda-brand, #005f55)",
          "border-color": "var(--loop-agenda-brand, #005f55)",
          color: "#ffffff",
          "-webkit-text-fill-color": "#ffffff",
          "font-size": "24px",
          "font-weight": "950",
          "line-height": "1",
          overflow: "hidden",
        }).forEach(([prop, value]) => setImportant(btn, prop, value));
      });

      toolbarEl.querySelectorAll(".agenda-new-booking-btn::before, .agenda-new-booking-btn::after");

      toolbarEl.querySelectorAll(".agenda-today-btn, .agenda-view-switch").forEach((el) => {
        Object.entries({
          display: "none",
          visibility: "hidden",
          width: "0px",
          "min-width": "0px",
          "max-width": "0px",
          height: "0px",
          "min-height": "0px",
          "max-height": "0px",
          margin: "0px",
          padding: "0px",
          overflow: "hidden",
          "pointer-events": "none",
        }).forEach(([prop, value]) => setImportant(el, prop, value));
      });

      const dayShell = rootEl.querySelector(".loop-resource-day-shell");
      if (dayShell) {
        setImportant(dayShell, "height", "100%");
        setImportant(dayShell, "min-height", "0px");
        setImportant(dayShell, "max-height", "100%");
        setImportant(dayShell, "margin", "0px");
        setImportant(dayShell, "padding", "0px");
        setImportant(dayShell, "overflow", "hidden");
      }

      const scrollEl = rootEl.querySelector(".loop-resource-day-scroll");
      if (scrollEl) {
        setImportant(scrollEl, "height", "100%");
        setImportant(scrollEl, "min-height", "0px");
        setImportant(scrollEl, "max-height", "100%");
        setImportant(scrollEl, "overflow", "auto");
      }
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(applyNativeToolbarV29);
    };

    window.__loopApplyNativeCalendarToolbarV29 = applyNativeToolbarV29;
    schedule();
    [60, 180, 420, 900, 1800, 3200].forEach((ms) => timers.push(window.setTimeout(schedule, ms)));

    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    try {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [activeTab, isNativeRuntime, viewportWidth]);


  useEffect(() => {
    // LOOP PHASE 4U — NATIVE ROUTE-LOCAL VIEWPORT V37
    // The calendar no longer mutates html/body/#root/.App. The route wrapper
    // owns the fixed viewport; this hook only stabilizes elements inside the
    // AdminBookingCalendar tree. That prevents scroll locks from leaking into
    // MainMenu, BusinessEditPage or any other route after leaving Agenda.
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!isNativeRuntime || activeTab !== "agenda") return undefined;

    let rafId = 0;
    const timers = [];
    let observer = null;

    const setImportant = (el, prop, value) => {
      if (!el || !el.style || value === undefined || value === null) return;
      try { el.style.setProperty(prop, String(value), "important"); } catch (_) {}
    };

    const applyStableNativeViewportV37 = () => {
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean.is-native");
      if (!rootEl) return;

      const toolbarH = "50px";
      const controlH = "38px";
      const isIosNative = rootEl.classList.contains("is-ios-native");
      const navGuardPx = (() => {
        try {
          const computed = window.getComputedStyle(rootEl).getPropertyValue("--loop-native-navigation-guard").trim();
          const n = Number.parseFloat(String(computed || "0").replace("px", ""));
          if (Number.isFinite(n) && n > 0) return Math.round(n);
        } catch (_) {}
        return rootEl.classList.contains("is-android-native") ? 64 : 0;
      })();
      const effectiveAndroidGuardPx = isIosNative ? 0 : Math.max(navGuardPx || 0, 64);

      // LOOP NATIVE GAP FIX V38 — the route viewport must start exactly where
      // the visible green app header ends. On Android/iOS app WebViews a fixed
      // child can be offset by an outer route shell, so setting top=headerBottom
      // can still render 14px lower. We first apply the intended top, measure
      // the real rendered gap, then subtract that gap from the CSS top.
      const routeViewportEl = document.querySelector(".calendar-route-viewport");
      const routeShellEl = document.querySelector(".calendar-route-shell");

      const pickVisibleNativeAppHeader = () => {
        const selectors = [
          "#app-main-header",
          ".loop-ref-header",
          ".loop-mobile-header-v2",
          ".app-header",
          ".topbar",
          "header",
        ];

        const candidates = [];
        selectors.forEach((selector) => {
          try {
            document.querySelectorAll(selector).forEach((el) => {
              if (el && !candidates.includes(el)) candidates.push(el);
            });
          } catch (_) {}
        });

        const visible = candidates.filter((el) => {
          try {
            const r = el.getBoundingClientRect();
            const s = window.getComputedStyle(el);
            return r.width > 0 && r.height > 24 && r.bottom > 0 && s.display !== "none" && s.visibility !== "hidden";
          } catch (_) {
            return false;
          }
        });

        return visible.find((el) => !rootEl.contains(el)) || visible[0] || null;
      };

      const appHeaderEl = pickVisibleNativeAppHeader();
      const measuredHeaderBottomPx = (() => {
        try {
          if (appHeaderEl) {
            const n = Math.round(appHeaderEl.getBoundingClientRect().bottom);
            if (Number.isFinite(n) && n > 0) return n;
          }
        } catch (_) {}

        try {
          const cssHeader = window.getComputedStyle(document.documentElement).getPropertyValue("--loop-mobile-header-total").trim();
          const n = Number.parseFloat(String(cssHeader || "0").replace("px", ""));
          if (Number.isFinite(n) && n > 0) return Math.round(n);
        } catch (_) {}

        return 94;
      })();

      const nativeHeaderPx = `${measuredHeaderBottomPx}px`;
      const nativeNavGuardPx = `var(--loop-native-navigation-guard, ${effectiveAndroidGuardPx}px)`;
      const nativeViewportHeight = `calc(100dvh - ${nativeHeaderPx} - ${nativeNavGuardPx})`;

      const setNativeHeaderVars = () => {
        try {
          document.documentElement.style.setProperty("--loop-native-header-h", nativeHeaderPx);
          document.documentElement.style.setProperty("--loop-mobile-header-total", nativeHeaderPx);
          document.body.style.setProperty("--loop-native-header-h", nativeHeaderPx);
          document.body.style.setProperty("--loop-mobile-header-total", nativeHeaderPx);
        } catch (_) {}
      };

      const applyRouteViewportTopLockV38 = () => {
        if (!routeViewportEl) return;

        setNativeHeaderVars();

        if (routeShellEl) {
          Object.entries({
            margin: "0px",
            "margin-top": "0px",
            padding: "0px",
            "padding-top": "0px",
            transform: "none",
            translate: "none",
          }).forEach(([prop, value]) => setImportant(routeShellEl, prop, value));
        }

        Object.entries({
          position: "fixed",
          top: nativeHeaderPx,
          left: "0px",
          right: "0px",
          bottom: nativeNavGuardPx,
          width: "100vw",
          height: nativeViewportHeight,
          "min-height": "0px",
          "max-height": nativeViewportHeight,
          margin: "0px",
          padding: "0px",
          overflow: "hidden",
          background: "#ffffff",
          transform: "none",
          translate: "none",
          "box-sizing": "border-box",
        }).forEach(([prop, value]) => setImportant(routeViewportEl, prop, value));

        let renderedTop = measuredHeaderBottomPx;
        let renderedGap = 0;
        let correctedTopPx = measuredHeaderBottomPx;

        try {
          renderedTop = Math.round(routeViewportEl.getBoundingClientRect().top);
          renderedGap = Math.round(renderedTop - measuredHeaderBottomPx);

          if (Math.abs(renderedGap) > 1) {
            const currentCssTop = Number.parseFloat(String(routeViewportEl.style.top || nativeHeaderPx).replace("px", ""));
            const baseTop = Number.isFinite(currentCssTop) ? currentCssTop : measuredHeaderBottomPx;
            correctedTopPx = Math.max(0, Math.round(baseTop - renderedGap));
            setImportant(routeViewportEl, "top", `${correctedTopPx}px`);

            // Keep height based on the visible header bottom, not the corrected CSS top.
            setImportant(routeViewportEl, "height", nativeViewportHeight);
            setImportant(routeViewportEl, "max-height", nativeViewportHeight);
          }

          const finalTop = Math.round(routeViewportEl.getBoundingClientRect().top);
          try {
            window.__LOOP_NATIVE_ROUTE_VIEWPORT_LOCK__ = {
              version: "V38",
              headerBottom: measuredHeaderBottomPx,
              firstRenderedTop: renderedTop,
              firstGap: renderedGap,
              correctedCssTop: correctedTopPx,
              finalRenderedTop: finalTop,
              finalGap: Math.round(finalTop - measuredHeaderBottomPx),
              navGuardPx: effectiveAndroidGuardPx,
              at: new Date().toISOString(),
            };
          } catch (_) {}
        } catch (_) {}
      };

      rootEl.classList.add("loop-native-stable-viewport-v38");
      rootEl.classList.add("loop-native-stable-viewport-v37");
      rootEl.classList.remove("loop-native-stable-viewport-v36");
      rootEl.classList.remove("loop-native-stable-viewport-v35");
      rootEl.classList.remove("loop-native-stable-viewport-v34");
      rootEl.classList.remove("loop-native-stable-viewport-v33");
      rootEl.classList.remove("loop-native-header-overlapped");
      rootEl.dataset.loopNativeStableViewport = "V38";

      Object.entries({
        position: "relative",
        top: "auto",
        left: "auto",
        right: "auto",
        bottom: "auto",
        width: "100%",
        height: "100%",
        "min-height": "0px",
        "max-height": "100%",
        margin: "0px",
        padding: "0px",
        overflow: "hidden",
        "overscroll-behavior": "none",
        transform: "none",
        translate: "none",
        "z-index": "auto",
        "--loop-mobile-toolbar-h": toolbarH,
        "--loop-agenda-mobile-toolbar-h": toolbarH,
        "--loop-native-android-nav-guard": `${effectiveAndroidGuardPx}px`,
        "--loop-native-header-h": nativeHeaderPx,
        "--loop-mobile-header-total": nativeHeaderPx,
      }).forEach(([prop, value]) => setImportant(rootEl, prop, value));

      applyRouteViewportTopLockV38();

      const workspace = rootEl.querySelector(".agenda-workspace-fixed, .loop-agenda-clean");
      const main = rootEl.querySelector(".admin-cal-main");
      const toolbar = rootEl.querySelector(".agenda-web-toolbar");
      const card = rootEl.querySelector(".admin-cal-card, .agenda-unified-card");
      const shell = rootEl.querySelector(".loop-resource-day-shell");
      const scroll = rootEl.querySelector(".loop-resource-day-scroll");
      const grid = rootEl.querySelector(".loop-resource-day-grid");
      const tools = rootEl.querySelector(".agenda-tools-floating-wrap");

      [workspace, main].forEach((el) => {
        if (!el) return;
        Object.entries({
          height: "100%",
          "min-height": "0px",
          "max-height": "100%",
          margin: "0px",
          padding: "0px",
          overflow: "hidden",
          "overscroll-behavior": "none",
        }).forEach(([prop, value]) => setImportant(el, prop, value));
      });

      if (toolbar) {
        Object.entries({
          flex: `0 0 ${toolbarH}`,
          "flex-basis": toolbarH,
          height: toolbarH,
          "min-height": toolbarH,
          "max-height": toolbarH,
          margin: "0px",
          padding: "6px 8px",
          top: "0px",
          position: "relative",
          display: "flex",
          "align-items": "center",
          "justify-content": "space-between",
          gap: "8px",
          overflow: "visible",
          "box-sizing": "border-box",
        }).forEach(([prop, value]) => setImportant(toolbar, prop, value));

        const leftEl = toolbar.querySelector(".agenda-web-toolbar-left");
        const dateEl = toolbar.querySelector(".agenda-date-anchor, .agenda-current-date");
        const rightEl = toolbar.querySelector(".agenda-web-toolbar-right");

        const groupBase = {
          position: "static",
          top: "auto",
          left: "auto",
          right: "auto",
          bottom: "auto",
          inset: "auto",
          height: controlH,
          "min-height": controlH,
          "max-height": controlH,
          margin: "0px",
          padding: "0px",
          transform: "none",
          translate: "none",
          display: "flex",
          "align-items": "center",
          overflow: "visible",
          "box-sizing": "border-box",
          "z-index": "auto",
        };

        [leftEl, dateEl, rightEl].forEach((el) => {
          if (!el) return;
          Object.entries(groupBase).forEach(([prop, value]) => setImportant(el, prop, value));
        });

        if (leftEl) {
          setImportant(leftEl, "flex", "0 0 84px");
          setImportant(leftEl, "width", "84px");
          setImportant(leftEl, "min-width", "84px");
          setImportant(leftEl, "max-width", "84px");
          setImportant(leftEl, "justify-content", "flex-start");
          setImportant(leftEl, "gap", "6px");
        }

        if (dateEl) {
          setImportant(dateEl, "flex", "1 1 auto");
          setImportant(dateEl, "width", "auto");
          setImportant(dateEl, "min-width", "0px");
          setImportant(dateEl, "max-width", "none");
          setImportant(dateEl, "justify-content", "center");
          setImportant(dateEl, "overflow", "hidden");
        }

        if (rightEl) {
          setImportant(rightEl, "flex", "0 0 86px");
          setImportant(rightEl, "width", "86px");
          setImportant(rightEl, "min-width", "86px");
          setImportant(rightEl, "max-width", "86px");
          setImportant(rightEl, "justify-content", "flex-end");
          setImportant(rightEl, "gap", "6px");
        }
      }

      if (card) {
        Object.entries({
          flex: "1 1 0px",
          height: `calc(100% - ${toolbarH})`,
          "min-height": "0px",
          "max-height": `calc(100% - ${toolbarH})`,
          margin: "0px",
          padding: "0px",
          overflow: "hidden",
        }).forEach(([prop, value]) => setImportant(card, prop, value));
      }

      if (shell) {
        Object.entries({
          flex: "1 1 auto",
          height: "100%",
          "min-height": "0px",
          "max-height": "100%",
          margin: "0px",
          padding: "0px",
          overflow: "hidden",
        }).forEach(([prop, value]) => setImportant(shell, prop, value));
      }

      if (scroll) {
        Object.entries({
          flex: "1 1 auto",
          height: "100%",
          "min-height": "0px",
          "max-height": "100%",
          margin: "0px",
          padding: "0px",
          overflow: "auto",
          "overflow-x": "auto",
          "overflow-y": "auto",
          "-webkit-overflow-scrolling": "touch",
          "overscroll-behavior": "contain",
          "touch-action": "pan-x pan-y",
        }).forEach(([prop, value]) => setImportant(scroll, prop, value));
      }

      if (grid) {
        setImportant(grid, "min-height", "100%");
      }

      if (tools) {
        // The route viewport already excludes Android's navigation bar. Keep the
        // tools launcher inside the calendar, not fixed to the global viewport.
        setImportant(tools, "position", "absolute");
        setImportant(tools, "bottom", "8px");
        setImportant(tools, "right", "10px");
        setImportant(tools, "z-index", "7000");
        setImportant(tools, "pointer-events", "auto");
      }
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(applyStableNativeViewportV37);
    };

    window.__loopApplyStableNativeViewportV37 = applyStableNativeViewportV37;
    window.__loopApplyStableNativeViewportV38 = applyStableNativeViewportV37;
    schedule();
    [50, 150, 360, 800, 1600, 3000].forEach((ms) => timers.push(window.setTimeout(schedule, ms)));
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    try {
      observer = new MutationObserver(schedule);
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean.is-native");
      if (rootEl) observer.observe(rootEl, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style"] });
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [activeTab, isNativeRuntime, viewportWidth]);

    // LOOP AGENDA — SINGLE + BUTTON + MINI CALENDAR CLOSE/TODAY FIX
  // Fixes:
  // 1) The create button sometimes rendered two "+" symbols because old CSS
  //    mixed real text, span icons and pseudo-elements across breakpoints.
  // 2) The date popover now closes when clicking outside.
  // 3) MiniMonth marks "today" separately from the selected date.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let rafId = 0;
    let observer = null;
    const timers = [];

    const isElementVisible = (el) => {
      if (!el) return false;
      try {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        return (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          style.opacity !== "0" &&
          rect.width > 0 &&
          rect.height > 0
        );
      } catch (_) {
        return false;
      }
    };

    const setHardStyle = (el, prop, value) => {
      try {
        if (el && el.style) el.style.setProperty(prop, value, "important");
      } catch (_) {}
    };

    const getTodayMeta = () => {
      const today = new Date();
      const day = String(today.getDate());
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const monthLong = today.toLocaleDateString("es-ES", { month: "long" }).toLowerCase();
      const monthShort = today.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toLowerCase();
      const year = String(today.getFullYear());
      return { today, day, iso, monthLong, monthShort, year };
    };

    const miniMonthLooksLikeCurrentMonth = (mini, meta) => {
      const text = String(mini?.textContent || "").toLowerCase();
      if (!text) return true;
      if (text.includes(meta.year) && (text.includes(meta.monthLong) || text.includes(meta.monthShort))) return true;
      return false;
    };

    const markMiniMonthToday = (rootEl) => {
      const meta = getTodayMeta();
      const miniMonths = Array.from(rootEl.querySelectorAll(".mini-month, .mini-month-card, .agenda-date-popover [class*='mini']"));
      miniMonths.forEach((mini) => {
        const shouldMarkByNumber = miniMonthLooksLikeCurrentMonth(mini, meta);
        const buttons = Array.from(mini.querySelectorAll("button"));
        buttons.forEach((btn) => {
          try {
            btn.classList.remove("loop-mini-day-is-today");
            btn.removeAttribute("data-loop-today");
            const label = [
              btn.getAttribute("aria-label"),
              btn.getAttribute("title"),
              btn.getAttribute("data-date"),
              btn.value,
              btn.textContent,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

            const cleanText = String(btn.textContent || "").replace(/\s+/g, " ").trim();
            const matchesFullDate =
              label.includes(meta.iso) ||
              (label.includes(meta.year) && (label.includes(meta.monthLong) || label.includes(meta.monthShort)) && new RegExp(`(^|\\D)${meta.day}(\\D|$)`).test(label));

            const matchesVisibleDayOnly =
              shouldMarkByNumber &&
              cleanText === meta.day &&
              !btn.closest("[aria-hidden='true']");

            if (matchesFullDate || matchesVisibleDayOnly) {
              btn.classList.add("loop-mini-day-is-today");
              btn.setAttribute("data-loop-today", "true");
              if (!btn.getAttribute("aria-label")) btn.setAttribute("aria-label", `Hoy, día ${meta.day}`);
            }
          } catch (_) {}
        });
      });
    };

    const normalizeCreateButtons = (rootEl) => {
      const toolbars = Array.from(rootEl.querySelectorAll(".agenda-web-toolbar"));
      toolbars.forEach((toolbarEl) => {
        const buttons = Array.from(toolbarEl.querySelectorAll(".agenda-new-booking-btn"));
        buttons.forEach((btn, index) => {
          const keep = index === 0;
          if (!keep) {
            btn.setAttribute("aria-hidden", "true");
            btn.setAttribute("tabindex", "-1");
            setHardStyle(btn, "display", "none");
            setHardStyle(btn, "pointer-events", "none");
            btn.dataset.loopDuplicateAddHidden = "true";
            return;
          }

          btn.classList.add("loop-single-create-plus-btn", "loop-single-create-plus-text-lock", "loop-native-plus-prepaint-v31");
          btn.removeAttribute("aria-hidden");
          btn.removeAttribute("tabindex");
          btn.setAttribute("aria-label", "Crear reserva");
          btn.setAttribute("title", "Crear reserva");
          btn.dataset.loopSinglePlus = "true";

          // CalendarToolbar owns the icon content.


          [
            ["display", "inline-flex"],
            ["align-items", "center"],
            ["justify-content", "center"],
            ["width", window.matchMedia("(max-width: 980px)").matches ? "38px" : "42px"],
            ["min-width", window.matchMedia("(max-width: 980px)").matches ? "38px" : "42px"],
            ["max-width", window.matchMedia("(max-width: 980px)").matches ? "38px" : "42px"],
            ["height", "38px"],
            ["min-height", "38px"],
            ["max-height", "38px"],
            ["padding", "0px"],
            ["overflow", "hidden"],
            ["position", "relative"],
            ["font-size", window.matchMedia("(max-width: 980px)").matches ? "24px" : "25px"],
            ["font-weight", "950"],
            ["line-height", "1"],
            ["color", "#ffffff"],
            ["text-indent", "0px"],
            ["white-space", "nowrap"],
            ["pointer-events", "auto"],
            ["z-index", "5"],
          ].forEach(([prop, value]) => setHardStyle(btn, prop, value));
        });
      });
    };

    const normalizeAgendaUi = () => {
      const roots = Array.from(document.querySelectorAll(".admin-calendar-root.phase23-clean"));
      roots.forEach((rootEl) => {
        normalizeCreateButtons(rootEl);
        markMiniMonthToday(rootEl);
      });
    };

    const scheduleNormalize = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(normalizeAgendaUi);
    };

    const getOpenDatePopover = () => {
      const popovers = Array.from(document.querySelectorAll(".admin-calendar-root.phase23-clean .agenda-date-popover"));
      return popovers.find(isElementVisible) || null;
    };

    const closeDatePopoverFromOutside = (event) => {
      const popover = getOpenDatePopover();
      if (!popover) return;

      const target = event.target;
      if (target?.closest?.(".agenda-date-popover")) return;
      if (target?.closest?.(".agenda-date-anchor")) return;
      if (target?.closest?.(".agenda-current-date")) return;

      const rootEl = popover.closest(".admin-calendar-root.phase23-clean") || document;
      const dateButton =
        rootEl.querySelector(".agenda-date-anchor .agenda-current-date") ||
        rootEl.querySelector(".agenda-date-anchor button") ||
        rootEl.querySelector(".agenda-current-date");

      if (!dateButton) return;

      window.setTimeout(() => {
        try {
          const stillOpen = getOpenDatePopover();
          if (!stillOpen) return;
          dateButton.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        } catch (_) {}
      }, 0);
    };

    scheduleNormalize();
    [80, 220, 600, 1200].forEach((ms) => timers.push(window.setTimeout(scheduleNormalize, ms)));

    document.addEventListener("pointerdown", closeDatePopoverFromOutside, true);
    window.addEventListener("resize", scheduleNormalize);
    window.addEventListener("orientationchange", scheduleNormalize);

    try {
      observer = new MutationObserver(scheduleNormalize);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "aria-selected", "aria-pressed", "aria-current"] });
    } catch (_) {}

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      document.removeEventListener("pointerdown", closeDatePopoverFromOutside, true);
      window.removeEventListener("resize", scheduleNormalize);
      window.removeEventListener("orientationchange", scheduleNormalize);
      if (observer) observer.disconnect();
    };
  }, [activeTab, selectedDate, currentMonthDate, viewportWidth]);



  // LOOP AGENDA — MINI MONTH TODAY DOM SCAN V8
  // V7 depended on button-level metadata. Some MiniMonth surfaces render day
  // numbers without useful date attributes/classes, so this pass scans the
  // visible calendar DOM by the actual day number and marks the real "today"
  // plus the selected day.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let rafId = 0;
    let observer = null;
    const timers = [];

    const toDateMeta = (value) => {
      const date = value ? new Date(value) : new Date();
      if (!date || Number.isNaN(date.getTime())) return null;
      return {
        date,
        day: String(date.getDate()),
        iso: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
        year: String(date.getFullYear()),
        yearNumber: date.getFullYear(),
        monthIndex: date.getMonth(),
        monthLong: date.toLocaleDateString("es-ES", { month: "long" }).toLowerCase(),
        monthShort: date.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toLowerCase(),
      };
    };

    const isVisible = (el) => {
      try {
        const r = el.getBoundingClientRect();
        const cs = window.getComputedStyle(el);
        return r.width > 0 && r.height > 0 && cs.display !== "none" && cs.visibility !== "hidden" && cs.opacity !== "0";
      } catch (_) {
        return false;
      }
    };

    const miniLooksLikeMonth = (mini, meta) => {
      if (!mini || !meta) return false;
      const text = String(mini.textContent || "").toLowerCase();
      if (text.includes(meta.year) && (text.includes(meta.monthLong) || text.includes(meta.monthShort))) return true;
      try {
        const stateMonth = currentMonthDate ? new Date(currentMonthDate) : null;
        if (stateMonth && !Number.isNaN(stateMonth.getTime())) {
          return stateMonth.getMonth() === meta.monthIndex && stateMonth.getFullYear() === meta.yearNumber;
        }
      } catch (_) {}
      return false;
    };

    const getMarkTarget = (el) => {
      const button = el.closest?.("button, [role='button'], [data-date], [aria-selected], [aria-pressed]");
      if (button && button.closest?.(".mini-month, .mini-month-card, .agenda-date-popover, .agenda-side-calendar")) return button;
      return el;
    };

    const markByMeta = (mini, meta, className, attrName) => {
      if (!mini || !meta || !miniLooksLikeMonth(mini, meta)) return;

      const all = Array.from(
        mini.querySelectorAll("button, [role='button'], [data-date], [aria-label], [title], span, div")
      );

      const marked = new Set();

      all.forEach((el) => {
        try {
          if (!isVisible(el)) return;

          const rawText = String(el.textContent || "").replace(/\s+/g, " ").trim();
          const label = [
            el.getAttribute?.("aria-label"),
            el.getAttribute?.("title"),
            el.getAttribute?.("data-date"),
            el.getAttribute?.("data-day"),
            el.getAttribute?.("datetime"),
            el.value,
            rawText,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const textIsDay = rawText === meta.day;
          const labelIsDate =
            label.includes(meta.iso) ||
            (label.includes(meta.year) &&
              (label.includes(meta.monthLong) || label.includes(meta.monthShort)) &&
              new RegExp(`(^|\\D)${meta.day}(\\D|$)`).test(label));

          if (!textIsDay && !labelIsDate) return;

          // Avoid marking the month title, weekday headers, or parent containers
          // whose text contains many days.
          if (rawText.length > 2 && !labelIsDate) return;

          const target = getMarkTarget(el);
          if (!target || marked.has(target)) return;

          target.classList.add(className);
          target.setAttribute(attrName, "true");
          target.style.setProperty("position", "relative", "important");

          if (!target.getAttribute("aria-label")) {
            const dayText = String(target.textContent || rawText || meta.day).replace(/\s+/g, " ").trim();
            target.setAttribute("aria-label", `${className.includes("today") ? "Hoy" : "Seleccionado"}, día ${dayText}`);
          }

          marked.add(target);
        } catch (_) {}
      });
    };

    const run = () => {
      const roots = Array.from(document.querySelectorAll(".admin-calendar-root.phase23-clean"));
      const todayMeta = toDateMeta(new Date());
      const selectedMeta = toDateMeta(selectedDate);

      roots.forEach((rootEl) => {
        const miniMonths = Array.from(
          rootEl.querySelectorAll(".mini-month, .mini-month-card, .agenda-date-popover, .agenda-side-calendar")
        );

        miniMonths.forEach((mini) => {
          mini
            .querySelectorAll?.(".loop-mini-day-is-today, .loop-mini-day-is-selected, [data-loop-today='true'], [data-loop-selected='true']")
            ?.forEach((el) => {
              try {
                el.classList.remove("loop-mini-day-is-today", "loop-mini-day-is-selected");
                el.removeAttribute("data-loop-today");
                el.removeAttribute("data-loop-selected");
              } catch (_) {}
            });

          markByMeta(mini, selectedMeta, "loop-mini-day-is-selected", "data-loop-selected");
          markByMeta(mini, todayMeta, "loop-mini-day-is-today", "data-loop-today");
        });
      });
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(run);
    };

    schedule();
    [60, 180, 420, 900, 1500].forEach((ms) => timers.push(window.setTimeout(schedule, ms)));

    try {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
        attributeFilter: ["class", "style", "aria-selected", "aria-pressed", "aria-current", "data-date"],
      });
    } catch (_) {}

    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [selectedDate, currentMonthDate, activeTab, viewportWidth]);



  // LOOP AGENDA — MINI MONTH TEXT-ONLY TODAY V10
  // Final visual/DOM pass:
  // - Today is text-only: bold teal/dark, no background/ring/dot.
  // - Selected date keeps the warm cream pill.
  // - Treat aria-current="date" as "today", not as selected.
  // - Run after clicks and mutations because MiniMonth re-renders after selecting a day.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    let rafId = 0;
    let observer = null;
    const timers = [];

    const normalizeDateMeta = (value) => {
      const date = value ? new Date(value) : new Date();
      if (!date || Number.isNaN(date.getTime())) return null;
      return {
        date,
        day: String(date.getDate()),
        iso: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
        year: String(date.getFullYear()),
        yearNumber: date.getFullYear(),
        monthIndex: date.getMonth(),
        monthLong: date.toLocaleDateString("es-ES", { month: "long" }).toLowerCase(),
        monthShort: date.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toLowerCase(),
      };
    };

    const cleanText = (el) => String(el?.textContent || "").replace(/\s+/g, " ").trim();

    const isVisible = (el) => {
      try {
        const r = el.getBoundingClientRect();
        const cs = window.getComputedStyle(el);
        return r.width > 0 && r.height > 0 && cs.display !== "none" && cs.visibility !== "hidden" && cs.opacity !== "0";
      } catch (_) {
        return false;
      }
    };

    const miniMatchesMonth = (mini, meta) => {
      if (!mini || !meta) return false;
      const text = cleanText(mini).toLowerCase();
      if (text.includes(meta.year) && (text.includes(meta.monthLong) || text.includes(meta.monthShort))) return true;

      try {
        const stateMonth = currentMonthDate ? new Date(currentMonthDate) : null;
        if (stateMonth && !Number.isNaN(stateMonth.getTime())) {
          return stateMonth.getMonth() === meta.monthIndex && stateMonth.getFullYear() === meta.yearNumber;
        }
      } catch (_) {}

      return false;
    };

    const dayTarget = (el) => {
      const interactive = el.closest?.("button, [role='button'], [data-date], [aria-selected], [aria-pressed], [aria-current]");
      if (interactive && interactive.closest?.(".mini-month, .mini-month-card, .agenda-date-popover, .agenda-side-calendar")) {
        return interactive;
      }
      return el;
    };

    const clearMarks = (mini) => {
      mini
        ?.querySelectorAll?.(".loop-mini-day-is-today, .loop-mini-day-is-selected, [data-loop-today='true'], [data-loop-selected='true']")
        ?.forEach((el) => {
          try {
            el.classList.remove("loop-mini-day-is-today", "loop-mini-day-is-selected");
            el.removeAttribute("data-loop-today");
            el.removeAttribute("data-loop-selected");
          } catch (_) {}
        });
    };

    const getCandidateTargets = (mini, meta) => {
      if (!mini || !meta || !miniMatchesMonth(mini, meta)) return [];

      const selector = [
        "button",
        "[role='button']",
        "[data-date]",
        "[data-day]",
        "[aria-label]",
        "[aria-current]",
        "[aria-selected]",
        "[aria-pressed]",
        "span",
        "div",
      ].join(",");

      const matches = [];
      const all = Array.from(mini.querySelectorAll(selector));

      all.forEach((el) => {
        try {
          if (!isVisible(el)) return;

          const text = cleanText(el);
          const label = [
            el.getAttribute?.("aria-label"),
            el.getAttribute?.("title"),
            el.getAttribute?.("data-date"),
            el.getAttribute?.("data-day"),
            el.getAttribute?.("datetime"),
            el.value,
            text,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          const exactDayText = text === meta.day;
          const fullDateMatch =
            label.includes(meta.iso) ||
            (label.includes(meta.year) &&
              (label.includes(meta.monthLong) || label.includes(meta.monthShort)) &&
              new RegExp(`(^|\\D)${meta.day}(\\D|$)`).test(label));

          // Avoid month titles/containers like "junio de 2026" or whole grids.
          if (!exactDayText && !fullDateMatch) return;
          if (text.length > 2 && !fullDateMatch) return;

          const target = dayTarget(el);
          if (!target || !isVisible(target)) return;
          if (!matches.includes(target)) matches.push(target);
        } catch (_) {}
      });

      return matches;
    };

    const markTarget = (target, type) => {
      try {
        if (type === "today") {
          target.classList.add("loop-mini-day-is-today");
          target.setAttribute("data-loop-today", "true");
          target.style.setProperty("background", "transparent", "important");
          target.style.setProperty("background-color", "transparent", "important");
          target.style.setProperty("box-shadow", "none", "important");
          target.style.setProperty("outline", "0", "important");
          target.style.setProperty("color", "#005f55", "important");
          target.style.setProperty("-webkit-text-fill-color", "#005f55", "important");
          target.style.setProperty("font-weight", "950", "important");
        } else {
          target.classList.add("loop-mini-day-is-selected");
          target.setAttribute("data-loop-selected", "true");
          target.style.removeProperty("background");
          target.style.removeProperty("background-color");
          target.style.removeProperty("box-shadow");
          target.style.removeProperty("outline");
          target.style.removeProperty("color");
          target.style.removeProperty("-webkit-text-fill-color");
          target.style.removeProperty("font-weight");
        }

        target.style.setProperty("position", "relative", "important");
      } catch (_) {}
    };

    const run = () => {
      const todayMeta = normalizeDateMeta(new Date());
      const selectedMeta = normalizeDateMeta(selectedDate);
      const roots = Array.from(document.querySelectorAll(".admin-calendar-root.phase23-clean"));

      roots.forEach((rootEl) => {
        const miniMonths = Array.from(
          rootEl.querySelectorAll(".agenda-date-popover .mini-month, .agenda-side-calendar .mini-month, .mini-month, .mini-month-card")
        );

        miniMonths.forEach((mini) => {
          clearMarks(mini);

          getCandidateTargets(mini, selectedMeta).forEach((target) => markTarget(target, "selected"));
          getCandidateTargets(mini, todayMeta).forEach((target) => markTarget(target, "today"));

          // Native MiniMonth may expose today only through aria-current="date".
          mini.querySelectorAll?.('[aria-current="date"]').forEach((target) => {
            if (isVisible(target)) markTarget(target, "today");
          });

          // If today is also selected, let selected pill remain but keep teal/bold text.
          mini.querySelectorAll?.(".loop-mini-day-is-today.loop-mini-day-is-selected, [data-loop-today='true'][data-loop-selected='true']").forEach((target) => {
            try {
              target.style.removeProperty("background");
              target.style.removeProperty("background-color");
              target.style.removeProperty("box-shadow");
            } catch (_) {}
          });
        });
      });
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(run);
      [40, 120, 260].forEach((ms) => timers.push(window.setTimeout(run, ms)));
    };

    schedule();
    [80, 250, 700, 1400].forEach((ms) => timers.push(window.setTimeout(schedule, ms)));

    const onPointerUp = (event) => {
      const t = event.target;
      if (t?.closest?.(".mini-month, .agenda-date-popover, .agenda-side-calendar, .agenda-current-date, .agenda-date-anchor")) {
        schedule();
      }
    };

    try {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
        attributeFilter: ["class", "style", "aria-selected", "aria-pressed", "aria-current", "data-date", "data-day"],
      });
    } catch (_) {}

    document.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      document.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      if (observer) observer.disconnect();
    };
  }, [selectedDate, currentMonthDate, activeTab, viewportWidth]);


  // Phase 21: one source of truth for web calendar column widths.
  // FullCalendar renders the resource header and the time grid as separate tables;
  // these CSS variables keep both tables using the exact same width formula.
  const alignedResourceCount = Math.max(
    1,
    Array.isArray(effectiveCalendarResources) && effectiveCalendarResources.length
      ? effectiveCalendarResources.length
      : Array.isArray(employees)
        ? employees.length
        : 1
  );
  // LOOP CALENDAR OVERLAP LAYOUT V1 — viewport width, not runtime platform, owns day-grid geometry.
  const alignedAxisWidthPx = viewportWidth <= 430 ? 46 : viewportWidth <= 760 ? 52 : 72;
  // Give each mobile lane enough space to read a customer name; retain horizontal team navigation.
  const alignedWorkerColumnWidthPx = agendaWorkerColumnWidth(viewportWidth, alignedAxisWidthPx, alignedResourceCount);
  const alignedCalendarMinWidth =
    effectiveCalendarView === "resourceTimeGridDay"
      ? `${alignedAxisWidthPx + alignedResourceCount * alignedWorkerColumnWidthPx}px`
      : "100%";

  const canShowAgendaRightRail = !isNativeRuntime && activeTab === "agenda" && viewportWidth >= 1181;
  const showAgendaRightRail = canShowAgendaRightRail && !rightRailCollapsed;
  const isCompactRightRail = showAgendaRightRail && viewportWidth < 1100;
  const visibleEmployeeCount = selectedSet.size === 0 ? (employees || []).length : selectedSet.size;
  const canWorkerSwitchToTeamAgenda = Boolean((isWorkerView || hardWorkerView) && (workerPermissions.canViewAllCalendars || workerPermissions.canViewSelectedCalendars));
  const canShowWorkerFilter = !agendaRequiresWorkerSetup && !calendarShouldUseCompanyNoWorkers && Array.isArray(employees) && employees.length > 0 && (!isWorkerView || canWorkerSwitchToTeamAgenda);
  // LOOP AGENDA MOBILE COMMAND BAR FIX
  // On small screens the same popover owns View + Worker filters so the header stays one row.
  const canShowAgendaMobileControls = activeTab === "agenda" && viewportWidth < 600;

  // LOOP WORKER AGENDA INITIAL LOAD V10
  // Worker links used to paint a generic email-based agenda first and only later
  // repaint with the real worker profile, availability stripes and bookings.
  // Keep the agenda behind a single skeleton until the profile has a real signal,
  // with a short failsafe so access never gets stuck if a profile is incomplete.
  const workerAgendaProfileReadyForFirstPaint = useMemo(() => {
    const workerScope = Boolean(hardWorkerView || isWorkerView || workerPanelScope);
    if (!workerScope) return true;
    const candidates = [
      currentWorkerEmployee,
      routeWorkerProfile,
      ...(Array.isArray(personalWorkerAgendaResource) ? personalWorkerAgendaResource : []),
      ...(Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources : []),
    ].filter(Boolean);
    const hasRealSignal = (profile = {}) => {
      const id = String(profile.id || profile.employeeId || profile.workerId || hardDetectedEmployeeDocId || "").trim();
      const email = String(profile.email || profile.employeeEmail || profile.workerEmail || hardWorkerEmailMode || "").trim().toLowerCase();
      const name = String(profile.name || profile.displayName || profile.employeeName || profile.workerName || profile.title || "").trim();
      const genericName = !name || name === "Mi agenda" || name === "Sin nombre" || (email && name.toLowerCase() === email.split("@")[0]);
      const availability = profile.availability || profile.employeeAvailability || profile.workerAvailability || profile.schedule || profile.workingHours || profile.hours;
      return Boolean(
        id &&
        (
          !genericName ||
          adminWorkerPhotoUrl(profile) ||
          availability ||
          profile.permissions ||
          profile.workerPermissions
        )
      );
    };
    return candidates.some(hasRealSignal);
  }, [hardWorkerView, isWorkerView, workerPanelScope, currentWorkerEmployee, routeWorkerProfile, personalWorkerAgendaResource, effectiveCalendarResources, hardDetectedEmployeeDocId, hardWorkerEmailMode]);

  const workerRichAgendaReadyForFirstPaint = useMemo(() => {
    const workerScope = Boolean(hardWorkerView || isWorkerView || workerPanelScope);
    if (!workerScope) return true;
    const candidates = [
      currentWorkerEmployee,
      routeWorkerProfile,
      ...(Array.isArray(personalWorkerAgendaResource) ? personalWorkerAgendaResource : []),
      ...(Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources : []),
    ].filter(Boolean);
    const hasRichSignal = (profile = {}) => {
      const id = String(profile.id || profile.employeeId || profile.workerId || hardDetectedEmployeeDocId || detectedEmployeeDocId || "").trim();
      const availability = profile.availability || profile.employeeAvailability || profile.workerAvailability || profile.schedule || profile.workingHours || profile.hours;
      return Boolean(
        id &&
        (
          adminWorkerPhotoUrl(profile) ||
          availability ||
          profile.permissions ||
          profile.workerPermissions ||
          (Array.isArray(profile.services) && profile.services.length > 0) ||
          (Array.isArray(profile.serviceIds) && profile.serviceIds.length > 0)
        )
      );
    };
    return candidates.some(hasRichSignal);
  }, [hardWorkerView, isWorkerView, workerPanelScope, currentWorkerEmployee, routeWorkerProfile, personalWorkerAgendaResource, effectiveCalendarResources, hardDetectedEmployeeDocId, detectedEmployeeDocId]);

  // LOOP AGENDA — TOOLBAR TOP GAP JS HARDLOCK
  // Some accumulated responsive CSS can still compute margin-top on the toolbar
  // at desktop-mid widths. Force the actual rendered toolbar to sit at the top.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    let rafId = 0;
    const timers = [];

    const setImportant = (el, prop, value) => {
      if (!el) return;
      try { el.style.setProperty(prop, value, "important"); } catch (_) {}
    };

    const clearForced = (el) => {
      if (!el) return;
      [
        "margin",
        "margin-top",
        "margin-block-start",
        "margin-bottom",
        "margin-block-end",
        "top",
        "transform",
        "translate",
      ].forEach((prop) => {
        try { el.style.removeProperty(prop); } catch (_) {}
      });
    };

    const apply = () => {
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean.is-web-revamp.is-desktop-agenda");
      if (!rootEl) return;

      const width = window.innerWidth || document.documentElement.clientWidth || 0;
      const height = window.innerHeight || document.documentElement.clientHeight || 0;
      const isLandscape = width > height;
      const isMidDesktop = width >= 981 && width <= 1180 && isLandscape;

      const toolbarEl = rootEl.querySelector(".agenda-web-toolbar, .loop-calendar-toolbar");
      if (!toolbarEl) return;

      if (!isMidDesktop) {
        clearForced(toolbarEl);
        return;
      }

      toolbarEl.classList.add("loop-toolbar-top-gap-hardlocked");
      setImportant(toolbarEl, "margin", "0");
      setImportant(toolbarEl, "margin-top", "0px");
      setImportant(toolbarEl, "margin-block-start", "0px");
      setImportant(toolbarEl, "margin-bottom", "0px");
      setImportant(toolbarEl, "margin-block-end", "0px");
      setImportant(toolbarEl, "top", "auto");
      setImportant(toolbarEl, "transform", "none");
      setImportant(toolbarEl, "translate", "none");
      setImportant(toolbarEl, "align-self", "stretch");

      // Keep the toolbar children vertically centered after removing the rogue top margin.
      [
        ".agenda-web-toolbar-left",
        ".agenda-date-anchor",
        ".agenda-web-toolbar-right",
      ].forEach((selector) => {
        const child = rootEl.querySelector(selector);
        if (!child) return;
        setImportant(child, "margin", "0");
        setImportant(child, "margin-top", "0px");
        setImportant(child, "margin-block-start", "0px");
        setImportant(child, "top", "auto");
        setImportant(child, "transform", "none");
        setImportant(child, "align-self", "center");
      });
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(apply);
    };

    schedule();
    [40, 120, 260, 520, 900].forEach((ms) => timers.push(window.setTimeout(schedule, ms)));
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
    };
  }, [activeTab, viewMode, rightRailCollapsed, viewportWidth]);




  const agendaEmployeeCount = Array.isArray(employees) ? employees.length : 0;
  const agendaSelectedEmployeesKey = Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds.map(String).sort().join("|") : "";
  const agendaSurfaceSignature = useMemo(() => [
    String(activeBusinessId || ""),
    activeTab,
    isWorkerView ? "worker" : "owner",
    "own-only",
    calendarShouldUseCompanyNoWorkers ? "no-workers" : "workers-mode",
    agendaRequiresWorkerSetup ? "setup-lock" : "setup-ready",
    isEventBusinessAdmin ? "event" : "service",
    agendaEmployeeCount,
    agendaSelectedEmployeesKey,
  ].join("::"), [activeBusinessId, activeTab, isWorkerView, calendarShouldUseCompanyNoWorkers, agendaRequiresWorkerSetup, isEventBusinessAdmin, agendaEmployeeCount, agendaSelectedEmployeesKey]);

  const [agendaSurfaceReady, setAgendaSurfaceReady] = useState(false);
  const [workerAgendaForcedSafePaint, setWorkerAgendaForcedSafePaint] = useState(false);
  const [iosAgendaRuntimeDebug, setIosAgendaRuntimeDebug] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const readDebug = () => {
      try {
        const next = window.__LOOP_IOS_AGENDA_V16D_DEBUG__ || window.__LOOP_IOS_WORKER_BOOKINGS_DEBUG__ || null;
        if (next) setIosAgendaRuntimeDebug(next);
      } catch (_) {}
    };
    readDebug();
    window.addEventListener("loop-ios-agenda-debug", readDebug);
    const timer = window.setInterval(readDebug, 1200);
    return () => {
      window.removeEventListener("loop-ios-agenda-debug", readDebug);
      window.clearInterval(timer);
    };
  }, [iosAgendaDebug]);

  const agendaDebugEnabled = useMemo(() => {
    // LOOP AGENDA IOS BOOKINGS FILTER V16H:
    // Never show raw JSON/debug text to real iOS users by default. Keep it
    // available only when explicitly requested with ?agendaDebug=1 or
    // ?loopDebug=agenda.
    try {
      const params = new URLSearchParams(routeLocation?.search || "");
      if (params.get("agendaDebug") === "1" || params.get("loopDebug") === "agenda") return true;
    } catch (_) {}
    return false;
  }, [routeLocation?.search]);

  const compactIosAgendaDebug = useMemo(() => {
    const raw = iosAgendaRuntimeDebug || iosAgendaDebug || null;
    const v16d = raw?.v16d || raw || {};
    let firstPaint = null;
    let bookingsDebug = null;
    try {
      if (typeof window !== "undefined") {
        firstPaint = window.__LOOP_WORKER_FIRST_PAINT_LOCK_DEBUG__ || null;
        bookingsDebug = window.__LOOP_IOS_WORKER_BOOKINGS_DEBUG__ || null;
      }
    } catch (_) {}
    return {
      version: "V16E",
      stage: v16d.stage || raw?.stage || raw?.v16dStage || null,
      native: v16d.native ?? isIosCapacitor(),
      platform: v16d.runtime?.importedPlatform || v16d.runtime?.windowPlatform || null,
      hasNativeBridge: v16d.runtime?.hasNativeBridge ?? null,
      manualToken: v16d.manualAuth?.hasIdToken ?? null,
      manualValid: v16d.manualAuth?.valid ?? null,
      manualReason: v16d.manualAuth?.reason || null,
      businessId: activeBusinessId || v16d.businessId || null,
      workerId: hardDetectedEmployeeDocId || detectedEmployeeDocId || v16d.employeeId || v16d.effectiveWorkerId || null,
      workerEmail: workerEmailMode || v16d.effectiveWorkerEmail || null,
      loading,
      agendaSurfaceReady,
      forcedSafePaint: workerAgendaForcedSafePaint,
      richProfileReady: workerRichAgendaReadyForFirstPaint,
      profileReady: workerAgendaProfileReadyForFirstPaint,
      firstPaintStage: firstPaint?.stage || null,
      bookingsStage: bookingsDebug?.stage || bookingsDebug?.v16dStage || null,
      resourceCount: Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.length : 0,
      resources: (Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources : []).slice(0, 3).map((r) => ({
        id: r.id || r.employeeId || null,
        name: r.name || r.displayName || r.title || null,
        email: r.email || r.employeeEmail || r.workerEmail || null,
        hasPhoto: Boolean(adminWorkerPhotoUrl(r)),
        hasAvailability: Boolean(r.availability || r.schedule || r.workingHours || r.hours),
      })),
      eventCount: Array.isArray(calendarEvents) ? calendarEvents.length : 0,
      eventResourceIds: Array.from(new Set((Array.isArray(calendarEvents) ? calendarEvents : []).map((ev) => String(ev?.resourceId || "").trim()).filter(Boolean))).slice(0, 10),
      error: v16d.error || bookingsDebug?.error || null,
      at: v16d.at || raw?.at || null,
    };
  }, [iosAgendaRuntimeDebug, iosAgendaDebug, activeBusinessId, hardDetectedEmployeeDocId, detectedEmployeeDocId, workerEmailMode, loading, agendaSurfaceReady, workerAgendaForcedSafePaint, workerRichAgendaReadyForFirstPaint, workerAgendaProfileReadyForFirstPaint, effectiveCalendarResources, calendarEvents]);

  const iosWorkerBookingsLoadedForPaint = useMemo(() => {
    const stage = String(compactIosAgendaDebug?.bookingsStage || "").toLowerCase();
    const hasBookingsHydrationSignal = Boolean(stage && (
      stage.includes("success") ||
      stage.includes("set_state") ||
      stage.includes("fallback_success") ||
      stage.includes("bookings_rest_fallback_success") ||
      stage.includes("bookings_function_success")
    ));
    const hasRouteWorkerIdentity = Boolean(
      hardDetectedEmployeeDocId ||
      detectedEmployeeDocId ||
      hardWorkerEmailMode ||
      workerEmailMode ||
      currentWorkerEmployee ||
      routeWorkerProfile
    );
    return Boolean(isIosCapacitor() && workerPanelScope && hasRouteWorkerIdentity && hasBookingsHydrationSignal);
  }, [compactIosAgendaDebug, workerPanelScope, hardDetectedEmployeeDocId, detectedEmployeeDocId, hardWorkerEmailMode, workerEmailMode, currentWorkerEmployee, routeWorkerProfile]);

  useEffect(() => {
    // LOOP AGENDA — FAST NAV + POPOVER LAYER FIX:
    // Show the loading gate only for the first stable paint of a business/context.
    // After that, date changes through arrows/MiniMonth should keep the calendar
    // mounted and simply update the visible events.
    if (agendaSurfaceReadyTimerRef.current) clearTimeout(agendaSurfaceReadyTimerRef.current);

    const businessKey = String(activeBusinessId || "");
    if (agendaSurfaceReadyBusinessRef.current !== businessKey) {
      agendaSurfaceReadyBusinessRef.current = businessKey;
      agendaSurfaceEverReadyRef.current = false;
      agendaSurfaceReadySignatureRef.current = "";
      setWorkerAgendaForcedSafePaint(false);
    }

    if (activeTab !== "agenda") {
      agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
      agendaSurfaceEverReadyRef.current = true;
      setAgendaSurfaceReady(true);
      return undefined;
    }

    if (!activeBusinessId) {
      agendaSurfaceReadySignatureRef.current = "";
      agendaSurfaceEverReadyRef.current = false;
      setAgendaSurfaceReady(false);
      return undefined;
    }

    const canPaintAgendaWithoutBusinessDoc = Boolean(hardWorkerView || isWorkerView);
    if (!businessDoc && !canPaintAgendaWithoutBusinessDoc) {
      agendaSurfaceReadySignatureRef.current = "";
      agendaSurfaceEverReadyRef.current = false;
      setAgendaSurfaceReady(false);
      return undefined;
    }

    const workerPaintRequested = Boolean(hardWorkerView || isWorkerView);
    if (workerPaintRequested) {
      // LOOP AGENDA INSTANT PAINT V16:
      // Do not block the center grid behind "Cargando tu agenda" once the route
      // already identifies the worker. The worker profile, business doc, services
      // and right rail can hydrate in layers; the resource grid should mount first.
      // This removes the slow white center while preserving the route/employeeId
      // contract that prevents the old wrong-company/wrong-worker agenda.
      const workerFastPaintResourceCount = Array.isArray(effectiveCalendarResources) ? effectiveCalendarResources.length : 0;
      const workerFastPaintPersonalCount = Array.isArray(personalWorkerAgendaResource) ? personalWorkerAgendaResource.length : 0;
      const workerFastPaintHasRouteIdentity = Boolean(hardDetectedEmployeeDocId || detectedEmployeeDocId || hardWorkerEmailMode || workerEmailMode || currentWorkerEmployee || routeWorkerProfile);
      // LOOP AGENDA CORRECT FIRST PAINT V16C:
      // Route identity alone produced the visible ED/email/no-stripes flash. Keep the
      // center gate until a rich worker profile signal is present. After the first
      // correct paint, normal date/view navigation stays fast via agendaSurfaceEverReadyRef.
      // LOOP AGENDA IOS EMAIL SAFE PAINT V16E:
      // In iOS email-mode the route may not have an employeeId, so the rich-profile
      // function cannot run. Once worker-scoped bookings have hydrated by email, or
      // loading finished with a valid email route, paint the safe worker agenda.
      const workerEmailModeSafePaintEligible = Boolean(
        isIosCapacitor() &&
        workerFastPaintHasRouteIdentity &&
        !workerRichAgendaReadyForFirstPaint &&
        (
          iosWorkerBookingsLoadedForPaint ||
          workerAgendaForcedSafePaint ||
          (!hardDetectedEmployeeDocId && !detectedEmployeeDocId && Boolean(hardWorkerEmailMode || workerEmailMode) && loading === false)
        )
      );
      const workerCanShowCorrectFirstPaint = Boolean(workerRichAgendaReadyForFirstPaint || workerEmailModeSafePaintEligible || (agendaSurfaceEverReadyRef.current && (workerFastPaintResourceCount > 0 || workerFastPaintPersonalCount > 0 || workerFastPaintHasRouteIdentity || workerAgendaProfileReadyForFirstPaint)));
      if (workerCanShowCorrectFirstPaint) {
        agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
        agendaSurfaceEverReadyRef.current = true;
        setWorkerAgendaForcedSafePaint(Boolean(workerEmailModeSafePaintEligible && !workerRichAgendaReadyForFirstPaint));
        setAgendaSurfaceReady(true);
        try {
          if (typeof window !== "undefined") {
            window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__ = {
              stage: workerRichAgendaReadyForFirstPaint ? "correct_worker_first_paint_v16c" : (workerEmailModeSafePaintEligible ? (iosWorkerBookingsLoadedForPaint ? "ios_worker_bookings_safe_paint_v16e" : "ios_email_safe_paint_v16e") : (workerAgendaProfileReadyForFirstPaint ? "ready_final_worker_profile_v10b" : "instant_worker_grid_paint_v16")),
              businessId: activeBusinessId || null,
              hardWorkerView,
              isWorkerView,
              workerName: currentWorkerEmployee?.name || currentWorkerEmployee?.displayName || currentWorkerEmployee?.employeeName || routeWorkerProfile?.name || routeWorkerProfile?.employeeName || null,
              workerId: currentWorkerEmployee?.id || currentWorkerEmployee?.employeeId || hardDetectedEmployeeDocId || detectedEmployeeDocId || null,
              resourceCount: workerFastPaintResourceCount,
              personalResourceCount: workerFastPaintPersonalCount,
              richProfileReady: workerRichAgendaReadyForFirstPaint,
              emailSafePaintEligible: workerEmailModeSafePaintEligible,
              bookingsHydratedForPaint: iosWorkerBookingsLoadedForPaint,
              forcedSafePaint: Boolean(workerEmailModeSafePaintEligible && !workerRichAgendaReadyForFirstPaint),
              businessDocReady: Boolean(businessDoc),
              at: new Date().toISOString(),
            };
            window.__LOOP_WORKER_FIRST_PAINT_LOCK_DEBUG__ = window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__;
          }
        } catch (_) {}
        return undefined;
      }

      agendaSurfaceReadySignatureRef.current = "";
      agendaSurfaceEverReadyRef.current = false;
      setAgendaSurfaceReady(false);
      try {
        if (typeof window !== "undefined") {
          window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__ = {
            stage: "locked_waiting_rich_worker_profile_v16d",
            businessId: activeBusinessId || null,
            hardWorkerView,
            isWorkerView,
            hardDetectedEmployeeDocId,
            detectedEmployeeDocId,
            hardWorkerEmailMode,
            workerEmailMode,
            resourceCount: workerFastPaintResourceCount,
            personalResourceCount: workerFastPaintPersonalCount,
            routeIdentity: workerFastPaintHasRouteIdentity,
            richProfileReady: workerRichAgendaReadyForFirstPaint,
            profileReady: workerAgendaProfileReadyForFirstPaint,
            emailSafePaintEligible: workerEmailModeSafePaintEligible,
            bookingsHydratedForPaint: iosWorkerBookingsLoadedForPaint,
            bookingsStage: compactIosAgendaDebug?.bookingsStage || null,
            native: isIosCapacitor(),
            at: new Date().toISOString(),
          };
          window.__LOOP_WORKER_FIRST_PAINT_LOCK_DEBUG__ = window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__;
          window.__LOOP_IOS_AGENDA_V16D_DEBUG__ = {
            ...(window.__LOOP_IOS_AGENDA_V16D_DEBUG__ || {}),
            version: "LOOP AGENDA IOS LOADER FALLBACK V16D",
            stage: "waiting_rich_worker_profile_v16d",
            businessId: activeBusinessId || null,
            employeeId: hardDetectedEmployeeDocId || detectedEmployeeDocId || null,
            routeIdentity: workerFastPaintHasRouteIdentity,
            richProfileReady: workerRichAgendaReadyForFirstPaint,
            profileReady: workerAgendaProfileReadyForFirstPaint,
            emailSafePaintEligible: workerEmailModeSafePaintEligible,
            bookingsHydratedForPaint: iosWorkerBookingsLoadedForPaint,
            bookingsStage: compactIosAgendaDebug?.bookingsStage || null,
            native: isIosCapacitor(),
            at: new Date().toISOString(),
          };
        }
      } catch (_) {}

      if (isIosCapacitor()) {
        // LOOP AGENDA IOS LOADER FALLBACK V16D:
        // Never leave native iOS permanently on "Cargando tu agenda".
        // Give the rich profile/function path time to win; if it does not, mount the
        // safe worker-scoped agenda and keep hydration running in the background.
        agendaSurfaceReadyTimerRef.current = setTimeout(() => {
          agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
          agendaSurfaceEverReadyRef.current = true;
          setWorkerAgendaForcedSafePaint(true);
          setAgendaSurfaceReady(true);
          try {
            if (typeof window !== "undefined") {
              window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__ = {
                ...(window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__ || {}),
                stage: "ios_forced_safe_paint_v16e",
                forcedSafePaint: true,
                businessId: activeBusinessId || null,
                employeeId: hardDetectedEmployeeDocId || detectedEmployeeDocId || null,
                at: new Date().toISOString(),
              };
              window.__LOOP_WORKER_FIRST_PAINT_LOCK_DEBUG__ = window.__LOOP_WORKER_AGENDA_INITIAL_LOAD_DEBUG__;
              window.__LOOP_IOS_AGENDA_V16D_DEBUG__ = {
                ...(window.__LOOP_IOS_AGENDA_V16D_DEBUG__ || {}),
                version: "LOOP AGENDA IOS LOADER FALLBACK V16D",
                stage: "ios_forced_safe_paint_v16e",
                forcedSafePaint: true,
                businessId: activeBusinessId || null,
                employeeId: hardDetectedEmployeeDocId || detectedEmployeeDocId || null,
                at: new Date().toISOString(),
              };
              try { window.dispatchEvent(new CustomEvent("loop-ios-agenda-debug", { detail: window.__LOOP_IOS_AGENDA_V16D_DEBUG__ })); } catch (_) {}
            }
          } catch (_) {}
        }, iosWorkerBookingsLoadedForPaint ? 250 : 2200);
        return () => {
          if (agendaSurfaceReadyTimerRef.current) clearTimeout(agendaSurfaceReadyTimerRef.current);
        };
      }

      return undefined;
    }

    if ((loading || !businessDoc) && !agendaSurfaceEverReadyRef.current) {
      // LOOP PHASE 4B — do not trap worker/mobile agenda behind the loader.
      // The grid can paint with empty/partial data and hydrate as Firestore returns.
      // Before this, worker agenda could stay on "Preparando agenda" until the user
      // tapped Mis cobros and came back, because `loading` stayed true too long.
      setAgendaSurfaceReady(false);
      agendaSurfaceReadyTimerRef.current = setTimeout(() => {
        agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
        agendaSurfaceEverReadyRef.current = true;
        setAgendaSurfaceReady(true);
      }, hardWorkerView || isWorkerView ? 360 : 720);
      return () => {
        if (agendaSurfaceReadyTimerRef.current) clearTimeout(agendaSurfaceReadyTimerRef.current);
      };
    }

    if (agendaSurfaceEverReadyRef.current) {
      agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
      setAgendaSurfaceReady(true);
      return undefined;
    }

    setAgendaSurfaceReady(false);
    agendaSurfaceReadyTimerRef.current = setTimeout(() => {
      agendaSurfaceReadySignatureRef.current = agendaSurfaceSignature;
      agendaSurfaceEverReadyRef.current = true;
      setAgendaSurfaceReady(true);
    }, 120);
    return () => {
      if (agendaSurfaceReadyTimerRef.current) clearTimeout(agendaSurfaceReadyTimerRef.current);
    };
  }, [activeTab, activeBusinessId, businessDoc, loading, agendaSurfaceSignature, hardWorkerView, isWorkerView, workerAgendaProfileReadyForFirstPaint, workerRichAgendaReadyForFirstPaint, workerAgendaForcedSafePaint, iosWorkerBookingsLoadedForPaint, compactIosAgendaDebug, currentWorkerEmployee, routeWorkerProfile, hardDetectedEmployeeDocId, detectedEmployeeDocId, hardWorkerEmailMode, workerEmailMode, personalWorkerAgendaResource, effectiveCalendarResources]);

  React.useLayoutEffect(() => {
    // V57: publish popover overflow/stacking state before the browser paints.
    if (typeof document === "undefined") return undefined;
    const open = Boolean(viewDropdownOpen || workerFilterOpen || mobileToolsOpen);
    document.body.classList.toggle("loop-agenda-popover-open", open);
    return () => {
      try { document.body.classList.remove("loop-agenda-popover-open"); } catch (_) {}
    };
  }, [viewDropdownOpen, workerFilterOpen, mobileToolsOpen]);

  useEffect(() => {
    if (activeTab !== "agenda") setMobileToolsOpen(false);
  }, [activeTab]);

  useEffect(() => {
    // LOOP PHASE 4D — mobile header offset measured from the toolbar, not the root.
    // Phase 4C correctly avoided the toolbar sitting under the green Loop header,
    // but on Chrome mobile emulation the root was already below the app header and
    // the previous algorithm still added a full header-height margin. That created
    // the white band Juan marked. This version computes the toolbar's base position
    // by subtracting the current offset, then only adds the exact missing overlap.
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    let rafId = 0;
    const headerSelectors = [
      "#app-main-header",
      ".loop-ref-header",
      ".loop-mobile-header-v2",
      ".app-header",
      ".topbar",
      "header",
    ];

    const readPx = (value) => {
      const n = Number.parseFloat(String(value || "0").replace("px", ""));
      return Number.isFinite(n) ? n : 0;
    };

    const pickVisibleAppHeader = (rootEl) => {
      const candidates = headerSelectors
        .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
        .filter((el, index, arr) => el && arr.indexOf(el) === index)
        .filter((el) => el !== rootEl && !rootEl.contains(el));

      return candidates.find((el) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        const visible = rect.height >= 36 && rect.width >= Math.min(260, window.innerWidth * 0.55) && style.display !== "none" && style.visibility !== "hidden";
        const nearTop = rect.top <= 4 && rect.bottom > 36 && rect.bottom < 140;
        return visible && nearTop;
      }) || null;
    };

    const applyMobileHeaderOffset = () => {
      const rootEl = document.querySelector(".admin-calendar-root.phase23-clean");
      if (!rootEl) return;

      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (!isMobile) {
        rootEl.style.removeProperty("--loop-agenda-header-offset");
        rootEl.classList.remove("is-loop-header-overlapped");
        return;
      }

      const toolbarEl = rootEl.querySelector(".agenda-web-toolbar");
      const headerEl = pickVisibleAppHeader(rootEl);
      if (!toolbarEl || !headerEl) {
        rootEl.style.setProperty("--loop-agenda-header-offset", "0px");
        rootEl.classList.remove("is-loop-header-overlapped");
        return;
      }

      const currentOffset = readPx(rootEl.style.getPropertyValue("--loop-agenda-header-offset") || window.getComputedStyle(rootEl).getPropertyValue("--loop-agenda-header-offset"));
      const headerRect = headerEl.getBoundingClientRect();
      const toolbarRect = toolbarEl.getBoundingClientRect();

      // Where the toolbar would be if our own dynamic offset were zero.
      const toolbarBaseTop = toolbarRect.top - currentOffset;
      const rawNeededOffset = Math.ceil(headerRect.bottom - toolbarBaseTop);

      // Add only what is needed to avoid overlap. If the toolbar already starts
      // below the header in normal document flow, this becomes 0 and removes the
      // white band instead of adding another header-height.
      const safeOffset = Math.max(0, Math.min(96, rawNeededOffset));

      rootEl.style.setProperty("--loop-agenda-header-offset", `${safeOffset}px`);
      rootEl.classList.toggle("is-loop-header-overlapped", safeOffset > 0);
    };

    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(applyMobileHeaderOffset);
    };

    schedule();
    const timers = [window.setTimeout(schedule, 80), window.setTimeout(schedule, 280), window.setTimeout(schedule, 800), window.setTimeout(schedule, 1400)];
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    window.addEventListener("scroll", schedule, { passive: true });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.removeEventListener("scroll", schedule);
    };
  }, [activeTab, viewportWidth, isNativeRuntime]);

  const agendaFinanceQuickSummary = useMemo(() => buildAgendaFinanceSummary({
    bookings: agendaFinanceSourceBookings,
    services,
    employees,
    selectedDate,
    period: "day",
    isWorkerView: hardWorkerView,
    currentWorkerEmployee,
    resolvedWorkerEmployeeForPermissions,
    detectedEmployeeDocId: hardDetectedEmployeeDocId || detectedEmployeeDocId,
    effectiveWorkerEmployeeId,
    workerEmailMode: hardWorkerEmailMode || workerEmailMode || createWorkerEmailFallback,
    selectedEmployeeIds: hardSelectedEmployeeIds,
  }), [agendaFinanceSourceBookings, services, employees, selectedDate, hardWorkerView, currentWorkerEmployee, resolvedWorkerEmployeeForPermissions, hardDetectedEmployeeDocId, detectedEmployeeDocId, effectiveWorkerEmployeeId, hardWorkerEmailMode, workerEmailMode, createWorkerEmailFallback, hardSelectedEmployeeIds]);

  return (
    <div
      data-workspace-section={activeTab}
      className={`admin-calendar-root phase23-clean agenda-polished ${className} ${isNativeRuntime ? `is-native is-native-web-calendar is-native-safe-fit ${isIosNativeRuntime ? "is-ios-native" : ""} ${isAndroidNativeRuntime ? "is-android-native" : ""} ${agendaUsesResourceDayGrid ? "is-native-worker-columns" : ""}` : "is-web-revamp"} ${isMobileAgendaSurface ? "is-mobile-agenda-surface" : ""} ${agendaUsesResourceDayGrid ? "is-resource-day-grid" : "is-unified-day-grid"} ${nativeVisibleEmployees.length <= 1 ? "is-single-worker" : ""} ${isWorkerView ? "is-worker-view" : ""} ${canUseSoloCompanyAgenda ? "is-no-worker-company-agenda" : ""} ${isPersonalWorkerAgenda ? "is-personal-worker-agenda" : ""} ${agendaRequiresWorkerSetup ? "is-worker-setup-locked" : ""} ${viewportWidth < 981 ? "is-narrow-agenda" : ""} ${!isNativeRuntime && viewportWidth < 600 ? "is-phone-agenda" : ""} ${!isNativeRuntime && viewportWidth >= 600 && viewportWidth <= 980 ? "is-tablet-agenda" : ""} ${!isNativeRuntime && viewportWidth > 980 ? "is-desktop-agenda" : ""} ${(viewDropdownOpen || workerFilterOpen) ? "is-agenda-popover-open" : ""} ${viewMode === "month" ? "is-month-view" : ""} ${!isNativeRuntime && sidebarWidth <= 148 ? "is-sidebar-compact" : ""} ${!isNativeRuntime && ["pendings", "clients", "services", "workers", "analytics", "finance"].includes(activeTab) ? "is-linked-section" : ""}`}
      style={{
        display: "flex",
        gap: 0,
        alignItems: "stretch",
        padding: 0,
        position: "relative",
        paddingBottom: 0,
        "--loop-calendar-resource-count": alignedResourceCount,
        "--loop-calendar-axis-w": `${alignedAxisWidthPx}px`,
        "--loop-calendar-worker-col-w": `${alignedWorkerColumnWidthPx}px`,
        "--loop-calendar-min-w": alignedCalendarMinWidth,
        "--loop-right-rail-w": `${Math.round(rightRailWidth)}px`,
        "--loop-sidebar-w": `${Math.round(sidebarWidth)}px`,
        "--loop-native-bottom-inset": `${Math.round(nativeTabsBottomPad || 0)}px`,
        "--loop-native-navigation-guard": `${Math.round(nativeNavigationGuardPx || 0)}px`,
        "--loop-native-viewport-h": `${Math.max(480, Math.round(viewportHeight || 0))}px`,
        "--loop-agenda-viewport-h": `${Math.max(360, Math.round(viewportHeight || 0))}px`,
        "--loop-agenda-runtime-bottom-safe": `${Math.round(isNativeRuntime ? Math.max(nativeTabsBottomPad || 0, isAndroidNativeRuntime ? nativeNavigationGuardPx || 0 : 0) : 0)}px`,
      }}
    >
      <style>{licenseHideStyle}</style>
      {workerAccessError && (isWorkerView || hardWorkerView) && <div role="alert" style={{position: 'absolute', top: 8, left: '10%', right: '10%', zIndex: 50, padding: 12, background: '#fff8eb', border: '1px solid #edd9ac', borderRadius: 8, fontSize: 13}}>{workerAccessError} <button type="button" onClick={() => setReloadBookingsKey(value => value + 1)} style={{padding: 10, marginLeft: 8}}>{t("Actualizar")}</button></div>}
      <style>{dynamicResourceHeaderCss}</style>
      <ScheduleExceptionDialog />
      <AdminScheduleChangeDialog
        change={scheduleChangeDialog}
        saving={scheduleChangeSaving}
        onCancel={handleScheduleChangeCancel}
        onConfirm={handleScheduleChangeConfirm}
      />
      {scheduleChangeNotice ? (
        <div className={`loop-schedule-change-toast ${scheduleChangeNotice.tone === "error" ? "is-error" : "is-success"}`} role="status" aria-live="polite">
          <strong>{scheduleChangeNotice.title}</strong>
          {scheduleChangeNotice.message ? <span>{scheduleChangeNotice.message}</span> : null}
        </div>
      ) : null}

      {workerFilterOpen && canShowWorkerFilter ? (
        <div
          className={`agenda-worker-filter-priority-overlay ${showAgendaRightRail ? "is-over-open-panel" : "is-over-calendar"}`}
          role="dialog"
          aria-modal="false"
          aria-label={t("Filtrar equipo visible")}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="agenda-worker-filter-priority-head">
            <div>
              <strong>{t("Equipo visible")}</strong>
              <span>{visibleEmployeeCount}{" " + t("de") + " "}{agendaAuthorizedEmployees.length}{" " + t("trabajadores")}</span>
            </div>
            <button
              type="button"
              onClick={() => setWorkerFilterOpen(false)}
              aria-label={t("Cerrar filtro de equipo")}
              title={t("Cerrar filtro")}
            >
              ×
            </button>
          </div>

          <p className="agenda-team-hint">{t("Elige las columnas que quieres ver. Esto no modifica los permisos.")}</p>
          <div className="agenda-worker-filter-priority-actions">
            <input type="search" aria-label={t("Buscar trabajador")} placeholder={t("Buscar trabajador")} value={workerFilterSearch} onChange={event => setWorkerFilterSearch(event.target.value)} />
            <button type="button" onClick={() => { setSelectedEmployeeIds([]); setWorkerFilterSearch(""); }}>{t("Mostrar todo el equipo")}</button>
          </div>

          <div className="agenda-worker-filter-priority-list">
            {agendaAuthorizedEmployees.filter(employee => String(employee.name || employee.displayName || employee.employeeName || employee.email || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(workerFilterSearch.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim())).map((employee, idx) => {
              const employeeId = String(employee?.id || employee?.employeeId || employee?.docId || employee?.uid || `employee-${idx}`);
              const name = employee?.name || employee?.displayName || employee?.employeeName || employee?.email || "Trabajador";
              const role = employee?.role || employee?.position || employee?.specialty || employee?.title || "";
              const photo = adminWorkerPhotoUrl(employee);
              const selected = selectedSet.size === 0 || selectedSet.has(employeeId);

              return (
                <button
                  key={employeeId}
                  type="button"
                  className={`agenda-worker-filter-priority-row ${selected ? "is-selected" : ""}`}
                  onClick={() => toggleEmployeeId(employeeId)}
                  aria-pressed={selected}
                >
                  <span className="agenda-worker-filter-priority-avatar">
                    {photo ? <img src={photo} alt="" draggable="false" /> : workerInitials(name)}
                  </span>
                  <span className="agenda-worker-filter-priority-copy">
                    <strong>{shortWorkerName(name)}</strong>
                    <em>{selected ? "En vista" : "Fuera de vista"}{role ? ` · ${role}` : ""}</em>
                  </span>
                  <span className="agenda-worker-filter-priority-check" aria-hidden="true">{selected ? "✓" : ""}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* LOOP AGENDA MADRE CLEANUP D7 V78: removed dead inline FullCalendar/native style block. */}

      {/* ✅ NATIVE date picker input (hidden, but focusable) */}
      {isNativeRuntime ? (
        <input
          ref={nativeDateInputRef}
          type="date"
          value={toInputDateValue(selectedDate)}
          onChange={(e) => {
            const next = fromInputDateValue(e.target.value);
            if (!next) return;
            setSelectedDate(startOfDay(next));
          }}
          // Keep it "invisible" but still focusable/clickable in iOS
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: 1,
            height: 1,
            opacity: 0,
            zIndex: -1,
          }}
          aria-hidden="true"
          tabIndex={-1}
        />
      ) : null}

      {/* LOOP ADMIN NAVIGATION V2 A1.1 — grouped, collapsible web navigation; mobile/native contract remains unchanged. */}
      {/* Left sidebar (web) */}
      {!isNativeRuntime ? (
        <aside className="admin-cal-sidebar is-resizable" style={{ "--loop-sidebar-current-w": `${Math.round(sidebarWidth)}px` }}>
          <div className="admin-sidebar-business-card" title={sidebarBusinessZone ? `${sidebarBusinessName} · ${sidebarBusinessZone}` : sidebarBusinessName}>
            <div className={`admin-sidebar-business-avatar ${sidebarBusinessLogo ? "has-logo" : ""}`} aria-hidden="true">
              {sidebarBusinessLogo ? <img src={sidebarBusinessLogo} alt="" draggable="false" /> : sidebarBusinessInitials}
            </div>
            <div className="admin-sidebar-business-copy">
              <strong>{sidebarBusinessName}</strong>
              {sidebarBusinessZone ? <span>{sidebarBusinessZone}</span> : <span className="is-muted">{t("Panel del negocio")}</span>}
            </div>
          </div>

          <nav className="loop-admin-sidebar-nav" aria-label={t("Navegación del negocio")}>
            {adminSidebarDesktopGroups.map((group) => {
              const groupOpen = sidebarWidth <= 148 || adminSidebarGroupsOpen?.[group.id] !== false;
              return (
                <section
                  key={group.id}
                  className={`loop-admin-nav-group ${groupOpen ? "is-open" : "is-collapsed"}`}
                  aria-label={t(group.label)}
                >
                  <button
                    type="button"
                    className="loop-admin-nav-group-toggle"
                    onClick={() => toggleAdminSidebarGroup(group.id)}
                    aria-expanded={groupOpen ? "true" : "false"}
                    title={`${groupOpen ? "Contraer" : "Expandir"} ${t(group.label)}`}
                  >
                    <span>{t(group.label)}</span>
                    <i aria-hidden="true">{groupOpen ? "⌃" : "⌄"}</i>
                  </button>

                  <div className="loop-admin-nav-group-body" hidden={!groupOpen}>
                    {group.items.map((item) => {
                      const isActive = adminSidebarItemIsActive(item);
                      return (
                        <button
                          key={item.key || `${group.id}-${item.tab}-${item.analyticsView || "main"}`}
                          type="button"
                          title={item.label}
                          data-label={item.label}
                          className={`admin-cal-section-pill loop-admin-nav-item ${isActive ? "is-active" : ""}`}
                          onClick={() => handleAdminSidebarItem(item)}
                          aria-current={isActive ? "page" : undefined}
                        >
                          <span className="admin-cal-section-icon"><LoopAdminPanelIcon name={item.icon} /></span>
                          <span className="admin-cal-section-copy"><strong>{t(item.label)}</strong></span>
                          {item.badge ? <span className="admin-cal-pill-badge">{item.badge}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </nav>

          {!workerPanelScope ? (
            <button
              type="button"
              className={`admin-agenda-plan-card is-${agendaLoopPlanCard.tone}`}
              onClick={() => handleAdminSidebarTab("settings")}
              title={`${agendaLoopPlanCard.label} · Ver beneficios`}
              aria-label={`${agendaLoopPlanCard.label}. Ver beneficios`}
            >
              <img src={agendaLoopPlanCard.image} alt="" draggable="false" />
            </button>
          ) : null}

          <button
            type="button"
            className="admin-cal-sidebar-resizer"
            aria-label={t("Cambiar ancho del menú lateral")}
            title={t("Arrastra para cambiar el ancho del menú")}
            onMouseDown={handleSidebarResizeStart}
            onTouchStart={handleSidebarResizeStart}
            onDoubleClick={() => {
              setSidebarWidth(188);
              try { window.localStorage.setItem("loopAgendaSidebarWidth", "188"); } catch (_) {}
            }}
          />

          <div className="mini-month">
            <MiniMonth
              currentMonthDate={currentMonthDate}
              selectedDate={selectedDate}
              onSelectDate={(d) => {
                setSelectedDate(d);
                setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
              }}
              onPrevMonth={() => setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              onNextMonth={() => setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            />
          </div>

        </aside>
      ) : null}

      {/* Main content — Phase 23B Layout Cleanup: agenda + right rail live in the same grid */}
      <div
        className={`agenda-workspace-fixed loop-agenda-clean ${showAgendaRightRail ? "has-right-rail" : "no-right-rail"} ${isCompactRightRail ? "is-compact-right-rail" : ""}`}
        style={{
          display: "grid",
          gridTemplateRows: "minmax(0, 1fr)",
          gridTemplateColumns: showAgendaRightRail ? (isCompactRightRail ? "minmax(0, 1fr) 300px" : "minmax(0, 1fr) 330px") : "minmax(0, 1fr)",
          alignItems: "stretch",
          width: "100%",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          className="admin-cal-main"
          style={{
            gridColumn: 1,
            gridRow: 1,
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
        {/* ✅ Native header */}
        {false ? (
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 6px 18px rgba(15,23,42,0.045)",
              border: "1px solid #eef2f7",
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <button
                onClick={() => {
                  if (viewMode === "weekCompany") {
                    setSelectedDate(
                      getAgendaWeekStart(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 7), effectiveAgendaSettings.weekStartsOn)
                    );
                  } else {
                    setSelectedDate(
                      startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 1))
                    );
                  }
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontWeight: 900,
                  color: "#0f172a",
                }}
              >
                {"<"}
              </button>

              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
                  {viewMode === "weekCompany" ? t("Semana") : "Agenda"}
                </div>
                <div style={{ fontSize: 16, color: "#0f172a", fontWeight: 950 }}>
                  {formatSelectedDateHuman(selectedDate)}
                </div>
              </div>

              <button
                onClick={() => {
                  if (viewMode === "weekCompany") {
                    setSelectedDate(
                      getAgendaWeekStart(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 7), effectiveAgendaSettings.weekStartsOn)
                    );
                  } else {
                    setSelectedDate(
                      startOfDay(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() + 1))
                    );
                  }
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontWeight: 900,
                  color: "#0f172a",
                }}
              >
                {">"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                type="button"
                onClick={() => setNativeFiltersOpen((s) => !s)}
                style={{
                  borderRadius: 999,
                  border: "1px solid #dbe3ee",
                  background: nativeFiltersOpen ? "#f0fdfa" : "#fff",
                  color: "#0f172a",
                  padding: "7px 10px",
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >
                Filtros
              </button>

              <button
                type="button"
                onClick={openNativeDatePicker}
                style={{
                  borderRadius: 999,
                  border: "1px solid #dbe3ee",
                  background: "#fff",
                  color: "#0f172a",
                  padding: "7px 10px",
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >{t("Calendario")}</button>

              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    const nextDate = getBestDayWhenLeavingRangeView(selectedDate, viewMode, effectiveAgendaSettings);
                    setViewMode("day");
                    setSelectedDate(nextDate);
                    setCurrentMonthDate(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
                  }}
                  style={{
                    borderRadius: 999,
                    border: viewMode === "day" ? "1px solid #14b8a6" : "1px solid #dbe3ee",
                    background: viewMode === "day" ? "#f0fdfa" : "#fff",
                    color: "#0f172a",
                    padding: "7px 10px",
                    fontWeight: 900,
                    fontSize: 12,
                  }}
                >{t("Día")}</button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode("weekCompany");
                    setSelectedDate(getAgendaWeekStart(selectedDate, effectiveAgendaSettings.weekStartsOn));
                  }}
                  style={{
                    borderRadius: 999,
                    border: viewMode === "weekCompany" ? "1px solid #14b8a6" : "1px solid #dbe3ee",
                    background: viewMode === "weekCompany" ? "#f0fdfa" : "#fff",
                    color: "#0f172a",
                    padding: "7px 10px",
                    fontWeight: 900,
                    fontSize: 12,
                  }}
                >{t("Semana")}</button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: 2,
                overflowX: "visible",
                WebkitOverflowScrolling: "touch",
                paddingBottom: 0,
              }}
            >
              {nativeWeekDays.map((d) => {
                const active = sameDay(d, selectedDate);
                const today = sameDay(d, new Date());
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    onClick={() => setSelectedDate(startOfDay(d))}
                    style={{
                      minWidth: 0,
                      borderRadius: 11,
                      border: active ? "1px solid #14b8a6" : "1px solid #e5e7eb",
                      background: active ? "#14b8a6" : today ? "#f8fafc" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      padding: "4px 2px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 7.5, fontWeight: 900, opacity: active ? 0.95 : 0.65, lineHeight: 1 }}>
                      {d.toLocaleDateString(locale, { weekday: "short" }).replace(".", "").toUpperCase()}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 950, lineHeight: 1 }}>{d.getDate()}</span>
                  </button>
                );
              })}
            </div>

            {nativeFiltersOpen ? (
              <div
                style={{
                  borderTop: "1px solid #eef2f7",
                  paddingTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>{t("Trabajadores visibles")}</div>
                  <button
                    type="button"
                    onClick={() => setSelectedEmployeeIds(employees.map((e) => String(e.id)))}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#0f172a",
                      fontWeight: 800,
                      fontSize: 13,
                    }}
                  >{t("Todos")}</button>
                </div>

                <div style={{ display: "flex", gap: 10, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
                  {(employees || []).map((w) => {
                    const checked = selectedSet.size === 0 || selectedSet.has(String(w.id));
                    const name = w.name || w.displayName || "Trabajador";
                    const photo = adminWorkerPhotoUrl(w);
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => toggleEmployeeId(w.id)}
                        style={{
                          minWidth: 92,
                          borderRadius: 18,
                          border: checked ? "2px solid #14b8a6" : "1px solid #e5e7eb",
                          background: checked ? "#f0fdfa" : "#fff",
                          padding: 10,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: 999,
                            overflow: "hidden",
                            background: "#f8fafc",
                            border: checked ? "2px solid #14b8a6" : "2px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {photo ? (
                            <img src={photo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <span style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{workerInitials(name)}</span>
                          )}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 800, color: "#0f172a", textAlign: "center", lineHeight: 1.1 }}>
                          {shortWorkerName(name)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : activeTab === "agenda" ? (
          <CalendarToolbar
            compact={isNativeRuntime || viewportWidth < 981}
            dropdownRef={dropdownRef}
            viewMode={viewMode}
            setViewMode={setViewMode}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            currentMonthDate={currentMonthDate}
            setCurrentMonthDate={setCurrentMonthDate}
            agendaSettings={effectiveAgendaSettings}
            viewDropdownOpen={viewDropdownOpen}
            setViewDropdownOpen={setViewDropdownOpen}
            workerFilterOpen={false}
            setWorkerFilterOpen={(nextValue) => {
              if (typeof nextValue === "function") {
                setWorkerFilterOpen((prev) => nextValue(prev));
                return;
              }
              if (nextValue === true) {
                setWorkerFilterOpen((prev) => !prev);
                return;
              }
              setWorkerFilterOpen(Boolean(nextValue));
            }}
            canCreateBooking={canCreateBooking && !agendaRequiresWorkerSetup}
            openSmartCreateForDate={openSmartCreateForDate}
            canShowAgendaRightRail={canShowAgendaRightRail}
            showAgendaRightRail={showAgendaRightRail}
            setRightRailCollapsed={setRightRailCollapsed}
            canShowWorkerFilter={canShowWorkerFilter}
            canShowAgendaMobileControls={canShowAgendaMobileControls}
            visibleEmployeeCount={visibleEmployeeCount}
            employees={employees}
            selectedSet={selectedSet}
            setSelectedEmployeeIds={setSelectedEmployeeIds}
            toggleEmployeeId={toggleEmployeeId}
            getAgendaWeekStart={getAgendaWeekStart}
            getBestDayWhenLeavingRangeView={getBestDayWhenLeavingRangeView}
            formatSelectedDateHuman={formatSelectedDateHuman}
            workerInitials={workerInitials}
            roleContext={agendaRoleContext}
          />
        ) : null}
        {/* LOOP AGENDA MADRE CLEANUP D2 V73 — removed disabled mobile week strip. */}

        <div
          className={`admin-cal-card ${activeTab === "agenda" ? "agenda-unified-card" : ""}`}
          onDoubleClickCapture={handleCalendarDoubleClickCapture}
          style={{
            background: "#fff",
            borderRadius: 0,
            padding: 0,
            boxShadow: "none",
            border: "none",
          }}
        >
          {activeTab === "agenda" && (bookingsLoadError || workerAccessError) ? (
            <div className="agenda-range-empty" role="alert"><p>{bookingsLoadError || workerAccessError}</p><button type="button" onClick={()=>triggerBookingAgendaRefresh('retry_range_load')}>{t("Reintentar")}</button></div>
          ) : activeTab === "agenda" && !agendaSurfaceReady ? (
            <div className="loop-agenda-surface-loading" role="status" aria-live="polite">
              <div className="loop-agenda-surface-loading-card">
                <strong>{workerPanelScope ? "Cargando tu agenda" : "Preparando agenda"}</strong>
                <span>{workerPanelScope ? "Sincronizando tu horario, reservas y permisos…" : "Sincronizando reservas, equipo y permisos…"}</span>
                {agendaDebugEnabled ? (
                  <pre
                    style={{
                      margin: "12px 0 0",
                      padding: "10px",
                      maxWidth: "min(82vw, 520px)",
                      maxHeight: 240,
                      overflow: "auto",
                      borderRadius: 12,
                      background: "rgba(15,23,42,0.06)",
                      color: "#334155",
                      fontSize: 11,
                      lineHeight: 1.35,
                      textAlign: "left",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {JSON.stringify(compactIosAgendaDebug, null, 2)}
                  </pre>
                ) : null}
              </div>
            </div>
          ) : activeTab === "agenda" && agendaRequiresWorkerSetup ? (
            <AgendaWorkerSetupLock
              businessName={businessDoc?.name || businessDoc?.businessName || businessDoc?.publicName || "tu negocio"}
              onGoWorkers={() => handleAdminSidebarTab("workers")}
              onGoServices={() => handleAdminSidebarTab("services")}
            />
          ) : activeTab === "agenda" && (
            <>
              {loading && <div className="agenda-range-summary" role="status">{t("Actualizando reservas…")}</div>}
              {canShowWorkerFilter && selectedEmployeeIds.length>0 && selectedEmployeeIds.length<employees.length && <div className="agenda-range-controls"><span>{selectedEmployeeIds.length}{" " + t("profesionales seleccionados")}</span><button type="button" onClick={()=>setSelectedEmployeeIds([])}>{t("Quitar filtro")}</button></div>}
              {/* LOOP AGENDA — SINGLE CUSTOM RENDER ROUTER HARD FIX
                  Agenda now has exactly one visual router for Day / Week / Month.
                  FullCalendar is no longer the fallback for the agenda tab, because it was
                  creating a second agenda style and making worker/day render blank. */}
              {isMobileAgendaSurface && viewMode === "day" && <div className="agenda-day-switch" role="group" aria-label={t("Presentación del día")}>
                <button type="button" aria-pressed={mobileDayPresentation === "list"} onClick={() => chooseMobileDayPresentation("list")}>{t("Mi día")}</button>
                <button type="button" aria-pressed={mobileDayPresentation === "team"} onClick={() => chooseMobileDayPresentation("team")}>{t("Equipo")}</button>
                {canShowWorkerFilter && <button type="button" onClick={() => setWorkerFilterOpen(true)}>{t("Filtrar equipo")}</button>}
              </div>}
              {isMobileAgendaSurface && viewMode === "day" && mobileDayPresentation === "list" ? (
                <AgendaDayList events={unifiedAgendaEvents} selectedDate={selectedDate} resources={effectiveCalendarResources} roleContext={agendaRoleContext} timeFormat={effectiveAgendaSettings.timeFormat} onEventClick={ev => {
                  const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || null;
                  if (raw) openEditForBooking(raw);
                }} onShowTeam={() => chooseMobileDayPresentation("team")} />
              ) : activeTab === "agenda" && viewMode === "month" ? (
                <LoopUnifiedMonthView
                  loading={loading}
                  onCreate={canCreateBooking ? day=>openSmartCreateForDate(day,getCreateResourceId()) : null}
                  events={monthAgendaEvents}
                  selectedDate={selectedDate}
                  locale={locale}
                  weekStartsOn={effectiveAgendaSettings.weekStartsOn}
                  timeFormat={effectiveAgendaSettings.timeFormat}
                  colorMode={effectiveAgendaSettings.colorMode}
                  viewportWidth={viewportWidth}
                  onDayClick={(day) => {
                    setSelectedDate(startOfDay(day));
                    setCurrentMonthDate(new Date(day.getFullYear(), day.getMonth(), 1));
                    setViewMode("day");
                  }}
                  onEventClick={(ev) => {
                    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || null;
                    if (raw) openEditForBooking(raw);
                  }}
                  roleContext={agendaRoleContext}
                />
              ) : activeTab === "agenda" && viewMode === "weekCompany" ? (
                <LoopUnifiedWeekView
                  events={unifiedAgendaEvents}
                  selectedDate={selectedDate}
                  height={isMobileAgendaSurface ? "100%" : calendarHeight}
                  locale={locale}
                  weekStartsOn={effectiveAgendaSettings.weekStartsOn}
                  showWeekends={effectiveAgendaSettings.showWeekends}
                  minTime={effectiveAgendaSettings.minTime}
                  maxTime={effectiveAgendaSettings.maxTime}
                  rowDensity={isMobileAgendaSurface ? "compact" : effectiveAgendaSettings.rowDensity}
                  timeFormat={effectiveAgendaSettings.timeFormat}
                  colorMode={effectiveAgendaSettings.colorMode}
                  viewportWidth={viewportWidth}
                  isWorkerView={isWorkerView}
                  resources={weekCalendarResources}
                  selectedResourceIds={selectedEmployeeIds}
                  businessDoc={businessDoc}
                  slotMinutes={effectiveAgendaSettings.slotMinutes}
                  onSlotClick={(slotDate) => openSmartCreateForDate(slotDate, hasWorkerPermissionContext && !ownerOrAdmin ? effectiveWorkerEmployeeId : getCreateResourceId())}
                  onDayClick={(day) => {
                    setSelectedDate(startOfDay(day));
                    setCurrentMonthDate(new Date(day.getFullYear(), day.getMonth(), 1));
                  }}
                  onEventClick={(ev) => {
                    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || null;
                    if (raw) openEditForBooking(raw);
                  }}
                  roleContext={agendaRoleContext}
                />
              ) : activeTab === "agenda" && viewMode === "day" ? (
                <LoopResourceDayGrid
                  resources={effectiveCalendarResources}
                  events={unifiedAgendaEvents}
                  selectedDate={selectedDate}
                  height={isMobileAgendaSurface ? "100%" : calendarHeight}
                  onSlotClick={(date, resourceId) => openSmartCreateForDate(date, resourceId)}
                  onEventClick={(ev) => {
                    const raw = ev?.extendedProps?.raw || ev?.raw || ev?.booking || null;
                    if (raw) openEditForBooking(raw);
                  }}
                  canEditEvents={canEdit}
                  onEventQuickChange={(change) => confirmAndApplyBookingScheduleChange({ ...change })}
                  slotMinutes={effectiveAgendaSettings.slotMinutes}
                  rowDensity={isMobileAgendaSurface ? "compact" : effectiveAgendaSettings.rowDensity}
                  timeFormat={effectiveAgendaSettings.timeFormat}
                  minTime={effectiveAgendaSettings.minTime}
                  maxTime={effectiveAgendaSettings.maxTime}
                  scrollStartTime={effectiveAgendaSettings.scrollStartTime}
                  colorMode={effectiveAgendaSettings.colorMode}
                  businessDoc={businessDoc}
                  axisWidthOverride={alignedAxisWidthPx}
                  resourceWidthOverride={alignedWorkerColumnWidthPx}
                  headerHeightOverride={isMobileAgendaSurface ? 60 : undefined}
                  roleContext={agendaRoleContext}
                />
              ) : activeTab === "agenda" ? (
                <div
                  className="loop-agenda-view-fallback-guard"
                  data-loop-agenda-cleanup-d1-v72="unexpected-view"
                  hidden
                />
              ) : null}


              {!isNativeRuntime && webServiceLegend.length ? (
                <div className="agenda-service-legend" aria-label={t("Leyenda de servicios")}>
                  {webServiceLegend.map((item) => (
                    <span key={item.label} className={`agenda-service-legend-item service-tone-${item.tone}`}>
                      <i aria-hidden="true" />
                      {item.label}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {activeTab === "pendings" && (
            <div className="loop-admin-section-scroll loop-admin-pendings-canvas">
              {!workerPanelScope && isCurrentUserOwnerOrAdmin() && activeBusinessId ? (
                <BookingPaymentReviewPanel key={activeBusinessId} db={db} businessId={activeBusinessId} onOpen={setEventModalBooking} />
              ) : null}
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              <PendingsPanel
                db={db}
                businessId={activeBusinessId}
                user={getEffectiveActor().uid || getEffectiveActor().email ? getEffectiveActor() : null}
                isAdmin={workerPanelScope ? true : isCurrentUserOwnerOrAdmin()}
                workerScope={Boolean(workerPanelScope)}
                workerEmployeeId={effectiveWorkerEmployeeId || hardDetectedEmployeeDocId || detectedEmployeeDocId}
                workerPermissions={workerPermissions}
                workerProfile={currentWorkerEmployee || routeWorkerProfile || null}
                focusedOnlyForWorker={true}
                onBack={() => setActiveTab("agenda")}
                employees={employees}
                services={services}
                allBookings={bookings}
                businessDoc={businessDoc}
                calendarSettings={agendaSettings}
                onRefresh={() => setReloadBookingsKey((k) => k + 1)}
                onApprovalModeSaved={(mode, nextSettings = {}) => {
                  const normalizedNext = normalizeAgendaSettings({
                    ...(agendaSettingsRef.current || agendaSettings || {}),
                    ...(nextSettings || {}),
                    approvalMode: mode === "auto" ? "auto" : "manual",
                  });
                  const savedPayload = serializeAgendaSettings(normalizedNext);
                  const savedSignature = agendaSettingsSignature(activeBusinessId, savedPayload);
                  agendaSettingsRef.current = normalizedNext;
                  setAgendaSettings(normalizedNext);
                  setAgendaSettingsDirty(false);
                  setAgendaSettingsSaveStatus("saved");
                  setAgendaSettingsSaveError("");
                  setAgendaSettingsSavedAt(new Date().toLocaleTimeString(interfaceLocale(), {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: normalizedNext.timeFormat === "12h",
                  }));
                  agendaSettingsLoadedSignatureRef.current = savedSignature;
                  agendaSettingsPendingFirestoreSignatureRef.current = savedSignature;
                }}
              />
            </div>
          )}

          {!workerPanelScope && activeTab === "workers" && (
            <div className="loop-admin-section-scroll loop-admin-panel-embedded loop-admin-workers-embedded">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              {editorNavigation.dialog}
              <WorkersSection
                businessId={activeBusinessId}
                businessDoc={businessDoc}
                employees={employees || []}
                services={services || []}
                onDeleteEmployee={handleAdminDeleteEmployee}
                onSaveEmployee={handleAdminSaveEmployee}
                onAccessSaved={() => setReloadBookingsKey(value => value + 1)}
                onAddEmployee={handleAdminAddEmployee}

                openCropper={null}
                onUploadEmployeeProjects={handleInlineUploadUnavailable}
              />
            </div>
          )}

          {!workerPanelScope && activeTab === "clients" && (
            <div className="loop-admin-section-scroll loop-admin-panel-embedded loop-admin-clients-embedded loop-admin-crm-direct-embedded" style={{ padding: 0, overflow: "auto" }}>
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              {editorNavigation.dialog}<CustomersPage businessId={activeBusinessId} embedded />
            </div>
          )}

          {!workerPanelScope && activeTab === "services" && (
            <div className="loop-admin-section-scroll loop-admin-panel-embedded loop-admin-services-embedded">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              {editorNavigation.dialog}
              <ServicesSection businessDoc={businessDoc}
                businessId={activeBusinessId}
                services={services || []}
                isEventsBusiness={isEventBusinessAdmin}
                onAddService={handleAdminAddService}
                onSaveService={handleAdminSaveService}
                onDeleteService={handleAdminDeleteService}

                onUploadServiceImage={handleInlineUploadUnavailable}
                onServicesChange={() => setReloadBookingsKey((k) => k + 1)}
              />
            </div>
          )}

          {!workerPanelScope && activeTab === "loyalty" && (
            <div className="loop-admin-section-scroll loop-admin-panel-embedded">
              {editorNavigation.dialog}
              <LoyaltySection businessId={activeBusinessId} businessDoc={businessDoc} services={services || []} db={db} />
            </div>
          )}
          {!workerPanelScope && activeTab === "memberships" && (
            <div className="loop-admin-section-scroll loop-admin-panel-embedded">
              {editorNavigation.dialog}
              <MembershipSection businessId={activeBusinessId} businessDoc={businessDoc} services={services || []} db={db} />
            </div>
          )}

          {!workerPanelScope && activeTab === "settings" && (
            <div className="loop-admin-section-scroll loop-admin-settings-canvas">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
            <AgendaSettingsPanel
              settings={agendaSettings}
              onChange={(next) => {
                const normalizedNext = normalizeAgendaSettings(next);
                agendaSettingsRef.current = normalizedNext;
                setAgendaSettings(normalizedNext);
                setAgendaSettingsDirty(true);
                setAgendaSettingsSaveStatus("idle");
                setAgendaSettingsSaveError("");
              }}
              onApplyDefaultView={(nextView) => setViewMode(nextView)}
              onReset={() => {
                const ok = typeof window === "undefined"
                  ? true
                  : window.confirm("¿Volver a los valores recomendados de agenda? Los cambios se aplicarán en pantalla y solo se guardarán si presionas Guardar cambios.");
                if (!ok) return;
                const defaults = normalizeAgendaSettings();
                agendaSettingsRef.current = defaults;
                setAgendaSettings(defaults);
                setViewMode(defaults.defaultView);
                setAgendaSettingsDirty(true);
                setAgendaSettingsSaveStatus("idle");
                setAgendaSettingsSaveError("");
              }}
              onSave={async () => {
                if (!activeBusinessId) {
                  setAgendaSettingsSaveStatus("error");
                  setAgendaSettingsSaveError("No se encontró el negocio activo para guardar.");
                  return;
                }

                const currentSettings = normalizeAgendaSettings(agendaSettingsRef.current || agendaSettings);
                const payload = serializeAgendaSettings(currentSettings);
                const savedSignature = agendaSettingsSignature(activeBusinessId, payload);
                setAgendaSettingsSaveStatus("saving");
                setAgendaSettingsSaveError("");

                try {
                  await updateBusinessCalendarSettingsSafe(activeBusinessId, payload);
                  agendaSettingsPendingFirestoreSignatureRef.current = savedSignature;
                  agendaSettingsRef.current = currentSettings;
                  setAgendaSettings(currentSettings);
                  const savedTime = new Date().toLocaleTimeString(interfaceLocale(), {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: currentSettings.timeFormat === "12h",
                  });
                  setAgendaSettingsSavedAt(savedTime);
                  setAgendaSettingsDirty(false);
                  setAgendaSettingsSaveStatus("saved");
                  agendaSettingsLoadedSignatureRef.current = savedSignature;
                } catch (error) {
                  console.error("Error saving calendar settings", error);
                  setAgendaSettingsSaveStatus("error");
                  setAgendaSettingsSaveError("No se pudo guardar. Revisa permisos de Firestore o intenta de nuevo.");
                }
              }}
              savedAt={agendaSettingsSavedAt}
              saveStatus={agendaSettingsSaveStatus}
              saveError={agendaSettingsSaveError}
              isDirty={agendaSettingsDirty}
            />
            </div>
          )}

          {activeTab === "finance" && workerPanelScope && canViewAgendaFinance && (
            <div className="loop-admin-section-scroll loop-admin-finance-canvas loop-worker-finance-canvas">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              <AgendaFinanceMini
                businessDoc={businessDoc}
                businessId={activeBusinessId}
                firestoreDb={db}
                bookings={agendaFinanceSourceBookings}
                services={services}
                employees={employees}
                selectedDate={selectedDate}
                isWorkerView={hardWorkerView}
                currentWorkerEmployee={currentWorkerEmployee}
                resolvedWorkerEmployeeForPermissions={resolvedWorkerEmployeeForPermissions}
                detectedEmployeeDocId={hardDetectedEmployeeDocId || detectedEmployeeDocId}
                effectiveWorkerEmployeeId={effectiveWorkerEmployeeId}
                workerEmailMode={hardWorkerEmailMode || workerEmailMode || createWorkerEmailFallback}
                selectedEmployeeIds={hardSelectedEmployeeIds}
                onBackToAgenda={() => handleAdminSidebarTab("agenda")}
                onOpenReports={!hardWorkerView && canViewBusinessAnalytics ? () => handleAdminSidebarTab("analytics") : null}
              />
            </div>
          )}

          {!workerPanelScope && activeTab === "finance" && canViewBusinessAnalytics && (
            <div className="loop-admin-linked-canvas loop-admin-analytics-canvas loop-admin-finance-canvas">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              <BusinessAnalytics embeddedNavigation businessId={activeBusinessId} db={db} permissions={actorBusinessPermissions} isOwnerOrAdmin={ownerOrAdmin} businessSettings={loopBusinessSettings} initialView="finance" />
            </div>
          )}

          {!workerPanelScope && activeTab === "analytics" && canViewBusinessAnalytics && (
            <div className="loop-admin-linked-canvas loop-admin-analytics-canvas">
              {showLinkedSectionHeader ? (
                <AgendaLinkedSectionHeader title={linkedSectionTitle} subtitle={linkedSectionSubtitle} onBack={() => handleAdminSidebarTab("agenda")} />
              ) : null}
              <BusinessAnalytics embeddedNavigation businessId={activeBusinessId} db={db} permissions={actorBusinessPermissions} isOwnerOrAdmin={ownerOrAdmin} businessSettings={loopBusinessSettings} initialView={analyticsInitialView} />
            </div>
          )}
        </div>
        </div>


        {canShowAgendaRightRail && rightRailCollapsed ? (
          <button
            type="button"
            className="agenda-right-rail-reopen-tab"
            onClick={() => setRightRailCollapsed(false)}
            aria-label={t("Mostrar panel lateral de calendario y resumen")}
            title={t("Mostrar panel lateral")}
          >
            <span aria-hidden="true">▦</span>
            <strong>{t("Panel")}</strong>
            <small>{t("Calendario y resumen")}</small>
          </button>
        ) : null}

        {showAgendaRightRail ? (
          <aside
            className="agenda-right-panel is-resizable"
            style={{
              gridColumn: 2,
              gridRow: 1,
              width: isCompactRightRail ? Math.min(320, rightRailWidth) : rightRailWidth,
              minWidth: isCompactRightRail ? Math.min(320, rightRailWidth) : rightRailWidth,
              maxWidth: isCompactRightRail ? Math.min(320, rightRailWidth) : rightRailWidth,
              height: "100%",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
          <button
            type="button"
            className="agenda-right-resizer"
            aria-label={t("Cambiar ancho del panel lateral")}
            title={t("Arrastra para cambiar el ancho del panel")}
            onMouseDown={handleRightRailResizeStart}
            onTouchStart={handleRightRailResizeStart}
          />
          <div className="agenda-right-panel-head">
            <div>
              <strong>{t("Mi día")}</strong>
              <span>{t("Próximos turnos y actividad")}</span>
            </div>
            <button
              type="button"
              className="agenda-right-panel-close-btn"
              onClick={() => setRightRailCollapsed(true)}
              aria-label={t("Ocultar panel lateral")}
              title={t("Ocultar panel lateral")}
            >
              <span aria-hidden="true">›</span>
              <em>Ocultar</em>
            </button>
          </div>
          <details className="agenda-side-card agenda-side-calendar"><summary>{t("Calendario del mes")}</summary>
            <MiniMonth
              currentMonthDate={currentMonthDate}
              selectedDate={selectedDate}
              onSelectDate={(d) => {
                setSelectedDate(d);
                setCurrentMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
              }}
              onPrevMonth={() => setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              onNextMonth={() => setCurrentMonthDate((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            />
          </details>

          <div className="agenda-side-card">
            <h4>Próximas reservas</h4>
            {webUpcomingEvents.length ? (
              <div className="agenda-next-list">
                {webUpcomingEvents.map((ev) => {
                  const raw = getBookingRawFromCalendarEvent(ev);
                  const start = ev?.start ? new Date(ev.start) : null;
                  return (
                    <button type="button" key={ev.id || `${ev.start}-${ev.title}`} onClick={() => handleEventClick({ event: ev })}>
                      <span>{start ? formatAgendaSideTime(start) : ""}</span>
                      <strong>{getBookingClientName(raw)}</strong>
                      <em>{getBookingServiceName(raw, ev?.title || "Reserva")}</em>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="agenda-empty-side">{t("Sin próximas reservas.")}</p>
            )}
          </div>

          <div className="agenda-side-card">
            <h4>{t("Resumen del día")}</h4>
            <div className="agenda-day-summary">
              <span>{t("Reservas")}<strong>{webDayEvents.length}</strong></span>
              <span>Trabajadores <strong>{(effectiveCalendarResources || []).length || serviceWorkerEmployees.length || employees.length || 0}</strong></span>
            </div>
            {webMostBookedServices.length ? (
              <div className="agenda-services-mini">
                {webMostBookedServices.map(([name, count]) => (
                  <span key={name}>{name}<b>{count}</b></span>
                ))}
              </div>
            ) : (
              null
            )}
          </div>
        </aside>
      ) : null}

      </div>

      {/* ✅ Native bottom tabs (fixed) with safe-area + android navbar inset */}
      {false ? (
        <div
          className="native-bottom-tabs"
          style={{
            // ✅ Fix eslint: no need for computed key
            "--native-bottom-inset": `${nativeTabsBottomPad}px`,
          }}
        >
          <button type="button" className={`native-tab ${activeTab === "agenda" ? "is-active" : ""}`} onClick={() => handleAdminSidebarTab("agenda")}>
            Agenda
          </button>
          <button type="button" className={`native-tab ${activeTab === "pendings" ? "is-active" : ""}`} onClick={() => handleAdminSidebarTab("pendings")}>{t("Reservas")}{effectivePendingBadgeCount > 0 ? <span className="native-tab-badge">{effectivePendingBadgeCount}</span> : null}
          </button>
          {canUseUnifiedFinanceShortcut ? (
            <button type="button" className={`native-tab ${(activeTab === "finance" || (!isWorkerView && activeTab === "analytics" && analyticsInitialView === "finance")) ? "is-active" : ""}`} onClick={() => handleAdminSidebarTab("finance")}>
              {workerPanelScope ? t("Cobros") : "Caja"}
            </button>
          ) : null}
          {!workerPanelScope ? (
            <button type="button" className={`native-tab ${activeTab === "clients" ? "is-active" : ""}`} onClick={() => handleAdminSidebarTab("clients")}>{t("Clientes")}</button>
          ) : null}
          {!workerPanelScope ? (
            <button type="button" className={`native-tab ${activeTab === "settings" ? "is-active" : ""}`} onClick={() => handleAdminSidebarTab("settings")}>
              Más
            </button>
          ) : null}
        </div>
      ) : null}




      {(isNativeRuntime || viewportWidth < 980) && unifiedAgendaMenuItems.length > 1 ? (
        <BusinessMobileNavigation
          groups={adminSidebarDesktopGroups.map(group => ({ ...group, items: group.items.filter(item => unifiedAgendaMenuItems.some(allowed => allowed.key === item.key)) }))}
          currentLabel={unifiedAgendaMenuItems.find(adminSidebarItemIsActive)?.label || "Agenda"}
          open={mobileToolsOpen}
          onOpenChange={setMobileToolsOpen}
          onSelect={handleAdminSidebarItem}
          isActive={adminSidebarItemIsActive}
          renderIcon={name => <LoopAdminPanelIcon name={name} />}
        />
      ) : null}

      {smartCreateNotice ? (
        <div className="admin-smart-create-toast">
          <strong>{smartCreateNotice.title}</strong>
          <span>{smartCreateNotice.message}</span>
        </div>
      ) : null}
      {/* LOOP AGENDA MADRE CLEANUP D2 V73 — removed disabled inline smart-create modal. BookingCreateModal is the live create flow. */}

      <EventViewModal
        open={Boolean(eventModalBooking)}
        onClose={() => setEventModalBooking(null)}
        booking={eventModalBooking}
        employees={bookingCreateEmployees}
        services={services}
        existingEvents={mergeCalendarEventsUnique(visibleAllEvents || [], visibleMonthRangeEvents || [])}
        existingBookings={visibleBookings}
        businessId={activeBusinessId}
        onAccept={() => eventModalBooking && handleAcceptBooking(eventModalBooking)}
        onReject={() => eventModalBooking && handleRejectBooking(eventModalBooking)}
        onCancelBooking={() => eventModalBooking && handleCancelBooking(eventModalBooking)}
        onStatusChange={(nextStatus) => eventModalBooking && updateBookingStatusWorkflow(eventModalBooking, nextStatus, "detail_modal")}
        onRefresh={()=>{setReloadBookingsKey(k=>k+1);setEventModalBooking(null);}}
        onPaymentSave={(paymentPayload) => eventModalBooking && updateBookingPayment(eventModalBooking, paymentPayload)}
        onProductSalesSave={(salesPayload) => eventModalBooking && updateBookingProductSales(eventModalBooking, salesPayload)}
        onReschedule={(reschedulePayload) => eventModalBooking && handleRescheduleBooking(eventModalBooking, reschedulePayload)}
        rescheduleSaving={rescheduleSaving}
        paymentSaving={statusWorkflowSaving}
        productSalesSaving={statusWorkflowSaving}
        statusWorkflowSaving={statusWorkflowSaving}
        statusFlowItems={ADMIN_BOOKING_STATUS_FLOW}
        clientHistory={eventModalClientHistory}
        permissions={actorBusinessPermissions}
        isOwnerOrAdmin={ownerOrAdmin}
        businessSettings={loopBusinessSettings}
        onEdit={() => {
          openEditForBooking(eventModalBooking);
          setEventModalBooking(null);
        }}
      />

      {sessionReceipt && <div className="session-receipt" role="status"><strong>{t("Sesión") + " "}{sessionReceipt.sessionNumber}{" " + t("de") + " "}{sessionReceipt.sessionTotal}{" " + t("agendada")}</strong><button type="button" onClick={()=>setSessionReceiptOpen(true)}>{t("Ver paquete")}</button><button type="button" aria-label={t("Cerrar confirmación")} onClick={()=>{setSessionReceiptOpen(false);setSessionReceipt(null);}}>×</button></div>}
      {sessionReceiptOpen && sessionReceipt && <dialog className="session-receipt-dialog" ref={node=>{if(node && !node.open)node.showModal();}} onCancel={()=>setSessionReceiptOpen(false)} aria-label={t("Paquete del cliente")}><button autoFocus type="button" onClick={()=>setSessionReceiptOpen(false)}>{t("Cerrar")}</button><CustomerSessionPrograms businessId={activeBusinessId} customerId={sessionReceipt.customerId}/></dialog>}
      {statusWorkflowNotice ? (
        <div className="admin-status-workflow-toast" role="status">
          <strong>{statusWorkflowNotice.title}</strong>
          <span>{statusWorkflowNotice.message}</span>
        </div>
      ) : null}

      {eventModalBooking && canViewCustomers ? (
        <div className="admin-client-history-panel" role="complementary" aria-label={t("Historial del cliente")}>
          <div className="admin-client-history-head">
            <span>{t("Historial del cliente")}</span>
            <strong>{eventModalClientHistory.customerName}</strong>
            <small>{eventModalClientHistory.lastVisitLabel} · {eventModalClientHistory.lastVisitSub}</small>
          </div>
          <div className="admin-client-history-stats">
            <div><span>{t("Reservas")}</span><strong>{eventModalClientHistory.totalVisits}</strong></div>
            <div><span>Completadas</span><strong>{eventModalClientHistory.completed}</strong></div>
            <div><span>{t("No asistió")}</span><strong>{eventModalClientHistory.noShow}</strong></div>
            <div><span>Total</span><strong>{eventModalClientHistory.totalSpentLabel}</strong></div>
          </div>
          <div className="admin-client-history-body">
            <div className="admin-client-history-favorite">
              <span>{t("Servicio más frecuente")}</span>
              <strong>{eventModalClientHistory.favoriteService}</strong>
              {eventModalClientHistory.upcoming ? <em>{eventModalClientHistory.upcoming}{" " + t("próxima reserva")}</em> : <em>{t("Sin próximas reservas visibles")}</em>}
            </div>
            <div className="admin-client-history-recent">
              {eventModalClientHistory.recent.length ? eventModalClientHistory.recent.map((item) => (
                <div key={item.id}>
                  <strong>{item.dateLabel}</strong>
                  <span>{item.service} · {item.status}{item.price ? ` · $${item.price % 1 === 0 ? item.price.toFixed(0) : item.price.toFixed(2)}` : ""}</span>
                </div>
              )) : <div><strong>{t("Primera vez")}</strong><span>{t("No hay visitas anteriores visibles.")}</span></div>}
            </div>
          </div>
          {eventModalClientHistory.notes.length ? (
            <div className="admin-client-history-notes">
              <span>{t("Notas recientes")}</span>
              {eventModalClientHistory.notes.map((note, idx) => <em key={`${idx}-${note}`}>{note}</em>)}
            </div>
          ) : null}
        </div>
      ) : null}

      {communicationNotice ? (
        <div className="admin-communication-toast" role="status">
          <strong>{communicationNotice.title}</strong>
          <span>{communicationNotice.message}</span>
        </div>
      ) : null}

      {eventModalBooking && canSendWhatsApp ? (
        <div className="admin-communication-panel" role="complementary" aria-label={t("Comunicación con cliente")}>
          <div className="admin-communication-head">
            <div>
              <span>{t("Comunicación")}</span>
              <strong>{t("Mensajes al cliente")}</strong>
              <small>{t("WhatsApp queda en standby. Por ahora puedes copiar mensajes o preparar email.")}</small>
            </div>
            <em className={adminCommunicationEmail(eventModalBooking) ? "is-ready" : "is-missing"}>
              {adminCommunicationEmail(eventModalBooking) ? "Email disponible" : "Falta email"}
            </em>
          </div>
          <div className="admin-communication-contact">
            <div><span>{t("Email")}</span><strong>{adminCommunicationEmail(eventModalBooking) || "No registrado"}</strong></div>
            <div><span>{t("Teléfono")}</span><strong>{adminCommunicationPhone(eventModalBooking) || "No registrado"}</strong></div>
          </div>
          <div className="admin-communication-actions">
            {eventModalCommunicationTemplates.map((tpl) => (
              <div className="admin-communication-template" key={tpl.id}>
                <div>
                  <strong>{tpl.label}</strong>
                  <span>{tpl.helper}</span>
                </div>
                <div>
                  <button type="button" disabled={communicationSaving} onClick={() => copyBookingCommunicationMessage(eventModalBooking, tpl)}>{t("Copiar")}</button>
                  <button type="button" disabled={communicationSaving || !adminCommunicationEmail(eventModalBooking)} onClick={() => openBookingEmailDraft(eventModalBooking, tpl)}>{t("Email")}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {eventModalBooking && canChangeStatus ? (
        <div className="admin-status-workflow-panel" role="group" aria-label={t("Cambiar estado de reserva")}>
          <div className="admin-status-workflow-copy">
            <span>{t("Flujo de estado")}</span>
            <strong>{adminStatusFlowMeta(adminStatusFlowCurrent(eventModalBooking)).label}</strong>
          </div>
          <div className="admin-status-workflow-actions">
            {ADMIN_BOOKING_STATUS_FLOW.filter((item) => ["pending", "confirmed", "in_progress", "completed", "no_show", "cancelled"].includes(item.value)).map((item) => (
              <button
                type="button"
                key={item.value}
                className={`status-${item.tone} ${adminStatusFlowCurrent(eventModalBooking) === item.value ? "is-active" : ""}`}
                disabled={statusWorkflowSaving}
                onClick={() => updateBookingStatusWorkflow(eventModalBooking, item.value, "quick_status_panel")}
                title={item.helper}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}


      {manualEditNotice ? (
        <div className="admin-manual-toast" role="status">
          <strong>{manualEditNotice.title}</strong>
          <span>{manualEditNotice.message}</span>
        </div>
      ) : null}

      {manualEditBooking && manualEditDraft ? (
        <div className="admin-manual-editor" role="dialog" aria-modal="true" aria-label={t("Editor manual de reserva")}>
          <button type="button" className="admin-manual-backdrop" aria-label={t("Cerrar editor")} onClick={closeManualEdit} />
          <div className="admin-manual-panel">
            <div className="admin-manual-head">
              <div>
                <span>{t("Editor manual")}</span>
                <strong>{manualEditDraft.customerName || getBookingClientName(manualEditBooking)}</strong>
                <small>{t("Edita datos operativos sin salir de la agenda.")}</small>
              </div>
              <button type="button" onClick={closeManualEdit} aria-label={t("Cerrar")}>×</button>
            </div>
            <div className="admin-manual-status-flow">
              {ADMIN_BOOKING_STATUS_FLOW.filter((item) => ["pending", "confirmed", "in_progress", "completed", "no_show", "cancelled"].includes(item.value)).map((item) => (
                <button
                  type="button"
                  key={item.value}
                  className={`status-${item.tone} ${adminStatusFlowNormalize(manualEditDraft.status) === item.value ? "is-active" : ""}`}
                  onClick={() => updateManualEditDraft("status", item.value)}
                >
                  <strong>{item.label}</strong>
                  <span>{item.helper}</span>
                </button>
              ))}
            </div>
            <div className="admin-manual-grid">
              <label className="wide"><span>{t("Cliente")}</span><input value={manualEditDraft.customerName} onChange={(e) => updateManualEditDraft("customerName", e.target.value)} /></label>
              <label><span>{t("Teléfono")}</span><input value={manualEditDraft.customerPhone} onChange={(e) => updateManualEditDraft("customerPhone", e.target.value)} /></label>
              <label><span>{t("Email")}</span><input value={manualEditDraft.customerEmail} onChange={(e) => updateManualEditDraft("customerEmail", e.target.value)} /></label>
              <label><span>{t("Fecha")}</span><input type="date" value={manualEditDraft.date} onChange={(e) => updateManualEditDraft("date", e.target.value)} /></label>
              <label><span>{t("Hora")}</span><input type="time" step="900" value={manualEditDraft.startTime} onChange={(e) => updateManualEditDraft("startTime", e.target.value)} /></label>
              <label><span>{t("Duración")}</span><select value={manualEditDraft.duration} onChange={(e) => updateManualEditDraft("duration", e.target.value)}><option value="15">15 min</option><option value="30">30 min</option><option value="45">45 min</option><option value="60">1 hora</option><option value="90">1h 30m</option><option value="120">2 horas</option><option value="180">3 horas</option><option value="240">4 horas</option></select></label>
              <label><span>{t("Estado")}</span><select value={manualEditDraft.status} onChange={(e) => updateManualEditDraft("status", e.target.value)}><option value="pending">{t("Pendiente")}</option><option value="confirmed">{t("Confirmada")}</option><option value="in_progress">En progreso</option><option value="completed">{t("Completada")}</option><option value="no_show">{t("No asistió")}</option><option value="cancelled">{t("Cancelada")}</option><option value="rejected">Rechazada</option></select></label>
              <label className="wide"><span>Servicio</span><select value={manualEditDraft.serviceId} onChange={(e) => updateManualEditDraft("serviceId", e.target.value)}><option value="">{t("Sin servicio")}</option>{services.map((s) => <option key={s.id} value={s.id}>{s.name || s.title || s.id}</option>)}</select></label>
              <label className="wide"><span>{t("Trabajador")}</span><select value={manualEditDraft.employeeId} onChange={(e) => updateManualEditDraft("employeeId", e.target.value)}><option value="">{t("Sin trabajador")}</option>{employees.map((w) => <option key={w.id} value={w.id}>{w.name || w.displayName || t("Trabajador")}</option>)}</select></label>
              <label><span>Total</span><input inputMode="decimal" value={manualEditDraft.totalPrice} onChange={(e) => updateManualEditDraft("totalPrice", e.target.value)} placeholder="0.00" /></label>
              <label className="wide notes"><span>{t("Notas internas")}</span><textarea value={manualEditDraft.notes} onChange={(e) => updateManualEditDraft("notes", e.target.value)} rows={3} placeholder={t("Notas para el equipo")} /></label>
            </div>
            <div className="admin-manual-actions">
              <button type="button" onClick={closeManualEdit}>{t("Cancelar")}</button>
              <button type="button" className="save" disabled={manualEditSaving} onClick={saveManualEditBooking}>{manualEditSaving ? t("Guardando...") : t("Guardar cambios")}</button>
            </div>
          </div>
        </div>
      ) : null}

      <BookingCreateModal
        key={editBooking?.id ? `edit-${editBooking.id}` : `create-${createModalDate ? new Date(createModalDate).getTime() : "new"}-${createModalResourceId || "none"}`}
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditBooking(null);
          setCreateModalInitialTime(null);
        }}
        date={createModalDate}
        initialTime={createModalInitialTime}
        defaultResourceId={String(createModalResourceId || "").trim() === "__loop_company_agenda__" ? null : (createModalResourceId || (!ownerOrAdmin ? (bookingCreateEmployees[0]?.id || bookingCreateEmployees[0]?.employeeId || null) : null))}
        employees={bookingCreateEmployees}
        services={services}
        existingEvents={mergeCalendarEventsUnique(visibleAllEvents || [], visibleMonthRangeEvents || [])}
        existingBookings={visibleBookings}
        businessId={activeBusinessId}
        businessDoc={businessDoc}
        isEventBusiness={isEventBusinessAdmin}
        permissions={actorBusinessPermissions}
        isOwnerOrAdmin={ownerOrAdmin}
        businessSettings={loopBusinessSettings}
        calendarSettings={effectiveAgendaSettings}
        initialBooking={editBooking || sessionBookingDraft}
        onCreate={async (payload) => {
          if (editBooking && editBooking.id) {
            await onCreateOrUpdateBooking({
              id: editBooking.id,
              previousStatus: editBooking.status || editBooking.confirmationStatus || null,
              previousConfirmationStatus: editBooking.confirmationStatus || editBooking.status || null,
              ...payload,
            });
            closeCreateModalAfterSave();
          } else {
            await onCreateOrUpdateBooking({ id: null, ...payload });
            closeCreateModalAfterSave();
          }
        }}
      />
    </div>
  );
}

